import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { useState, useEffect, useCallback, useRef } from "react";

const VENDOR_URL = "vendor.denizzy.com";

const openVendorSite = async (e) => {
  if (e) e.preventDefault();
  if (Capacitor.isNativePlatform()) {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url: `https://${VENDOR_URL}` });
    return;
  }
  const isTWA = document.referrer.includes("android-app://");
  if (isTWA) {
    window.location.href = 
      `intent://${VENDOR_URL}/#Intent;scheme=https;package=com.android.chrome;action=android.intent.action.VIEW;end`;
  } else {
    window.open(`https://${VENDOR_URL}`, "_blank");
  }
};
const C = {
  deep:"#0A2540",ocean:"#1A5F7A",wave:"#2E9BB5",foam:"#7ECFDF",
  saffron:"#F4A61D",health:"#1A7A5E",fresh:"#E85D26",
  ink:"#0D1B2A",slate:"#4A6580",mist:"#A8BDD0",
  pearl:"#F0F5F8",bg:"#F4F8FA",white:"#FFFFFF",
  success:"#16A34A",danger:"#DC2626",warning:"#D97706",
};

// ─── API Layer ────────────────────────────────────────────────
const BASE = "https://denizzy.onrender.com/api";

// ═══════════════════════════════════════════════════════════════
// AUTOCOMPLETE DATA — paste at top of App.js after imports
// ═══════════════════════════════════════════════════════════════

const MEDICINE_AUTOCOMPLETE = [
  // Pain & Fever
  "Paracetamol 500mg","Paracetamol 650mg","Dolo 650mg","Crocin 500mg","Crocin 650mg",
  "Combiflam","Ibuprofen 400mg","Ibuprofen 600mg","Brufen 400mg",
  "Aspirin 75mg","Aspirin 150mg","Ecosprin 75mg","Ecosprin 150mg",
  "Diclofenac 50mg","Voveran 50mg","Voltaren Gel",
  "Nimesulide 100mg","Nise 100mg","Nimica 100mg",
  "Mefenamic Acid 500mg","Meftal 500mg","Meftal Spas",
  "Naproxen 250mg","Naproxen 500mg",
  "Tramadol 50mg","Ultracet","Ketorolac 10mg",
  "Etoricoxib 60mg","Etoricoxib 90mg","Arcoxia 90mg",
  "Celecoxib 100mg","Celecoxib 200mg","Celebrex 200mg",
  "Aceclofenac 100mg","Aceclo 100mg",

  // Antibiotics
  "Amoxicillin 250mg","Amoxicillin 500mg","Mox 500mg","Novamox 500mg",
  "Augmentin 375mg","Augmentin 625mg","Amoxyclav 625mg",
  "Azithromycin 250mg","Azithromycin 500mg","Azee 500mg","Zithromax 500mg",
  "Ciprofloxacin 250mg","Ciprofloxacin 500mg","Cifran 500mg",
  "Levofloxacin 250mg","Levofloxacin 500mg","Levoquin 500mg",
  "Metronidazole 200mg","Metronidazole 400mg","Flagyl 400mg",
  "Doxycycline 100mg","Doxt-S 100mg",
  "Cefixime 200mg","Taxim-O 200mg",
  "Cephalexin 250mg","Cephalexin 500mg",
  "Clarithromycin 250mg","Clarithromycin 500mg","Claribid 500mg",
  "Nitrofurantoin 100mg","Amikacin 500mg",
  "Norfloxacin 400mg","Norflox 400mg",
  "Ofloxacin 200mg","Oflox 200mg",
  "Cefpodoxime 100mg","Cefpodoxime 200mg",
  "Cefuroxime 250mg","Cefuroxime 500mg",
  "Clindamycin 150mg","Clindamycin 300mg",
  "Linezolid 600mg","Zyvox 600mg",

  // Antifungal
  "Fluconazole 150mg","Fluka 150mg","Forcan 150mg",
  "Itraconazole 100mg","Canditral 100mg",
  "Clotrimazole Cream","Candid B Cream",
  "Terbinafine 250mg","Terbicip 250mg",
  "Ketoconazole Shampoo","Nizoral Shampoo",
  "Voriconazole 200mg",

  // Antiviral
  "Acyclovir 200mg","Acyclovir 400mg","Zovirax 200mg",
  "Oseltamivir 75mg","Tamiflu 75mg",
  "Valacyclovir 500mg","Valacyclovir 1000mg",

  // Antacids & GI
  "Omeprazole 20mg","Omez 20mg",
  "Pantoprazole 40mg","Pan 40mg","Pantocid 40mg",
  "Rabeprazole 20mg","Razo 20mg",
  "Esomeprazole 20mg","Nexium 20mg",
  "Ranitidine 150mg","Rantac 150mg",
  "Famotidine 20mg","Famotin 20mg",
  "Domperidone 10mg","Domstal 10mg",
  "Metoclopramide 10mg","Perinorm 10mg",
  "Ondansetron 4mg","Ondansetron 8mg","Emeset 4mg",
  "Dicyclomine 10mg","Cyclopam",
  "Loperamide 2mg","Imodium 2mg",
  "Tinidazole 500mg","Tiniba 500mg",
  "Sucralfate 1g","Bisacodyl 5mg","Dulcolax 5mg",
  "Lactulose Syrup","Isabgol Husk",
  "Gelusil Syrup","Digene Gel","Antacid Syrup",
  "Drotaverine 40mg","No-Spa 40mg",
  "Trimebutine 100mg",

  // Diabetes
  "Metformin 500mg","Metformin 850mg","Metformin 1000mg",
  "Glycomet 500mg","Glucophage 500mg",
  "Glimepiride 1mg","Glimepiride 2mg","Amaryl 2mg",
  "Glibenclamide 2.5mg","Glibenclamide 5mg",
  "Sitagliptin 100mg","Januvia 100mg",
  "Vildagliptin 50mg","Galvus 50mg",
  "Dapagliflozin 10mg","Forxiga 10mg",
  "Empagliflozin 10mg","Jardiance 10mg",
  "Canagliflozin 100mg","Invokana 100mg",
  "Pioglitazone 15mg","Pioglitazone 30mg",
  "Teneligliptin 20mg","Tenepure 20mg",
  "Linagliptin 5mg","Trajenta 5mg",
  "Insulin Glargine","Lantus Solostar",
  "Insulin Human Mixtard","Mixtard 30",
  "Insulin Aspart","NovoRapid",
  "Insulin Regular","Actrapid",

  // Blood Pressure / Cardiology
  "Amlodipine 5mg","Amlodipine 10mg","Amlong 5mg","Stamlo 5mg",
  "Atenolol 25mg","Atenolol 50mg","Tenormin 50mg",
  "Telmisartan 40mg","Telmisartan 80mg","Telma 40mg",
  "Losartan 25mg","Losartan 50mg","Losar 50mg",
  "Ramipril 2.5mg","Ramipril 5mg","Cardace 2.5mg",
  "Enalapril 5mg","Enalapril 10mg",
  "Olmesartan 20mg","Benitec 20mg",
  "Metoprolol 25mg","Metoprolol 50mg","Metolar 25mg",
  "Carvedilol 3.125mg","Carvedilol 6.25mg",
  "Bisoprolol 2.5mg","Bisoprolol 5mg",
  "Nebivolol 5mg",
  "Furosemide 40mg","Lasix 40mg",
  "Hydrochlorothiazide 12.5mg","Hydrochlorothiazide 25mg",
  "Spironolactone 25mg","Aldactone 25mg",
  "Indapamide 1.5mg","Indapamide 2.5mg",
  "Clopidogrel 75mg","Clopilet 75mg","Plavix 75mg",
  "Rosuvastatin 10mg","Rosuvastatin 20mg","Rosulip 10mg",
  "Atorvastatin 10mg","Atorvastatin 20mg","Atorvastatin 40mg","Atorva 20mg",
  "Simvastatin 20mg","Simvastatin 40mg",
  "Digoxin 0.25mg","Lanoxin 0.25mg",
  "Warfarin 1mg","Warfarin 2mg","Warf 2mg",
  "Rivaroxaban 10mg","Rivaroxaban 20mg","Xarelto 20mg",
  "Apixaban 2.5mg","Apixaban 5mg","Eliquis 5mg",
  "Dabigatran 110mg","Dabigatran 150mg","Pradaxa 150mg",
  "Nitroglycerine Tablet","Sorbitrate 5mg",
  "Isosorbide Mononitrate 10mg","Monotrate 10mg",
  "Nifedipine 10mg","Nifedipine 30mg",
  "Ivabradine 5mg","Ivabradine 7.5mg",
  "Amiodarone 100mg","Amiodarone 200mg",

  // Thyroid
  "Levothyroxine 25mcg","Levothyroxine 50mcg","Levothyroxine 75mcg","Levothyroxine 100mcg",
  "Eltroxin 50mcg","Thyronorm 50mcg","Thyronorm 100mcg",
  "Carbimazole 5mg","Neo-Mercazole 5mg",
  "Propylthiouracil 50mg",

  // Respiratory / Allergy / Asthma
  "Salbutamol Inhaler","Asthalin Inhaler","Ventolin Inhaler",
  "Salbutamol 2mg","Salbutamol 4mg",
  "Budesonide Inhaler","Budecort Inhaler",
  "Formoterol + Budesonide Inhaler","Foracort Inhaler",
  "Montelukast 10mg","Montair 10mg","Singulair 10mg",
  "Fexofenadine 120mg","Fexofenadine 180mg","Allegra 120mg","Allegra 180mg",
  "Cetirizine 10mg","Cetriz 10mg","Okacet 10mg",
  "Levocetirizine 5mg","Lcz 5mg","L-Cet 5mg",
  "Loratadine 10mg","Lorfast 10mg",
  "Chlorpheniramine 4mg","Avil 25mg",
  "Dextromethorphan Syrup","Benadryl Cough Syrup",
  "Ambroxol 30mg","Mucolite 30mg",
  "Carbocisteine 375mg","Bromhexine 8mg",
  "Ipratropium Inhaler","Atrovent Inhaler",
  "Tiotropium Inhaler","Spiriva Inhaler",
  "Theophylline 100mg","Theophylline 200mg",
  "Prednisolone 5mg","Prednisolone 10mg","Wysolone 5mg",
  "Mometasone Inhaler","Fluticasone Inhaler",
  "Salmeterol + Fluticasone Inhaler","Seretide Inhaler",

  // Vitamins & Supplements
  "Vitamin D3 60000 IU","Vitamin D3 1000 IU","Uprise D3","Calcirol",
  "Vitamin B12 500mcg","Methylcobalamin 500mcg","Neurobion Forte",
  "Vitamin C 500mg","Limcee 500mg",
  "Calcium + Vitamin D3","Shelcal 500mg","Calcimax",
  "Folic Acid 5mg","Folvite 5mg",
  "Iron 150mg","Ferrous Sulphate 200mg","Ferium XT",
  "Multivitamin Tablets","Supradyn","Becosules",
  "Zinc 20mg","Zincovit",
  "Omega 3 Fatty Acids","Cod Liver Oil",
  "Biotin 5mg","Bioten 5mg",
  "Magnesium 250mg","Mecobalamin 500mcg",
  "Vitamin E 400 IU","Evion 400mg",
  "Lycopene + Multivitamin",

  // Neurology / Psychiatry
  "Alprazolam 0.25mg","Alprazolam 0.5mg","Alprax 0.25mg",
  "Clonazepam 0.5mg","Clonazepam 1mg","Clonapax 0.5mg",
  "Diazepam 5mg","Diazepam 10mg",
  "Escitalopram 5mg","Escitalopram 10mg","Nexito 10mg",
  "Sertraline 25mg","Sertraline 50mg","Zoloft 50mg",
  "Fluoxetine 10mg","Fluoxetine 20mg","Prozac 20mg",
  "Amitriptyline 10mg","Amitriptyline 25mg",
  "Quetiapine 25mg","Quetiapine 100mg","Seroquel 100mg",
  "Olanzapine 5mg","Olanzapine 10mg","Olimelt 5mg",
  "Risperidone 1mg","Risperidone 2mg",
  "Lithium Carbonate 300mg","Licab 300mg",
  "Phenytoin 100mg","Eptoin 100mg",
  "Carbamazepine 200mg","Tegretol 200mg",
  "Valproate 200mg","Valproate 500mg","Depakote 200mg",
  "Levetiracetam 250mg","Levetiracetam 500mg","Levera 500mg",
  "Gabapentin 100mg","Gabapentin 300mg","Gabapin 300mg",
  "Pregabalin 75mg","Pregabalin 150mg","Lyrica 75mg",
  "Donepezil 5mg","Aricept 5mg",
  "Zolpidem 5mg","Zolpidem 10mg",
  "Melatonin 3mg","Melatonin 5mg",
  "Betahistine 8mg","Betahistine 16mg","Vertin 8mg",
  "Cinnarizine 25mg","Stugeron 25mg",
  "Hydroxyzine 10mg","Atarax 10mg",
  "Promethazine 25mg","Phenergan 25mg",
  "Dimenhydrinate 50mg",
  "Bupropion 150mg","Wellbutrin 150mg",
  "Venlafaxine 37.5mg","Venlafaxine 75mg",
  "Duloxetine 30mg","Duloxetine 60mg",
  "Mirtazapine 15mg","Mirtazapine 30mg",

  // Women's Health / Hormones
  "Oral Contraceptive Pills","Mala-D","Yasmin","Femilon",
  "Progesterone 100mg","Progesterone 200mg","Susten 200mg",
  "Norethisterone 5mg","Primolut-N 5mg",
  "Emergency Contraceptive","I-Pill","Unwanted 72",
  "Clomiphene 50mg","Siphene 50mg",
  "Estradiol Valerate 2mg",
  "Medroxyprogesterone 10mg","Provera 10mg",
  "Dydrogesterone 10mg","Duphaston 10mg",
  "Tranexamic Acid 500mg","Pause 500mg",

  // Eye / Ear / Nose
  "Ciprofloxacin Eye Drops","Ciplox Eye Drops",
  "Tobramycin Eye Drops","Tobrex Eye Drops",
  "Dexamethasone Eye Drops","Dexacort Eye Drops",
  "Timolol Eye Drops 0.5%",
  "Latanoprost Eye Drops","Xalatan Eye Drops",
  "Artificial Tears","Refresh Tears","Systane Eye Drops",
  "Ofloxacin Ear Drops","Gentamicin Ear Drops",
  "Oxymetazoline Nasal Spray","Nasivion Spray",
  "Fluticasone Nasal Spray","Flonase Spray",
  "Mometasone Nasal Spray","Nasonex Spray",
  "Xylometazoline Nasal Spray","Otrivin Spray",
  "Beclomethasone Nasal Spray",

  // Skin / Dermatology
  "Hydrocortisone Cream 1%","Betamethasone Cream","Betnovate Cream",
  "Clobetasol Cream 0.05%","Tenovate Cream",
  "Fusidic Acid Cream","Fucidin Cream",
  "Mupirocin Ointment 2%","Bactroban Ointment",
  "Tretinoin Cream 0.025%","Retin-A Cream",
  "Adapalene Gel 0.1%","Deriva Gel",
  "Benzoyl Peroxide 2.5%","Persol AC Gel",
  "Calamine Lotion","Lacto Calamine",
  "Permethrin Cream 5%","Scabizma Cream",
  "Isotretinoin 10mg","Sotret 10mg","Accutane 10mg",
  "Salicylic Acid Gel","Clindamycin + Nicotinamide Gel",
  "Tacrolimus Ointment 0.03%","Tacrolimus Ointment 0.1%",

  // Ortho / Muscle Relaxants
  "Thiocolchicoside 4mg","Thiocolchicoside 8mg","Myospaz",
  "Tizanidine 2mg","Sirdalud 2mg",
  "Baclofen 10mg","Lioresal 10mg",
  "Methocarbamol 750mg",
  "Glucosamine 500mg","Glucosamine + Chondroitin",
  "Calcium + Glucosamine","Shelcal HD",
  "Colchicine 0.5mg","Colchicine 1mg",
  "Allopurinol 100mg","Allopurinol 300mg","Zyloric 100mg",
  "Febuxostat 40mg","Febustat 40mg",

  // Urology
  "Tamsulosin 0.4mg","Urimax 0.4mg",
  "Finasteride 1mg","Finasteride 5mg","Propecia 1mg",
  "Alfuzosin 10mg","Xatral 10mg",
  "Sildenafil 50mg","Sildenafil 100mg","Viagra 50mg",
  "Tadalafil 5mg","Tadalafil 10mg","Cialis 10mg",
  "Oxybutynin 5mg","Solifenacin 5mg","Vesicare 5mg",
  "Dutasteride 0.5mg","Avodart 0.5mg",

  // Immunosuppressants / Rheumatology
  "Hydroxychloroquine 200mg","Plaquenil 200mg","HCQS 200mg",
  "Methotrexate 2.5mg","Folitrax 2.5mg",
  "Sulfasalazine 500mg","Salazopyrin 500mg",
  "Azathioprine 50mg","Imuran 50mg",
  "Methylprednisolone 4mg","Medrol 4mg",
  "Deflazacort 6mg","Defcort 6mg",
  "Leflunomide 10mg","Arava 10mg",

  // Antiparasitic / Deworming
  "Albendazole 400mg","Zentel 400mg","Bandy 400mg",
  "Mebendazole 100mg","Vermox 100mg",
  "Ivermectin 3mg","Ivermectin 6mg",
  "Praziquantel 600mg",
  "Chloroquine 250mg","Lariago 250mg",

  // Miscellaneous
  "ORS Sachet","Electral ORS","Oral Rehydration Salts",
  "Activated Charcoal 250mg",
  "Heparin Injection",
  "Enoxaparin 40mg","Clexane 40mg",
  "Vitamin K 10mg",
  "Dexamethasone 4mg",
  "Dexamethasone 0.5mg",
  "Betamethasone Injection",
  "Dimenhydrinate Injection",
];

const LAB_TESTS_AUTOCOMPLETE = [
  // Blood Count
  "CBC - Complete Blood Count",
  "Haemoglobin",
  "Platelet Count",
  "WBC - White Blood Cell Count",
  "Differential Count",
  "Peripheral Blood Smear",
  "Reticulocyte Count",
  "ESR - Erythrocyte Sedimentation Rate",
  "Blood Group & Rh Type",
  "G6PD Deficiency",
  "Sickling Test",

  // Sugar / Diabetes
  "Blood Sugar Fasting",
  "Blood Sugar Post Prandial (PP)",
  "Random Blood Sugar",
  "HbA1c - Glycated Haemoglobin",
  "Insulin Fasting",
  "C-Peptide",
  "Glucose Tolerance Test (GTT)",

  // Lipids / Cardiac Risk
  "Lipid Profile",
  "Total Cholesterol",
  "HDL Cholesterol",
  "LDL Cholesterol",
  "Triglycerides",
  "VLDL Cholesterol",
  "Homocysteine",
  "Lipoprotein (a)",
  "hs-CRP - High Sensitivity CRP",
  "Troponin I",
  "Troponin T",
  "CK-MB",
  "CPK Total",
  "BNP - Brain Natriuretic Peptide",
  "NT-proBNP",
  "LDH - Lactate Dehydrogenase",

  // Liver
  "Liver Function Test (LFT)",
  "SGOT (AST)",
  "SGPT (ALT)",
  "GGTP",
  "Alkaline Phosphatase (ALP)",
  "Bilirubin Total",
  "Bilirubin Direct",
  "Bilirubin Indirect",
  "Protein Total",
  "Albumin",
  "Globulin",
  "A:G Ratio",
  "Prothrombin Time (PT/INR)",
  "APTT",

  // Kidney
  "Kidney Function Test (KFT)",
  "Serum Creatinine",
  "Blood Urea Nitrogen (BUN)",
  "Uric Acid",
  "Electrolytes",
  "Sodium",
  "Potassium",
  "Chloride",
  "Bicarbonate",
  "Calcium",
  "Phosphorus",
  "Magnesium",

  // Thyroid
  "Thyroid Profile (T3, T4, TSH)",
  "TSH - Thyroid Stimulating Hormone",
  "T3 - Triiodothyronine",
  "T4 - Thyroxine",
  "Free T3 (FT3)",
  "Free T4 (FT4)",
  "Anti-TPO Antibody",
  "Anti-Thyroglobulin Antibody",
  "Thyroglobulin",

  // Vitamins & Minerals
  "Vitamin D (25-OH)",
  "Vitamin B12",
  "Serum Iron",
  "TIBC - Total Iron Binding Capacity",
  "Ferritin",
  "Folate / Folic Acid",
  "Zinc",
  "Copper",
  "Selenium",
  "Vitamin A",
  "Vitamin E",

  // Hormones
  "FSH - Follicle Stimulating Hormone",
  "LH - Luteinizing Hormone",
  "Prolactin",
  "Estradiol (E2)",
  "Progesterone",
  "Testosterone",
  "DHEA-S",
  "Cortisol",
  "PTH - Parathyroid Hormone",
  "Growth Hormone",
  "IGF-1",
  "Beta HCG - Pregnancy Test",
  "AMH - Anti Mullerian Hormone",
  "ACTH",

  // Infectious Disease
  "Dengue NS1 Antigen",
  "Dengue IgG & IgM",
  "Malaria Antigen Test",
  "Typhoid - Widal Test",
  "Typhoid IgM",
  "COVID-19 RT-PCR",
  "COVID-19 Antigen Test",
  "HIV 1 & 2 Antibody",
  "HBsAg - Hepatitis B Surface Antigen",
  "Anti HCV - Hepatitis C Antibody",
  "Hepatitis B Core Antibody (Anti-HBc)",
  "VDRL - Syphilis",
  "RPR Test",
  "Mantoux Test (TB Skin Test)",
  "TB Gold - IGRA",
  "Leptospira Antibody",
  "Chikungunya IgM",
  "Scrub Typhus IgM",
  "H. Pylori Antigen Stool Test",
  "H. Pylori Antibody Blood Test",

  // Inflammation / Autoimmune
  "CRP - C Reactive Protein",
  "RA Factor - Rheumatoid Arthritis",
  "ANA - Antinuclear Antibody",
  "ASLO - Anti Streptolysin O",
  "Anti-dsDNA",
  "Complement C3",
  "Complement C4",
  "HLA B27",
  "Total IgE",
  "Food Allergy Panel",
  "Respiratory Allergy Panel",

  // Coagulation
  "D-Dimer",
  "Fibrinogen",
  "Protein C",
  "Protein S",
  "Factor V Leiden",

  // Urine Tests
  "Urine Routine & Microscopy",
  "Urine Culture & Sensitivity",
  "Urine Protein",
  "Urine Creatinine",
  "Urine Microalbumin",
  "24 Hour Urine Protein",
  "Urine Pregnancy Test",
  "Urine Calcium",
  "Urine Uric Acid",

  // Stool Tests
  "Stool Routine & Microscopy",
  "Stool Culture & Sensitivity",
  "Stool Occult Blood",
  "H. Pylori Stool Antigen",
  "Rotavirus Antigen",

  // Cancer Markers
  "PSA - Prostate Specific Antigen",
  "CEA - Carcinoembryonic Antigen",
  "AFP - Alpha Fetoprotein",
  "CA 125",
  "CA 19-9",
  "CA 15-3",
  "Beta-2 Microglobulin",

  // Blood Culture / Microbiology
  "Blood Culture & Sensitivity",
  "Sputum Culture & Sensitivity",
  "Throat Swab Culture",
  "Wound Swab Culture",
  "Urine Culture & Sensitivity",

  // Genetic / Special
  "BRCA 1 & 2",
  "JAK2 Mutation",
  "BCR-ABL",
  "Karyotyping",
  "Fragile X Syndrome",

  // Health Packages
  "Basic Health Checkup Package",
  "Comprehensive Health Checkup Package",
  "Diabetes Profile Package",
  "Cardiac Risk Profile Package",
  "Liver Profile Package",
  "Kidney Profile Package",
  "Thyroid Profile Package",
  "Women's Health Package",
  "Men's Health Package",
  "Senior Citizens Package",
  "Pre-Employment Package",
  "PCOD Profile Package",
  "Fertility Profile Package",
  "Anemia Profile Package",
  "Pre-Marital Package",
  "Annual Health Checkup",

  // Radiology (labs that offer)
  "X-Ray Chest PA View",
  "X-Ray Spine",
  "X-Ray Knee",
  "X-Ray Hand / Wrist",
  "Ultrasound Abdomen",
  "Ultrasound Pelvis",
  "Ultrasound Thyroid",
  "Ultrasound Kidney & Bladder",
  "ECG - Electrocardiogram",
  "2D Echo - Echocardiogram",
  "Stress Test (TMT)",
  "CT Scan Head",
  "CT Scan Chest",
  "CT Scan Abdomen & Pelvis",
  "MRI Brain",
  "MRI Spine (Lumbar)",
  "MRI Spine (Cervical)",
  "MRI Knee",
  "Bone Density (DEXA Scan)",
  "Mammography",
  "PET Scan",
];

// ── Google Maps API Key ───────────────────────────────
const MAPS_KEY = "AIzaSyC7HSVuUzHHt0OVMoA8Z4z4-DHLkFH1eN4";

// ── GPS Location Detection ────────────────────────────
const detectGPSLocation = () => new Promise(async (resolve, reject) => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Geolocation } = await import("@capacitor/geolocation");
      let perm = await Geolocation.checkPermissions();
      if (perm.location !== "granted") perm = await Geolocation.requestPermissions();
      if (perm.location !== "granted") { reject("Location permission denied"); return; }
      const pos = await Geolocation.getCurrentPosition({ timeout: 10000 });
      const { latitude: lat, longitude: lng } = pos.coords;
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`);
      const data = await res.json();
      if (data.status !== "OK") { reject("Geocoding failed"); return; }
      const components = data.results[0]?.address_components || [];
      const get = (type) => components.find(c => c.types.includes(type))?.long_name || "";
      const area = get("sublocality_level_1") || get("sublocality") || get("neighborhood") || get("locality");
      const city = get("locality") || get("administrative_area_level_2");
      resolve({ area, city, lat, lng });
    } catch(e) { reject(e.message || "Location error"); }
    return;
  }
  if (!navigator.geolocation) { reject("GPS not supported"); return; }
  navigator.geolocation.getCurrentPosition(
    async (pos) => {
      try {
        const { latitude: lat, longitude: lng } = pos.coords;
        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`);
        const data = await res.json();
        if (data.status !== "OK") { reject("Geocoding failed"); return; }
        const components = data.results[0]?.address_components || [];
        const get = (type) => components.find(c => c.types.includes(type))?.long_name || "";
        const area = get("sublocality_level_1") || get("sublocality") || get("neighborhood") || get("locality");
        const city = get("locality") || get("administrative_area_level_2");
        resolve({ area, city, lat, lng });
      } catch(e) { reject(e.message); }
    },
    (err) => reject(err.message),
    { timeout: 10000, maximumAge: 60000, enableHighAccuracy: false }
  );
});
// ── Image compression — shrinks camera photo before upload ───────────────
// Camera photos are 5-10MB. This compresses to ~100KB (JPEG 60%, max 1000px)
const compressImage = (file, maxPx=1000, quality=0.6) => new Promise(resolve => {
  const img = new Image();
  const reader = new FileReader();
  reader.onload = e => {
    img.onload = () => {
      const ratio = Math.min(maxPx/img.width, maxPx/img.height, 1);
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.width  * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => resolve(blob || file), "image/jpeg", quality);
    };
    img.src = e.target.result;
  };
  reader.readAsDataURL(file);
});


const ls = { get:(k)=>{ try{return localStorage.getItem(k);}catch{return null;} }, set:(k,v)=>{ try{localStorage.setItem(k,v);}catch{} }, del:(k)=>{ try{localStorage.removeItem(k);}catch{} } };
const getToken = () => ls.get("dz_patient_token");
const saveToken = (t) => ls.set("dz_patient_token", t);
const clearToken = () => ls.del("dz_patient_token");

// Persist active request across reloads
// Per-vertical request storage
// ── Track request IDs that have already resulted in a placed order ──────────
// FIX #10: even if a stored "active request" key lingers (e.g. due to an
// in-flight quote-poll re-writing it after order placement), we never show it
// again once its _id is in this list.
const getOrderedRequestIds = () => { try { return JSON.parse(ls.get("dz_ordered_request_ids")||"[]"); } catch { return []; } };
const markRequestOrdered = (id) => {
  if (!id) return;
  try {
    const ids = getOrderedRequestIds();
    if (!ids.includes(id)) { ids.push(id); ls.set("dz_ordered_request_ids", JSON.stringify(ids.slice(-50))); }
  } catch {}
};

const getStoredRequest = (vertical) => {
  try {
    const key = vertical ? `dz_active_request_${vertical}` : "dz_active_request";
    const r = ls.get(key);
    if (!r) return null;
    const parsed = JSON.parse(r);
    // If this request has no _id (stray quoteCount-only object) or was already
    // ordered, treat it as not-active and clean up storage.
    if (!parsed?._id || getOrderedRequestIds().includes(parsed._id)) {
      ls.del(key);
      return null;
    }
    return parsed;
  } catch { return null; }
};
const storeRequest = (r) => {
  try {
    const vertical = r?.vertical || "medical";
    ls.set(`dz_active_request_${vertical}`, JSON.stringify(r));
    ls.set("dz_active_request", JSON.stringify(r)); // legacy compat
  } catch {}
};
const clearStoredRequest = (vertical) => {
  if (vertical) ls.del(`dz_active_request_${vertical}`);
  else {
    ls.del("dz_active_request");
    ls.del("dz_active_request_medical");
    ls.del("dz_active_request_lab");
  }
};

async function api(method, path, body) {
  try {
    const headers = { "Content-Type": "application/json" };
    const t = getToken();
    if (t) headers["Authorization"] = `Bearer ${t}`;
    const res = await fetch(`${BASE}${path}`, {
      method, headers,
      body: body ? JSON.stringify(body) : undefined,
    });
    return res.json();
  } catch {
    return { success: false, message: "Network error" };
  }
}


const LABS_COLOR = "#0891B2"; 
// ── Distance calculator — free, no API needed ─────────────────────────────────
const getDistanceKm = (lat1, lng1, lat2, lng2) => {
  if (!lat1||!lng1||!lat2||!lng2) return null;
  const R = 6371;
  const dLat = (lat2-lat1)*Math.PI/180;
  const dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)*Math.sin(dLat/2)+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)*Math.sin(dLng/2);
  const d = R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
  return d < 1 ? `${Math.round(d*1000)}m` : `${d.toFixed(1)}km`;
};
// Raw numeric distance in km — for sorting only
const getDistanceKmRaw = (lat1, lng1, lat2, lng2) => {
  if (!lat1||!lng1||!lat2||!lng2) return 99999;
  const R = 6371;
  const dLat = (lat2-lat1)*Math.PI/180;
  const dLng = (lng2-lng1)*Math.PI/180;
  const a = Math.sin(dLat/2)*Math.sin(dLat/2)+Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)*Math.sin(dLng/2);
  return R*2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
};
// Denizzy Labs teal — also defined near LabsLandingScreen

// ── Memorable order ID: DZ-M0047 format ─────────────────────────────────────
const formatOrderId = (id, vertical, orderNumber) => {
  if (!id) return "—";
  const prefix = vertical === "medical" ? "M" : vertical === "lab" ? "L" : "O";
  // Use sequential number if available (DZ-M-00001 format)
  if (orderNumber) return `DZ-${prefix}-${String(orderNumber).padStart(5,"0")}`;
  // Fallback to hex-based format
  const num = String(parseInt(id.slice(-4), 16) % 9000 + 1000);
  return `DZ-${prefix}${num}`;
};


// ── Reminder alarm sound + vibration ────────────────────────────────────────
// Shared AudioContext — created/unlocked on first user tap (required by Safari's
// autoplay policy). Without this, programmatic alarms fail silently on iOS Safari.
const getAudioCtx = () => {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    if (!window.__dzAudioCtx) window.__dzAudioCtx = new AudioCtx();
    // Safari suspends the context until a user gesture resumes it
    if (window.__dzAudioCtx.state === "suspended") {
      try { window.__dzAudioCtx.resume(); } catch {}
    }
    return window.__dzAudioCtx;
  } catch { return null; }
};

// Call this from a real user-gesture handler (tap anywhere) to unlock audio for Safari
const unlockAudio = () => {
  const ctx = getAudioCtx();
  if (!ctx) return;
  try {
    // Play a near-silent buffer to fully unlock the context on iOS
    const buffer = ctx.createBuffer(1, 1, 22050);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);
  } catch {}
};

const playAlarm = (times=3) => {
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    const play = (delay, freq=880, dur=0.4) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.6, ctx.currentTime + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + dur);
      osc.start(ctx.currentTime + delay);
      osc.stop(ctx.currentTime + delay + dur);
    };
    for (let i=0; i<times; i++) {
      play(i * 0.6, 880, 0.3);
      play(i * 0.6 + 0.15, 1100, 0.25);
    }
    try { if (navigator.vibrate) navigator.vibrate([400, 200, 400, 200, 400]); } catch {}
  } catch(e) { console.log("Audio error:", e); }
};

// ── Check reminders and fire alarm/notification for any due now ─────────────
const checkAndFireReminders = () => {
  const d = new Date();
  const hhmm = `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  const today = d.toISOString().split("T")[0];
  let changed = false;
  const rems = getRems().map(rem => {
    if (!rem.active) return rem;
    const alreadyFired = (rem.firedLog || {})[today]?.includes(hhmm);
    if (alreadyFired) return rem;
    // Check both scheduled times and snooze map
    const snoozeFiring = window.__dzSnoozeMap && window.__dzSnoozeMap[rem.id] === hhmm;
    if (!rem.times.includes(hhmm) && !snoozeFiring) return rem;
    if (snoozeFiring) { delete window.__dzSnoozeMap[rem.id]; }
    // Fire!
    playAlarm(3);
    const msg = rem.forSelf
      ? `💊 Time to take ${rem.medicineName}! (${rem.timing} food) — Your body needs care!`
      : `💊 Reminder: ${rem.personName} needs to take ${rem.medicineName}`;
    try {
      if (window.Notification && Notification.permission === "granted") {
        new Notification("🔔 Denizzy Reminder", { body: msg, icon: "/favicon.ico", requireInteraction: true });
      }
    } catch {}
    // Dispatch event so App shows in-app overlay while app is open
    try { window.dispatchEvent(new CustomEvent("dz-alarm", { detail: { ...rem, firedAt: hhmm } })); } catch {}
    changed = true;
    return { ...rem, firedLog: { ...(rem.firedLog||{}), [today]: [...((rem.firedLog||{})[today]||[]), hhmm] } };
  });
  if (changed) saveRems(rems);
};

// ─── City + Area helpers ─────────────────────────────────────────────────────
const getCity = () => ls.get("dz_patient_city") || null;
const getArea = () => ls.get("dz_patient_area") || null;
const saveCity = (c) => ls.set("dz_patient_city", c);
const saveArea = (a) => ls.set("dz_patient_area", a);

// Area-level coordinates (lon, lat) for accurate 5km routing
const AREA_COORDS = {
  "Hulimavu":[77.6108,12.8754],"BTM Layout":[77.6101,12.9165],"JP Nagar":[77.5784,12.9077],
  "Bannerghatta":[77.5971,12.8389],"Arekere":[77.6181,12.8666],"Gottigere":[77.6022,12.8501],
  "Begur":[77.6230,12.8600],"Jayanagar":[77.5834,12.9299],"Basavanagudi":[77.5740,12.9426],
  "Uttarahalli":[77.5541,12.8914],"Koramangala":[77.6245,12.9352],"HSR Layout":[77.6389,12.9116],
  "Indiranagar":[77.6412,12.9784],"Marathahalli":[77.7013,12.9591],"Whitefield":[77.7480,12.9698],
  "Bellandur":[77.6758,12.9259],"Sarjapur":[77.7021,12.9070],"Domlur":[77.6382,12.9607],
  "Bommanahalli":[77.6341,12.8934],"Electronic City":[77.6770,12.8399],
  "Yelahanka":[77.5963,13.1007],"Hebbal":[77.5933,13.0454],"Yeshwanthpur":[77.5352,13.0277],
  "Rajajinagar":[77.5532,12.9984],"Malleshwaram":[77.5704,13.0039],
  "Silk Board":[77.6261,12.9176],"Madiwala":[77.6213,12.9218],
};



// ═══════════════════════════════════════════════════════════════════════════════
// LOCATION PICKER — GPS + Search + Map (India-wide)
// ═══════════════════════════════════════════════════════════════════════════════
function LocationPickerScreen({ onSelect, currentCity, currentArea, isChanging }) {
  const [mode, setMode] = useState("search");
  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapCoords, setMapCoords] = useState({ lat:20.5937, lng:78.9629 });
  const [mapAddress, setMapAddress] = useState("");

  // Search using Google Geocoding API
  useEffect(()=>{
    if (search.length < 3) { setSuggestions([]); return; }
    const timer = setTimeout(async()=>{
      try {
        const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(search+", India")}&key=${MAPS_KEY}`);
        const data = await res.json();
        if (data.results) {
          setSuggestions(data.results.slice(0,6).map(r=>{
            const comp = r.address_components;
            const get = (type)=>comp.find(c=>c.types.includes(type))?.long_name||"";
            const area = get("sublocality_level_1")||get("sublocality")||get("neighborhood")||get("locality");
            const city = get("locality")||get("administrative_area_level_2");
            return { label:r.formatted_address, area, city, lat:r.geometry.location.lat, lng:r.geometry.location.lng };
          }));
        }
      } catch(e){}
    }, 500);
    return ()=>clearTimeout(timer);
  },[search]);

  const handleGPS = async()=>{
    setGpsLoading(true); setError("");
    try {
      const { area, city, lat, lng } = await detectGPSLocation();
      if (!city) throw new Error("Could not detect location");
      saveCity(city); saveArea(area||city);
      onSelect(city, area||city, lat, lng);
    } catch(e){ setError("Could not detect location. Please search manually or pick on map."); }
    setGpsLoading(false);
  };

  const handlePick = (s)=>{
    saveCity(s.city); saveArea(s.area||s.city);
    onSelect(s.city, s.area||s.city, s.lat, s.lng);
  };

  // Load Leaflet map
  useEffect(()=>{
    if (mode !== "map") return;
    let map = null;
    const loadLeaflet = async()=>{
      if (!window.L) {
        if (!document.getElementById("leaflet-css")) {
          const link = document.createElement("link");
          link.id = "leaflet-css"; link.rel = "stylesheet";
          link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
          document.head.appendChild(link);
        }
        await new Promise(resolve=>{
          const script = document.createElement("script");
          script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
          script.onload = resolve; document.head.appendChild(script);
        });
      }
      if (!mapRef.current || leafletMapRef.current) return;
      const L = window.L;
      // Try to center on patient's last known coords
      const storedCoords = (()=>{ try { const c=ls.get("dz_patient_coords"); return c?JSON.parse(c):null; } catch{return null;} })();
      const center = storedCoords ? [storedCoords[1], storedCoords[0]] : [mapCoords.lat, mapCoords.lng];
      map = L.map(mapRef.current).setView(center, 14);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{ attribution:"© OpenStreetMap", maxZoom:19 }).addTo(map);
      const marker = L.marker(center,{ draggable:true }).addTo(map);
      markerRef.current = marker; leafletMapRef.current = map;
      const updateAddr = async(lat,lng)=>{
        setMapCoords({lat,lng});
        try {
          const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${MAPS_KEY}`);
          const data = await res.json();
          if (data.results?.[0]) setMapAddress(data.results[0].formatted_address.split(",").slice(0,3).join(","));
        } catch{}
      };
      marker.on("dragend",()=>{ const p=marker.getLatLng(); updateAddr(p.lat,p.lng); });
      map.on("click",e=>{ marker.setLatLng(e.latlng); updateAddr(e.latlng.lat,e.latlng.lng); });
      updateAddr(center[0], center[1]);
    };
    loadLeaflet();
    return ()=>{ if(leafletMapRef.current){leafletMapRef.current.remove();leafletMapRef.current=null;} };
  },[mode]);

  const confirmMapLocation = async()=>{
    setLoading(true);
    try {
      const res = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${mapCoords.lat},${mapCoords.lng}&key=${MAPS_KEY}`);
      const data = await res.json();
      if (data.results?.[0]) {
        const comp = data.results[0].address_components;
        const get = (type)=>comp.find(c=>c.types.includes(type))?.long_name||"";
        const area = get("sublocality_level_1")||get("sublocality")||get("neighborhood")||get("locality");
        const city = get("locality")||get("administrative_area_level_2");
        saveCity(city); saveArea(area||city);
        onSelect(city, area||city, mapCoords.lat, mapCoords.lng);
      }
    } catch(e){ setError("Failed to get address. Try again."); }
    setLoading(false);
  };

  if (mode==="map") return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", background:C.bg }}>
      <div style={{ background:C.deep, padding:"16px 20px", display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
        <button onClick={()=>setMode("search")} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:10, width:36, height:36, color:"white", fontSize:20, cursor:"pointer" }}>‹</button>
        <div style={{ color:C.white, fontWeight:700, fontSize:16 }}>🗺️ Pick on Map</div>
      </div>
      <div style={{ background:C.ocean+"15", padding:"8px 16px", fontSize:12, color:C.ocean, fontWeight:600, flexShrink:0 }}>
        Tap on map or drag the pin to set your exact location
      </div>
      <div ref={mapRef} style={{ flex:1, minHeight:300, zIndex:1 }} />
      <div style={{ padding:16, background:C.white, borderTop:`1px solid ${C.pearl}`, flexShrink:0, zIndex:2, position:"relative" }}>
        {mapAddress && <div style={{ fontSize:13, color:C.slate, marginBottom:10, display:"flex", gap:8 }}><span>📍</span><span>{mapAddress}</span></div>}
        {error && <div style={{ color:C.danger, fontSize:12, marginBottom:10 }}>⚠️ {error}</div>}
        <Btn full color={C.ocean} loading={loading} onClick={confirmMapLocation}>✅ Confirm This Location</Btn>
      </div>
    </div>
  );

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", background:C.bg, overflowY:"hidden" }}>
      <div style={{ background:C.deep, padding:isChanging?"44px 24px 28px":"58px 24px 28px", position:"relative", overflow:"hidden", flexShrink:0 }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 50" preserveAspectRatio="none" height="50" width="100%"><path d="M0,25 C80,48 160,3 240,25 C320,48 370,8 400,25 L400,50 L0,50 Z" fill={C.foam}/></svg>
        <div style={{ position:"relative", textAlign:"center" }}>
          {!isChanging && <div style={{ marginBottom:14 }}><Logo size={36} white /></div>}
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:20, fontWeight:700 }}>
            {isChanging ? "Change Location" : "Where are you?"}
          </div>
          <div style={{ color:"rgba(255,255,255,0.6)", fontSize:13, marginTop:6 }}>
            Search any city or area in India
          </div>
        </div>
      </div>

      <div style={{ padding:"12px 16px", background:C.white, borderBottom:`1px solid ${C.pearl}`, flexShrink:0 }}>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:16 }}>🔍</span>
          <input value={search} onChange={e=>{ setSearch(e.target.value); setError(""); }}
            placeholder="Search area, city or pincode..." autoFocus
            style={{ width:"100%", background:C.bg, border:`2px solid ${search?C.ocean:C.pearl}`, borderRadius:14, padding:"12px 14px 12px 40px", fontSize:15, outline:"none", boxSizing:"border-box" }} />
          {search && <button onClick={()=>{ setSearch(""); setSuggestions([]); }} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:C.pearl, border:"none", borderRadius:6, width:24, height:24, cursor:"pointer", fontSize:12 }}>✕</button>}
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto" }}>
        {!search && (
          <div style={{ padding:"16px 16px 8px" }}>
            <div onClick={handleGPS}
              style={{ display:"flex", alignItems:"center", gap:14, padding:"16px", background:C.ocean+"10", borderRadius:16, border:`1.5px solid ${C.ocean}30`, cursor:"pointer", marginBottom:10, opacity:gpsLoading?0.7:1 }}>
              <div style={{ width:46, height:46, borderRadius:14, background:C.ocean, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
                {gpsLoading?"⏳":"📍"}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:15, color:C.ocean }}>{gpsLoading?"Detecting location...":"Use Current Location"}</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>GPS auto-detect · Most accurate</div>
              </div>
              {!gpsLoading && <span style={{ color:C.ocean, fontSize:20 }}>›</span>}
            </div>

            <div onClick={()=>setMode("map")}
              style={{ display:"flex", alignItems:"center", gap:14, padding:"16px", background:C.health+"10", borderRadius:16, border:`1.5px solid ${C.health}30`, cursor:"pointer", marginBottom:16 }}>
              <div style={{ width:46, height:46, borderRadius:14, background:C.health, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>🗺️</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:15, color:C.health }}>Pick on Map</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>Drag pin to your exact location</div>
              </div>
              <span style={{ color:C.health, fontSize:20 }}>›</span>
            </div>

            {error && <div style={{ background:C.danger+"15", borderRadius:12, padding:"10px 14px", marginBottom:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}

            <div style={{ background:C.saffron+"15", borderRadius:14, padding:"12px 14px", marginBottom:16, border:`1px solid ${C.saffron}30` }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.warning, marginBottom:4 }}>📦 Ordering for someone else?</div>
              <div style={{ fontSize:12, color:C.slate, lineHeight:1.6 }}>Search or pick their location. You can always change it later from home screen.</div>
            </div>

            {currentCity && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:10 }}>Current Location</div>
                <div onClick={()=>onSelect(currentCity, currentArea||currentCity)}
                  style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", background:C.white, borderRadius:14, border:`2px solid ${C.ocean}`, cursor:"pointer" }}>
                  <span style={{ fontSize:22 }}>📍</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>{currentArea||currentCity}</div>
                    <div style={{ fontSize:12, color:C.slate }}>{currentCity}</div>
                  </div>
                  <span style={{ color:C.ocean, fontSize:13, fontWeight:700 }}>Keep →</span>
                </div>
              </div>
            )}
          </div>
        )}

        {suggestions.length > 0 && (
          <div style={{ padding:"8px 16px" }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:10 }}>Select Location</div>
            {suggestions.map((s,i)=>(
              <div key={i} onClick={()=>handlePick(s)}
                style={{ display:"flex", alignItems:"center", gap:14, padding:"14px 16px", background:C.white, borderRadius:14, border:`1px solid ${C.pearl}`, marginBottom:8, cursor:"pointer" }}>
                <div style={{ width:38, height:38, borderRadius:12, background:C.ocean+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>📍</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>{s.area||s.city}</div>
                  <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>{s.label.split(",").slice(-3).join(",").trim()}</div>
                </div>
                <span style={{ color:C.ocean, fontSize:18 }}>›</span>
              </div>
            ))}
          </div>
        )}

        {search.length >= 3 && suggestions.length === 0 && (
          <div style={{ textAlign:"center", padding:"40px 20px" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>🔍</div>
            <div style={{ fontSize:14, color:C.slate }}>No results for "{search}"</div>
            <div style={{ fontSize:12, color:C.mist, marginTop:6 }}>Try a different area name or pincode</div>
            <Btn small color={C.ocean} style={{ marginTop:16 }} onClick={()=>{ saveCity(search); saveArea(search); onSelect(search, search); }}>
              Use "{search}" anyway →
            </Btn>
          </div>
        )}
        <div style={{ height:24 }} />
      </div>
    </div>
  );
}

// ─── Static Data ──────────────────────────────────────────────

const POPULAR_MEDS = ["Dolo 650mg","Pantoprazole 40mg","Azithromycin 500mg","Metformin 500mg","Cetirizine 10mg","Augmentin 625mg","Paracetamol 500mg","Omeprazole 20mg"];

// ─── Components ───────────────────────────────────────────────
const Btn = ({ children, onClick, variant="primary", full, small, color, style:s, disabled, loading }) => {
  const v = {
    primary:{ background:color||C.ocean, color:C.white },
    outline:{ background:"transparent", color:color||C.ocean, border:`2px solid ${color||C.ocean}` },
    ghost:{ background:C.pearl, color:C.slate },
    danger:{ background:C.danger+"15", color:C.danger, border:`1px solid ${C.danger}30` },
  };
  return <button onClick={onClick} disabled={disabled||loading} style={{ ...v[variant], border:v[variant].border||"none", borderRadius:12, padding:small?"8px 14px":"12px 20px", fontFamily:"inherit", fontWeight:700, fontSize:small?12:14, cursor:(disabled||loading)?"not-allowed":"pointer", width:full?"100%":"auto", opacity:(disabled||loading)?0.6:1, ...(s||{}) }}>{loading?"Please wait...":children}</button>;
};
const Card = ({ children, style:s, onClick }) => <div onClick={onClick} style={{ background:C.white, borderRadius:16, padding:16, border:`1px solid ${C.pearl}`, boxShadow:"0 2px 12px rgba(10,37,64,0.05)", cursor:onClick?"pointer":"default", ...(s||{}) }}>{children}</div>;
const Badge = ({ children, color, small }) => <span style={{ background:color+"18", color, borderRadius:20, padding:small?"3px 9px":"5px 12px", fontSize:small?10:12, fontWeight:600, whiteSpace:"nowrap" }}>{children}</span>;
const Toggle = ({ value, onChange, color }) => (
  <div onClick={()=>onChange(!value)} style={{ width:46, height:25, borderRadius:13, background:value?(color||C.ocean):C.mist, position:"relative", cursor:"pointer", flexShrink:0 }}>
    <div style={{ width:19, height:19, borderRadius:"50%", background:C.white, position:"absolute", top:3, left:value?24:3, transition:"left 0.2s" }} />
  </div>
);
const TopBar = ({ title, onBack, right, color, subtitle }) => (
  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", background:color||C.white, borderBottom:color?"none":`1px solid ${C.pearl}`, position:"sticky", top:0, zIndex:10 }}>
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      {onBack&&<button onClick={onBack} style={{ background:color?"rgba(255,255,255,0.2)":C.pearl, border:"none", borderRadius:10, width:36, height:36, fontSize:18, cursor:"pointer", color:color?C.white:C.ink, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>}
      <div>
        {title&&<div style={{ fontWeight:700, fontSize:17, color:color?C.white:C.ink }}>{title}</div>}
        {subtitle&&<div style={{ fontSize:11, color:color?"rgba(255,255,255,0.6)":C.slate }}>{subtitle}</div>}
      </div>
    </div>
    {right}
  </div>
);
const Logo = ({ size=32, white:w }) => (
  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
    <svg width={size} height={size} viewBox="0 0 56 56" fill="none">
      <rect width="56" height="56" rx="14" fill={w?"rgba(255,255,255,0.15)":C.deep}/>
      <path d="M14 14 H24 Q40 14 40 28 Q40 42 24 42 H14 Z" fill="none" stroke={w?"rgba(255,255,255,0.9)":C.foam} strokeWidth="3.5" strokeLinejoin="round"/>
      <path d="M20 28 Q24 23 28 28 Q32 33 36 28" stroke={C.saffron} strokeWidth="2.8" strokeLinecap="round" fill="none"/>
    </svg>
    <span style={{ fontFamily:"Georgia,serif", fontWeight:700, fontSize:size*0.72, color:w?C.white:C.deep }}>Den<span style={{ color:C.saffron }}>izzy</span></span>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SIDEBAR NAVIGATION — with quotation tabs
// ═══════════════════════════════════════════════════════════════════════════════
// QUOTE BANNER — sidebar card showing active request status
// ══════════════════════════════════════════════════════
function QuoteBanner({ req, vertical, onTap, onClose, navigate }) {
  const color = vertical==="medical" ? C.health : LABS_COLOR;
  if (!req) return (
    <div style={{ background:C.pearl, borderRadius:12, padding:"12px 14px", textAlign:"center" }}>
      <div style={{ fontSize:13, color:C.slate, marginBottom:8 }}>No active {vertical==="medical"?"medicine":"lab"} request</div>
      <button onClick={()=>{ if(navigate) navigate(vertical==="medical"?"medSearch":"labsLanding"); if(onClose) onClose(); }}
        style={{ background:color, border:"none", borderRadius:10, padding:"8px 16px", color:"white", fontWeight:700, fontSize:12, cursor:"pointer" }}>
        {vertical==="medical"?"Get Medicine Quotes →":"Get Lab Quotes →"}
      </button>
    </div>
  );
  const diff = req.expiresAt ? new Date(req.expiresAt) - new Date() : 0;
  const isExpired = diff <= 0;
  const h = Math.floor(diff/3600000), m = Math.floor((diff%3600000)/60000);
  const timeLeft = isExpired ? "Expired" : h > 0 ? h+"h "+m+"m left" : m+"m left";
  return (
    <div onClick={()=>{ if(onTap) onTap(); if(navigate) navigate("browseWhileWaiting"); if(onClose) onClose(); }}
      style={{ background:isExpired?C.warning+"08":color+"12", borderRadius:12, padding:"12px 14px", cursor:"pointer", border:"1.5px solid "+(isExpired?C.warning:color)+"30" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:isExpired?C.warning:color }}>
            {vertical==="medical"?"💊":"🔬"} {isExpired?"Expired — check quotes":"Quotes live"}
          </div>
          <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>Tap to see quotes →</div>
        </div>
        {req.quoteCount > 0 && (
          <div style={{ background:color, borderRadius:20, padding:"4px 10px", textAlign:"center" }}>
            <div style={{ color:"white", fontWeight:800, fontSize:13 }}>{req.quoteCount}</div>
          </div>
        )}
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
        <div style={{ fontSize:11, color:isExpired?C.danger:C.warning, fontWeight:600 }}>⏱ {timeLeft}</div>
        {!isExpired&&<div style={{ fontSize:11, color:color, fontWeight:600 }}>● LIVE</div>}
      </div>
    </div>
  );
}

function Sidebar({ open, onClose, navigate, user, currentRequest, activeRequests, setCurrentRequest, orders, city, area, onLogout }) {
  const [quotesTab, setQuotesTab] = useState(null);
  const [autoOpened, setAutoOpened] = useState(false);

  // Derive both from activeRequests (not just currentRequest)
  const medReq = activeRequests && activeRequests.medical && activeRequests.medical._id ? activeRequests.medical : null;
  const labReq = activeRequests && activeRequests.lab && activeRequests.lab._id ? activeRequests.lab : null;
  const hasActiveReq = !!(medReq || labReq);
  const pendingOrders = orders.filter(o=>o.status!=="delivered"&&o.status!=="cancelled");
  // Auto-open the correct tab when sidebar opens
  useEffect(()=>{ 
    if (open && hasActiveReq && !autoOpened) { 
      // Default to medicine tab if active, else lab
      setQuotesTab(medReq ? "medical" : "lab"); 
      setAutoOpened(true); 
    }
    if (!open) setAutoOpened(false);
  },[open, hasActiveReq]);

  const navItems = [
    { icon:"🏠", label:"Home", screen:"home" },
    { icon:"💊", label:"Buy Medicines", screen:"medSearch" },
    { icon:"🔬", label:"Lab Tests", screen:"labsLanding" },
    { icon:"🏪", label:"Nearby Shops", screen:"shops" },
    { icon:"💊", label:"Reminders", screen:"reminders" },
    { icon:"📋", label:"My Prescriptions", screen:"savedPrescriptions" },
    { icon:"📦", label:"My Orders", screen:"orders", badge:pendingOrders.length },
    { icon:"🎁", label:"Refer a Friend", screen:"referral" },
    { icon:"🔔", label:"Notifications", screen:"alerts" },
    { icon:"👤", label:"Profile & Settings", screen:"profile" },
    { icon:"🎧", label:"Support", screen:"support" },
    { icon:"🏪", label:"Register your Shop", screen:"vendorSignup", external:"https://vendor.denizzy.com" },
    { icon:"🚪", label:"Logout", screen:"logout" },
  ];


  if (!open) return null;

  return (
    <div style={{ position:"fixed", inset:0, zIndex:999, display:"flex" }}>
      <div onClick={onClose} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.5)" }} />
      <div style={{ position:"relative", width:"82%", maxWidth:320, background:C.white, height:"100%", display:"flex", flexDirection:"column", boxShadow:"4px 0 24px rgba(0,0,0,0.25)" }}>

        {/* Header */}
        <div style={{ background:`linear-gradient(135deg, ${C.deep}, ${C.ocean})`, padding:"48px 20px 20px", flexShrink:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(255,255,255,0.2)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>👤</div>
            <div>
              <div style={{ color:C.white, fontWeight:700, fontSize:16 }}>{user?.name||"Welcome!"}</div>
              <div style={{ color:"rgba(255,255,255,0.6)", fontSize:12, marginTop:2 }}>📍 {area||city||"Set location"}</div>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div style={{ flex:1, overflowY:"auto" }}>

          {/* ─── Navigation ─────────────────────────────────────────── */}
          <div style={{ padding:"6px 0" }}>
            {navItems.map(item=>(
              <div key={item.screen} onClick={()=>{ if(item.external){openVendorSite();onClose();}else if(item.screen==="logout"){onLogout&&onLogout();onClose();}else{navigate(item.screen);onClose();} }}
                style={{ display:"flex", alignItems:"center", gap:14, padding:"13px 20px", cursor:"pointer", borderBottom:`1px solid ${C.bg}` }}>
                <span style={{ fontSize:20, width:26, textAlign:"center" }}>{item.icon}</span>
                <span style={{ fontSize:14, fontWeight:600, color:C.ink, flex:1 }}>{item.label}</span>
                {(item.badge||0)>0&&(
                  <div style={{ background:C.danger, borderRadius:20, padding:"1px 8px" }}>
                    <span style={{ color:"white", fontSize:11, fontWeight:700 }}>{item.badge}</span>
                  </div>
                )}
                <span style={{ color:C.mist, fontSize:16 }}>›</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding:"14px 20px", borderTop:`1px solid ${C.pearl}`, background:C.bg, flexShrink:0 }}>
          <div style={{ fontSize:11, color:C.mist, textAlign:"center" }}>Denizzy v1.0 · denizzy.com</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// RATING POPUP — auto-shows when order delivered
// ═══════════════════════════════════════════════════════════════════════════════
function RatingPopup({ order, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    await onSubmit(order._id, rating, review);
    setLoading(false);
    onClose();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:998, display:"flex", alignItems:"flex-end" }}>
      <div style={{ background:C.white, borderRadius:"24px 24px 0 0", width:"100%", padding:"24px 24px 40px" }}>
        <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 20px" }} />
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <div style={{ fontSize:48 }}>⭐</div>
          <div style={{ fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, color:C.ink, marginTop:10 }}>How was your experience?</div>
          <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>Rate {order?.vendor?.name||"the shop"}</div>
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:16 }}>
          {[1,2,3,4,5].map(star=>(
            <button key={star} onClick={()=>setRating(star)}
              style={{ background:"none", border:"none", fontSize:38, cursor:"pointer", transform:star<=rating?"scale(1.2)":"scale(1)", transition:"transform 0.15s", filter:star<=rating?"none":"grayscale(1) opacity(0.4)" }}>⭐</button>
          ))}
        </div>
        {rating>0&&(
          <>
            <textarea value={review} onChange={e=>setReview(e.target.value)} placeholder="Share your experience (optional)..." rows={2}
              style={{ width:"100%", background:C.bg, border:`1px solid ${C.pearl}`, borderRadius:12, padding:"10px 12px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box", marginBottom:12 }} />
            <Btn full color={C.saffron} style={{ color:C.ink }} loading={loading} onClick={submit}>Submit Rating →</Btn>
          </>
        )}
        <button onClick={onClose} style={{ display:"block", width:"100%", background:"none", border:"none", color:C.mist, fontSize:13, cursor:"pointer", marginTop:10, fontFamily:"inherit" }}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

const BottomNav = ({ active, navigate, remindCount=0 }) => (
  <div style={{ display:"flex", background:C.white, borderTop:`1px solid ${C.pearl}`, position:"sticky", bottom:0, zIndex:100 }}>
    {[{id:"home",icon:"🏠",label:"Home"},{id:"shops",icon:"🏪",label:"Shops"},{id:"reminders",icon:"💊",label:"Remind",badge:remindCount},{id:"orders",icon:"📦",label:"Orders"},{id:"profile",icon:"👤",label:"Profile"}].map(t=>(
      <button key={t.id} onClick={()=>navigate(t.id)} style={{ flex:1, border:"none", background:"none", padding:"8px 0 10px", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:2, position:"relative" }}>
        <span style={{ fontSize:18 }}>{t.icon}</span>
        {(t.badge||0)>0&&<div style={{ position:"absolute", top:4, right:"16%", width:15, height:15, borderRadius:"50%", background:C.danger, display:"flex", alignItems:"center", justifyContent:"center" }}><span style={{ color:C.white, fontSize:9, fontWeight:700 }}>{t.badge>9?"9+":t.badge}</span></div>}
        <span style={{ fontSize:9, fontWeight:active===t.id?700:400, color:active===t.id?C.ocean:C.mist }}>{t.label}</span>
        {active===t.id&&<div style={{ width:4, height:4, borderRadius:"50%", background:C.ocean }} />}
      </button>
    ))}
  </div>
);
const QuotesPill = ({ count, navigate }) => (
  <div onClick={()=>navigate("quotes")} style={{ position:"absolute", top:8, left:"50%", transform:"translateX(-50%)", zIndex:999, background:C.health, borderRadius:24, padding:"8px 20px", display:"flex", alignItems:"center", gap:10, boxShadow:"0 4px 20px rgba(26,122,94,0.4)", cursor:"pointer", whiteSpace:"nowrap" }}>
    <div style={{ width:8, height:8, borderRadius:"50%", background:"#4ADE80" }} />
    <span style={{ color:C.white, fontWeight:700, fontSize:13 }}>{count>0?`${count} quote${count>1?"s":""} received!`:"Waiting for quotes..."}</span>
    <span style={{ color:"rgba(255,255,255,0.7)", fontSize:12 }}>Tap →</span>
  </div>
);

// Delivery Modal
function DeliveryModal({ onSelect, onClose, vertical, currentCity, currentArea }) {
  const color = vertical==="medical"?C.health:LABS_COLOR;
  const [step, setStep] = useState("choose");
  const [form, setForm] = useState({ flatNo:"", street:"", area:"", city:"Bangalore", pincode:"", landmark:"" });
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));
  const isValid = form.flatNo && form.street && form.area && form.pincode.length===6;
  return (
    <div style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.65)", display:"flex", alignItems:"flex-end", zIndex:200 }}>
      <div style={{ background:C.white, borderRadius:"24px 24px 0 0", width:"100%", maxHeight:"90%", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"16px 24px 0", flexShrink:0 }}>
          <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 14px" }} />
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div style={{ fontFamily:"Georgia,serif", fontSize:18, fontWeight:700, color:C.deep }}>🚚 Delivery Address</div>
            <button onClick={onClose} style={{ background:C.pearl, border:"none", borderRadius:8, width:30, height:30, fontSize:15, cursor:"pointer" }}>✕</button>
          </div>
        </div>
        <div style={{ overflowY:"auto", flex:1, padding:"0 24px 32px" }}>
          {step==="choose" ? (
            <>
              <div onClick={()=>onSelect({type:"current",area:currentArea||"",city:currentCity||"",pincode:"",flatNo:"",street:"",landmark:""})}
                style={{ display:"flex", alignItems:"center", gap:14, padding:14, background:color+"10", borderRadius:14, border:`1.5px solid ${color}30`, marginBottom:12, cursor:"pointer" }}>
                <div style={{ width:42, height:42, borderRadius:12, background:color+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>📍</div>
                <div style={{ flex:1 }}><div style={{ fontWeight:700, fontSize:14, color:C.ink }}>Deliver to {currentArea||currentCity||"My Location"}</div><div style={{ fontSize:12, color:C.slate }}>{currentCity||"Current location"} · Tap to use this</div></div>
                <span style={{ color, fontSize:18 }}>›</span>
              </div>
              <div onClick={()=>setStep("new")} style={{ display:"flex", alignItems:"center", gap:14, padding:"12px 14px", background:C.bg, borderRadius:14, border:`1.5px dashed ${C.mist}`, cursor:"pointer" }}>
                <div style={{ width:42, height:42, borderRadius:12, background:C.pearl, display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>➕</div>
                <div><div style={{ fontWeight:600, fontSize:13, color:C.ink }}>New Address</div><div style={{ fontSize:12, color:C.slate }}>Enter full delivery address</div></div>
              </div>
            </>
          ) : (
            <>
              <button onClick={()=>setStep("choose")} style={{ background:"none", border:"none", color, fontWeight:600, fontSize:13, cursor:"pointer", marginBottom:14, padding:0 }}>← Back</button>
              {[["flatNo","Flat / House No. *","e.g. 12/3"],["street","Street / Road *","e.g. Main Road"],["area","Area / Locality *","e.g. Hulimavu"],["city","City *","e.g. Bangalore"],["pincode","Pincode *","6-digit","tel"],["landmark","Landmark (optional)","e.g. Near Bus Stop"]].map(([field,label,ph,type="text"])=>(
                <div key={field} style={{ marginBottom:11 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:4, textTransform:"uppercase", letterSpacing:"1px" }}>{label}</div>
                  <input value={form[field]} onChange={e=>setF(field,e.target.value)} placeholder={ph} type={type}
                    style={{ width:"100%", background:C.bg, border:`1.5px solid ${form[field]?color+"60":C.pearl}`, borderRadius:10, padding:"10px 12px", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
              ))}
              <Btn full color={color} disabled={!isValid} onClick={()=>onSelect({type:"new",...form})}>✅ Confirm Address →</Btn>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// LOGIN — Phone OTP (real backend)
// ═══════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [step, setStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  // Pick up ?ref= referral code from URL — or user can enter it manually
  const [refCode, setRefCode] = useState(()=>{ try { return new URLSearchParams(window.location.search).get("ref")||""; } catch { return ""; } });
  const [showRefInput, setShowRefInput] = useState(false);

  useEffect(()=>{
    if (resendTimer>0) { const t=setTimeout(()=>setResendTimer(r=>r-1),1000); return ()=>clearTimeout(t); }
  },[resendTimer]);

  const sendOtp = async () => {
    if (phone.length!==10) return setError("Enter valid 10-digit number");
    if (!agreedTerms) return setError("Please accept the Terms & Conditions to continue");
    setLoading(true); setError("");
    const res = await api("POST","/auth/user/send-otp",{phone, ...(refCode?{ref:refCode}:{})});
    if (res.success) { setStep("otp"); setResendTimer(30); }
    else setError(res.message||"Failed to send OTP");
    setLoading(false);
  };

  const verifyOtp = async () => {
    if (otp.length!==6) return setError("Enter 6-digit OTP");
    setLoading(true); setError("");
    const res = await api("POST","/auth/user/verify-otp",{phone,otp,...(refCode?{ref:refCode}:{})});
    if (res.success && res.token) { saveToken(res.token); onLogin(res.token, res.user, res.isNewUser); }
    else setError(res.message||"Invalid OTP");
    setLoading(false);
  };

  if (step==="otp") return (
    <div style={{ flex:1, background:C.bg, overflowY:"auto" }}>
      <div style={{ background:C.deep, padding:"48px 24px 40px", textAlign:"center", borderBottomLeftRadius:28, borderBottomRightRadius:28 }}>
        <Logo size={40} white />
        <div style={{ color:"rgba(255,255,255,0.6)", fontSize:13, marginTop:8 }}>OTP sent to +91 {phone}</div>
      </div>
      <div style={{ padding:"32px 24px" }}>
        <div style={{ fontFamily:"Georgia,serif", fontSize:22, fontWeight:700, color:C.deep, marginBottom:8 }}>Enter OTP</div>
        <input value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="• • • • • •" type="tel" maxLength={6}
          style={{ width:"100%", background:C.white, border:`2px solid ${C.ocean}`, borderRadius:12, padding:"16px 14px", fontSize:24, fontFamily:"monospace", outline:"none", boxSizing:"border-box", marginBottom:16, textAlign:"center", letterSpacing:"8px" }} />
        {error&&<div style={{ background:C.danger+"15", borderRadius:12, padding:"10px 14px", marginBottom:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}
        <Btn full style={{ background:C.deep }} onClick={verifyOtp} loading={loading}>Verify & Login →</Btn>
        <button onClick={()=>{if(resendTimer===0){setOtp("");sendOtp();}}} style={{ width:"100%", background:"none", border:"none", color:resendTimer>0?C.mist:C.ocean, fontSize:14, cursor:resendTimer>0?"not-allowed":"pointer", marginTop:14, fontFamily:"inherit", fontWeight:600 }}>
          {resendTimer>0?`Resend OTP in ${resendTimer}s`:"Resend OTP"}
        </button>
        <button onClick={()=>{setStep("phone");setError("");setOtp("");}} style={{ display:"block", margin:"12px auto 0", background:"none", border:"none", color:C.slate, fontSize:13, cursor:"pointer" }}>← Change number</button>
      </div>
    </div>
  );

  return (
    <div style={{ flex:1, background:C.bg, overflowY:"auto" }}>
      <div style={{ background:C.deep, padding:"56px 24px 48px", borderBottomLeftRadius:28, borderBottomRightRadius:28, position:"relative", overflow:"hidden" }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 50" preserveAspectRatio="none" height="50" width="100%"><path d="M0,25 C80,48 160,3 240,25 C320,48 370,8 400,25 L400,50 L0,50 Z" fill={C.foam}/></svg>
        <div style={{ position:"relative" }}>
          <Logo size={44} white />
          <div style={{ color:"rgba(255,255,255,0.65)", fontSize:14, marginTop:14, lineHeight:1.7 }}>Compare prices for medicines and lab tests from shops and labs near you.</div>
          <div style={{ display:"flex", gap:16, marginTop:16 }}>
            {[["💊","Medicine"],["🔬","Lab Tests"],["💰","Best Price"]].map(([icon,label])=>(
              <div key={label} style={{ textAlign:"center" }}>
                <div style={{ fontSize:22 }}>{icon}</div>
                <div style={{ color:"rgba(255,255,255,0.5)", fontSize:10, marginTop:4 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={{ padding:"32px 24px" }}>
        <div style={{ fontFamily:"Georgia,serif", fontSize:24, fontWeight:700, color:C.deep, marginBottom:6 }}>Welcome to Denizzy</div>
        <div style={{ color:C.slate, fontSize:14, marginBottom:24 }}>Enter your mobile number to continue</div>
        <div style={{ fontSize:11, color:C.slate, fontWeight:700, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>Mobile Number</div>
        <div style={{ display:"flex", gap:10, marginBottom:8 }}>
          <div style={{ background:C.white, border:`1.5px solid ${C.pearl}`, borderRadius:12, padding:"12px 14px", fontSize:14, color:C.slate, whiteSpace:"nowrap" }}>🇮🇳 +91</div>
          <input value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="10-digit mobile" type="tel" maxLength={10}
            style={{ flex:1, background:C.white, border:`1.5px solid ${C.pearl}`, borderRadius:12, padding:"12px 14px", fontSize:15, outline:"none", fontFamily:"inherit" }} />
        </div>
        <div onClick={()=>setAgreedTerms(!agreedTerms)} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"12px 14px", background:C.white, borderRadius:12, border:`1.5px solid ${agreedTerms?C.deep:C.pearl}`, marginTop:12, cursor:"pointer" }}>
          <div style={{ width:20, height:20, borderRadius:6, border:`2px solid ${agreedTerms?C.deep:C.mist}`, background:agreedTerms?C.deep:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
            {agreedTerms&&<span style={{ color:C.white, fontSize:13 }}>✓</span>}
          </div>
          <div style={{ fontSize:12, color:C.slate, lineHeight:1.6 }}>
           I agree to Denizzy's <span onClick={(e)=>{ e.stopPropagation(); window.open("https://denizzy.com/terms-and-conditions.html","_blank"); }} style={{ color:C.ocean, fontWeight:700, textDecoration:"underline" }}>Terms & Conditions</span> and <span onClick={(e)=>{ e.stopPropagation(); window.open("https://denizzy.com/privacy-policy.html","_blank"); }} style={{ color:C.ocean, fontWeight:700, textDecoration:"underline" }}>Privacy Policy</span>.
          </div>
        </div>
        {error&&<div style={{ background:C.danger+"15", borderRadius:12, padding:"10px 14px", marginTop:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}

        {/* Referral code — shown automatically if ?ref= in URL, or via "Have a code?" toggle */}
        {refCode ? (
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:12, background:"#FFF9EC", border:`1.5px solid ${C.saffron}50`, borderRadius:12, padding:"10px 14px" }}>
            <span style={{ fontSize:14 }}>🎁</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:11, color:C.slate }}>Referral code applied</div>
              <div style={{ fontFamily:"monospace", fontWeight:800, color:C.deep, fontSize:14, letterSpacing:1 }}>{refCode.toUpperCase()}</div>
            </div>
            <button onClick={()=>setRefCode("")} style={{ background:"none", border:"none", color:C.mist, fontSize:16, cursor:"pointer", padding:4 }}>✕</button>
          </div>
        ) : (
          <div style={{ marginTop:12 }}>
            <button onClick={()=>setShowRefInput(v=>!v)}
              style={{ background:"none", border:"none", color:C.ocean, fontSize:13, fontWeight:600, cursor:"pointer", padding:0, fontFamily:"inherit" }}>
              🎁 {showRefInput ? "▲ Hide" : "Have a referral code?"}
            </button>
            {showRefInput && (
              <div style={{ display:"flex", gap:8, marginTop:8 }}>
                <input
                  value={refCode}
                  onChange={e=>setRefCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g,""))}
                  placeholder="e.g. ABC123"
                  maxLength={20}
                  style={{ flex:1, background:C.white, border:`1.5px solid ${C.saffron}`, borderRadius:12, padding:"11px 14px", fontSize:14, outline:"none", fontFamily:"monospace", letterSpacing:1, fontWeight:700 }}
                />
                {refCode && (
                  <div style={{ background:"#FFF9EC", borderRadius:12, padding:"11px 14px", fontSize:12, color:C.deep, fontWeight:700, whiteSpace:"nowrap", display:"flex", alignItems:"center" }}>
                    ✅ Applied
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <Btn full style={{ background:C.deep, marginTop:16 }} onClick={sendOtp} loading={loading} disabled={!agreedTerms}>Get OTP →</Btn>
      </div>
      {showTerms && <TermsModal onClose={()=>setShowTerms(false)} onAccept={()=>{ setAgreedTerms(true); setShowTerms(false); }} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// TERMS & CONDITIONS MODAL
// ═══════════════════════════════════════════════════════════
function TermsModal({ onClose, onAccept }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1200, display:"flex", alignItems:"flex-end", justifyContent:"center" }} onClick={onClose}>
      <div style={{ background:C.white, borderRadius:"20px 20px 0 0", width:"100%", maxWidth:390, maxHeight:"85vh", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"18px 20px 12px", borderBottom:`1px solid ${C.pearl}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <div style={{ fontFamily:"Georgia,serif", fontSize:18, fontWeight:700, color:C.deep }}>Terms & Conditions</div>
          <button onClick={onClose} style={{ background:C.pearl, border:"none", borderRadius:8, width:32, height:32, fontSize:16, cursor:"pointer" }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"16px 20px", fontSize:13, color:C.slate, lineHeight:1.7 }}>
          <p style={{ marginBottom:12 }}><b>Denizzy is a marketplace platform only.</b> We connect patients with independent local pharmacies, medical shops and diagnostic labs ("Vendors"). Denizzy does not manufacture, sell, stock, dispense, deliver or test any medicines, products or lab samples.</p>

          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>1. Vendor Responsibility</div>
          <p style={{ marginBottom:12 }}>
            All medicines, products, lab tests and services shown on Denizzy are provided directly by the Vendor. The Vendor is solely and entirely responsible for:
          </p>
          <ul style={{ marginBottom:12, paddingLeft:18 }}>
            <li style={{ marginBottom:4 }}>The genuineness, quality, expiry, storage and safety of all medicines and products.</li>
            <li style={{ marginBottom:4 }}>Holding a valid Drug License (for pharmacies) and all applicable regulatory licenses/approvals (for labs).</li>
            <li style={{ marginBottom:4 }}>The accuracy of prices, quotes, billing and invoices shared with the patient.</li>
            <li style={{ marginBottom:4 }}>Dispensing medicines only against valid prescriptions where required by law.</li>
            <li style={{ marginBottom:4 }}>The quality, accuracy, hygiene and timeliness of lab sample collection and test reports.</li>
            <li style={{ marginBottom:4 }}>Packing, handling and delivering orders safely and on time, if delivery is offered.</li>
            <li>Any loss, damage, adverse reaction, incorrect medicine/test, delay or dispute arising from the product or service provided.</li>
          </ul>

          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>2. Denizzy's Role</div>
          <p style={{ marginBottom:12 }}>
            Denizzy only provides a platform to discover nearby Vendors, compare quotes and communicate. Denizzy is <b>not a party</b> to the transaction between the patient and the Vendor, does not employ the Vendor's staff or delivery personnel, and is <b>not responsible</b> for the quality, safety, legality, delivery or outcome of any product or service ordered through a Vendor.
          </p>

          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>3. Delivery</div>
          <p style={{ marginBottom:12 }}>
            Where a Vendor offers home delivery or home sample collection, this service is arranged and performed entirely by the Vendor (or their delivery partner). Denizzy does not operate its own delivery fleet and is not liable for delivery delays, damages or losses in transit.
          </p>

          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>4. Medical Disclaimer</div>
          <p style={{ marginBottom:12 }}>
            Information shown on Denizzy is for convenience only and is not medical advice. Always consult a qualified doctor or pharmacist before taking any medicine or undergoing any lab test.
          </p>

          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>5. Data & Privacy</div>
          <p style={{ marginBottom:12 }}>
            By using Denizzy, you consent to your request details (medicines/tests required, location, contact number) being shared with nearby Vendors so they can provide quotes. Your contact details are shared with a Vendor only after you confirm an order with them. We do not sell your personal data to third parties.
          </p>

          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>6. Disputes & Grievances</div>
          <p style={{ marginBottom:12 }}>
            Any complaint regarding a product, test, billing or delivery should first be raised with the concerned Vendor. Denizzy may assist in communication between patient and Vendor but is not liable for resolving or compensating for such disputes.
          </p>

          <p style={{ marginBottom:4, fontSize:11, color:C.mist }}>By tapping "I Agree & Continue" or checking the consent box on the login screen, you acknowledge that you have read and accepted these Terms & Conditions and Privacy Policy.</p>
        </div>
        <div style={{ padding:"12px 20px 20px", borderTop:`1px solid ${C.pearl}`, flexShrink:0 }}>
          <Btn full style={{ background:C.deep }} onClick={onAccept}>I Agree & Continue</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ACTIVE REQUEST CARD — used on home page for both verticals
// ═══════════════════════════════════════════════════════════
function ActiveReqCard({ req, vertical, onClick }) {
  const isLab = vertical === "lab";
  const color = isLab ? "#0891B2" : C.health;
  const isExpired = req && req.expiresAt && new Date(req.expiresAt) < new Date();
  if (!req || !req._id) return null;
  const count = req.quoteCount || 0;
  return (
    <div onClick={onClick} style={{ background:isExpired?C.warning+"08":color+"10", borderRadius:14, padding:"12px 14px", marginBottom:8, border:`1.5px solid ${isExpired?C.warning:color}30`, cursor:"pointer" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:13, color:isExpired?C.warning:color }}>
            {isLab
              ? (isExpired?"⏰ Lab Request Expired":"🔬 Lab Request Active")
              : (isExpired?"⏰ Medicine Request Expired":"💊 Medicine Request Active")}
          </div>
          <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>
            {isExpired
              ? "Quotes still available · Tap to confirm or cancel"
              : count > 0
                ? `${count} quote${count!==1?"s":""} received · Tap to compare`
                : (isLab?"Labs are quoting · Tap to check":"Shops are quoting · Tap to check")}
          </div>
        </div>
        {count > 0 ? (
          <div style={{ background:color, borderRadius:20, padding:"4px 12px", marginLeft:10, flexShrink:0 }}>
            <div style={{ color:"white", fontWeight:800, fontSize:14 }}>💬 {count}</div>
          </div>
        ) : (
          <span style={{ color:isExpired?C.warning:color, fontSize:20, marginLeft:8 }}>›</span>
        )}
      </div>
      {isExpired && (
        <div style={{ background:C.warning+"20", borderRadius:8, padding:"5px 10px", marginTop:6 }}>
          <div style={{ fontSize:11, color:C.warning, fontWeight:600 }}>⚠️ Quoting window closed — you can still confirm received quotes</div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// HOME
// ═══════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// CANCEL ORDER MODAL (patient cancels a confirmed order)
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// REPORT ISSUE MODAL — patient reports order issue via WhatsApp
// ═══════════════════════════════════════════════════════════════════════════════
function ReportIssueModal({ order, onClose, supportNumber }) {
  const isLab = order.vertical === "lab";
  const color = isLab ? LABS_COLOR : C.health;
  const ISSUES = isLab ? [
    "Lab is not responding after confirmation",
    "Wrong test booked",
    "Appointment time issue",
    "Home collection not arranged",
    "Report not received",
    "Billing or payment issue",
    "Other",
  ] : [
    "Item missing from order",
    "Wrong items delivered",
    "Order not delivered yet",
    "Pharmacy not responding",
    "Delivery taking too long",
    "Billing or payment issue",
    "Other",
  ];
  const [issue, setIssue] = useState("");
  const [note, setNote] = useState("");
  const orderId = order.orderNumber
    ? `DZ-${order.vertical==="medical"?"M":"L"}${String(order.orderNumber).padStart(5,"0")}`
    : order._id?.slice(-8);
  const openWhatsApp = () => {
    if (!issue) return;
    const num = supportNumber || "9876543210";
    const msg = `Hi Denizzy Support 👋\n\n*Order:* ${orderId}\n*Issue:* ${issue}${note.trim()?`\n*Details:* ${note.trim()}`:""}`;
    window.open(`https://wa.me/91${num}?text=${encodeURIComponent(msg)}`, "_blank");
    onClose();
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1200, display:"flex", alignItems:"flex-end" }} onClick={onClose}>
      <div style={{ background:C.white, borderRadius:"20px 20px 0 0", width:"100%", maxHeight:"85vh", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"16px 20px 12px", borderBottom:`1px solid ${C.pearl}`, flexShrink:0 }}>
          <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 14px" }} />
          <div style={{ fontWeight:800, fontSize:17, color:C.ink }}>Need Help?</div>
          <div style={{ fontSize:12, color:C.slate, marginTop:4 }}>Order {orderId} · We'll help on WhatsApp</div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"16px 20px 28px" }}>
          <div style={{ fontWeight:700, fontSize:13, color:C.ink, marginBottom:10 }}>What's the issue? <span style={{ color:C.danger }}>*</span></div>
          {ISSUES.map(iss=>(
            <div key={iss} onClick={()=>setIssue(iss)}
              style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", borderRadius:12, border:`1.5px solid ${issue===iss?color:C.pearl}`, background:issue===iss?color+"08":C.white, marginBottom:8, cursor:"pointer" }}>
              <div style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${issue===iss?color:C.mist}`, background:issue===iss?color:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {issue===iss&&<div style={{ width:8, height:8, borderRadius:"50%", background:C.white }} />}
              </div>
              <span style={{ fontSize:13, color:C.ink }}>{iss}</span>
            </div>
          ))}
          {issue&&(
            <div style={{ marginTop:4, marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.ink, marginBottom:6 }}>Additional details (optional)</div>
              <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
                placeholder="Any extra info to help us resolve faster..."
                style={{ width:"100%", background:C.bg, border:`1.5px solid ${C.pearl}`, borderRadius:10, padding:"9px 12px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box" }} />
            </div>
          )}
          <div style={{ background:"#E8F5E9", borderRadius:12, padding:"10px 14px", marginBottom:16, border:"1px solid #C8E6C9" }}>
            <div style={{ fontSize:12, color:"#2E7D32" }}>💬 Opens WhatsApp with your issue pre-filled. Our team responds within the hour.</div>
          </div>
          <Btn full color="#25D366" disabled={!issue} onClick={openWhatsApp}>💬 Contact Support on WhatsApp</Btn>
          <button onClick={onClose} style={{ width:"100%", background:"none", border:"none", color:C.slate, fontSize:14, cursor:"pointer", marginTop:12, padding:"8px 0", fontWeight:600 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CancelOrderModal({ order, onClose, onCancelled }) {
  const isLab = order.vertical === "lab";
  const color = isLab ? LABS_COLOR : C.health;
  const REASONS = isLab ? [
    "Found a better price at another lab",
    "Doctor cancelled / changed the test",
    "I booked the wrong test(s)",
    "Wrong prescription / referral uploaded",
    "Can't make the appointment time",
    "Lab is not responding after confirmation",
    "Other",
  ] : [
    "Found medicines at a better price elsewhere",
    "Doctor changed my prescription",
    "I ordered the wrong medicines",
    "Wrong prescription uploaded",
    "Delivery time is too long",
    "Wrong delivery location",
    "Pharmacy is not responding after confirmation",
    "Other",
  ];
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [hasComplaint, setHasComplaint] = useState(false);
  const [complaintText, setComplaintText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const canSubmit = reason && (reason !== "Other" || note.trim()) && (!hasComplaint || complaintText.trim().length >= 20);
  const submit = async () => {
    if (!canSubmit) return;
    setLoading(true); setError("");
    const body = { cancellationReason: reason, cancellationNote: note.trim() || null };
    if (hasComplaint && complaintText.trim()) body.complaint = { description: complaintText.trim() };
    const res = await api("PATCH", `/requests/orders/${order._id}/cancel`, body);
    if (res.success) { onCancelled(); }
    else { setError(res.message || "Failed to cancel order"); setLoading(false); }
  };
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:1200, display:"flex", alignItems:"flex-end" }} onClick={onClose}>
      <div style={{ background:C.white, borderRadius:"20px 20px 0 0", width:"100%", maxHeight:"85vh", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"16px 20px 12px", borderBottom:`1px solid ${C.pearl}`, flexShrink:0 }}>
          <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 14px" }} />
          <div style={{ fontWeight:800, fontSize:17, color:C.ink }}>Cancel Order</div>
          <div style={{ fontSize:12, color:C.slate, marginTop:4 }}>Please tell us why you're cancelling</div>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"16px 20px 28px" }}>
          <div style={{ fontWeight:700, fontSize:13, color:C.ink, marginBottom:10 }}>Select a reason <span style={{ color:C.danger }}>*</span></div>
          {REASONS.map(r=>(
            <div key={r} onClick={()=>setReason(r)}
              style={{ display:"flex", alignItems:"center", gap:12, padding:"11px 14px", borderRadius:12, border:`1.5px solid ${reason===r?C.danger:C.pearl}`, background:reason===r?C.danger+"08":C.white, marginBottom:8, cursor:"pointer" }}>
              <div style={{ width:20, height:20, borderRadius:"50%", border:`2px solid ${reason===r?C.danger:C.mist}`, background:reason===r?C.danger:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {reason===r&&<div style={{ width:8, height:8, borderRadius:"50%", background:C.white }} />}
              </div>
              <span style={{ fontSize:13, color:C.ink }}>{r}</span>
            </div>
          ))}
          {reason&&(
            <div style={{ marginTop:4, marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.ink, marginBottom:6 }}>
                {reason==="Other"?<>Details <span style={{ color:C.danger }}>*</span></>:"Additional details (optional)"}
              </div>
              <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3}
                placeholder={reason==="Other"?"Please describe your reason...":"Any extra context? (optional)"}
                style={{ width:"100%", background:C.bg, border:`1.5px solid ${note?C.danger+"60":C.pearl}`, borderRadius:10, padding:"9px 12px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box" }} />
            </div>
          )}
          {reason&&(
            <div style={{ background:"#FFF3E0", borderRadius:12, padding:"12px 14px", marginBottom:16, border:"1px solid #FFE0B2" }}>
              <div onClick={()=>setHasComplaint(!hasComplaint)} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", marginBottom:hasComplaint?10:0 }}>
                <div style={{ width:20, height:20, borderRadius:5, border:`2px solid ${hasComplaint?"#E65100":C.mist}`, background:hasComplaint?"#E65100":"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {hasComplaint&&<span style={{ color:C.white, fontSize:13 }}>✓</span>}
                </div>
                <span style={{ fontSize:13, color:"#E65100", fontWeight:600 }}>Report an issue with this {isLab?"lab":"pharmacy"}</span>
              </div>
              {hasComplaint&&(
                <textarea value={complaintText} onChange={e=>setComplaintText(e.target.value)} rows={3}
                  placeholder={`Describe the issue (min 20 characters)...`}
                  style={{ width:"100%", background:C.white, border:`1.5px solid ${complaintText.length>=20?"#E65100":"#FFE0B2"}`, borderRadius:10, padding:"9px 12px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box" }} />
              )}
            </div>
          )}
          {error&&<div style={{ background:C.danger+"15", borderRadius:10, padding:"10px 12px", marginBottom:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}
          <Btn full color={C.danger} disabled={!canSubmit} loading={loading} onClick={submit}>Confirm Cancellation</Btn>
          <button onClick={onClose} style={{ width:"100%", background:"none", border:"none", color:C.slate, fontSize:14, cursor:"pointer", marginTop:12, padding:"8px 0", fontWeight:600 }}>Keep Order</button>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// CANCEL REQUEST MODAL — with warning about spending more
// ═══════════════════════════════════════════════════════════════════════════════
function CancelRequestModal({ request, onCancel, onKeep }) {
  const [loading, setLoading] = useState(false);
  const isLab = request?.vertical === "lab";
  const color = isLab ? "#0891B2" : C.health;

  const doCancel = async () => {
    setLoading(true);
    await api("DELETE", `/requests/${request._id}`);
    setLoading(false);
    onCancel();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div style={{ background:C.white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:480 }}>
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>⚠️</div>
          <div style={{ fontWeight:800, fontSize:17, color:C.ink }}>Cancel this request?</div>
        </div>

        <div style={{ background:C.warning+"12", borderRadius:14, padding:14, marginBottom:14, border:`1px solid ${C.warning}30` }}>
          <div style={{ fontWeight:700, fontSize:13, color:C.warning, marginBottom:6 }}>💡 Wait — you might spend more!</div>
          <div style={{ fontSize:13, color:C.slate, lineHeight:1.7 }}>
            Please wait <strong>{isLab?"2 hours":"2 hours"}</strong> to know the best {isLab?"test prices":"medicine prices"}. Shops are reviewing your request right now.
          </div>
          <div style={{ fontSize:12, color:C.warning, marginTop:8, fontWeight:600 }}>
            If you cancel and reorder later, you may pay more — prices vary by shop.
          </div>
        </div>

        <div style={{ background:C.danger+"10", borderRadius:12, padding:12, marginBottom:20, border:`1px solid ${C.danger}20` }}>
          <div style={{ fontSize:12, color:C.danger, lineHeight:1.6 }}>
            ❌ If you cancel, <strong>all vendors will be notified</strong> and their quotes will be closed. You'll need to send a fresh request.
          </div>
        </div>

        <Btn full color={color} onClick={onKeep} style={{ marginBottom:10 }}>⏳ Keep Waiting for Best Price</Btn>
        <button onClick={doCancel} disabled={loading}
          style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"12px 0", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer" }}>
          {loading ? "Cancelling..." : "Yes, Cancel Request"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ACTIVE REQUEST BLOCKER — shown when patient tries to start same vertical again
// ═══════════════════════════════════════════════════════════════════════════════
function ActiveRequestBlocker({ vertical, existingRequest, isExpired, quoteCount, onViewRequest, onCancelRequest }) {
  const isLab = vertical === "lab";
  const color = isLab ? "#0891B2" : C.health;
  const timeLeft = !isExpired && existingRequest?.expiresAt ? (() => {
    const diff = new Date(existingRequest.expiresAt) - new Date();
    if (diff <= 0) return null;
    const h = Math.floor(diff/3600000), m = Math.floor((diff%3600000)/60000);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  })() : null;

  // Expired with quotes — must confirm or cancel first
  if (isExpired && quoteCount > 0) return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <div style={{ background:`linear-gradient(135deg,${C.warning},#e8920a)`, padding:"32px 20px 24px", textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:8 }}>💬</div>
        <div style={{ fontFamily:"Georgia,serif", fontSize:18, fontWeight:700, color:C.white }}>
          You have {quoteCount} pending quote{quoteCount!==1?"s":""}
        </div>
        <div style={{ color:"rgba(255,255,255,0.85)", fontSize:13, marginTop:6, lineHeight:1.5 }}>
          {isLab?"Lab quotes":"Shop quotes"} are waiting for your decision
        </div>
      </div>
      <div style={{ padding:16 }}>
        <div style={{ background:C.warning+"10", borderRadius:14, padding:14, marginBottom:16, border:`1px solid ${C.warning}30` }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.warning, marginBottom:6 }}>⏰ Request expired — quotes received</div>
          <div style={{ fontSize:13, color:C.slate, lineHeight:1.6 }}>
            Your previous {isLab?"lab test":"medicine"} request expired, but <strong>{quoteCount} {isLab?"lab":"shop"}{quoteCount!==1?"s":""} already quoted</strong>. Please confirm or cancel these quotes before sending a new request.
          </div>
        </div>
        <Btn full color={color} onClick={onViewRequest} style={{ marginBottom:10 }}>
          ✅ View & Confirm Quotes ({quoteCount})
        </Btn>
        <button onClick={onCancelRequest}
          style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"12px 0", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer" }}>
          🚫 Cancel All Quotes — Start Fresh
        </button>
      </div>
    </div>
  );

  // Active (not expired) — regular blocker
  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <div style={{ background:`linear-gradient(135deg,${isLab?"#065f75,#0891B2":`${C.deep},${color}`})`, padding:"32px 20px 24px", textAlign:"center" }}>
        <div style={{ fontSize:48, marginBottom:8 }}>⏳</div>
        <div style={{ fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, color:C.white }}>Request Already Active</div>
        <div style={{ color:"rgba(255,255,255,0.75)", fontSize:13, marginTop:6 }}>
          Your {isLab?"lab test":"medicine"} request is being reviewed
        </div>
      </div>
      <div style={{ padding:16 }}>
        <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:14, border:`1px solid ${C.pearl}` }}>
          {timeLeft && (
            <div style={{ background:color+"10", borderRadius:10, padding:"10px 14px", marginBottom:10, border:`1px solid ${color}20` }}>
              <div style={{ fontWeight:700, fontSize:13, color }}>⏱️ {timeLeft} remaining</div>
              <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>Shops have {timeLeft} to respond with quotes</div>
            </div>
          )}
          <div style={{ fontSize:13, color:C.slate }}>You cannot send another {isLab?"lab test":"medicine"} request while one is active.</div>
        </div>
        <Btn full color={color} onClick={onViewRequest} style={{ marginBottom:10 }}>👁️ View Active Request</Btn>
        <button onClick={onCancelRequest}
          style={{ width:"100%", background:"none", border:`1px solid ${C.danger}`, borderRadius:14, padding:"12px 0", color:C.danger, fontWeight:600, fontSize:14, cursor:"pointer" }}>
          Cancel Current Request
        </button>
      </div>
    </div>
  );
}

function HomeScreen({ navigate, onSelectOrder, user, orders, settings, city, area, currentRequest, activeRequests, setCurrentRequest, gpsLoading, gpsDetected, onChangeLocation }) {
  const recentOrders = orders.slice(0,2);
 const activeOrder = orders.find(o => {

  if (o.status === "cancelled") return false;

  if (o.vertical === "medical") {
    return o.status !== "delivered";
  }

  if (o.vertical === "lab") {
    return o.status !== "report_shared";
  }

  return true;

});
  const name = user?.name || (user?.phone ? `User ${user.phone.slice(-4)}` : "there");
  const isLiveCity = city === "Bangalore";
  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <div style={{ background:C.deep, padding:"20px 20px 28px", position:"relative", overflow:"hidden" }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 40" preserveAspectRatio="none" height="40" width="100%"><path d="M0,20 C80,38 160,3 240,20 C320,38 370,5 400,20 L400,40 L0,40 Z" fill={C.foam}/></svg>
        <div style={{ position:"relative" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <Logo size={28} white />
            <button onClick={()=>navigate("cityPicker")} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:10, padding:"8px 12px", color:C.white, fontSize:12, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
             <span>{gpsLoading ? "⏳" : gpsDetected ? "🎯" : "📍"}</span>
  <span>{gpsLoading ? "Detecting..." : (area||city||"Set location")}</span>
              <span style={{ fontSize:10, opacity:0.6 }}>▼</span>
            </button>
          </div>
          <div style={{ color:"rgba(255,255,255,0.7)", fontSize:13, marginBottom:4 }}>Hello, {name}!</div>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:18, fontWeight:700 }}>What do you need today?</div>
        </div>
      </div>
 {/* GPS detected banner */}
{gpsDetected && !gpsLoading && (
  <div style={{ background:C.health+"12", borderLeft:`4px solid ${C.health}`, padding:"8px 16px", margin:"8px 16px 0", borderRadius:"0 10px 10px 0", display:"flex", alignItems:"center", gap:8 }}>
    <span style={{ fontSize:14 }}>🎯</span>
    <div style={{ fontSize:12, color:C.health, fontWeight:600 }}>
      Location detected: {area}
    </div>
    <button onClick={()=>navigate("cityPicker")} style={{ marginLeft:"auto", background:"none", border:"none", color:C.ocean, fontSize:11, cursor:"pointer", fontWeight:600 }}>
      Change →
    </button>
  </div>
)}

      {/* Resume pending request banner */}
      {currentRequest?._id && (
        <div onClick={()=>navigate("browseWhileWaiting")} style={{ background:C.health+"12", borderLeft:`4px solid ${C.health}`, padding:"12px 16px", margin:"12px 16px 0", borderRadius:"0 12px 12px 0", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:C.health }}>🔄 Request in progress</div>
            <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>Tap to check quotes from nearby shops</div>
          </div>
          <span style={{ color:C.health, fontSize:20 }}>›</span>
        </div>
      )}

     {activeOrder && (
  <Card
    style={{
      margin:"12px 16px 0",
      cursor:"pointer",
      border:`2px solid ${C.ocean}30`
    }}
    onClick={() => {
    onSelectOrder(activeOrder);
    navigate("orderDetail");
}}k={() => onSelectOrder(activeOrder)}
  >

    <div style={{
      display:"flex",
      justifyContent:"space-between",
      alignItems:"center"
    }}>

      <div>

        <div style={{
          fontWeight:700,
          color:C.ocean,
          fontSize:14
        }}>
          🛵 Current Order
        </div>

        <div style={{
          marginTop:4,
          fontFamily:"monospace",
          fontSize:12
        }}>
          {formatOrderId(
            activeOrder._id,
            activeOrder.vertical,
            activeOrder.orderNumber
          )}
        </div>

      </div>

      <Badge
  color={
    activeOrder.status==="out_for_delivery"
      ? C.ocean
      : activeOrder.status==="packed"
      ? C.warning
      : activeOrder.status==="ready"
      ? C.ocean
      : activeOrder.status==="report_shared"
      ? C.success
      : C.health
  }
>

  {/* CONFIRMED */}
  {activeOrder.status==="confirmed" &&
    (activeOrder.vertical==="lab"
      ? "✅ Booking Confirmed"
      : "✅ Confirmed")}

  {/* PREPARING */}
  {activeOrder.status==="preparing" &&
    (activeOrder.vertical==="lab"
      ? (activeOrder.deliveryType==="home"
          ? "🧪 Preparing for Collection"
          : "🧪 Preparing for Lab Visit")
      : "⚙️ Preparing")}

  {/* PACKED */}
  {activeOrder.status==="packed" &&
    (activeOrder.vertical==="lab"
      ? "🚗 Staff On the Way"
      : "📦 Packed for Delivery")}

  {/* READY */}
  {activeOrder.status==="ready" &&
    (activeOrder.vertical==="lab"
      ? "📍 Ready for Lab Visit"
      : "📦 Ready for Pickup")}

  {/* OUT FOR DELIVERY */}
  {activeOrder.status==="out_for_delivery" &&
    (activeOrder.vertical==="lab"
      ? "🚗 Collection Tracking"
      : "🚚 Live Tracking")}

  {/* DELIVERED */}
  {activeOrder.status==="delivered" &&
    (activeOrder.vertical==="lab"
      ? "🧪 Sample Collected"
      : "🎉 Delivered")}

  {/* REPORT SHARED */}
  {activeOrder.status==="report_shared" &&
    "📄 Report Shared"}

</Badge>

    </div>

  </Card>
)}

      <div style={{ padding:"16px" }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
          <div onClick={()=>navigate("medSearch")} style={{ background:`linear-gradient(135deg, #0F5240, ${C.health})`, borderRadius:20, padding:"20px 18px", cursor:"pointer" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>💊</div>
            <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:15, fontWeight:700 }}>💊 Buy Medicines</div>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:11, marginTop:4 }}>Upload prescription · Get best price</div>
          </div>
          <div onClick={()=>navigate("labsLanding")} style={{ background:`linear-gradient(135deg, #065f75, #0891B2)`, borderRadius:20, padding:"20px 18px", cursor:"pointer" }}>
            <div style={{ fontSize:32, marginBottom:8 }}>🔬</div>
            <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:15, fontWeight:700 }}>🔬 Lab Tests</div>
            <div style={{ color:"rgba(255,255,255,0.7)", fontSize:11, marginTop:4 }}>Upload referral · Compare lab prices</div>
          </div>
        </div>
        <div style={{ display:"flex", gap:10, marginBottom:16 }}>
          {[{icon:"🏪",label:"Nearby Shops",screen:"shops"},{icon:"🎧",label:"Support",screen:"support"},{icon:"📦",label:"My Orders",screen:"orders"}].map(a=>(
            <div key={a.label} onClick={()=>navigate(a.screen)} style={{ flex:1, background:C.white, borderRadius:14, padding:"14px 10px", textAlign:"center", border:`1px solid ${C.pearl}`, cursor:"pointer" }}>
              <div style={{ fontSize:24 }}>{a.icon}</div>
              <div style={{ fontSize:11, color:C.slate, fontWeight:600, marginTop:6 }}>{a.label}</div>
            </div>
          ))}
        </div>
        <div style={{ fontWeight:700, fontSize:15, color:C.ink, marginBottom:12 }}>📦 Recent Orders</div>
        {recentOrders.length===0 ? (
          <Card><div style={{ textAlign:"center", padding:"20px 0", color:C.mist, fontSize:14 }}>No orders yet. Start by requesting medicines or groceries!</div></Card>
        ) : recentOrders.map(o=>(
          <Card key={o._id} style={{ marginBottom:10 }} onClick={()=>navigate("orders")}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ display:"flex", gap:12 }}>
                <div style={{ width:40, height:40, borderRadius:12, background:(o.vertical==="medical"?C.health:LABS_COLOR)+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{o.vertical==="medical"?"💊":"🔬"}</div>
                <div>
                  <div style={{ fontSize:12, fontFamily:"monospace", color:o.vertical==="medical"?C.health:LABS_COLOR, fontWeight:700 }}>{formatOrderId(o._id, o.vertical, o.orderNumber)}</div>
                  <div style={{ fontSize:13, color:C.ink }}>{o.vendor?.name||"Vendor"}</div>
                </div>
              </div>
              <Badge color={o.status==="delivered"?C.slate:o.status==="out_for_delivery"?C.ocean:C.saffron} small>{o.status==="delivered"?"🏁 Done":o.status==="out_for_delivery"?"🚚 On the way":"⚙️ "+o.status}</Badge>
            </div>
          </Card>
        ))}
      </div>

      {/* Active request cards — both verticals */}
      {activeRequests && (activeRequests.medical || activeRequests.lab) && (
        <div style={{ padding:"0 16px 12px" }}>
          {activeRequests.medical && activeRequests.medical._id && (
            <ActiveReqCard req={activeRequests.medical} vertical="medical"
              onClick={()=>{ setCurrentRequest(activeRequests.medical); navigate("browseWhileWaiting"); }} />
          )}
          {activeRequests.lab && activeRequests.lab._id && (
            <ActiveReqCard req={activeRequests.lab} vertical="lab"
              onClick={()=>{ setCurrentRequest(activeRequests.lab); navigate("browseWhileWaiting"); }} />
          )}
        </div>
      )}

      {/* Platform disclaimer strip */}
      <div style={{ padding:"4px 16px 0" }}>
        <div style={{ background:"#f8f9fa", borderRadius:10, padding:"8px 12px", border:"1px solid #dee2e6" }}>
          <div style={{ fontSize:10, color:"#6c757d", textAlign:"center", lineHeight:1.5 }}>
            Denizzy is a marketplace platform. We connect patients with local pharmacies and labs. Denizzy is not responsible for medicine quality, test accuracy, or delivery. All transactions are between you and the vendor.
          </div>
        </div>
      </div>

      {/* Vendor signup banner */}
      <div style={{ padding:"0 16px 24px" }}>
        <a href="https://vendor.denizzy.com" onClick={openVendorSite} rel="noopener noreferrer" style={{ textDecoration:"none" }}>
          <div style={{ background:`linear-gradient(135deg, ${C.deep}, ${C.ocean})`, borderRadius:20, padding:"16px 20px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ color:C.white, fontWeight:700, fontSize:14, fontFamily:"Georgia,serif" }}>🏪 Own a pharmacy or lab?</div>
              <div style={{ color:"rgba(255,255,255,0.65)", fontSize:12, marginTop:3 }}>Join Denizzy · Start getting orders today</div>
            </div>
            <div style={{ background:C.saffron, borderRadius:14, padding:"10px 18px", flexShrink:0 }}>
              <span style={{ color:C.deep, fontWeight:700, fontSize:13 }}>Join Free →</span>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PINCODE CONFIRM MODAL — shown when patient picks a specific shop
// ═══════════════════════════════════════════════════════════
function PincodeConfirmModal({ selectedShop, count, onConfirm, onClose }) {
  if (!selectedShop) return null;
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
      <div style={{ background:"white", borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:480 }}>
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <div style={{ fontSize:40, marginBottom:8 }}>📤</div>
          <div style={{ fontWeight:800, fontSize:17, color:"#0a2540" }}>Send to All Shops in This Area?</div>
        </div>
        <div style={{ background:"#e8f4fd", borderRadius:14, padding:14, marginBottom:16, border:"1px solid #bee3f8" }}>
          <div style={{ fontSize:13, color:"#0a2540", lineHeight:1.7 }}>
            Your quote request will be sent to <strong>all {count} {selectedShop.type==="medical"?"medical shops":"labs"} in pincode {selectedShop.pincode}</strong>.
            You will get quotes from all of them and can pick the best price.
          </div>
        </div>
        <div style={{ background:"#fff3cd", borderRadius:12, padding:"10px 14px", marginBottom:20, border:"1px solid #ffc107" }}>
          <div style={{ fontSize:12, color:"#856404", lineHeight:1.6 }}>
            Quotes are from independent {selectedShop.type==="medical"?"pharmacies":"diagnostic labs"}. Denizzy is a marketplace and does not guarantee pricing, availability, or quality.
          </div>
        </div>
        <button onClick={onConfirm}
          style={{ width:"100%", background:selectedShop.type==="medical"?"#1A7A5E":"#0891B2", color:"white", border:"none", borderRadius:14, padding:"14px 0", fontWeight:700, fontSize:15, cursor:"pointer", marginBottom:10 }}>
          Yes, Send to All {count} Shops in {selectedShop.pincode}
        </button>
        <button onClick={onClose}
          style={{ width:"100%", background:"none", border:"1px solid #dee2e6", borderRadius:14, padding:"12px 0", color:"#6c757d", fontWeight:600, fontSize:14, cursor:"pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ShopsScreen({ navigate, settings, patientCoords, shops:shopsProp }) {
  const shops = shopsProp || [];
  const [loading, setLoading] = useState(!shopsProp);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedShop, setSelectedShop] = useState(null);
  const [showPincodeConfirm, setShowPincodeConfirm] = useState(false);
  const [visibleCount, setVisibleCount] = useState(15);

  // Reset to first 15 whenever search or filter changes
  useEffect(()=>{ setVisibleCount(15); }, [search, typeFilter]);

  let filtered = shops.filter(s=>{
    if (typeFilter!=="all" && s.type!==typeFilter) return false;
    if (search.trim()) return (s.name||"").toLowerCase().includes(search.toLowerCase()) || (s.area||"").toLowerCase().includes(search.toLowerCase()) || (s.pincode||"").includes(search);
    return true;
  });

  // Sort by distance — nearest first
  if (patientCoords) {
    filtered = filtered.sort((a, b) => {
      const getDist = (s) => {
        if (!s.location || (s.location[0]===0 && s.location[1]===0)) return 99999;
        return getDistanceKmRaw(patientCoords[1], patientCoords[0], s.location[1], s.location[0]);
      };
      return getDist(a) - getDist(b);
    });
  }

  // Paginate — show 15 at a time
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  // Count shops in selected shop's pincode
  const shopsInSamePincode = selectedShop ? shops.filter(s=>s.pincode===selectedShop.pincode && s.type===selectedShop.type) : [];

  const handleRequestQuote = () => {
    if (shopsInSamePincode.length > 1) {
      setShowPincodeConfirm(true);
    } else {
      navigate(selectedShop.type==="medical"?"medSearch":"labsLanding");
    }
  };

  // Pincode confirmation modal

  if (selectedShop) {
    const shopColor = selectedShop.type==="medical"?C.health:"#0891B2";
    const shopIcon  = selectedShop.type==="medical"?"💊":"🔬";
    const hasRealStats = selectedShop.rating || selectedShop.totalOrders>0 || selectedShop.avgResponseTimeMinutes;
    return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      {showPincodeConfirm && (
        <PincodeConfirmModal
          selectedShop={selectedShop}
          count={shopsInSamePincode.length}
          onConfirm={()=>{ setShowPincodeConfirm(false); navigate(selectedShop.type==="medical"?"medSearch":"labsLanding"); }}
          onClose={()=>setShowPincodeConfirm(false)}
        />
      )}

      {/* ── Hero header ── */}
      <div style={{ background:shopColor+"12", padding:"16px 16px 20px", borderBottom:`1px solid ${shopColor}20` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
          <button onClick={()=>setSelectedShop(null)} style={{ background:C.white, border:`1px solid ${C.pearl}`, borderRadius:10, width:36, height:36, fontSize:18, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
          <div style={{ background:selectedShop.isOpen?C.success+"18":C.danger+"12", borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:700, color:selectedShop.isOpen?C.success:C.danger }}>
            {selectedShop.isOpen?"🟢 Open Now":"🔴 Currently Offline"}
          </div>
        </div>

        {selectedShop.shopPhoto ? (
          <img src={selectedShop.shopPhoto} alt={selectedShop.name} style={{ width:"100%", height:150, objectFit:"cover", borderRadius:14, marginBottom:14 }} />
        ) : (
          <div style={{ width:60, height:60, borderRadius:18, background:shopColor+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:28, marginBottom:12 }}>{shopIcon}</div>
        )}

        <div style={{ fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, color:C.ink }}>{selectedShop.isBlurred?"Medical Shop":selectedShop.name}</div>
        <div style={{ fontSize:13, color:C.slate, marginTop:3 }}>
          {selectedShop.type==="medical"?"💊 Pharmacy":"🔬 Diagnostic Lab"}
          {selectedShop.area && ` · 📍 ${selectedShop.area}`}
          {selectedShop.city && selectedShop.city!==selectedShop.area && `, ${selectedShop.city}`}
        </div>
        {patientCoords && selectedShop.location && !(selectedShop.location[0]===0&&selectedShop.location[1]===0) && (
          <div style={{ fontSize:12, color:shopColor, fontWeight:700, marginTop:4 }}>
            📏 {getDistanceKm(patientCoords[1],patientCoords[0],selectedShop.location[1],selectedShop.location[0])} away
          </div>
        )}

        {/* Verified badges */}
        <div style={{ display:"flex", gap:7, marginTop:12, flexWrap:"wrap" }}>
          <span style={{ background:C.health+"15", border:`1px solid ${C.health}30`, borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.health }}>✅ GSTIN Verified</span>
          <span style={{ background:shopColor+"15", border:`1px solid ${shopColor}30`, borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:700, color:shopColor }}>🪪 Licence Verified</span>
          {selectedShop.capabilities?.homeDelivery&&<span style={{ background:C.ocean+"15", border:`1px solid ${C.ocean}30`, borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.ocean }}>🚚 Home Delivery</span>}
          {selectedShop.plan==="paid"&&<span style={{ background:C.saffron+"15", border:`1px solid ${C.saffron}30`, borderRadius:20, padding:"4px 10px", fontSize:11, fontWeight:700, color:C.warning }}>⭐ Premium Partner</span>}
        </div>
      </div>

      <div style={{ padding:16 }}>

        {/* ── Stats ── only show real data, honest about new shops */}
        <div style={{ display:"flex", gap:10, marginBottom:16 }}>
          {selectedShop.rating ? (
            <div style={{ flex:1, background:C.white, borderRadius:14, padding:"14px 10px", textAlign:"center", border:`1px solid ${C.pearl}` }}>
              <div style={{ fontSize:20 }}>⭐</div>
    <div style={{ fontFamily:"monospace", fontSize:16, fontWeight:700, color:C.saffron, marginTop:5 }}>
  {selectedShop.rating?parseFloat(selectedShop.rating).toFixed(1):"New"}
</div>
<div style={{ fontSize:10, color:C.slate, marginTop:2 }}>Rating</div>
            </div>
          ) : (
            <div style={{ flex:1, background:C.health+"08", borderRadius:14, padding:"14px 10px", textAlign:"center", border:`1px solid ${C.health}20` }}>
              <div style={{ fontSize:20 }}>🆕</div>
              <div style={{ fontSize:12, fontWeight:700, color:C.health, marginTop:5 }}>New</div>
              <div style={{ fontSize:10, color:C.slate, marginTop:2 }}>On Denizzy</div>
            </div>
          )}
          <div style={{ flex:1, background:C.white, borderRadius:14, padding:"14px 10px", textAlign:"center", border:`1px solid ${C.pearl}` }}>
  <div style={{ fontSize:20 }}>🏆</div>
  <div style={{ fontFamily:"monospace", fontSize:16, fontWeight:700, color:C.success, marginTop:5 }}>{selectedShop.totalOrders||0}</div>
  <div style={{ fontSize:10, color:C.slate, marginTop:2 }}>Won</div>
</div>
        </div>

        {/* ── Address ── */}
        {(selectedShop.address||selectedShop.area||selectedShop.pincode) && (
          <Card style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.5px" }}>📍 Address</div>
            {selectedShop.address&&<div style={{ fontSize:13, color:C.ink, marginBottom:4 }}>{selectedShop.address}</div>}
            <div style={{ fontSize:13, color:C.slate }}>
              {[selectedShop.area, selectedShop.city, selectedShop.pincode].filter(Boolean).join(", ")}
            </div>
          </Card>
        )}

        {/* ── Working Hours ── */}
        {(selectedShop.workingHoursObj||selectedShop.workingHours) && (
          <Card style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.5px" }}>🕐 Working Hours</div>
            {selectedShop.workingHoursObj?.weekday&&(
              <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:selectedShop.workingHoursObj?.weekend?`1px solid ${C.pearl}`:"none" }}>
                <span style={{ fontSize:12, color:C.slate }}>Mon – Sat</span>
                <span style={{ fontSize:13, fontWeight:600, color:C.ink }}>{selectedShop.workingHoursObj.weekday}</span>
              </div>
            )}
            {selectedShop.workingHoursObj?.weekend&&(
              <div style={{ display:"flex", justifyContent:"space-between", padding:"6px 0" }}>
                <span style={{ fontSize:12, color:C.slate }}>Sunday</span>
                <span style={{ fontSize:13, fontWeight:600, color:C.ink }}>{selectedShop.workingHoursObj.weekend}</span>
              </div>
            )}
            {!selectedShop.workingHoursObj&&selectedShop.workingHours&&(
              <div style={{ fontSize:13, color:C.ink }}>{selectedShop.workingHours}</div>
            )}
          </Card>
        )}

        {/* ── Services / Capabilities ── */}
        {selectedShop.capabilities&&Object.values(selectedShop.capabilities).some(Boolean)&&(
          <Card style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.5px" }}>✨ Services</div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {selectedShop.capabilities.homeDelivery&&<Badge color={C.ocean}>🚚 Home Delivery</Badge>}
              {selectedShop.capabilities.prescriptionRequired&&<Badge color={C.health}>📋 Prescription Required</Badge>}
              {selectedShop.capabilities.walkIn&&<Badge color={C.success}>🚶 Walk-ins Welcome</Badge>}
              {selectedShop.capabilities.emergency&&<Badge color={C.danger}>🆘 24h Emergency</Badge>}
            </div>
          </Card>
        )}

        {/* ── Drug Licence ── */}
        {selectedShop.drugLicense&&(
          <Card style={{ marginBottom:14 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.5px" }}>🪪 Verification</div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0" }}>
              <span style={{ fontSize:12, color:C.slate }}>Drug Licence No.</span>
              <span style={{ fontSize:13, fontWeight:700, color:C.health, fontFamily:"monospace" }}>{selectedShop.drugLicense.slice(0,4)}••••{selectedShop.drugLicense.slice(-4)}</span>
            </div>
            {selectedShop.gstin&&(
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"4px 0", borderTop:`1px solid ${C.pearl}`, marginTop:4 }}>
                <span style={{ fontSize:12, color:C.slate }}>GSTIN</span>
                <span style={{ fontSize:13, fontWeight:700, color:C.health, fontFamily:"monospace" }}>{selectedShop.gstin.slice(0,4)}••••{selectedShop.gstin.slice(-4)}</span>
              </div>
            )}
          </Card>
        )}

        {/* ── Pincode info ── */}
        {shopsInSamePincode.length>1&&(
          <div style={{ background:C.ocean+"10", borderRadius:12, padding:"10px 14px", marginBottom:14, border:`1px solid ${C.ocean}20` }}>
            <div style={{ fontSize:12, color:C.ocean, fontWeight:600 }}>
              📍 {shopsInSamePincode.length} {selectedShop.type==="medical"?"pharmacies":"labs"} in pincode {selectedShop.pincode} — your request goes to all of them
            </div>
          </div>
        )}

        {/* ── CTAs ── */}
        <Btn full color={shopColor} onClick={handleRequestQuote} style={{ marginBottom:10 }}>
          📤 Request Quote from {shopsInSamePincode.length>1?`All ${shopsInSamePincode.length} Shops in ${selectedShop.pincode}`:"This Shop"}
        </Btn>
        <div style={{ display:"flex", gap:10, marginBottom:16 }}>
          {selectedShop.phone&&!selectedShop.isBlurred&&(
            <a href={`tel:${selectedShop.phone}`} style={{ textDecoration:"none", flex:1 }}>
              <Btn full variant="outline" color={shopColor}>📞 Call Shop</Btn>
            </a>
          )}
          {(()=>{
            const addrText=[selectedShop.address,selectedShop.area,selectedShop.city,selectedShop.pincode].filter(Boolean).join(", ");
            const coords=selectedShop.location||selectedShop.coordinates;
            if(!addrText&&(!coords||coords[0]===0)) return null;
            const mapsUrl=addrText?`https://maps.google.com/?q=${encodeURIComponent(addrText)}`:`https://maps.google.com/?q=${coords[1]},${coords[0]}`;
            return (
              <div style={{ flex:1 }} onClick={async()=>{ try{await navigator.clipboard.writeText(addrText);}catch{} window.open(mapsUrl,"_blank"); }}>
                <Btn full variant="outline" color={C.ocean}>🗺️ Directions</Btn>
              </div>
            );
          })()}
        </div>

        {/* ── Disclaimer ── */}
        <div style={{ background:C.pearl, borderRadius:12, padding:"10px 14px" }}>
          <div style={{ fontSize:11, color:C.slate, lineHeight:1.6 }}>
            🔒 <strong>Denizzy Marketplace:</strong> We connect you with local {selectedShop.type==="medical"?"pharmacies":"labs"}. Transactions are directly between you and the vendor. Always verify before purchase.
          </div>
        </div>
      </div>
    </div>
  );}


  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <div style={{ background:C.deep, padding:"18px 16px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
          <button onClick={()=>navigate("home")} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:10, width:36, height:36, fontSize:18, cursor:"pointer", color:C.white, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>‹</button>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:17, fontWeight:700 }}>🏪 Nearby Shops & Labs</div>
        </div>
        {/* Search by name */}
        <div style={{ position:"relative", marginBottom:8 }}>
          <span style={{ position:"absolute", left:12, top:"50%", transform:"translateY(-50%)", fontSize:14 }}>🔍</span>
          <input value={search} onChange={e=>{ setSearch(e.target.value); }}
            placeholder="Search by shop name or area..."
            style={{ width:"100%", background:"rgba(255,255,255,0.15)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:12, padding:"10px 14px 10px 34px", fontSize:13, outline:"none", boxSizing:"border-box", color:C.white }} />
        </div>

      </div>

      {/* Type filter */}
      <div style={{ padding:"8px 16px 2px", background:C.white, borderBottom:`1px solid ${C.pearl}`, display:"flex", gap:8, flexWrap:"wrap" }}>
        {[["all","All"],["medical","💊 Medical"],["lab","🔬 Lab"]].map(([val,label])=>(
          <button key={val} onClick={()=>setTypeFilter(val)} style={{ background:typeFilter===val?C.ocean:C.pearl, color:typeFilter===val?C.white:C.slate, border:"none", borderRadius:20, padding:"7px 16px", fontSize:12, cursor:"pointer", fontWeight:600 }}>{label}</button>
        ))}
        <span style={{ fontSize:12, color:C.slate, alignSelf:"center", marginLeft:"auto" }}>
          {filtered.length} shop{filtered.length!==1?"s":""}

        </span>
      </div>

      {/* ✅ Trust badge */}
      <div style={{ margin:"12px 16px 4px", background:"#E8F8F0", borderRadius:12, padding:"10px 14px", border:"1px solid #C6EFDC", display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:22, flexShrink:0 }}>✅</span>
        <div>
          <div style={{ fontSize:12, fontWeight:700, color:C.health }}>All shops are verified</div>
          <div style={{ fontSize:11, color:C.slate, marginTop:1 }}>GSTIN &amp; Drug Licence / Lab Registration checked before listing</div>
        </div>
      </div>

      <div style={{ padding:"12px 16px" }}>

        {loading ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:C.mist }}>Loading nearby shops...</div>
        ) : filtered.length===0 ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:C.mist }}>No shops found</div>
        ) : visible.map(shop=>(
          <Card key={shop.id} style={{ marginBottom:12 }} onClick={()=>setSelectedShop(shop)}>
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <div style={{ flex:1 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                  <span style={{ fontSize:20 }}>{shop.type==="medical"?"🏥":shop.type==="lab"?"🔬":"🏪"}</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>{shop.isBlurred?"Medical Shop":shop.name}</div>
                    <div style={{ fontSize:12, color:C.slate }}>
  {shop.area}{shop.pincode?<span style={{ color:C.ocean, fontWeight:600 }}> · 📍{shop.pincode}</span>:""}
</div>
{shop.drugLicense&&<div style={{ fontSize:11, color:C.health, marginTop:2 }}>🪪 DL: {shop.drugLicense.slice(0,4)}****{shop.drugLicense.slice(-4)}</div>}
{patientCoords && shop.location && !(shop.location[0]===0 && shop.location[1]===0) && (
  <div style={{ fontSize:11, color:C.ocean, marginTop:2, fontWeight:600 }}>
    📍 {getDistanceKm(patientCoords[1], patientCoords[0], shop.location[1], shop.location[0])} away
  </div>
)}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8, marginTop:8, flexWrap:"wrap" }}>
                  {shop.isOpen?<Badge color={C.success} small>🟢 Online</Badge>:<Badge color={C.danger} small>🔴 Offline</Badge>}
                  {shop.capabilities&&shop.capabilities.homeDelivery&&<Badge color={C.ocean} small>🚚 Delivery</Badge>}
                  <span style={{ fontSize:12, color:C.slate }}>⭐{shop.rating}</span>
                </div>
              </div>
              <span style={{ color:C.mist, fontSize:20 }}>›</span>
            </div>
          </Card>
        ))}

        {/* Load More */}
        {hasMore && (
          <button
            onClick={()=>setVisibleCount(c=>c+15)}
            style={{ width:"100%", background:C.white, border:`1.5px solid ${C.ocean}`, borderRadius:12, padding:"13px 0", fontSize:13, fontWeight:700, color:C.ocean, cursor:"pointer", marginBottom:12 }}>
            Load More ({filtered.length - visibleCount} more shops)
          </button>
        )}

        {/* Showing count */}
        {filtered.length > 0 && (
          <div style={{ textAlign:"center", fontSize:11, color:C.mist, marginBottom:8 }}>
            Showing {Math.min(visibleCount, filtered.length)} of {filtered.length} shop{filtered.length!==1?"s":""}
          </div>
        )}

        {/* Bottom disclaimer */}
        <div style={{ background:C.pearl, borderRadius:12, padding:"10px 14px", marginTop:8 }}>
          <div style={{ fontSize:11, color:C.slate, lineHeight:1.6, textAlign:"center" }}>
            🔒 Denizzy is a marketplace connecting patients with local shops. We do not guarantee pricing, medicine quality, or delivery. Always verify before purchase.
          </div>
        </div>
      </div>
    </div>
  );
}

function MedSearchScreen({ navigate, onSetItems }) {
  const [medSuggestions, setMedSuggestions] = useState([]);
  const [items, setItems] = useState([]); // [{ name, quantity }]
  const [customInput, setCustomInput] = useState("");
  const addItem = (med) => { if (med.trim() && !items.find(i=>i.name===med.trim())) setItems(prev=>[...prev,{ name:med.trim(), quantity:1 }]); };
  const removeItem = (name) => setItems(prev=>prev.filter(i=>i.name!==name));
  const updateQty = (name, delta) => setItems(prev=>prev.map(i=>i.name===name?{...i,quantity:Math.max(1,i.quantity+delta)}:i));
  const addCustom = () => {
    if (!customInput.trim()) return;
    customInput.split(",").map(m=>m.trim()).filter(Boolean).forEach(m=>addItem(m));
    setCustomInput("");
  };
  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="💊 Denizzy Health" onBack={()=>navigate("home")} color={C.health}
        right={items.length>0&&<button onClick={()=>{onSetItems(items,"medical");navigate("medRequest");}} style={{ background:C.saffron,border:"none",borderRadius:10,padding:"6px 14px",fontWeight:700,fontSize:13,cursor:"pointer",color:C.ink }}>Send ({items.length}) →</button>} />
      <div style={{ padding:"12px 16px" }}>
        {/* Prescription upload */}
        <div onClick={()=>navigate("prescription")} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:C.health+"10", borderRadius:14, border:`1.5px dashed ${C.health}`, marginBottom:14, cursor:"pointer" }}>
          <span style={{ fontSize:24 }}>📷</span>
          <div style={{ flex:1 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.health }}>Upload Prescription</div>
            <div style={{ fontSize:12, color:C.slate }}>Photo masked for privacy · Disclaimer shown</div>
          </div>
          <span style={{ color:C.health, fontSize:18 }}>›</span>
        </div>

        {/* Manual type medicine name */}
        <div style={{ background:C.white, borderRadius:14, padding:"12px 14px", marginBottom:12, border:`1px solid ${C.pearl}` }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>Type Medicine Name</div>
           <div style={{ position:"relative" }}>
           <div style={{ display:"flex", gap:8 }}>
            <input
              value={customInput}
              onChange={e=>{
  setCustomInput(e.target.value);
  const last = e.target.value.split(",").pop().trim().toLowerCase();
  if(last.length<2){ setMedSuggestions([]); return; }
  setMedSuggestions(MEDICINE_AUTOCOMPLETE.filter(m=>m.toLowerCase().includes(last)).slice(0,6));
}}
              onKeyDown={e=>e.key==="Enter"&&addCustom()}
              placeholder="e.g. Paracetamol 500mg, Amoxicillin..."
              style={{ flex:1, background:C.bg, border:`1.5px solid ${customInput?C.health:C.pearl}`, borderRadius:10, padding:"10px 12px", fontSize:13, outline:"none" }}
            />
            <button onClick={addCustom}
              style={{ background:C.health, border:"none", borderRadius:10, padding:"10px 14px", color:"white", fontWeight:700, fontSize:13, cursor:"pointer", flexShrink:0 }}>
              Add
            </button>
          </div>
           {medSuggestions.length>0&&(
    <div style={{ position:"absolute", top:"100%", left:0, right:0, background:C.white, borderRadius:12, boxShadow:"0 6px 24px rgba(0,0,0,0.12)", zIndex:200, border:`1px solid ${C.pearl}`, overflow:"hidden", marginTop:4 }}>
      {medSuggestions.map((med,i)=>(
        <div key={med} onClick={()=>{ addItem(med); setCustomInput(""); setMedSuggestions([]); }}
          style={{ padding:"11px 14px", fontSize:13, color:C.ink, borderBottom:i<medSuggestions.length-1?`1px solid ${C.pearl}`:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
          <span>💊</span><span>{med}</span>
        </div>
      ))}
    </div>
  )}
</div>
          <div style={{ fontSize:11, color:C.mist, marginTop:6 }}>Separate multiple medicines with a comma. Press Enter or tap Add.</div>
        </div>

        {/* Added items with quantity controls */}
        {items.length>0&&(
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>Your List ({items.length})</div>
            {items.map(item=>(
              <div key={item.name} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:C.white, borderRadius:12, border:`1px solid ${C.health}20`, marginBottom:8 }}>
                <div style={{ display:"flex", gap:10, alignItems:"center", flex:1, minWidth:0 }}>
                  <span>💊</span>
                  <span style={{ fontSize:13, fontWeight:600, color:C.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.name}</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:5, flexShrink:0 }}>
                  <button onClick={()=>updateQty(item.name,-1)}
                    style={{ width:28,height:28,background:C.bg,border:`1px solid ${C.pearl}`,borderRadius:8,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:C.slate }}>−</button>
                  <span style={{ minWidth:22,textAlign:"center",fontWeight:700,fontSize:14,color:C.ink }}>{item.quantity}</span>
                  <button onClick={()=>updateQty(item.name,1)}
                    style={{ width:28,height:28,background:C.health+"20",border:`1px solid ${C.health}40`,borderRadius:8,fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",color:C.health,fontWeight:700 }}>+</button>
                  <button onClick={()=>removeItem(item.name)}
                    style={{ background:C.danger+"15",color:C.danger,border:"none",borderRadius:8,padding:"4px 10px",fontSize:11,cursor:"pointer",marginLeft:2 }}>✕</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Popular chips */}
        <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:10 }}>Quick Select</div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          {POPULAR_MEDS.map(med=>(
            <button key={med} onClick={()=>addItem(med)} style={{ background:items.some(i=>i.name===med)?C.health:C.white, color:items.some(i=>i.name===med)?C.white:C.ink, border:`1px solid ${items.some(i=>i.name===med)?C.health:C.pearl}`, borderRadius:20, padding:"8px 14px", fontSize:13, cursor:"pointer" }}>
              {items.some(i=>i.name===med)?"✓ ":"+ "}{med}
            </button>
          ))}
        </div>
        {items.length>0&&<Btn full color={C.health} onClick={()=>{onSetItems(items,"medical");navigate("medRequest");}}>Compare Prices for {items.length} Medicine{items.length!==1?"s":""} →</Btn>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// MED REQUEST — posts to real backend
// ═══════════════════════════════════════════════════════════
function MedRequestScreen({ navigate, requestItems, onRequestCreated, city, area, prescriptionUrl, prescriptionAttached, patientCoords, settings }) {
  const [consent, setConsent] = useState(false);
  const [subPref, setSubPref] = useState("noSub");
  const [urgent, setUrgent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");

  // hasPrescription = true if user went through prescription flow
  // even if Firebase upload failed, we still send 'As per prescription' item
  const hasPrescription = prescriptionAttached || !!prescriptionUrl;
  const items = (requestItems && requestItems.length > 0)
    ? requestItems.map(item =>
        typeof item === "string"
          ? { name: item, quantity: 1, unit: "units" }
          : { name: item.name, quantity: item.quantity || 1, unit: "units" }
      )
    : hasPrescription
      ? [{ name:"As per prescription", quantity:1, unit:"units" }]
      : [];

  const SUB_OPTIONS = [
    {key:"noSub",icon:"🔒",label:"Exact medicine only",sub:"Only the exact brand and medicine listed — no substitutes.",color:C.danger},
    {key:"any",icon:"✅",label:"Any equivalent accepted",sub:"Pharmacist can suggest best alternative, generic or branded.",color:C.success},
  ];

  const submit = async () => {
    setLoading(true); setError("");
    const res = await api("POST","/requests",{
      vertical:"medical",
      items,
      city: city || "",
      area: area || "",
      filters:{ exactItemsOnly:subPref==="noSub", substitutionPref:subPref, urgent, radiusKm:settings?.requestRadiusKm||5, sortBy:"price" },
      location:{ type:"Point", coordinates: patientCoords || AREA_COORDS[area] || [77.6108, 12.8754] },
      prescriptionUrl: prescriptionUrl || null,
      notes: notes.trim() || null,
    });
    if (res.success && res.data) {
  onRequestCreated({ ...res.data, vertical:"medical", location:{ type:"Point", coordinates: patientCoords||[0,0] } });
  navigate("browseWhileWaiting");
} else if (res.code === "NO_VENDORS_IN_AREA") {
  navigate("noVendors");
} else {
  setError(res.message||"Failed to send request");
}
    setLoading(false);
  };

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg, position:"relative" }}>
      {/* Delivery modal removed - using starting location inline */}
      <TopBar title="💊 Send Request" onBack={()=>navigate("medSearch")} color={C.health} />
      <div style={{ padding:16 }}>
        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:10 }}>
            💊 Medicines {hasPrescription && items[0]?.name==="As per prescription" ? "(from prescription)" : `(${items.length})`}
          </div>
          {hasPrescription && items[0]?.name==="As per prescription" ? (
            <div style={{ background:C.health+"10", borderRadius:10, padding:12, border:`1px solid ${C.health}20` }}>
              <div style={{ fontWeight:600, fontSize:13, color:C.health }}>📷 Prescription photo attached</div>
              <div style={{ fontSize:12, color:C.slate, marginTop:4, lineHeight:1.6 }}>The pharmacist will read your prescription and quote all medicines shown in the photo. You don't need to type them manually.</div>
            </div>
          ) : (
            items.map((item,i)=>(
              <div key={i} style={{ padding:"7px 0", borderBottom:i<items.length-1?`1px solid ${C.pearl}`:"none", fontSize:13, color:C.ink }}>{item.name} × {item.quantity}</div>
            ))
          )}
        </Card>
        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:6 }}>💊 Substitution Preference</div>
          <div style={{ fontSize:12, color:C.slate, marginBottom:12 }}>Can the pharmacist suggest an equivalent if exact item is unavailable?</div>
          {SUB_OPTIONS.map(opt=>(
            <div key={opt.key} onClick={()=>setSubPref(opt.key)} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"10px 12px", borderRadius:10, border:`1.5px solid ${subPref===opt.key?opt.color:C.pearl}`, background:subPref===opt.key?opt.color+"10":C.bg, marginBottom:8, cursor:"pointer" }}>
              <div style={{ width:18, height:18, borderRadius:"50%", border:`2px solid ${subPref===opt.key?opt.color:C.mist}`, background:subPref===opt.key?opt.color:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>
                {subPref===opt.key&&<div style={{ width:8, height:8, borderRadius:"50%", background:C.white }} />}
              </div>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:subPref===opt.key?opt.color:C.ink }}>{opt.icon} {opt.label}</div>
                <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>{opt.sub}</div>
              </div>
            </div>
          ))}
        </Card>
        {/* Delivery preference removed — patient chooses pickup or delivery per quote in the quote page */}
        {/* Urgent toggle — red warning */}
        <Card style={{ marginBottom:12, border:urgent?`2px solid ${C.danger}40`:`1px solid ${C.pearl}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:urgent?C.danger:C.ink }}>⚡ Urgent Order</div>
              <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>Shops will prioritise your request immediately</div>
            </div>
            <Toggle value={urgent} onChange={setUrgent} color={C.danger} />
          </div>
          {urgent && (
            <div style={{ marginTop:12, background:C.danger+"10", borderRadius:10, padding:"10px 14px", border:`1px solid ${C.danger}25` }}>
              <div style={{ fontWeight:700, fontSize:12, color:C.danger, marginBottom:4 }}>⚠️ Important — Read before proceeding</div>
              <div style={{ fontSize:11, color:C.slate, lineHeight:1.6 }}>
                Shops may charge <strong>additional urgent fees</strong> on top of their quoted price. Denizzy does not control these charges. The shop will inform you of any urgent surcharge before confirming your order.
              </div>
            </div>
          )}
        </Card>

        {/* Notes field */}
        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:8 }}>📝 Notes (optional)</div>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3}
            placeholder="Any additional info for the pharmacist? e.g. 'For my 5-year-old child', 'Senior citizen', 'Diabetic patient'..."
            style={{ width:"100%", background:C.bg, border:`1.5px solid ${notes?C.health+"60":C.pearl}`, borderRadius:12, padding:"10px 14px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box", fontFamily:"inherit", lineHeight:1.6 }} />
        </Card>

        {/* Marketplace disclaimer */}
        <div style={{ background:"#f8f9fa", borderRadius:12, padding:"10px 14px", marginBottom:12, border:"1px solid #dee2e6" }}>
          <div style={{ fontSize:11, color:"#6c757d", lineHeight:1.6 }}>
            🔒 <strong>Denizzy Marketplace:</strong> Your request goes to nearby independent pharmacies. Denizzy does not sell medicines or guarantee pricing. Prescription validity, medicine quality, and dispensing are the pharmacist's legal responsibility. Verify all medicines before accepting.
          </div>
        </div>

        {(prescriptionAttached || prescriptionUrl) && (
          <div style={{ background:prescriptionUrl?C.health+"10":C.warning+"10", borderRadius:14, padding:"12px 16px", marginBottom:12, border:`1.5px solid ${prescriptionUrl?C.health:C.warning}40` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom: prescriptionUrl ? 8 : 0 }}>
              <span style={{ fontSize:20 }}>📷</span>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:13, color:prescriptionUrl?C.health:C.warning }}>
                  {prescriptionUrl ? "✅ Prescription uploaded" : "⏳ Uploading prescription..."}
                </div>
                <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>
                  {prescriptionUrl ? "📍 Shared with nearby pharmacies to get you the best price" : "Please wait — compressing & uploading your photo"}
                </div>
              </div>
            </div>
            {prescriptionUrl && (
              <div style={{ borderRadius:10, overflow:"hidden", border:`1px solid ${C.health}30` }}>
                <img src={prescriptionUrl} alt="Your prescription" style={{ width:"100%", maxHeight:120, objectFit:"cover", display:"block" }} />
                <div style={{ background:C.health+"15", padding:"6px 12px", fontSize:11, color:C.health, fontWeight:600 }}>
                  🔒 Personal details masked · Only medicines visible to pharmacist
                </div>
              </div>
            )}
          </div>
        )}
        <div onClick={()=>setConsent(!consent)} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"14px 16px", background:C.white, borderRadius:14, border:`1px solid ${consent?C.health:C.pearl}`, marginBottom:16, cursor:"pointer" }}>
          <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${consent?C.health:C.mist}`, background:consent?C.health:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
            {consent&&<span style={{ color:C.white, fontSize:14 }}>✓</span>}
          </div>
          <div style={{ fontSize:12, color:C.slate, lineHeight:1.6 }}>I consent to sharing my request with nearby pharmacies to get quotes. My personal details are shared only after I confirm a quote.</div>
        </div>
        {error&&<div style={{ background:C.danger+"15", borderRadius:12, padding:"10px 14px", marginBottom:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}
        {(prescriptionAttached && !prescriptionUrl) ? (
          <div style={{ background:C.warning+"15", borderRadius:14, padding:"12px 16px", textAlign:"center", border:`1px solid ${C.warning}30` }}>
            <div style={{ fontWeight:700, fontSize:13, color:C.warning }}>⏳ Uploading prescription...</div>
            <div style={{ fontSize:12, color:C.slate, marginTop:4 }}>Please wait until your prescription photo uploads before sending. Usually takes under 10 seconds.</div>
          </div>
        ) : (
          <Btn full color={LABS_COLOR} disabled={!consent} loading={loading} onClick={submit}>🔍 Send & Find Best Prices →</Btn>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// BROWSE WHILE WAITING — polls for real quotes
// ═══════════════════════════════════════════════════════════
function BrowseWhileWaiting({ navigate, currentRequest, onCancelRequest, settings }) {
  const [cancelRequestPrompt, setCancelRequestPrompt] = useState(false);
  const [cancelQuotesPrompt, setCancelQuotesPrompt] = useState(false);
  const [quoteCount, setQuoteCount] = useState(0);
  const [expired, setExpired] = useState(false);
  const [timeLeft, setTimeLeft] = useState("--");
  const [expiryMsg, setExpiryMsg] = useState("");
  const [isFlagged, setIsFlagged] = useState(currentRequest?.status==="flagged");
  const [flagCategory, setFlagCategory] = useState(currentRequest?.flagReason||null);
  const [flagReasonText, setFlagReasonText] = useState(null);
  const isLab = currentRequest?.vertical === "lab";
  const color = isLab ? "#0891B2" : C.health;

  useEffect(()=>{
    if (!currentRequest || !currentRequest._id) return;
    let stopped = false;
    const fetchQuotes = async () => {
      if (stopped) return;
      const res = await api("GET", `/requests/${currentRequest._id}`);
      if (stopped) return; // FIX #10: request may have been ordered/cleared while awaiting
      if (res.success && res.data) {
        // Stop polling if request is completed
        if (res.data.status === "completed") { stopped = true; clearInterval(qInterval); return; }
        // Check if flagged — notify patient UI
        if (res.data.status === "flagged") {
          setIsFlagged(true);
          setFlagCategory(res.data.flagReason || "prescription_issue");
          // Show the specific reason vendors gave
          if (res.data.flagReasonText) setFlagReasonText(res.data.flagReasonText);
          else if (res.data.flags?.length > 0) setFlagReasonText(res.data.flags[res.data.flags.length-1].reason || null);
          stopped = true; clearInterval(qInterval); return;
        }
        if (res.data.quotes) {
          const count = res.data.quotes.length;
          setQuoteCount(count);
          // Store count in the request so App can check it without polling —
          // but only if an active request object still exists for this vertical
          // (don't resurrect a key that was deleted because an order was placed)
          if (count > 0) {
            const vertical = currentRequest.vertical || "medical";
            const existing = ls.get("dz_active_request_" + vertical);
            if (existing) {
              const stored = JSON.parse(existing || "{}");
              stored.quoteCount = count;
              ls.set("dz_active_request_" + vertical, JSON.stringify(stored));
            }
          }
        }
      }
    };
    fetchQuotes();
    const qInterval = setInterval(fetchQuotes, 20000);
    return () => { stopped = true; clearInterval(qInterval); };
  }, [currentRequest ? currentRequest._id : null]);

  useEffect(()=>{
    if (!currentRequest || !currentRequest.expiresAt) return;
    const tick = () => {
      const diff = new Date(currentRequest.expiresAt) - new Date();
      if (diff <= 0) {
        setExpired(true);
        setTimeLeft("Expired");
        setExpiryMsg("New quotes can no longer be submitted — but you can still confirm quotes you already received.");
      } else {
        const h = Math.floor(diff/3600000), m = Math.floor((diff%3600000)/60000), s = Math.floor((diff%60000)/1000);
        setTimeLeft(h>0?`${h}h ${m}m`:`${m}m ${s}s`);
      }
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  },[currentRequest ? currentRequest.expiresAt : null]);

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg, position:"relative" }}>
      {/* Cancel modals */}
      {cancelRequestPrompt && <CancelRequestModal request={currentRequest}
        onKeep={()=>setCancelRequestPrompt(false)}
        onCancel={()=>{ onCancelRequest&&onCancelRequest(currentRequest?.vertical); setCancelRequestPrompt(false); navigate("home"); }} />}
      {cancelQuotesPrompt && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:999, display:"flex", alignItems:"flex-end", justifyContent:"center" }}>
          <div style={{ background:C.white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", maxWidth:480 }}>
            <div style={{ textAlign:"center", marginBottom:16 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>🚫</div>
              <div style={{ fontWeight:800, fontSize:17, color:C.ink }}>Don't want to order now?</div>
            </div>
            <div style={{ background:C.pearl, borderRadius:12, padding:12, marginBottom:20, fontSize:13, color:C.slate, lineHeight:1.7 }}>
              If you cancel, all vendor quotes will be closed and you'll need to send a fresh request next time. Quotes received so far will be permanently deleted.
            </div>
            <Btn full color={color} onClick={()=>setCancelQuotesPrompt(false)} style={{ marginBottom:10 }}>← Keep Quotes</Btn>
            <button onClick={()=>{ onCancelRequest&&onCancelRequest(currentRequest?.vertical); setCancelQuotesPrompt(false); navigate("home"); }}
              style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"12px 0", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer" }}>
              Yes, Cancel All Quotes
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ background:C.deep+"99", padding:"10px 16px", display:"flex", alignItems:"center", gap:12, position:"sticky", top:0, zIndex:10 }}>
        <button onClick={()=>navigate("home")} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:10, width:36, height:36, color:"white", fontSize:18, cursor:"pointer" }}>‹</button>
        <span style={{ color:"white", fontWeight:700, fontSize:15 }}>
          {isLab?"🔬 Lab Request":"💊 Medicine Request"}
        </span>
        {!expired && (
          <button onClick={()=>setCancelRequestPrompt(true)}
            style={{ marginLeft:"auto", background:"rgba(255,80,80,0.2)", border:"1px solid rgba(255,80,80,0.4)", borderRadius:8, padding:"4px 12px", color:"#ff6b6b", fontSize:11, cursor:"pointer", fontWeight:600 }}>
            Cancel Request
          </button>
        )}
      </div>

      {/* Hero — status header */}
      <div style={{ background:expired?C.warning:color, padding:"28px 20px 20px", position:"relative", overflow:"hidden", transition:"background 0.5s" }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 40" preserveAspectRatio="none" height="40" width="100%"><path d="M0,20 C80,38 160,3 240,20 C320,38 370,5 400,20 L400,40 L0,40 Z" fill="white"/></svg>
        <div style={{ position:"relative" }}>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:20, fontWeight:700 }}>
            {expired ? "⏰ Quoting Window Closed" : quoteCount>0 ? `🎉 ${quoteCount} Quote${quoteCount!==1?"s":""} Received!` : "📤 Request Sent!"}
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10, flexWrap:"wrap" }}>
            {!expired && (
              <div style={{ background:"rgba(255,255,255,0.2)", borderRadius:10, padding:"5px 12px" }}>
                <span style={{ color:C.white, fontWeight:700, fontSize:12 }}>⏱️ Closes in {timeLeft}</span>
              </div>
            )}
            {quoteCount>0 && (
              <div style={{ background:"rgba(255,255,255,0.25)", borderRadius:10, padding:"5px 12px" }}>
                <span style={{ color:C.white, fontWeight:700, fontSize:12 }}>💬 {quoteCount} {quoteCount===1?"quote":"quotes"}</span>
              </div>
            )}
          </div>
          {expiryMsg&&<div style={{ color:"rgba(255,255,255,0.85)", fontSize:12, marginTop:8, lineHeight:1.5 }}>{expiryMsg}</div>}
        </div>
      </div>

      <div style={{ padding:16 }}>

        {/* ── FLAGGED WARNING — shown when request flagged by vendors ── */}
        {isFlagged && (
          <div style={{ background:flagCategory==="none_available"?"#FFF8E1":"#FFF3F3", borderRadius:18, padding:18, marginBottom:14, border:`2px solid ${flagCategory==="none_available"?"#FFC107":C.danger}`, boxShadow:"0 4px 16px rgba(0,0,0,0.08)" }}>
            <div style={{ fontSize:32, marginBottom:10, textAlign:"center" }}>
              {flagCategory==="none_available"?"💊":"⚠️"}
            </div>
            {flagCategory==="none_available" ? (
              <>
                <div style={{ fontWeight:800, fontSize:16, color:"#E65100", marginBottom:8, textAlign:"center" }}>Medicines Not Found Nearby</div>
                {flagReasonText && (
                  <div style={{ background:"rgba(0,0,0,0.06)", borderRadius:10, padding:"8px 14px", marginBottom:10, fontSize:12, color:"#5D4037", textAlign:"center", fontStyle:"italic" }}>
                    Pharmacies reported: "{flagReasonText}"
                  </div>
                )}
                <div style={{ fontSize:13, color:"#5D4037", lineHeight:1.7, marginBottom:14, textAlign:"center" }}>
                  Accepting substitutes will allow pharmacists to suggest the best available alternatives.
                </div>
                <Btn full color={C.health} onClick={()=>{ onCancelRequest&&onCancelRequest(currentRequest?.vertical); navigate("medSearch"); }}
                  style={{ marginBottom:10 }}>
                  ✅ Start New Request with Substitutes Allowed
                </Btn>
                <button onClick={()=>{ onCancelRequest&&onCancelRequest(currentRequest?.vertical); navigate("home"); }}
                  style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"11px 0", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                  ❌ Cancel Request
                </button>
              </>
            ) : (
              <>
                <div style={{ fontWeight:800, fontSize:16, color:C.danger, marginBottom:8, textAlign:"center" }}>Action Needed on Your Request</div>
                {flagReasonText && (
                  <div style={{ background:"rgba(0,0,0,0.06)", borderRadius:10, padding:"8px 14px", marginBottom:10, fontSize:12, color:"#7B1F1F", textAlign:"center", fontStyle:"italic" }}>
                    Pharmacies reported: "{flagReasonText}"
                  </div>
                )}
                <div style={{ fontSize:13, color:C.slate, lineHeight:1.7, marginBottom:14, textAlign:"center" }}>
                  Please re-upload a clearer photo or start a fresh request.
                </div>
                <Btn full color={C.health} onClick={()=>{ onCancelRequest&&onCancelRequest(currentRequest?.vertical); navigate("prescription"); }}
                  style={{ marginBottom:10 }}>
                  📷 Re-upload Prescription
                </Btn>
                <button onClick={()=>{ onCancelRequest&&onCancelRequest(currentRequest?.vertical); navigate("home"); }}
                  style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"11px 0", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer" }}>
                  ❌ Cancel Request
                </button>
              </>
            )}
          </div>
        )}

        {/* ── View quotes CTA — shown when quotes exist ── */}
        {quoteCount > 0 && (
          <div style={{ background:color+"12", borderRadius:16, padding:16, marginBottom:14, border:`2px solid ${color}30` }}>
            <div style={{ fontWeight:700, fontSize:15, color, marginBottom:4 }}>
              🎉 {quoteCount} Quote{quoteCount!==1?"s":""} Ready to Compare!
            </div>
            <div style={{ fontSize:13, color:C.slate, marginBottom:12, lineHeight:1.6 }}>
              {expired
                ? "Quoting window is closed — but you can still confirm any quote you received."
                : "Compare prices and confirm the best one before the window closes."}
            </div>
            <Btn full color={color} onClick={()=>navigate("quotes")}>
              View & Confirm Quotes →
            </Btn>
          </div>
        )}

        {/* ── Main info card — shown while waiting ── */}
        {!expired && quoteCount===0 && (
          <div style={{ background:C.white, borderRadius:18, padding:18, marginBottom:14, border:`1px solid ${C.pearl}`, boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
              <div style={{ width:44, height:44, borderRadius:14, background:color+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
                {isLab?"🔬":"💊"}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:15, color:C.ink }}>Your request is live!</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>Sent to {isLab?"labs":"pharmacies"} in {settings?.requestRadiusKm||5} km radius of {currentRequest?.area||"your area"}</div>
              </div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {[
                { icon:"📡", text:"Your request has been sent to all " + (isLab?"diagnostic labs":"medical shops") + " within 5 km of your area." },
                { icon:"⏳", text:"You will start receiving quotes based on vendor speed and " + (isLab?"test availability":"medicine availability") + "." },
                { icon:"✅", text:"You can confirm any quote before the window closes — the sooner you confirm, the quicker your order." },
                { icon:"💡", text:"The longer you wait, the more quotes you may receive — giving you better prices to compare." },
              ].map((item,i)=>(
                <div key={i} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                  <span style={{ fontSize:16, flexShrink:0, marginTop:1 }}>{item.icon}</span>
                  <span style={{ fontSize:13, color:C.slate, lineHeight:1.6 }}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── After quoting window closed with no quotes ── */}
        {expired && quoteCount===0 && (
          <div style={{ background:C.warning+"10", borderRadius:16, padding:16, marginBottom:14, border:`1px solid ${C.warning}30` }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.warning, marginBottom:6 }}>⏰ No quotes received this time</div>
            <div style={{ fontSize:13, color:C.slate, lineHeight:1.6, marginBottom:12 }}>No shops responded within the time window. For best results, try sending a fresh request between 9am–9pm.</div>
            <Btn full color={color} onClick={()=>navigate(isLab?"labsLanding":"medSearch")}>
              Send New Request →
            </Btn>
          </div>
        )}

        {/* ── Cancel quotes when expired with quotes ── */}
        {expired && quoteCount>0 && (
          <button onClick={()=>setCancelQuotesPrompt(true)}
            style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"12px 0", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer", marginBottom:14 }}>
            🚫 Don't want to order — Cancel All Quotes
          </button>
        )}

        {/* Browse verticals */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginTop:8 }}>
          <div onClick={()=>navigate("medSearch")} style={{ background:`linear-gradient(135deg, #0F5240, ${C.health})`, borderRadius:18, padding:"16px 14px", cursor:"pointer" }}>
            <div style={{ fontSize:26, marginBottom:4 }}>💊</div>
            <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:14, fontWeight:700 }}>Medicines</div>
          </div>
          <div onClick={()=>navigate("labsLanding")} style={{ background:`linear-gradient(135deg, #065f75, #0891B2)`, borderRadius:18, padding:"16px 14px", cursor:"pointer" }}>
            <div style={{ fontSize:26, marginBottom:4 }}>🔬</div>
            <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:14, fontWeight:700 }}>Lab Tests</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExpandableTab({ title, icon, accentColor, children, defaultOpen=false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius:14, border:`1.5px solid ${accentColor}40`, overflow:"hidden", marginBottom:10 }}>
      <div onClick={()=>setOpen(o=>!o)} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"13px 16px", background:accentColor+"10", cursor:"pointer" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <span style={{ fontSize:18 }}>{icon}</span>
          <span style={{ fontWeight:700, fontSize:13, color:accentColor }}>{title}</span>
        </div>
        <span style={{ fontSize:12, color:accentColor, fontWeight:700 }}>{open?"▲ Close":"▼ Tap to view"}</span>
      </div>
      {open && (
        <div style={{ padding:"12px 16px", borderTop:`1px solid ${accentColor}20` }}>
          {children}
        </div>
      )}
    </div>
  );
}

function CollapsibleRequest({ request }) {
  const items = (request?.items||[]).filter(it=>it.itemName!=="From billing"&&it.itemName!=="As per prescription");
  return (
    <ExpandableTab title={request?.prescriptionUrl?"Your Prescription":"Your Request List"} icon="📋" accentColor={C.health}>
      {request?.prescriptionUrl ? (
        <img src={request.prescriptionUrl} alt="Prescription" style={{ width:"100%", borderRadius:10, objectFit:"cover", maxHeight:220 }} />
      ) : items.length>0 ? (
        <div>
          {items.map((it,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:i<items.length-1?`1px solid ${C.pearl}`:"none" }}>
              <span style={{ fontSize:13, color:C.ink }}>{it.itemName||it.name}</span>
              {it.quantity&&<span style={{ fontSize:12, color:C.slate }}>×{it.quantity}</span>}
            </div>
          ))}
        </div>
      ) : <div style={{ fontSize:12, color:C.slate }}>No items found</div>}
    </ExpandableTab>
  );
}

function BillingTab({ billingPhotoUrl, items, vendorNotes, onFullImage }) {
  const filteredItems = (items||[]).filter(it=>it.itemName!=="As per prescription");
  return (
    <ExpandableTab title="Shop's Billing Details" icon="🧾" accentColor={C.ocean}>
      {billingPhotoUrl && (
        <div style={{ marginBottom:filteredItems.length>0||vendorNotes?12:0 }}>
          <div style={{ fontSize:12, color:C.slate, marginBottom:6 }}>Tap photo to view full size · Verify items and prices</div>
          <img src={billingPhotoUrl} alt="Billing" onClick={()=>onFullImage&&onFullImage(billingPhotoUrl)}
            style={{ width:"100%", borderRadius:10, objectFit:"cover", maxHeight:200, cursor:"zoom-in", border:`1px solid ${C.ocean}20` }} />
        </div>
      )}
      {vendorNotes && (
        <div style={{ background:C.ocean+"10", borderRadius:10, padding:"8px 12px", marginBottom:filteredItems.length>0?10:0, border:`1px solid ${C.ocean}20` }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.ocean, marginBottom:2 }}>📝 Shop Note</div>
          <div style={{ fontSize:12, color:C.ink }}>"{vendorNotes}"</div>
        </div>
      )}
      {filteredItems.length>0 && (
        <div>
          {filteredItems.map((item,j)=>(
            <div key={j} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:13, borderBottom:j<filteredItems.length-1?`1px solid ${C.pearl}`:"none" }}>
              <span style={{ color:item.isAvailable===false?C.danger:C.slate }}>{item.itemName||item.name}{item.isAvailable===false?" ❌":""}{item.substitute?` → ${item.substitute}`:""}</span>
              <span style={{ fontFamily:"monospace", fontWeight:600 }}>₹{item.price||0}</span>
            </div>
          ))}
        </div>
      )}
      {!billingPhotoUrl && filteredItems.length===0 && !vendorNotes && (
        <div style={{ fontSize:12, color:C.mist, textAlign:"center", padding:"8px 0" }}>Shop hasn't uploaded billing details yet</div>
      )}
    </ExpandableTab>
  );
}

function QuotesScreen({ navigate, currentRequest, onSelectQuote, patientCoords:pCoords, allVendors }) {
  // Helper: get vendor coordinates — use allVendors (same data as shops tab)
  const getVendorCoords = (q) => {
    const vendorId = q.vendor?._id?.toString();
    // First: look up from allVendors (same source as shops tab) ✅
    const v = allVendors?.find(v=>(v._id||v.id)?.toString()===vendorId);
    if (v?.location && v.location[0]!==0 && v.location[1]!==0) return v.location;
    // Fallback: use coordinates from quote response
    if (q.vendor?.coordinates && q.vendor.coordinates[0]!==0 && q.vendor.coordinates[1]!==0)
      return q.vendor.coordinates;
    return null;
  };
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  // Per-quote delivery choice: { [quoteId]: "pickup" | "delivery" }
  const [deliveryChoice, setDeliveryChoice] = useState({});
  // Per-quote custom delivery charge (editable)
  const [customDelivery, setCustomDelivery] = useState({});
  const [showVendorAddr, setShowVendorAddr] = useState(null); // stores vendor obj
  const [fullImage, setFullImage] = useState(null); // billing photo full view

  useEffect(()=>{
    if (!currentRequest?._id) { setLoading(false); return; }
    api("GET",`/requests/${currentRequest._id}`).then(res=>{
      if (res.success && res.data?.quotes) setQuotes(res.data.quotes);
      setLoading(false);
    });
  },[currentRequest]);

  const sorted = [...quotes].sort((a,b)=>(a.totalAmount||0)-(b.totalAmount||0));

  // Build the effective quote based on delivery choice
  const getEffectiveQuote = (q) => {
    const choice = deliveryChoice[q._id] || (q.homeDelivery ? "delivery" : "pickup");
    const isDelivery = choice === "delivery";
    const vendorDelivCharge = q.deliveryCharge ?? null; // null = not specified, 0 = confirmed free
    const userDelivCharge = customDelivery[q._id] !== undefined
      ? Number(customDelivery[q._id])
      : (vendorDelivCharge !== null ? vendorDelivCharge : 50); // ₹50 estimate ONLY if truly unspecified
    const delivCharge = isDelivery ? userDelivCharge : 0;
    const total = (q.subtotal || q.totalAmount || 0) + delivCharge;
    return { ...q, homeDelivery: isDelivery, deliveryCharge: delivCharge, totalAmount: total, _chosenDelivery: isDelivery };
  };

  if (loading) return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:C.bg }}>
      <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
      <div style={{ color:C.slate }}>Checking for quotes...</div>
    </div>
  );

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="💰 Compare Quotes" onBack={()=>navigate("browseWhileWaiting")} />

      {/* Delivery tip banner */}
      <div style={{ margin:"12px 16px 0", background:C.ocean+"12", borderRadius:14, padding:"10px 14px", border:`1px solid ${C.ocean}20` }}>
        <div style={{ fontSize:12, color:C.ocean, fontWeight:600 }}>💡 You can switch any quote to home delivery below. Delivery charge will be confirmed by the shop.</div>
      </div>

      {/* Full-size billing photo viewer */}
      {fullImage && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:1100, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setFullImage(null)}>
          <img src={fullImage} alt="Billing full view" style={{ maxWidth:"95%", maxHeight:"85%", objectFit:"contain", borderRadius:8 }} onClick={e=>e.stopPropagation()} />
          <button onClick={()=>setFullImage(null)} style={{ position:"absolute", top:20, right:20, background:"rgba(255,255,255,0.15)", border:"none", borderRadius:10, width:40, height:40, color:"white", fontSize:20, cursor:"pointer" }}>✕</button>
          <div style={{ position:"absolute", bottom:30, color:"rgba(255,255,255,0.6)", fontSize:12 }}>Tap anywhere to close</div>
        </div>
      )}

      {/* Vendor Address Modal */}
      {showVendorAddr && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1000, display:"flex", alignItems:"flex-end" }} onClick={()=>setShowVendorAddr(null)}>
          <div style={{ background:C.white, borderRadius:"20px 20px 0 0", padding:"24px 20px 36px", width:"100%", boxSizing:"border-box" }} onClick={e=>e.stopPropagation()}>
            <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 20px" }} />
            <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:16 }}>
              <div style={{ width:48, height:48, borderRadius:14, background:C.health+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>
                {currentRequest?.vertical==="lab"?"🔬":"💊"}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:17, color:C.ink }}>{showVendorAddr.name||"Shop"}</div>
                <div style={{ fontSize:12, color:C.slate }}>{showVendorAddr.area||showVendorAddr.city||""}</div>
              </div>
            </div>
            {/* Address — same as order detail page */}
            <div style={{ background:C.bg, borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
              <div style={{ fontSize:11, color:C.slate, fontWeight:700, marginBottom:6, textTransform:"uppercase" }}>📍 Address</div>
              {showVendorAddr.address && (
                <div style={{ fontSize:14, color:C.ink, lineHeight:1.5, marginBottom:4 }}>{showVendorAddr.address}</div>
              )}
              <div style={{ fontSize:13, color:C.slate }}>
                {[showVendorAddr.area, showVendorAddr.city && showVendorAddr.city!==showVendorAddr.area?showVendorAddr.city:null].filter(Boolean).join(", ")||showVendorAddr.city||"—"}
                {showVendorAddr.pincode ? ` - ${showVendorAddr.pincode}` : ""}
              </div>
              {!showVendorAddr.address && (
                <div style={{ fontSize:11, color:C.mist, marginTop:6 }}>Street address not provided · Call shop to confirm location</div>
              )}
            </div>
            {/* Call Shop */}
            {showVendorAddr.phone && (
              <a href={`tel:${showVendorAddr.phone}`} style={{ textDecoration:"none", display:"block", marginBottom:12 }}>
                <div style={{ background:C.ocean+"10", borderRadius:12, padding:"12px 14px", display:"flex", alignItems:"center", gap:12 }}>
                  <span style={{ fontSize:20 }}>📞</span>
                  <div><div style={{ fontWeight:700, fontSize:14, color:C.ocean }}>Call Shop</div>
                  <div style={{ fontSize:12, color:C.slate }}>{showVendorAddr.phone}</div></div>
                </div>
              </a>
            )}
            {/* Working Hours — matches order detail */}
            {showVendorAddr.workingHours?.weekday && (
              <div style={{ background:C.bg, borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                <div style={{ fontSize:11, color:C.slate, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>⏰ Working Hours</div>
                <div style={{ fontSize:13, color:C.ink }}>Weekday: {showVendorAddr.workingHours.weekday}</div>
                {showVendorAddr.workingHours.weekend && <div style={{ fontSize:13, color:C.ink }}>Weekend: {showVendorAddr.workingHours.weekend}</div>}
              </div>
            )}
            {/* Google Maps — copies address to clipboard then opens Maps */}
            {(() => {
              const addrText = [showVendorAddr.address, showVendorAddr.area, showVendorAddr.city, showVendorAddr.pincode].filter(Boolean).join(", ");
              const coords = showVendorAddr.coordinates || showVendorAddr.location?.coordinates;
              const hasCoords = coords && coords[0]!==0 && coords[1]!==0;
              if (!addrText && !hasCoords) return null;
              const mapsUrl = addrText
                ? `https://maps.google.com/?q=${encodeURIComponent(addrText)}`
                : `https://maps.google.com/?q=${coords[1]},${coords[0]}`;
              return (
                <div onClick={async()=>{
                  try { await navigator.clipboard.writeText(addrText); } catch {}
                  window.open(mapsUrl, "_blank");
                }}
                  style={{ display:"flex", alignItems:"center", gap:10, background:"#E8F5E9", borderRadius:12, padding:"10px 14px", marginBottom:12, border:"1px solid #C8E6C9", cursor:"pointer" }}>
                  <span style={{ fontSize:18 }}>🗺️</span>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#2E7D32" }}>View on Google Maps</div>
                    <div style={{ fontSize:11, color:C.slate }}>Address copied to clipboard automatically</div>
                  </div>
                </div>
              );
            })()}
            {/* Distance */}
            {pCoords && (() => {
              const coords = showVendorAddr.coordinates;
              if (!coords || (coords[0]===0 && coords[1]===0)) return null;
              const d = getDistanceKm(pCoords[1],pCoords[0],coords[1],coords[0]);
              return d ? (
                <div style={{ background:C.ocean+"08", borderRadius:12, padding:"10px 14px", marginBottom:12 }}>
                  <div style={{ fontSize:13, color:C.ocean, fontWeight:600 }}>📍 {d} from your location</div>
                </div>
              ) : null;
            })()}
            <button onClick={()=>setShowVendorAddr(null)} style={{ width:"100%", background:C.ink, border:"none", borderRadius:14, padding:"14px", color:C.white, fontWeight:700, fontSize:15, cursor:"pointer" }}>Close</button>
          </div>
        </div>
      )}

        <div style={{ padding:"12px 16px" }}>
        {(currentRequest?.prescriptionUrl || (currentRequest?.items||[]).length>0) && (
          <CollapsibleRequest request={currentRequest} />
        )}
        {sorted.length===0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⏳</div>
            <div style={{ fontSize:15, fontWeight:600, color:C.ink, marginBottom:8 }}>No quotes yet</div>
            <div style={{ fontSize:13, color:C.slate, marginBottom:20 }}>Shops are reviewing your request. Check back in a few minutes.</div>
            <Btn color={C.health} onClick={()=>navigate("browseWhileWaiting")}>← Keep Browsing</Btn>
          </div>
        ) : sorted.map((q,i)=>{
          const eff = getEffectiveQuote(q);
          const isExpanded = sel===i;
          const choice = deliveryChoice[q._id] || (q.homeDelivery ? "delivery" : "pickup");
          const isDelivery = choice==="delivery";
          const isLabReq = currentRequest?.vertical === "lab";
          const origDelivCharge = q.deliveryCharge || 0;
          // If vendor set 0 delivery charge = free delivery. Don't allow estimate override.
          const custDelivCharge = origDelivCharge === 0 ? 0 : (customDelivery[q._id] !== undefined ? Number(customDelivery[q._id]) : origDelivCharge);

          return (
            <div key={q._id||i} style={{ marginBottom:14 }}>
              <div style={{ background:C.white, borderRadius:18, border:isExpanded?`2px solid ${C.health}`:`1.5px solid ${C.pearl}`, overflow:"hidden", position:"relative", boxShadow:"0 2px 10px rgba(0,0,0,0.06)" }}>
                {i===0&&<div style={{ position:"absolute", top:-1, left:0, right:0, background:C.saffron, padding:"4px 16px", display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontSize:12, fontWeight:800, color:C.deep }}>🏆 Best Price</span>
                </div>}

                {/* Main quote info */}
                <div style={{ padding:16, paddingTop:i===0?28:16 }} onClick={()=>setSel(isExpanded?null:i)}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"nowrap", minWidth:0 }}>
                        <div style={{ fontWeight:700, fontSize:15, color:C.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{q.vendor?.name||"Medical Shop"}</div>
                        <span onClick={(e)=>{ e.stopPropagation(); setShowVendorAddr(q.vendor); }}
                          style={{ fontSize:10, color:C.ocean, fontWeight:600, cursor:"pointer", background:C.ocean+"10", borderRadius:6, padding:"2px 8px", flexShrink:0 }}>
                          📍 Address
                        </span>
                      </div>
                      <div style={{ fontSize:12, color:C.slate, marginTop:3 }}>
⭐{q.vendor?.rating||4.0}{q.vendor?.avgResponseTimeMinutes?` · ⚡${q.vendor.avgResponseTimeMinutes}m`:""}
  {pCoords && (() => {
    const coords = getVendorCoords(q);
    if (!coords || (coords[0]===0 && coords[1]===0)) return null;
    const d = getDistanceKm(pCoords[1], pCoords[0], coords[1], coords[0]);
    return d ? <span style={{ color:C.ocean, fontWeight:600 }}>{" · 📍 "}{d}{" away"}</span> : null;
  })()}
</div>
                      {(q.vendor?.address||q.vendor?.area||q.vendor?.city) && (
                        <div style={{ fontSize:11, color:C.slate, marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          📍 {[
                            q.vendor?.address,
                            q.vendor?.area,
                            q.vendor?.city,
                            q.vendor?.pincode ? `- ${q.vendor.pincode}` : null
                          ].filter(Boolean).join(", ")}
                        </div>
                      )}
                    
                      <div style={{ display:"flex", gap:6, marginTop:8, flexWrap:"wrap" }}>
                        {q.allAvailable?<Badge color={C.success} small>✅ All items</Badge>:<Badge color={C.warning} small>⚠️ Has substitutes</Badge>}
                        <Badge color={isDelivery?C.ocean:C.slate} small>{isLabReq?(isDelivery?"🏠 Home Collection":"🔬 Lab Visit"):(isDelivery?"🚚 Home Delivery":"🏃 Pickup")}</Badge>
                        {q.readyIn && !isLabReq && <Badge color={C.saffron} small>⏱️ {q.readyIn==="now"?"Ready Now":q.readyIn==="hours"?"Few Hours":q.readyIn==="tomorrow"?"Tomorrow":q.readyIn}</Badge>}
                        {q.homeDelivery && Number(q.deliveryTime)>0 && !isLabReq && (
                          <Badge color={C.health} small>
                            🚀 {Number(q.deliveryTime)>=60
                              ? `${Math.floor(Number(q.deliveryTime)/60)}hr${Number(q.deliveryTime)%60>0?` ${Number(q.deliveryTime)%60}m`:""} delivery`
                              : `${Number(q.deliveryTime)} min delivery`}
                          </Badge>
                        )}
      {isLabReq && q.availableDate && (
  <Badge color={LABS_COLOR} small>
    📅 {q.availableDate==="today"?"Today":q.availableDate==="tomorrow"?"Tomorrow":q.availableDate}
  </Badge>
)}
                        {isLabReq&&q.reportReady&&<Badge color={LABS_COLOR} small>📋 Report: {q.reportReady}</Badge>}
                        {isLabReq && q.vendorNotes && (
  <div style={{ fontSize:11, color:C.slate, marginTop:6, fontStyle:"italic" }}>
    📝 "{q.vendorNotes}"
  </div>
)}
                      </div>
                    </div>
                    <div style={{ textAlign:"right", flexShrink:0, marginLeft:8 }}>
                      <div style={{ fontFamily:"monospace", fontSize:22, fontWeight:800, color:C.health }}>₹{eff.totalAmount}</div>
                      {isDelivery && <div style={{ fontSize:11, color:C.slate }}>incl. ₹{custDelivCharge} {isLabReq?"collection":"delivery"}</div>}
                      <div style={{ fontSize:11, color:C.white, marginTop:4, background:isExpanded?C.slate:C.ocean, borderRadius:8, padding:"3px 10px", display:"inline-block" }}>{isExpanded?"▲ Less":"▼ Details"}</div>
                    </div>
                  </div>
                </div>

                {/* ── Delivery toggle — always visible ── */}
                <div style={{ padding:"0 16px 14px" }}>
          {isLabReq && (q.availableDate || q.availableTime) && (
  <div style={{ background:LABS_COLOR+"10", borderRadius:12, padding:"10px 14px", marginBottom:10, border:`1px solid ${LABS_COLOR}20` }}>
    <div style={{ fontSize:12, fontWeight:700, color:LABS_COLOR, marginBottom:4 }}>📅 Appointment Offered</div>
    {q.availableDate && <div style={{ fontSize:13, color:C.ink, fontWeight:600 }}>📅 {q.availableDate==="today"?"Today":q.availableDate==="tomorrow"?"Tomorrow":new Date(q.availableDate).toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"})}</div>}
    {q.availableTime && <div style={{ fontSize:13, color:C.ink, marginTop:3 }}>🕒 {q.availableTime}</div>}
  </div>
)}                  

{/* Billing details — tap to expand */}
<div onClick={()=>setSel(isExpanded?null:i)}
  style={{ background:C.white, borderRadius:12, padding:"11px 14px", marginBottom:10, border:`1px solid ${C.pearl}`, display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer" }}>
  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
    <span style={{ fontSize:16 }}>🧾</span>
    <span style={{ fontSize:13, fontWeight:700, color:C.ink }}>Shop's Billing Details</span>
  </div>
  <div style={{ fontSize:11, color:C.white, background:isExpanded?C.slate:C.ocean, borderRadius:8, padding:"3px 10px", fontWeight:700 }}>
    {isExpanded?"▲ Close":"▼ Tap to view"}
  </div>
</div>
			
<div style={{ background:C.bg, borderRadius:14, padding:12 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.slate, marginBottom:8 }}>{isLabReq?"How would you like your sample collected?":"How would you like to receive this?"}</div>
                    <div style={{ display:"flex", gap:8 }}>
                      {/* Pickup / Lab Visit option */}
                      <button onClick={()=>setDeliveryChoice(d=>({...d,[q._id]:"pickup"}))}
                        style={{ flex:1, background:!isDelivery?C.ink:"transparent", border:`2px solid ${!isDelivery?C.ink:C.pearl}`, borderRadius:12, padding:"10px 8px", cursor:"pointer", transition:"all 0.2s" }}>
                        <div style={{ fontSize:16, marginBottom:2 }}>{isLabReq?"🔬":"🏃"}</div>
                        <div style={{ fontSize:12, fontWeight:700, color:!isDelivery?C.white:C.ink }}>{isLabReq?"🔬 Lab Visit":"🏃 Pickup"}</div>
                        <div style={{ fontSize:13, fontFamily:"monospace", fontWeight:800, color:!isDelivery?C.white:C.health, marginTop:2 }}>₹{q.subtotal||q.totalAmount}</div>
                        <div style={{ fontSize:10, color:!isDelivery?"rgba(255,255,255,0.7)":C.mist }}>{isLabReq?"Visit the lab":"No delivery charge"}</div>
                      </button>

                      {/* Home Delivery / Home Collection — only if vendor offers it */}
                      {q.homeDelivery ? (
                        <button onClick={()=>setDeliveryChoice(d=>({...d,[q._id]:"delivery"}))}
                          style={{ flex:1, background:isDelivery?C.ocean:"transparent", border:`2px solid ${isDelivery?C.ocean:C.pearl}`, borderRadius:12, padding:"10px 8px", cursor:"pointer", transition:"all 0.2s" }}>
                          <div style={{ fontSize:16, marginBottom:2 }}>{isLabReq?"🏠":"🚚"}</div>
                          <div style={{ fontSize:12, fontWeight:700, color:isDelivery?C.white:C.ink }}>{isLabReq?"🏠 Home Collection":"🚚 Home Delivery"}</div>
                          <div style={{ fontSize:13, fontFamily:"monospace", fontWeight:800, color:isDelivery?"white":C.ocean, marginTop:2 }}>
                            ₹{(q.subtotal||q.totalAmount||0) + custDelivCharge}
                          </div>
                          <div style={{ fontSize:10, color:isDelivery?"rgba(255,255,255,0.7)":C.mist }}>
                            {origDelivCharge===0 ? (isLabReq?"🎁 Free collection":"🎁 Free delivery") : `incl. ₹${custDelivCharge} ${isLabReq?"collection":"delivery"}`}
                          </div>
                        </button>
                      ) : (
                        <div style={{ flex:1, background:C.bg, border:`2px solid ${C.pearl}`, borderRadius:12, padding:"10px 8px", textAlign:"center", opacity:0.5 }}>
                          <div style={{ fontSize:16, marginBottom:2 }}>🚫</div>
                          <div style={{ fontSize:12, fontWeight:700, color:C.slate }}>{isLabReq?"No Home Collection":"No Delivery"}</div>
                          <div style={{ fontSize:10, color:C.mist, marginTop:2 }}>{isLabReq?"Lab visit only":"Pickup only"}</div>
                        </div>
                      )}
                    </div>

                    {/* Editable delivery charge — only when delivery selected and vendor didn't specify */}
                    {isDelivery && (
                      <div style={{ marginTop:8, background:C.ocean+"08", borderRadius:10, padding:"8px 12px", border:`1px solid ${C.ocean}20` }}>
                        <div style={{ fontSize:11, color:C.ocean, fontWeight:600 }}>
                          📍 {isLabReq?"Collection":"Delivery"} to: {(() => {
                            const req = currentRequest;
                            const area = req?.area || req?.city || "your location";
                            return area;
                          })()}
                        </div>
                        <div style={{ fontSize:10, color:C.slate, marginTop:2 }}>
                          This uses your current request location. If you need home collection elsewhere, start a new request from that location.
                        </div>
                      </div>
                    )}
                    {isDelivery && origDelivCharge===0 && (
                      <div style={{ marginTop:8, background:C.success+"10", borderRadius:10, padding:"6px 10px", border:`1px solid ${C.success}20` }}>
                        <div style={{ fontSize:11, color:C.success, fontWeight:700 }}>{isLabReq?"🎁 Free Home Collection!":"🎁 Free Delivery by this shop!"}</div>
                        {Number(q.deliveryTime)>0 ? (
                          <div style={{ fontSize:12, color:C.ink, marginTop:4, fontWeight:600 }}>{isLabReq?"🧪 Technician arrives in":"🚚 Delivery in:"} <b>{Number(q.deliveryTime)>=60?`${Math.floor(Number(q.deliveryTime)/60)} hr${Number(q.deliveryTime)%60>0?` ${Number(q.deliveryTime)%60} min`:""}`:` ${Number(q.deliveryTime)} mins`}</b></div>
                        ) : (
                          <div style={{ fontSize:11, color:C.mist, marginTop:2 }}>Delivery time: confirm with shop after order</div>
                        )}
                      </div>
                    )}
                    {isDelivery && origDelivCharge>0 && (
                      <div style={{ marginTop:8, background:C.success+"10", borderRadius:10, padding:"6px 10px", border:`1px solid ${C.success}20` }}>
                        <div style={{ fontSize:11, color:C.success, fontWeight:600 }}>{isLabReq?`✅ Home collection confirmed for ₹${origDelivCharge}`:`✅ Shop confirmed delivery for ₹${origDelivCharge}`}</div>
                        {Number(q.deliveryTime)>0 ? (
                          <div style={{ fontSize:12, color:C.ink, marginTop:4, fontWeight:600 }}>{isLabReq?"🧪 Technician arrives in:":"🚚 Delivery in:"} <b>{Number(q.deliveryTime)>=60?`${Math.floor(Number(q.deliveryTime)/60)} hr${Number(q.deliveryTime)%60>0?` ${Number(q.deliveryTime)%60} min`:""}`:` ${Number(q.deliveryTime)} mins`}</b></div>
                        ) : (
                          <div style={{ fontSize:11, color:C.mist, marginTop:2 }}>Delivery time: confirm with shop after order</div>
                        )}
                      </div>
                    )}
                    {isLabReq && q.reportReady && (
                      <div style={{ marginTop:8, background:LABS_COLOR+"10", borderRadius:10, padding:"6px 10px", border:`1px solid ${LABS_COLOR}20` }}>
                        <div style={{ fontSize:11, color:LABS_COLOR, fontWeight:600 }}>📋 Report ready in: {q.reportReady} after sample collection</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div style={{ padding:"0 16px 16px", borderTop:`1px solid ${C.pearl}` }}>
                   <div style={{ paddingTop:12 }}>
                    <BillingTab
  billingPhotoUrl={q.billingPhotoUrl}
  items={q.items}
  vendorNotes={q.vendorNotes}
  onFullImage={setFullImage}
/>
                     
  {isLabReq && (q.availableDate || q.availableTime) && (
  <div
    style={{
      background:LABS_COLOR+"10",
      borderRadius:10,
      padding:"8px 12px",
      marginBottom:10,
      border:`1px solid ${LABS_COLOR}20`
    }}
  >
    <div
      style={{
        fontSize:12,
        fontWeight:700,
        color:LABS_COLOR,
        marginBottom:4
      }}
    >
      📅 Appointment
    </div>

    <div style={{ fontSize:12, color:C.ink }}>
      📅 {q.availableDate || "-"}
    </div>

    <div style={{ fontSize:12, color:C.ink, marginTop:3 }}>
      🕒 {q.availableTime || "Any Time"}
    </div>
  </div>
)}

 {q.vendorNotes && (
                        <div style={{ background:C.ocean+"10", borderRadius:10, padding:"8px 12px", marginBottom:10, border:`1px solid ${C.ocean}20` }}>
                          <div style={{ fontSize:12, fontWeight:700, color:C.ocean, marginBottom:2 }}>📝 Shop Note</div>
                          <div style={{ fontSize:12, color:C.ink }}>"{q.vendorNotes}"</div>
                        </div>
                      )}
                      {(q.items||[]).filter(it=>it.itemName!=="From billing"&&it.itemName!=="As per prescription").map((item,j)=>(
                        <div key={j} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:13 }}>
                          <span style={{ color:C.slate }}>{item.itemName||item.name}</span>
                          <span style={{ fontFamily:"monospace", fontWeight:600 }}>₹{item.price||0}</span>
                        </div>
                      ))}
                      <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:13, color:C.slate }}>
                        <span>{isLabReq?"Lab tests subtotal":"Medicines subtotal"}</span>
                        <span style={{ fontFamily:"monospace" }}>₹{q.subtotal||q.totalAmount}</span>
                      </div>
                      {isDelivery && (
                        <div style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", fontSize:13, color:origDelivCharge===0?C.success:C.ocean }}>
                          <span>{origDelivCharge===0?(isLabReq?"🎁 Free Collection":"🎁 Free Delivery"):(isLabReq?"🏠 Collection charge":"🚚 Delivery charge")}</span>
                          <span style={{ fontFamily:"monospace", fontWeight:origDelivCharge===0?700:400 }}>{origDelivCharge===0?"FREE":"₹"+custDelivCharge}</span>
                        </div>
                      )}
                      <div style={{ display:"flex", justifyContent:"space-between", borderTop:`2px solid ${C.pearl}`, paddingTop:10, marginTop:6 }}>
                        <span style={{ fontSize:14, fontWeight:700, color:C.ink }}>Total</span>
                        <span style={{ fontFamily:"monospace", fontWeight:800, fontSize:17, color:C.health }}>₹{eff.totalAmount}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Confirm button */}
                <div style={{ padding:"0 16px 16px" }}>
                 <Btn full color={isDelivery?C.ocean:C.health} onClick={()=>{
                    const maxAmount = Math.max(...quotes.map(q=>q.totalAmount||0));
                    onSelectQuote({ ...eff, _maxQuoteAmount: maxAmount, _quoteCount: quotes.length });
                    navigate("confirmOrder");
                  }}>
                    {isDelivery
                      ? (isLabReq?"🏠 Confirm Home Collection →":"🚚 Confirm with Home Delivery →")
                      : (isLabReq?"🔬 Confirm Lab Visit →":"🏃 Confirm Pickup →")}
                  </Btn>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// CONFIRM ORDER — real API
// ═══════════════════════════════════════════════════════════
function ConfirmOrderScreen({ navigate, selectedQuote, currentRequest, onOrderPlaced, patientCoords:pCoords }) {
  const [isLab] = useState(
  currentRequest?.vertical === "lab" || selectedQuote?.vertical === "lab"
);
  const accentColor = isLab ? "#0891B2" : C.health;
  const [step, setStep] = useState("summary");
  const [form, setForm] = useState({ flatNo:"", street:"", area:"", city:"Bangalore", pincode:"", landmark:"", phone:"" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherResult, setVoucherResult] = useState(null); // { discount, message, valid }
  const [voucherLoading, setVoucherLoading] = useState(false);
  const [availableVouchers, setAvailableVouchers] = useState([]);
  const [referralVouchers, setReferralVouchers] = useState([]);

  // Fetch patient's available vouchers on mount — filtered by vertical
  // so lab-only vouchers don't appear on medical checkouts and vice versa.
  const orderVertical = currentRequest?.vertical || (isLab ? "lab" : "medical");
  useEffect(() => {
    api("GET", `/vouchers/my?vertical=${orderVertical}`).then(res => {
      if (res.success) setAvailableVouchers(res.data?.available || []);
    });
    // Fetch referral vouchers separately — filtered by vertical at display time
    api("GET", "/vouchers/my-referral").then(res => {
      if (res.success) setReferralVouchers(res.data || []);
    });
  }, [orderVertical]);

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  const validateVoucher = async () => {
    if (!voucherCode.trim()) return;
    setVoucherLoading(true); setVoucherResult(null);
    const total = (selectedQuote?.totalAmount||0);
    // Pass vertical so backend rejects lab-only vouchers on medical orders and vice versa
    const res = await api("POST", "/vouchers/validate", { code: voucherCode.trim().toUpperCase(), orderAmount: total, vertical: orderVertical });
    setVoucherResult(res.success ? { valid:true, discount:res.discount, message:res.message, code:voucherCode.trim().toUpperCase() } : { valid:false, message:res.message });
    setVoucherLoading(false);
  };

  const finalAmount = Math.max(0, (selectedQuote?.totalAmount||0) - (voucherResult?.valid ? voucherResult.discount : 0));
  const comparisonSavings = Math.max(0, (selectedQuote?._maxQuoteAmount||0) - (selectedQuote?.totalAmount||0));
const quoteCount = selectedQuote?._quoteCount || 0;

  const isHomeCollection = selectedQuote?.homeDelivery;
  const [chosenDelivery, setChosenDelivery] = useState(selectedQuote?.homeDelivery || false);
  const origDelivCharge = selectedQuote?.deliveryCharge || 0;
  const deliveryTotal = (selectedQuote?.subtotal || 0) + origDelivCharge;
  const pickupTotal = selectedQuote?.subtotal || 0;
  const isLabVisit = isLab && !isHomeCollection;
  const isValid = form.phone.length===10 && (chosenDelivery ? form.flatNo&&form.area&&form.pincode.length===6 : true);

  const placeOrder = async () => {
    setLoading(true); setError("");
    const res = await api("POST","/orders",{
      quoteId:selectedQuote?._id,
      requestId:currentRequest?._id,
      deliveryType: chosenDelivery ? "home" : "pickup",
      deliveryAddress: chosenDelivery ? form : null,
      customerPhone: form.phone,
      deliveryCharge: chosenDelivery ? origDelivCharge : 0,
      ...(isLab && { labVisitType: chosenDelivery ? "home_collection" : "lab_visit" }),
      ...(isLab && selectedQuote?.availableDate && { visitDate: selectedQuote.availableDate, visitSlot: selectedQuote.availableTime||null }),
      ...(voucherResult?.valid && { voucherCode: voucherResult.code, discountAmount: voucherResult.discount }),
    });
    if (res.success && res.data) {
      const newOrderId = res.data._id||res.data.quoteId;
      setOrderId(newOrderId);
      setOrderNumber(res.data.orderNumber);
      if (selectedQuote?.billingPhotoUrl || selectedQuote?.items?.length) {
  localStorage.setItem(`dz_billing_${res.data._id}`, JSON.stringify({
    billingPhotoUrl: selectedQuote.billingPhotoUrl || null,
    billingItems: selectedQuote.items || [],
    vendorNotes: selectedQuote.vendorNotes || null,
  }));
}
      // Apply voucher usage record — pass vertical for audit trail
      if (voucherResult?.valid && newOrderId) {
        api("POST", "/vouchers/apply", { code: voucherResult.code, orderId: newOrderId, orderAmount: selectedQuote?.totalAmount||0, vertical: orderVertical });
      }
      onOrderPlaced(res.data);
      setStep("confirmed");
    } else {
      setError(res.message||"Failed to place order");
    }
    setLoading(false);
  };

  const savedAddresses = (() => { try { return JSON.parse(ls.get("dz_saved_addresses")||"[]"); } catch { return []; } })();
  const [useNewAddr, setUseNewAddr] = useState(savedAddresses.length===0);

  // ── Auto-select saved address if it matches the patient's current request area ──
  // (FIX #9: if mobile/GPS-detected location matches a saved address, pre-fill it
  // automatically so the patient doesn't have to re-enter/re-select it)
  useEffect(() => {
    if (!isHomeCollection || savedAddresses.length === 0) return;
    const reqArea = (currentRequest?.area || "").trim().toLowerCase();
    if (!reqArea) return;
    const match = savedAddresses.find(addr => (addr.area||"").trim().toLowerCase() === reqArea);
    if (match) {
      setForm(f => ({
        ...f,
        flatNo: match.flatNo||"", street: match.street||"",
        area: match.area||"", city: match.city||"Bangalore",
        pincode: match.pincode||"", landmark: match.landmark||"",
      }));
      setUseNewAddr(false);
    }
  // eslint-disable-next-line
  }, []);

  // ── ORDER CONFIRMED SCREEN ────────────────────────────────────────────────────
  if (step==="confirmed") {
    return (
      <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
        <div style={{ background: isLab ? "linear-gradient(135deg,#065f75,#0891B2)" : `linear-gradient(135deg,${C.health},#2eab6e)`, padding:"40px 24px 28px", textAlign:"center", position:"relative", overflow:"hidden" }}>
          <svg style={{ position:"absolute", bottom:0, opacity:0.1 }} viewBox="0 0 400 40" preserveAspectRatio="none" height="40" width="100%"><path d="M0,20 C80,38 160,3 240,20 C320,38 370,5 400,20 L400,40 L0,40 Z" fill="white"/></svg>
          <div style={{ fontSize:56, marginBottom:10 }}>{isLab?"🔬":"🎉"}</div>
          <div style={{ fontFamily:"Georgia,serif", fontSize:22, fontWeight:700, color:C.white }}>
            {isLab ? "Booking Confirmed!" : "Order Confirmed!"}
          </div>
          <div style={{ color:"rgba(255,255,255,0.8)", fontSize:14, marginTop:6 }}>
            {isLab ? (isHomeCollection ? "Lab will call you to confirm sample collection details." : "Lab will call you to confirm your visit details.") : "Your order has been placed successfully."}
          </div>
        </div>
        <div style={{ padding:16 }}>
          {comparisonSavings > 0 && quoteCount > 1 && (
            <div style={{
              background: "linear-gradient(135deg, #F4A61D, #E85D26)",
              borderRadius: 18, padding: "16px 18px", marginBottom: 14, textAlign: "center",
              boxShadow: "0 8px 20px rgba(244,166,29,0.3)",
            }}>
              <div style={{ fontSize: 26, marginBottom: 4 }}>🎉</div>
              <div style={{ color: C.white, fontWeight: 800, fontSize: 18 }}>You saved ₹{comparisonSavings}!</div>
              <div style={{ color: "rgba(255,255,255,0.9)", fontSize: 12, marginTop: 4 }}>
                By comparing {quoteCount} quotes instead of paying ₹{selectedQuote?._maxQuoteAmount}
              </div>
            </div>
          )}
          <Card style={{ marginBottom:12 }}>
            {[
              ["Order ID", formatOrderId(orderId, isLab ? "lab" : "medical", orderNumber)],
              [isLab?"Lab":"Shop", selectedQuote?.vendor?.phone
                ? <a href={`tel:${selectedQuote.vendor.phone}`} style={{ color:accentColor, textDecoration:"none", fontWeight:700 }}>📞 {selectedQuote?.vendor?.name}</a>
                : selectedQuote?.vendor?.name],
              ["Amount", voucherResult?.valid ? (
                <span>
                  <span style={{ textDecoration:"line-through", color:C.mist, fontSize:12 }}>₹{selectedQuote?.totalAmount||0}</span>
                  {"  "}
                  <span style={{ color:C.success, fontWeight:800 }}>₹{finalAmount}</span>
                </span>
              ) : "₹"+(selectedQuote?.totalAmount||0)],
              ...(voucherResult?.valid ? [["🎟️ Denizzy Discount", <span style={{ color:C.success, fontWeight:700 }}>-₹{voucherResult.discount}</span>]] : []),
              ...(isLab ? [
  ["Type", chosenDelivery ? "🏠 Home Sample Collection" : "🔬 Lab Visit"],
] : [
  ["Type", chosenDelivery ? "🚚 Home Delivery" : "🏃 Pickup"],
]),
...(isLab && selectedQuote?.availableDate ? [["📅 Appointment", `${(d=>{const dt=new Date();if(d==="tomorrow")dt.setDate(dt.getDate()+1);return d==="today"?"Today":d==="tomorrow"?"Tomorrow":new Date(d).toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"});})(selectedQuote.availableDate)}${selectedQuote.availableTime?` · ${selectedQuote.availableTime}`:""}`]] : []),
["Status", isLab ? "✅ Booking Confirmed" : "⚙️ Preparing your order"],
            ].map(([l,v],i,arr)=>(
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 0", borderBottom:i<arr.length-1?`1px solid ${C.pearl}`:"none" }}>
                <span style={{ fontSize:12, color:C.slate }}>{l}</span>
                <span style={{ fontSize:13, fontWeight:700, color:C.ink }}>{v}</span>
              </div>
            ))}
          </Card>

          {/* Lab info card */}
          {isLab && (
            <div style={{ background:accentColor+"10", borderRadius:16, padding:"14px 16px", marginBottom:12, border:`1.5px solid ${accentColor}30` }}>
              <div style={{ fontWeight:700, fontSize:14, color:accentColor, marginBottom:8 }}>
                {isHomeCollection ? "🏠 Home Collection" : "🔬 Lab Visit"}
              </div>
              <div style={{ fontSize:13, color:C.slate, lineHeight:1.7 }}>
                {isHomeCollection
                  ? "The lab will call you to confirm the technician's arrival time."
                  : "The lab will call you to confirm your visit time and any preparation instructions."}
              </div>
            </div>
          )}

          {/* Shop info — tap to see address */}
          {selectedQuote?.vendor && (
            <div style={{ background:C.bg, borderRadius:14, padding:"12px 16px", marginBottom:12, border:`1px solid ${C.pearl}` }}>
              <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:4 }}>
               {selectedQuote.vendor.name || (isLab ? "Laboratory" : "Pharmacy")}
              </div>
              {(selectedQuote.vendor?.address||selectedQuote.vendor?.area||selectedQuote.vendor?.city) && (
                <div style={{ fontSize:12, color:C.slate, marginBottom:8 }}>
                  📍 {[
                    selectedQuote.vendor?.address,
                    selectedQuote.vendor?.area,
                    selectedQuote.vendor?.city,
                    selectedQuote.vendor?.pincode
                  ].filter(Boolean).join(", ")}
                </div>
              )}
              {selectedQuote.vendor.phone && (
                <a href={`tel:${selectedQuote.vendor.phone}`} style={{ textDecoration:"none", display:"block" }}>
                  <div style={{ background:C.success+"12", borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
                    <span style={{ fontSize:18 }}>📞</span>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:13, color:C.success }}>Call {selectedQuote.vendor.name}</div>
                      <div style={{ fontSize:11, color:C.slate }}>{selectedQuote.vendor.phone}</div>
                    </div>
                  </div>
                </a>
              )}
            </div>
          )}

          {/* Vendor billing photo — helps patient verify at pickup */}
          {selectedQuote?.billingPhotoUrl && (
            <Card style={{ marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.ink, marginBottom:8 }}>📸 Shop's Billing Photo</div>
              <img src={selectedQuote.billingPhotoUrl} alt="Billing" style={{ width:"100%", borderRadius:12, objectFit:"cover", maxHeight:220, border:`1px solid ${C.pearl}` }} />
              <div style={{ fontSize:11, color:C.slate, marginTop:8, lineHeight:1.5 }}>
                💡 Use this photo to verify your items and prices when you pick up or receive delivery.
              </div>
            </Card>
          )}

          {chosenDelivery && form?.area && (
            <Card style={{ marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.ink, marginBottom:8 }}>{isLab ? "🏠 Sample Collection Address" : "🏠 Delivery Address"}</div>
              {form.flatNo&&<div style={{ fontSize:13, color:C.slate }}>{form.flatNo}{form.street&&`, ${form.street}`}</div>}
              <div style={{ fontSize:13, color:C.ink, fontWeight:600 }}>{form.area}, {form.city} - {form.pincode}</div>
              {form.landmark&&<div style={{ fontSize:12, color:C.mist, marginTop:2 }}>📍 Near {form.landmark}</div>}
            </Card>
          )}

          {isLab && (
            <div style={{ background:C.pearl, borderRadius:14, padding:"12px 16px", marginBottom:12 }}>
              <div style={{ fontWeight:700, fontSize:12, color:C.ink, marginBottom:8 }}>📋 Before your {isHomeCollection?"collection":"visit"}:</div>
              {["Carry your doctor's referral / prescription","Carry a valid photo ID (Aadhaar / PAN)","Inform the lab if you are diabetic or on medications",isHomeCollection?"Ensure someone is home to receive the technician":"Arrive at the lab on time"].map((tip,i)=>(
                <div key={i} style={{ fontSize:12, color:C.slate, marginBottom:4, display:"flex", gap:8 }}>
                  <span style={{ color:accentColor, flexShrink:0 }}>✓</span>{tip}
                </div>
              ))}
            </div>
          )}

          {!isLab && chosenDelivery && (
            <div style={{ background:C.warning+"12", borderRadius:14, padding:"12px 16px", marginBottom:16, border:`1px solid ${C.warning}30` }}>
              <div style={{ fontWeight:700, fontSize:13, color:C.warning, marginBottom:6 }}>⚠️ Delivery Disclaimer</div>
              <div style={{ fontSize:12, color:C.slate, lineHeight:1.7 }}>Denizzy is a marketplace. <strong>Not responsible for delivery</strong> — arranged directly by the shop.</div>
            </div>
          )}

          <Btn full color={accentColor} onClick={()=>navigate("orders")} style={{ marginBottom:10 }}>View My Orders →</Btn>
          <Btn full variant="ghost" onClick={()=>navigate("home")}>Back to Home</Btn>
          <div style={{ background:C.pearl, borderRadius:12, padding:"10px 14px", marginTop:12 }}>
            <div style={{ fontSize:11, color:C.mist, textAlign:"center", lineHeight:1.6 }}>
              🔒 {isLab ? "Your referral is securely retained for 30 days." : "Your prescription and billing photos are securely retained for 30 days."}
            </div>
          </div>
        </div>
      </div>
    );
  }

  
  // ── SUMMARY STEP ─────────────────────────────────────────────────────────────
// ── MAIN STEP — summary + contact + address all in one scrollable page ────────
  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg, paddingBottom:90 }}>
      <TopBar title={isLab?"🔬 Confirm Booking":"✅ Confirm Order"} onBack={()=>navigate("quotes")} color={accentColor} />
      <div style={{ padding:16 }}>

        {/* Vendor */}
        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:15, color:accentColor }}>{selectedQuote?.vendor?.name||"Shop"}</div>
          <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>⭐{selectedQuote?.vendor?.rating||4.0}</div>
        </Card>

        {/* Amount summary */}
        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:10 }}>{isLab?"🧪 Tests & Amount":"💰 Order Summary"}</div>
          {(selectedQuote?.items||[]).map((item,i)=>(
            <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.pearl}`, fontSize:13 }}>
              <span>{item.name||item.itemName}</span><span style={{ fontFamily:"monospace", fontWeight:600 }}>₹{item.price||0}</span>
            </div>
          ))}
          {chosenDelivery && origDelivCharge>0 && (
            <div style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:`1px solid ${C.pearl}`, fontSize:13, color:accentColor }}>
              <span>{isLab?"🏠 Home collection charge":"🚚 Delivery charge"}</span>
              <span style={{ fontFamily:"monospace", fontWeight:600 }}>₹{selectedQuote.deliveryCharge}</span>
            </div>
          )}
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:10, paddingTop:10, borderTop:`2px solid ${C.pearl}` }}>
            <span style={{ fontWeight:700 }}>Total</span>
            <span style={{ fontFamily:"monospace", fontSize:18, fontWeight:700, color:accentColor }}>₹{finalAmount}</span>
          </div>
          {voucherResult?.valid&&(
            <div style={{ marginTop:6, background:C.success+"10", borderRadius:8, padding:"6px 10px", fontSize:12, color:C.success, display:"flex", justifyContent:"space-between" }}>
              <span>Voucher saves ₹{voucherResult.discount}</span>
              <span style={{ fontWeight:800 }}>✅ Applied</span>
            </div>
          )}
          {selectedQuote?.homeDelivery && selectedQuote?._chosenDelivery && selectedQuote?.deliveryCharge == null && (
            <div style={{ background:C.warning+"10", borderRadius:10, padding:"8px 12px", marginTop:10, border:`1px solid ${C.warning}20` }}>
              <div style={{ fontSize:11, color:C.warning, fontWeight:600 }}>⚠️ {isLab?"Home collection charge will be confirmed by the lab.":"Delivery charge will be confirmed by the shop."}</div>
            </div>
          )}
        </Card>

        {/* Savings banner */}
        {comparisonSavings > 0 && quoteCount > 1 && (
          <div style={{ background:"linear-gradient(135deg,#F4A61D,#E85D26)", borderRadius:14, padding:"12px 16px", marginBottom:12, textAlign:"center", boxShadow:"0 6px 16px rgba(244,166,29,0.3)" }}>
            <div style={{ color:C.white, fontWeight:800, fontSize:15 }}>🎉 You're saving ₹{comparisonSavings} by comparing {quoteCount} quotes!</div>
          </div>
        )}

       {/* Order type — fixed from quote page, no toggle */}
        <Card style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div style={{ fontSize:13, color:C.slate }}>📍 {isLab?"Collection Type":"Order Type"}</div>
            <div style={{ fontWeight:700, fontSize:13, color:accentColor }}>
              {chosenDelivery ? (isLab?"🏠 Home Collection":"🚚 Home Delivery") : (isLab?"🔬 Lab Visit":"🏃 Pickup")}
            </div>
          </div>
          <div style={{ fontSize:11, color:C.mist, marginTop:4 }}>← Go back to change</div>
        </Card>

       {isLab && selectedQuote?.availableDate && (
  <Card style={{ marginBottom:12 }}>
    <div style={{ fontSize:12, color:C.slate }}>📅 Appointment Slot</div>
    <div style={{ fontWeight:700, fontSize:14, color:accentColor, marginTop:4 }}>
      {(d=>{const dt=new Date();if(d==="tomorrow")dt.setDate(dt.getDate()+1);return d==="today"?"Today":d==="tomorrow"?"Tomorrow":new Date(d).toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"});})(selectedQuote.availableDate)}
{selectedQuote.availableTime?` · ${selectedQuote.availableTime}`:""}
    </div>
    <div style={{ fontSize:11, color:C.mist, marginTop:3 }}>Lab will confirm exact time after booking</div>
  </Card>
)}

        {/* Phone — always required */}
        <div style={{ background:C.white, borderRadius:14, padding:14, marginBottom:12, border:`1.5px solid ${form.phone.length===10?accentColor+"60":C.pearl}` }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:8, textTransform:"uppercase", letterSpacing:"1px" }}>📞 Mobile Number *</div>
          <input value={form.phone} onChange={e=>setF("phone",e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="10-digit mobile" type="tel" maxLength={10}
            style={{ width:"100%", background:C.bg, border:"none", borderRadius:10, padding:"12px 14px", fontSize:15, outline:"none", boxSizing:"border-box" }} />
        </div>

        {/* Address — only for home delivery/collection */}
        {chosenDelivery && (
          <div style={{ background:C.white, borderRadius:14, padding:14, marginBottom:12, border:`1px solid ${C.pearl}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, marginBottom:10, textTransform:"uppercase", letterSpacing:"1px" }}>📍 {isLab?"Collection Address":"Delivery Address"}</div>
            {/* Saved addresses */}
            {savedAddresses.length>0 && !useNewAddr && (
              <div style={{ marginBottom:12 }}>
                {savedAddresses.map((addr,i)=>(
                  <div key={i} onClick={()=>{ setF("flatNo",addr.flatNo||""); setF("street",addr.street||""); setF("area",addr.area||""); setF("city",addr.city||"Bangalore"); setF("pincode",addr.pincode||""); setF("landmark",addr.landmark||""); }}
                    style={{ padding:"10px 12px", background:C.bg, borderRadius:12, marginBottom:6, border:`2px solid ${form.area===addr.area&&form.pincode===addr.pincode?accentColor:C.pearl}`, cursor:"pointer" }}>
                    <div style={{ fontWeight:700, fontSize:13, color:C.ink }}>{addr.flatNo&&`${addr.flatNo}, `}{addr.street}</div>
                    <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>{addr.area}, {addr.city} - {addr.pincode}</div>
                    {form.area===addr.area&&form.pincode===addr.pincode&&<div style={{ fontSize:11, color:accentColor, fontWeight:700, marginTop:2 }}>✓ Selected</div>}
                  </div>
                ))}
                <button onClick={()=>setUseNewAddr(true)} style={{ background:"none", border:`1.5px dashed ${accentColor}`, borderRadius:10, padding:"8px 14px", color:accentColor, fontWeight:600, fontSize:12, cursor:"pointer", width:"100%", marginTop:4 }}>+ Add New Address</button>
              </div>
            )}
            {/* Address form */}
            {(useNewAddr || savedAddresses.length===0) && (
              <>
                {savedAddresses.length>0&&<button onClick={()=>setUseNewAddr(false)} style={{ background:"none",border:"none",color:accentColor,fontSize:12,cursor:"pointer",marginBottom:10,fontWeight:600,padding:0 }}>← Use Saved Address</button>}
                {[["flatNo","Flat / House No. *","e.g. 12/3"],["street","Street / Road","e.g. Main Road"],["area","Area *","e.g. Hulimavu"],["city","City","Bangalore"],["pincode","Pincode *","6-digit","tel"],["landmark","Landmark (optional)","Near Bus Stop"]].map(([field,label,ph,type="text"])=>(
                  <div key={field} style={{ marginBottom:10 }}>
                    <div style={{ fontSize:11, color:C.slate, marginBottom:4 }}>{label}</div>
                    <input value={form[field]} onChange={e=>setF(field,e.target.value)} placeholder={ph} type={type}
                      style={{ width:"100%", background:C.bg, border:`1.5px solid ${form[field]?accentColor+"60":C.pearl}`, borderRadius:10, padding:"10px 12px", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Voucher */}
        <div style={{ background:C.white, borderRadius:14, padding:14, marginBottom:12, border:`1.5px solid ${voucherResult?.valid?C.success+"60":C.pearl}` }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>🎟️ Voucher / Referral Code</div>

          {/* Referral voucher chips — shown first, filtered by vertical */}
          {referralVouchers.filter(v=>!v.vertical||v.vertical==="both"||v.vertical===orderVertical).length>0 && !voucherResult?.valid && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:10, color:C.saffron, fontWeight:700, marginBottom:5 }}>🎁 Your Referral Rewards</div>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {referralVouchers
                  .filter(v=>!v.vertical||v.vertical==="both"||v.vertical===orderVertical)
                  .map((v,i)=>(
                  <button key={i} onClick={()=>{ setVoucherCode(v.code); setVoucherResult(null); }}
                    style={{ background:`linear-gradient(135deg,${C.saffron}20,${C.ocean}08)`, border:`1.5px dashed ${C.saffron}70`, borderRadius:20, padding:"5px 12px", fontSize:12, fontWeight:700, color:C.deep, cursor:"pointer" }}>
                    <span style={{ fontFamily:"monospace", letterSpacing:1 }}>{v.code}</span> <span style={{ color:C.saffron }}>{v.discountType==="flat"?"₹":""}{v.discountAmount}{v.discountType==="percent"?"%":""} off</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* General public voucher chips */}
          {availableVouchers.length>0 && !voucherResult?.valid && (
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
              {availableVouchers
                .filter(v => !v.vertical || v.vertical === "both" || v.vertical === orderVertical)
                .slice(0,5).map((v,i)=>(
                <button key={i} onClick={()=>{ setVoucherCode(v.code); setVoucherResult(null); }}
                  style={{ background:`linear-gradient(135deg,${C.ocean}12,${C.saffron}10)`, border:`1.5px dashed ${C.ocean}50`, borderRadius:20, padding:"5px 12px", fontSize:12, fontWeight:700, color:C.deep, cursor:"pointer" }}>
                  <span style={{ fontFamily:"monospace", letterSpacing:1 }}>{v.code}</span> <span style={{ color:C.success }}>{v.discountType==="flat"?"₹":""}{v.discountAmount}{v.discountType==="percent"?"%":""} off</span>
                </button>
              ))}
            </div>
          )}
          <div style={{ display:"flex", gap:8 }}>
            <input value={voucherCode} onChange={e=>{ setVoucherCode(e.target.value.toUpperCase()); setVoucherResult(null); }} placeholder="Enter code (e.g. DENI50)"
              style={{ flex:1, background:C.bg, border:`1.5px solid ${C.pearl}`, borderRadius:10, padding:"10px 12px", fontSize:13, outline:"none", textTransform:"uppercase", letterSpacing:1 }} />
            <button onClick={validateVoucher} disabled={voucherLoading||!voucherCode.trim()}
              style={{ background:accentColor, border:"none", borderRadius:10, padding:"0 16px", color:C.white, fontWeight:700, fontSize:13, cursor:"pointer", opacity:(!voucherCode.trim()||voucherLoading)?0.5:1 }}>
              {voucherLoading?"...":"Apply"}
            </button>
          </div>
          {voucherResult&&<div style={{ marginTop:8, fontSize:13, fontWeight:600, color:voucherResult.valid?C.success:C.danger }}>{voucherResult.valid?`✅ ${voucherResult.message}`:`❌ ${voucherResult.message}`}</div>}
        </div>

        {error&&<div style={{ background:C.danger+"15", borderRadius:12, padding:"10px 14px", marginBottom:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}

        {isLab && selectedQuote?.vendor?.phone && (
          <div style={{ background:accentColor+"08", borderRadius:14, padding:"10px 14px", marginBottom:12, border:`1px solid ${accentColor}20` }}>
            <div style={{ fontSize:12, color:accentColor }}>📞 After booking, the lab will call you to confirm timing and preparation instructions.</div>
          </div>
        )}
      </div>

      {/* Sticky confirm button */}
      <div style={{ position:"fixed", bottom:0, left:"50%", transform:"translateX(-50%)", width:"100%", maxWidth:390, padding:"12px 16px", background:C.white, borderTop:`1px solid ${C.pearl}`, zIndex:50 }}>
        <Btn full color={accentColor}
disabled={form.phone.length!==10||(chosenDelivery&&(!form.flatNo||!form.area||form.pincode.length!==6))}
          onClick={placeOrder} loading={loading}>
          {isLab?"✅ Confirm Booking →":"✅ Confirm Order →"}
        </Btn>
        {!isValid && (
          <div style={{ textAlign:"center", fontSize:11, color:C.mist, marginTop:6 }}>
            {form.phone.length!==10 ? "Enter your mobile number to continue" : "Enter delivery address to continue"}
          </div>
        )}
      </div>
    </div>
  );
}
function OrdersScreen({ orders, loading, onSelectOrder, onRateOrder, navigate }) {
  const [showAll, setShowAll] = useState(false);
  const statusColor = { confirmed:C.success, preparing:C.saffron, ready:C.ocean, out_for_delivery:C.ocean, delivered:C.slate, cancelled:C.danger };
  const getStatusLabel = (order) => {

  // 🔬 LAB ORDERS
  if (order.vertical === "lab") {

    if (order.deliveryType === "home") {
      return {
        confirmed: "✅ Booking Confirmed",
        preparing: "🧪 Preparing for Home Collection",
        packed: "🚗 Staff Out for Collection",
        delivered: "🧪 Sample Collected",
        report_shared: "📄 Report Shared",
        cancelled: "❌ Cancelled"
      };
    }

    // 🔬 Lab Visit
    return {
      confirmed: "✅ Booking Confirmed",
      preparing: "🧪 Preparing for Lab Visit",
      ready: "📍 Ready for Lab Visit",
      delivered: "🧪 Sample Collected",
      report_shared: "📄 Report Shared",
      cancelled: "❌ Cancelled"
    };
  }

  // 💊 MEDICINE
  return {
    confirmed: "✅ Confirmed",
    preparing: "⚙️ Preparing",
    packed: "📦 Packed for Delivery",
    ready: "📦 Ready for Pickup",
    out_for_delivery: "🚚 Out for Delivery",
    delivered: "🏁 Delivered",
    cancelled: "❌ Cancelled"
  };

};
  const ratedIds = (() => { try { return JSON.parse(ls.get("dz_rated_orders")||"[]"); } catch { return []; } })();

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="📦 My Orders" onBack={()=>navigate("home")} />
      <div style={{ padding:"12px 16px" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:"60px 0", color:C.mist }}>Loading orders...</div>
        ) : orders.length===0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📦</div>
            <div style={{ fontSize:15, fontWeight:600, color:C.ink }}>No orders yet</div>
            <div style={{ fontSize:13, color:C.slate, marginTop:6 }}>Your confirmed orders will appear here</div>
          </div>
        ) : (showAll ? orders : orders.slice(0,5)).map(order => {
          const color = order.vertical==="medical" ? C.health : LABS_COLOR;
          const sColor = statusColor[order.status] || C.slate;
          const needsRating =
(
  (order.vertical==="medical" && order.status==="delivered") ||
  (order.vertical==="lab" && order.status==="report_shared")
) &&
!ratedIds.includes(order._id);
          return (
            <div key={order._id}
              onClick={()=>onSelectOrder&&onSelectOrder(order)}
              style={{ background:C.white, borderRadius:18, border:`1.5px solid ${needsRating?C.saffron+"60":C.pearl}`, marginBottom:14, overflow:"hidden", cursor:"pointer", boxShadow:"0 2px 8px rgba(0,0,0,0.06)" }}>
              {/* Top status bar */}
              <div style={{ background:sColor+"15", padding:"6px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${sColor}20` }}>
                <span style={{ fontSize:11, fontWeight:700, color:sColor }}>{getStatusLabel(order)[order.status] || order.status}</span>
                <span style={{ fontSize:12, fontFamily:"monospace", fontWeight:700, color:color }}>{formatOrderId(order._id, order.vertical, order.orderNumber)}</span>
              </div>
              {/* Main card */}
              <div style={{ padding:"12px 16px", display:"flex", gap:12, alignItems:"center" }}>
                <div style={{ width:46, height:46, borderRadius:14, background:color+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, flexShrink:0 }}>
                  {order.vertical==="medical"?"💊":"🔬"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:C.ink }}>{order.vendor?.name||"Shop"}</div>
                  <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>₹{order.totalAmount||0} · {order.vertical==="lab" ? (order.deliveryType==="home"?"🏠 Home Collection":"🔬 Lab Visit") : (order.deliveryType==="home"?"🚚 Delivery":"🏃 Pickup")}</div>
           {order.vertical==="lab" && order.visitDate && (
  <div style={{ fontSize:11, color:"#0891B2", marginTop:2, fontWeight:600 }}>
    {(d=>{const dt=new Date();if(d==="tomorrow")dt.setDate(dt.getDate()+1);return d==="today"?"Today":d==="tomorrow"?"Tomorrow":new Date(d).toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"});})(order.visitDate)}{order.visitSlot?` · ${order.visitSlot}`:""}
  </div>
)}
                  {order.requestItems?.length>0 && (
                    <div style={{ fontSize:11, color:C.mist, marginTop:3 }}>
                      {order.vertical==="lab"?"🔬":"💊"} {order.requestItems.slice(0,3).map(i=>i.name||i.itemName).filter(Boolean).join(", ")}{order.requestItems.length>3?"...":""}
                    </div>
                  )}
                  <div style={{ fontSize:11, color:C.mist, marginTop:1 }}>{order.createdAt ? new Date(order.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"}) : ""}</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6 }}>
                  {needsRating && (
                    <button onClick={e=>{ e.stopPropagation(); onRateOrder&&onRateOrder(order); }}
                      style={{ background:C.saffron, border:"none", borderRadius:10, padding:"5px 10px", fontSize:11, color:C.ink, fontWeight:700, cursor:"pointer" }}>
                      ⭐ Rate
                    </button>
                  )}
                  <span style={{ color:C.mist, fontSize:20 }}>›</span>
                </div>
              </div>
            </div>
          );
        })}
        {!showAll && orders.length > 5 && (
          <div style={{ padding:"8px 16px 16px", textAlign:"center" }}>
            <button onClick={()=>setShowAll(true)}
              style={{ background:"transparent", border:`1px solid ${C.pearl}`, borderRadius:10, padding:"8px 24px", fontSize:13, color:C.slate, cursor:"pointer", fontWeight:600 }}>
              See all {orders.length} orders →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PROFILE — Swiggy/Zomato style professional profile page
// ═══════════════════════════════════════════════════════════

// Modal screens for Privacy, Terms, About
function InfoModal({ title, onClose, children }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.55)", zIndex:1200, display:"flex", alignItems:"flex-end" }} onClick={onClose}>
      <div style={{ background:C.white, borderRadius:"20px 20px 0 0", width:"100%", maxHeight:"85vh", display:"flex", flexDirection:"column" }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:"16px 20px 12px", borderBottom:`1px solid ${C.pearl}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexShrink:0 }}>
          <div style={{ fontFamily:"Georgia,serif", fontSize:17, fontWeight:700, color:C.deep }}>{title}</div>
          <button onClick={onClose} style={{ background:C.pearl, border:"none", borderRadius:8, width:32, height:32, fontSize:16, cursor:"pointer", color:C.ink }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:"auto", padding:"16px 20px 28px", fontSize:13, color:C.slate, lineHeight:1.8 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REFERRAL SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
function ReferralScreen({ navigate, user }) {
  const [referralData, setReferralData] = useState(null);
  const [myReferralVouchers, setMyReferralVouchers] = useState([]);
  const [copyDone, setCopyDone] = useState(false);

  useEffect(() => {
    api("GET", "/vouchers/referral").then(res => { if (res.success) setReferralData(res.data); });
    api("GET", "/vouchers/my-referral").then(res => { if (res.success) setMyReferralVouchers(res.data || []); });
  }, []);

  const shareOnWhatsApp = () => {
    if (!referralData?.referralCode) return;
    const code = referralData.referralCode;
    const reward = referralData.refereeReward || 30;
    const link = referralData.referralLink || `https://denizzy.com/?ref=${code}`;
    const msg =
`🏥 *Denizzy* — Your local health marketplace!

Order medicines & book lab tests from nearby shops, delivered to your door.

🎁 Use my code *${code}* when you sign up and get *₹${reward} OFF* your first order!

👉 ${link}

_(Download & open the link to apply the discount)_`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const copyLink = async () => {
    if (!referralData?.referralCode) return;
    const link = referralData.referralLink || `https://denizzy.com/?ref=${referralData.referralCode}`;
    try { await navigator.clipboard.writeText(link); setCopyDone(true); setTimeout(()=>setCopyDone(false),2500); }
    catch { window.prompt("Copy your referral link:", link); }
  };

  return (
    <div style={{ flex:1, overflowY:"auto", background:"#F6F7FB" }}>
      <TopBar title="🎁 Refer a Friend" onBack={()=>navigate("home")} />
      <div style={{ padding:16 }}>

        {/* Hero */}
        <div style={{ background:`linear-gradient(135deg, ${C.saffron}, ${C.ocean})`, borderRadius:20, padding:"28px 20px", marginBottom:16, textAlign:"center" }}>
          <div style={{ fontSize:48, marginBottom:10 }}>🎁</div>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, marginBottom:6 }}>Invite Friends, Earn Rewards</div>
          <div style={{ color:"rgba(255,255,255,0.85)", fontSize:13, lineHeight:1.6 }}>
            You earn <strong>₹{referralData?.referrerReward||50}</strong> and your friend gets <strong>₹{referralData?.refereeReward||30} off</strong> their first order
          </div>
        </div>

        {/* Referral code */}
        {referralData?.programActive ? (
          <div style={{ background:C.white, borderRadius:18, padding:"16px 16px", marginBottom:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:13, color:C.slate, marginBottom:8 }}>Your referral code</div>
            <div style={{ background:"#F6F7FB", borderRadius:12, padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12, border:`2px dashed ${C.saffron}` }}>
              <div style={{ fontSize:22, fontWeight:800, letterSpacing:3, color:C.deep, fontFamily:"monospace" }}>{referralData?.referralCode||"..."}</div>
              <button onClick={copyLink}
                style={{ background:copyDone?"#16A34A":C.ocean+"20", border:"none", borderRadius:10, padding:"8px 14px", color:copyDone?C.white:C.ocean, fontWeight:700, fontSize:13, cursor:"pointer" }}>
                {copyDone?"✅ Copied!":"📋 Copy"}
              </button>
            </div>
            <button onClick={shareOnWhatsApp}
              style={{ width:"100%", background:"#25D366", border:"none", borderRadius:12, padding:"14px 0", color:C.white, fontWeight:700, fontSize:15, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:8 }}>
              💬 Share on WhatsApp
            </button>
            <div style={{ fontSize:11, color:C.slate, textAlign:"center" }}>
              Opens WhatsApp with your code pre-filled · Friend gets ₹{referralData?.refereeReward||30} off
            </div>
            {referralData?.referralCount > 0 && (
              <div style={{ marginTop:10, background:C.success+"10", borderRadius:10, padding:"8px 12px", textAlign:"center" }}>
                <span style={{ fontSize:13, color:C.success, fontWeight:700 }}>
                  🎉 {referralData.referralCount} friend{referralData.referralCount!==1?"s":""} joined using your code!
                </span>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background:C.white, borderRadius:18, padding:20, marginBottom:12, textAlign:"center" }}>
            <div style={{ fontSize:13, color:C.slate }}>Referral program not active right now. Check back soon!</div>
          </div>
        )}

        {/* How it works */}
        <div style={{ background:C.white, borderRadius:18, padding:"16px 16px", marginBottom:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
          <div style={{ fontSize:13, fontWeight:700, color:C.ink, marginBottom:12 }}>How it works</div>
          {[
            ["1️⃣","Share your code","Send your referral code to a friend"],
            ["2️⃣","Friend signs up","They register on Denizzy with your code"],
            ["3️⃣","Both get rewarded",`You get ₹${referralData?.referrerReward||50}, they get ₹${referralData?.refereeReward||30} off`],
          ].map(([num,title,desc])=>(
            <div key={num} style={{ display:"flex", gap:12, marginBottom:12, alignItems:"flex-start" }}>
              <span style={{ fontSize:20, flexShrink:0 }}>{num}</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:C.ink }}>{title}</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>{desc}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Earned vouchers */}
        {myReferralVouchers.length > 0 && (
          <div style={{ background:C.white, borderRadius:18, padding:"16px 16px", marginBottom:12, boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.saffron, marginBottom:10 }}>🎁 Your Referral Rewards</div>
            {myReferralVouchers.map((v,i)=>(
              <div key={i} style={{ background:`linear-gradient(135deg, ${C.saffron}18, ${C.ocean}08)`, borderRadius:12, padding:"11px 14px", marginBottom:8, border:`1.5px dashed ${C.saffron}60`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontFamily:"monospace", fontSize:15, fontWeight:800, color:C.deep, letterSpacing:1 }}>{v.code}</div>
                  <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>{v.description || "Referral reward"}</div>
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:C.success }}>₹{v.discountAmount||v.amount||0} off</div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}

function ProfileScreen({ navigate, user, settings, onLogout }) {
  const [savedAddresses, setSavedAddresses] = useState(()=>{
    try { return JSON.parse(ls.get("dz_saved_addresses")||"[]"); } catch { return []; }
  });
  const [editingAddr, setEditingAddr] = useState(null);
  const [addrForm, setAddrForm] = useState({ label:"Home", flatNo:"", street:"", area:"", city:"Bangalore", pincode:"", landmark:"" });
  const setAF = (k,v) => setAddrForm(f=>({...f,[k]:v}));
 const [modal, setModal] = useState(null); // "privacy" | "terms" | "about" | "vouchers"
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [nameSaving, setNameSaving] = useState(false);

  const saveName = async () => {
    if (!nameInput.trim()) return;
    setNameSaving(true);
    await api("PUT", "/auth/user/profile", { name: nameInput.trim() });
    setNameSaving(false);
    setEditingName(false);
  };

useEffect(() => {
  const path = window.location.pathname;
  if (path === "/privacy") setModal("privacy");
  else if (path === "/terms") setModal("terms");
}, []);

  const [referralData, setReferralData] = useState(null);
  const [myVouchers, setMyVouchers] = useState([]);
  const [myReferralVouchers, setMyReferralVouchers] = useState([]);

  useEffect(() => {
    api("GET", "/vouchers/referral").then(res => { if (res.success) setReferralData(res.data); });
    api("GET", "/vouchers/my").then(res => {
      if (res.success) setMyVouchers(res.data?.available || []);
    });
    api("GET", "/vouchers/my-referral").then(res => {
      if (res.success) setMyReferralVouchers(res.data || []);
    });
  }, []);

  const [copyDone, setCopyDone] = useState(false);

  const shareOnWhatsApp = () => {
    if (!referralData?.referralCode) return;
    const code = referralData.referralCode;
    const reward = referralData.refereeReward || 30;
    const link = referralData.referralLink || `https://denizzy.com/?ref=${code}`;
    const msg =
`🏥 *Denizzy* — Your local health marketplace!

Order medicines & book lab tests from nearby shops, delivered to your door.

🎁 Use my code *${code}* when you sign up and get *₹${reward} OFF* your first order!

👉 ${link}

_(Download & open the link to apply the discount)_`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const copyReferralLink = async () => {
    if (!referralData?.referralCode) return;
    const code = referralData.referralCode;
    const link = referralData.referralLink || `https://denizzy.com/?ref=${code}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopyDone(true);
      setTimeout(() => setCopyDone(false), 2500);
    } catch {
      // clipboard not available — show the link in an alert as last resort
      window.prompt("Copy your referral link:", link);
    }
  };

  const saveAddr = () => {
    const updated = [...savedAddresses];
    const newAddr = { ...addrForm, savedAt: new Date().toISOString() };
    if (editingAddr === "new") updated.unshift(newAddr);
    else updated[editingAddr] = newAddr;
    setSavedAddresses(updated.slice(0,6));
    ls.set("dz_saved_addresses", JSON.stringify(updated.slice(0,6)));
    setEditingAddr(null);
  };

  const deleteAddr = (i) => {
    const updated = savedAddresses.filter((_,idx)=>idx!==i);
    setSavedAddresses(updated);
    ls.set("dz_saved_addresses", JSON.stringify(updated));
  };

  const startEdit = (i) => {
    setAddrForm(i==="new" ? { label:"Home", flatNo:"", street:"", area:"", city:"Bangalore", pincode:"", landmark:"" } : { ...savedAddresses[i] });
    setEditingAddr(i);
  };

  // Address edit form
  if (editingAddr !== null) return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title={editingAddr==="new"?"➕ Add Address":"✏️ Edit Address"} onBack={()=>setEditingAddr(null)} />
      <div style={{ padding:16 }}>
        <div style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>Label</div>
          <div style={{ display:"flex", gap:8 }}>
            {["Home","Work","Family","Other"].map(l=>(
              <button key={l} onClick={()=>setAF("label",l)}
                style={{ background:addrForm.label===l?C.ocean:C.white, color:addrForm.label===l?C.white:C.ink, border:`1.5px solid ${addrForm.label===l?C.ocean:C.pearl}`, borderRadius:20, padding:"7px 16px", fontSize:12, cursor:"pointer", fontWeight:600 }}>
                {l==="Home"?"🏠":l==="Work"?"💼":l==="Family"?"👨‍👩‍👧":"📍"} {l}
              </button>
            ))}
          </div>
        </div>
        {[
          ["flatNo","Flat / House No. *","e.g. 12/3, Apt 4B"],
          ["street","Street / Road","e.g. Lake Road"],
          ["area","Area / Locality *","e.g. Hulimavu"],
          ["city","City","Bangalore"],
          ["pincode","Pincode *","6-digit","tel"],
          ["landmark","Landmark (optional)","e.g. Near Bus Stop"],
        ].map(([field,label,ph,type="text"])=>(
          <div key={field} style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:6 }}>{label}</div>
            <input value={addrForm[field]||""} onChange={e=>setAF(field,e.target.value)} placeholder={ph} type={type}
              style={{ width:"100%", background:C.white, border:`1.5px solid ${addrForm[field]?C.ocean+"60":C.pearl}`, borderRadius:12, padding:"11px 14px", fontSize:14, outline:"none", boxSizing:"border-box" }} />
          </div>
        ))}
        <Btn full color={C.ocean} disabled={!addrForm.flatNo||!addrForm.area||addrForm.pincode.length!==6} onClick={saveAddr}>
          💾 Save Address
        </Btn>
      </div>
    </div>
  );

  // Quick stats for display
  const initials = user?.name ? user.name.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2) : "U";

  return (
    <div style={{ flex:1, overflowY:"auto", background:"#F6F7FB" }}>

      {/* ── Hero Header ── */}
      <div style={{ background:`linear-gradient(135deg, ${C.deep} 0%, #1A3A5C 100%)`, padding:"0 0 24px", position:"relative", overflow:"hidden" }}>
        {/* Decorative wave */}
        <svg style={{ position:"absolute", bottom:-1, left:0, right:0, opacity:0.12 }} viewBox="0 0 390 40" preserveAspectRatio="none" height="40" width="100%">
          <path d="M0,20 C80,38 160,3 240,20 C320,38 370,5 390,20 L390,40 L0,40 Z" fill="white"/>
        </svg>

        {/* Back button */}
        <div style={{ padding:"12px 16px 0", display:"flex", alignItems:"center", position:"relative" }}>
          <button onClick={()=>navigate("home")} style={{ background:"rgba(255,255,255,0.12)", border:"none", borderRadius:10, width:36, height:36, fontSize:18, cursor:"pointer", color:C.white, display:"flex", alignItems:"center", justifyContent:"center" }}>‹</button>
          <div style={{ flex:1, textAlign:"center", color:"rgba(255,255,255,0.8)", fontWeight:700, fontSize:15 }}>My Profile</div>
          <div style={{ width:36 }}/>
        </div>

        {/* Avatar + name */}
        <div style={{ textAlign:"center", paddingTop:16, position:"relative" }}>
          <div style={{ width:76, height:76, borderRadius:"50%", background:`linear-gradient(135deg, ${C.ocean}, ${C.wave})`, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px", boxShadow:"0 4px 16px rgba(0,0,0,0.25)", border:"3px solid rgba(255,255,255,0.2)" }}>
            <span style={{ color:C.white, fontSize:26, fontWeight:800, fontFamily:"Georgia,serif" }}>{initials}</span>
          </div>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            {user?.name || "Welcome!"}
            <button onClick={()=>{ setNameInput(user?.name||""); setEditingName(true); }}
              style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, width:28, height:28, fontSize:13, cursor:"pointer", color:C.white, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>✏️</button>
          </div>

          {/* Name edit bottom sheet */}
          {editingName && (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1200, display:"flex", alignItems:"flex-end" }} onClick={()=>setEditingName(false)}>
              <div style={{ background:C.white, borderRadius:"20px 20px 0 0", width:"100%", padding:"20px 20px 32px", boxSizing:"border-box" }} onClick={e=>e.stopPropagation()}>
                <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 16px" }} />
                <div style={{ fontWeight:700, fontSize:16, color:C.ink, marginBottom:14 }}>Edit Your Name</div>
                <input value={nameInput} onChange={e=>setNameInput(e.target.value)}
                  placeholder="e.g. Rahul Kumar" autoFocus
                  style={{ width:"100%", background:C.bg, border:`2px solid ${nameInput?C.ocean:C.pearl}`, borderRadius:14, padding:"14px 16px", fontSize:15, outline:"none", boxSizing:"border-box", marginBottom:12 }} />
                <div style={{ fontSize:12, color:C.slate, marginBottom:16 }}>This name helps pharmacies and labs address you personally.</div>
                <Btn full color={C.ocean} disabled={!nameInput.trim()} loading={nameSaving} onClick={saveName}>Save Name</Btn>
                <button onClick={()=>setEditingName(false)} style={{ width:"100%", background:"none", border:"none", color:C.slate, fontSize:14, cursor:"pointer", marginTop:10, padding:"8px 0", fontWeight:600, fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ color:"rgba(255,255,255,0.55)", fontSize:13, marginTop:4 }}>
            +91 {user?.phone||"—"}
          </div>
          {/* Badges row */}
          <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:12, flexWrap:"wrap" }}>
            <div style={{ background:"rgba(255,255,255,0.12)", borderRadius:20, padding:"4px 12px", fontSize:11, color:"rgba(255,255,255,0.8)", fontWeight:600 }}>
              💊 Medicine Orders
            </div>
            <div style={{ background:"rgba(255,255,255,0.12)", borderRadius:20, padding:"4px 12px", fontSize:11, color:"rgba(255,255,255,0.8)", fontWeight:600 }}>
              🔬 Lab Tests
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding:"16px 14px 24px" }}>

        {/* ── Quick Actions ── */}
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:16 }}>
          {[
            { icon:"📦", label:"My Orders", action:()=>navigate("orders") },
            { icon:"🔔", label:"Alerts", action:()=>navigate("alerts") },
            { icon:"🎧", label:"Support", action:()=>navigate("support") },
          ].map((item,i)=>(
            <button key={i} onClick={item.action}
              style={{ background:C.white, border:`1px solid ${C.pearl}`, borderRadius:16, padding:"14px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"pointer", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>
              <span style={{ fontSize:22 }}>{item.icon}</span>
              <span style={{ fontSize:11, fontWeight:700, color:C.ink }}>{item.label}</span>
            </button>
          ))}
        </div>

        {/* ── Saved Addresses ── */}
        <div style={{ background:C.white, borderRadius:18, marginBottom:12, overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
          <div style={{ padding:"16px 16px 12px", display:"flex", justifyContent:"space-between", alignItems:"center", borderBottom:`1px solid ${C.pearl}` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:34, height:34, borderRadius:10, background:C.ocean+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>📍</div>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>Saved Addresses</div>
                <div style={{ fontSize:11, color:C.slate }}>{savedAddresses.length} saved location{savedAddresses.length!==1?"s":""}</div>
              </div>
            </div>
            <button onClick={()=>startEdit("new")} style={{ background:C.ocean, border:"none", borderRadius:10, padding:"6px 14px", color:C.white, fontWeight:700, fontSize:12, cursor:"pointer" }}>+ Add</button>
          </div>
          {savedAddresses.length===0 ? (
            <div style={{ padding:"20px 16px", textAlign:"center" }}>
              <div style={{ fontSize:11, color:C.mist, marginBottom:10 }}>No saved addresses yet</div>
              <button onClick={()=>startEdit("new")} style={{ background:"none", border:`1.5px dashed ${C.ocean}`, borderRadius:10, padding:"8px 20px", color:C.ocean, fontWeight:600, fontSize:12, cursor:"pointer" }}>
                + Add Your First Address
              </button>
            </div>
          ) : savedAddresses.map((addr,i)=>(
            <div key={i} style={{ padding:"12px 16px", borderBottom:i<savedAddresses.length-1?`1px solid ${C.pearl}`:"none", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:C.ocean+"12", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                {addr.label==="Home"?"🏠":addr.label==="Work"?"💼":addr.label==="Family"?"👨‍👩‍👧":"📍"}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.ocean, marginBottom:2 }}>{addr.label}</div>
                <div style={{ fontSize:13, fontWeight:600, color:C.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{[addr.flatNo, addr.street].filter(Boolean).join(", ")}</div>
                <div style={{ fontSize:12, color:C.slate }}>{addr.area}, {addr.city} - {addr.pincode}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                <button onClick={()=>startEdit(i)} style={{ background:C.ocean+"12", border:"none", borderRadius:8, width:30, height:30, color:C.ocean, cursor:"pointer", fontSize:14 }}>✏️</button>
                <button onClick={()=>deleteAddr(i)} style={{ background:C.danger+"12", border:"none", borderRadius:8, width:30, height:30, color:C.danger, cursor:"pointer", fontSize:14 }}>🗑️</button>
              </div>
            </div>
          ))}
        </div>

        {/* ── Referrals & Vouchers ── */}
        {referralData?.programActive && (
          <div style={{ background:C.white, borderRadius:18, marginBottom:12, overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
            <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.pearl}` }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.mist, textTransform:"uppercase", letterSpacing:"1.5px" }}>Referrals & Rewards</div>
            </div>

            {/* Referral code card */}
            <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.pearl}` }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <div style={{ width:38, height:38, borderRadius:12, background:C.saffron+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>🔗</div>
                <div>
                  <div style={{ fontSize:14, fontWeight:700, color:C.ink }}>Invite Friends</div>
                  <div style={{ fontSize:12, color:C.slate }}>You get ₹{referralData?.referrerReward||50}, they get ₹{referralData?.refereeReward||30}</div>
                </div>
              </div>
              <div style={{ background:"#F6F7FB", borderRadius:12, padding:"10px 14px", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, border:`1.5px dashed ${C.saffron}` }}>
                <div>
                  <div style={{ fontSize:11, color:C.slate, marginBottom:2 }}>Your referral code</div>
                  <div style={{ fontSize:18, fontWeight:800, letterSpacing:2, color:C.deep, fontFamily:"monospace" }}>{referralData?.referralCode||"..."}</div>
                </div>
                <button onClick={copyReferralLink}
                  style={{ background:copyDone?"#16A34A":C.ocean+"20", border:"none", borderRadius:10, padding:"7px 12px", color:copyDone?"white":C.ocean, fontWeight:700, fontSize:12, cursor:"pointer" }}>
                  {copyDone ? "✅ Copied!" : "📋 Copy"}
                </button>
              </div>
              {/* WhatsApp share — most reliable on Android/Capacitor */}
              <button onClick={shareOnWhatsApp}
                style={{ width:"100%", background:"#25D366", border:"none", borderRadius:12, padding:"12px 0", color:"white", fontWeight:700, fontSize:14, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, marginBottom:6 }}>
                💬 Share on WhatsApp
              </button>
              <div style={{ fontSize:11, color:C.slate, textAlign:"center", marginBottom:10 }}>
                Opens WhatsApp with a message your friends can use to install and get ₹{referralData?.refereeReward||30} off
              </div>
              {referralData?.referralCount > 0 && (
                <div style={{ fontSize:12, color:C.success, fontWeight:600 }}>
                  🎉 {referralData.referralCount} friend{referralData.referralCount!==1?"s":""} joined using your code!
                </div>
              )}
            </div>

            {/* Available vouchers */}
            {/* ── 🎁 Referral Vouchers — shown first, separate section ── */}
            {myReferralVouchers.length > 0 && (
              <div style={{ padding:"12px 16px", borderBottom:`1px solid ${C.pearl}` }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.saffron, marginBottom:10, textTransform:"uppercase", letterSpacing:"0.5px" }}>🎁 Your Referral Rewards</div>
                {myReferralVouchers.map((v,i)=>(
                  <div key={i} style={{ background:`linear-gradient(135deg, ${C.saffron}18, ${C.ocean}08)`, borderRadius:12, padding:"11px 14px", marginBottom:8, border:`1.5px dashed ${C.saffron}60`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
                        <span style={{ fontFamily:"monospace", fontSize:14, fontWeight:800, color:C.deep, letterSpacing:1 }}>{v.code}</span>
                        {v.type==="welcome" && <span style={{ fontSize:10, background:C.saffron+"30", color:C.deep, borderRadius:6, padding:"1px 6px", fontWeight:700 }}>Welcome</span>}
                        {v.type==="reward"  && <span style={{ fontSize:10, background:C.ocean+"20",  color:C.ocean,borderRadius:6, padding:"1px 6px", fontWeight:700 }}>Reward</span>}
                      </div>
                      <div style={{ fontSize:11, color:C.slate }}>
                        {v.description || (v.type==="welcome" ? "Use on your first order" : "Referral reward")}
                        {v.vertical && v.vertical!=="both" ? ` · ${v.vertical==="lab"?"🔬 Lab only":"💊 Medical only"}` : ""}
                      </div>
                    </div>
                    <div style={{ fontSize:17, fontWeight:800, color:C.saffron, marginLeft:10 }}>
                      {v.discountType==="flat"?"₹":""}{v.discountAmount}{v.discountType==="percent"?"%":""} off
                    </div>
                  </div>
                ))}
                <div style={{ fontSize:11, color:C.mist }}>Apply these codes at checkout</div>
              </div>
            )}

            {/* ── 🎟️ General Vouchers ── */}
            {myVouchers.length > 0 && (
              <div style={{ padding:"12px 16px" }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.slate, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.5px" }}>🎟️ Available Vouchers</div>
                {myVouchers.map((v,i)=>(
                  <div key={i} style={{ background:`linear-gradient(135deg, ${C.ocean}12, ${C.saffron}10)`, borderRadius:12, padding:"10px 14px", marginBottom:8, border:`1px dashed ${C.ocean}40`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:800, color:C.deep, fontFamily:"monospace", letterSpacing:1 }}>{v.code}</div>
                      <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>
                        {v.description||"Discount voucher"}
                        {v.vertical&&v.vertical!=="both"?` · ${v.vertical==="lab"?"🔬 Lab only":"💊 Medical only"}`:""}
                        {v.minOrderAmount>0?` · Min ₹${v.minOrderAmount}`:""}
                      </div>
                    </div>
                    <div style={{ fontSize:16, fontWeight:800, color:C.success }}>
                      {v.discountType==="flat"?"₹":""}{v.discountAmount}{v.discountType==="percent"?"%":""} off
                    </div>
                  </div>
                ))}
                <div style={{ fontSize:11, color:C.mist, marginTop:4 }}>Apply these codes at checkout</div>
              </div>
            )}
          </div>
        )}

        {/* ── Account Section ── */}
        <div style={{ background:C.white, borderRadius:18, marginBottom:12, overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
          <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.pearl}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.mist, textTransform:"uppercase", letterSpacing:"1.5px" }}>Account</div>
          </div>
          {[
            { icon:"🏪", iconBg:"#FF6B3520", label:"Register your Shop/Lab", sub:"Become a Denizzy vendor", action: openVendorSite },
            { icon:"💊", iconBg:C.health+"20", label:"Medicine Reminders", sub:"Set alarms for medicines", action:()=>navigate("reminders") },
          ].map((item,i,arr)=>(
            <div key={i} onClick={item.action}
              style={{ padding:"13px 16px", display:"flex", alignItems:"center", gap:12, cursor:"pointer", borderBottom:i<arr.length-1?`1px solid ${C.pearl}`:"none" }}>
              <div style={{ width:38, height:38, borderRadius:12, background:item.iconBg||C.ocean+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{item.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.ink }}>{item.label}</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:1 }}>{item.sub}</div>
              </div>
              <span style={{ color:C.mist, fontSize:18 }}>›</span>
            </div>
          ))}
        </div>

        {/* ── Legal / About ── */}
        <div style={{ background:C.white, borderRadius:18, marginBottom:12, overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
          <div style={{ padding:"14px 16px", borderBottom:`1px solid ${C.pearl}` }}>
            <div style={{ fontSize:11, fontWeight:700, color:C.mist, textTransform:"uppercase", letterSpacing:"1.5px" }}>Legal & Info</div>
          </div>
          {[
            { icon:"🔒", iconBg:"#6366F120", label:"Privacy Policy", sub:"How we handle your data", external:"https://denizzy.com/privacy-policy.html" },
     { icon:"📄", iconBg:"#0EA5E920", label:"Terms & Conditions", sub:"Platform usage terms", external:"https://denizzy.com/terms-and-conditions.html" },
            { icon:"ℹ️", iconBg:C.saffron+"20", label:"About Denizzy", sub:"Our mission and story", modal:"about" },
          ].map((item,i,arr)=>(
            <div key={i} onClick={()=>item.external ? window.open(item.external,"_blank") : setModal(item.modal)}
              style={{ padding:"13px 16px", display:"flex", alignItems:"center", gap:12, cursor:"pointer", borderBottom:i<arr.length-1?`1px solid ${C.pearl}`:"none" }}>
              <div style={{ width:38, height:38, borderRadius:12, background:item.iconBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{item.icon}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:C.ink }}>{item.label}</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:1 }}>{item.sub}</div>
              </div>
              <span style={{ color:C.mist, fontSize:18 }}>›</span>
            </div>
          ))}
        </div>

        {/* ── Logout ── */}
        <div style={{ background:C.white, borderRadius:18, marginBottom:20, overflow:"hidden", boxShadow:"0 1px 6px rgba(0,0,0,0.06)" }}>
          <button onClick={onLogout}
            style={{ width:"100%", padding:"15px 16px", background:"none", border:"none", display:"flex", alignItems:"center", gap:12, cursor:"pointer" }}>
            <div style={{ width:38, height:38, borderRadius:12, background:C.danger+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>🚪</div>
            <div style={{ flex:1, textAlign:"left" }}>
              <div style={{ fontSize:14, fontWeight:600, color:C.danger }}>Logout</div>
              <div style={{ fontSize:12, color:C.mist, marginTop:1 }}>Sign out of your account</div>
            </div>
            <span style={{ color:C.danger, fontSize:18 }}>›</span>
          </button>
        </div>

        {/* Version footer */}
        <div style={{ textAlign:"center", color:C.mist, fontSize:11, paddingBottom:8 }}>
          Denizzy v{settings?.appVersion||"1.0.0"} · denizzy.com
          <div style={{ marginTop:2, fontSize:10 }}>Made with ❤️ in Bangalore</div>
        </div>
      </div>

      {/* ── Privacy Policy Modal ── */}
      {modal==="privacy" && (
        <InfoModal title="🔒 Privacy Policy" onClose={()=>setModal(null)}>
          <p style={{ marginBottom:12 }}><b>Last updated: June 2026</b></p>
          <p style={{ marginBottom:12 }}>Denizzy is a hyperlocal marketplace platform. We take your privacy seriously. Here is how we handle your information.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>1. Information We Collect</div>
          <p style={{ marginBottom:10 }}>We collect your mobile number (for login via OTP), name, address, and location (GPS) when you use the app — as a patient or as a vendor partner. We also collect your medicine and lab test requests to connect you with nearby vendors.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>2. How We Use Your Information</div>
          <p style={{ marginBottom:10 }}>Your phone number is used solely for OTP-based login. Your request details (medicines/tests, location) are shared with nearby vendors so they can send you quotes. Your delivery address is shared with the vendor only after you confirm an order.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>3. Data Sharing</div>
          <p style={{ marginBottom:10 }}>We do not sell your personal data to any third parties. Vendor partners receive only the minimum information needed to fulfill your order. We use Fast2SMS for OTP delivery and Firebase for push notifications.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>4. Vendor Partner Data</div>
<p style={{ marginBottom:10 }}>If you register as a vendor partner, we additionally collect your shop name, owner name, shop address, GSTIN, drug license / lab registration number, and shop location (GPS) to verify and list your business. This information is shown to customers (except where marked confidential) so they can find and order from your shop. We use this data solely to operate the marketplace and do not sell it to third parties.</p>
<div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>5. Data Security</div>
          <p style={{ marginBottom:10 }}>Your data is stored securely on MongoDB Atlas servers. We use JWT tokens for authentication and HTTPS for all communications.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>6. Your Rights</div>
          <p style={{ marginBottom:10 }}>You can delete your account by contacting us at support@denizzy.com. Upon deletion, your personal data is removed from our systems within 30 days.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>7. Contact Us</div>
          <p>For any privacy concerns, contact: <b>support@denizzy.com</b></p>
        </InfoModal>
      )}

      {/* ── Terms & Conditions Modal ── */}
      {modal==="terms" && (
        <InfoModal title="📄 Terms & Conditions" onClose={()=>setModal(null)}>
          <p style={{ marginBottom:12 }}><b>Last updated: June 2026</b></p>
          <p style={{ marginBottom:12 }}><b>Denizzy is a marketplace platform only.</b> We connect patients with independent local pharmacies and diagnostic labs ("Vendors"). Denizzy does not sell, stock, or deliver medicines or conduct lab tests.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>1. Vendor Responsibility</div>
          <p style={{ marginBottom:10 }}>All medicines, products, lab tests and services are provided directly by the Vendor. The Vendor is solely responsible for the genuineness, quality, expiry, storage and safety of all medicines; holding a valid Drug License and all applicable regulatory approvals; accuracy of prices, billing and invoices; and the quality, accuracy, hygiene and timeliness of lab sample collection and test reports.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>2. Denizzy's Role</div>
          <p style={{ marginBottom:10 }}>Denizzy is not a party to the transaction between you and the Vendor. We are not responsible for the quality, safety, legality, delivery or outcome of any product or service ordered through a Vendor.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>3. Delivery</div>
          <p style={{ marginBottom:10 }}>Home delivery or home sample collection is arranged and performed entirely by the Vendor. Denizzy does not operate its own delivery fleet and is not liable for delivery delays or damages.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>4. Medical Disclaimer</div>
          <p style={{ marginBottom:10 }}>Information on Denizzy is for convenience only and is not medical advice. Always consult a qualified doctor or pharmacist before taking any medicine or undergoing any lab test.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>5. User Conduct</div>
          <p style={{ marginBottom:10 }}>You agree not to misuse the platform, submit false requests, or harass vendors. Denizzy reserves the right to suspend accounts that violate these terms.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>6. Disputes</div>
          <p>Any complaint regarding a product, test, billing or delivery should first be raised with the concerned Vendor. Denizzy may assist in communication but is not liable for resolving such disputes.</p>
        </InfoModal>
      )}

      {/* ── About Denizzy Modal ── */}
      {modal==="about" && (
        <InfoModal title="ℹ️ About Denizzy" onClose={()=>setModal(null)}>
          <div style={{ textAlign:"center", marginBottom:20 }}>
            <div style={{ width:64, height:64, borderRadius:18, background:C.deep, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px", fontSize:30 }}>💊</div>
            <div style={{ fontFamily:"Georgia,serif", fontSize:20, fontWeight:700, color:C.deep }}>Denizzy</div>
            <div style={{ fontSize:12, color:C.slate, marginTop:4 }}>Hyperlocal Health Marketplace</div>
          </div>
          <p style={{ marginBottom:12 }}>Denizzy is a Bangalore-based hyperlocal marketplace that connects patients with nearby pharmacies and diagnostic labs. We help you compare prices, find medicines fast, and book lab tests — all from your phone.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>Our Mission</div>
          <p style={{ marginBottom:12 }}>To make healthcare more accessible and affordable for everyone by connecting patients with trusted local health providers in their neighbourhood.</p>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>What We Offer</div>
          <div style={{ background:C.health+"10", borderRadius:12, padding:12, marginBottom:10, border:`1px solid ${C.health}20` }}>
            <div style={{ fontWeight:700, fontSize:13, color:C.health, marginBottom:4 }}>💊 Denizzy Health</div>
            <div style={{ fontSize:12, color:C.slate }}>Compare medicine prices from nearby pharmacies. Upload your prescription and get quotes from multiple shops instantly.</div>
          </div>
          <div style={{ background:"#0891B220", borderRadius:12, padding:12, marginBottom:16, border:"1px solid #0891B230" }}>
            <div style={{ fontWeight:700, fontSize:13, color:"#0891B2", marginBottom:4 }}>🔬 Denizzy Labs</div>
            <div style={{ fontSize:12, color:C.slate }}>Book diagnostic tests at competitive prices. Choose between home sample collection or lab visit.</div>
          </div>
          <div style={{ fontWeight:700, color:C.ink, marginBottom:6 }}>Contact Us</div>
          <p style={{ marginBottom:4 }}>📧 support@denizzy.com</p>
          <p style={{ marginBottom:4 }}>🌐 denizzy.com</p>
          <p style={{ marginBottom:12 }}>📍 Hulimavu, Bangalore - 560076</p>
          <div style={{ textAlign:"center", fontSize:12, color:C.mist }}>Made with ❤️ in Bangalore · v{settings?.appVersion||"1.0.0"}</div>
        </InfoModal>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// SUPPORT
// ═══════════════════════════════════════════════════════════
function SupportScreen({ settings, navigate }) {
  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="🎧 Support" onBack={navigate ? ()=>navigate("home") : null} />
      <div style={{ padding:16 }}>
        <Card style={{ marginBottom:14 }}>
          {[
            {icon:"💬",bg:"#25D36620",title:"WhatsApp Support",sub:`+91 ${settings?.supportWhatsapp||"9876543210"} · ${settings?.supportHours||"9am-9pm"}`,href:`https://wa.me/91${settings?.supportWhatsapp||"9876543210"}`},
            {icon:"✉️",bg:C.ocean+"20",title:"Email Support",sub:settings?.supportEmail||"support@denizzy.com",href:`mailto:${settings?.supportEmail||"support@denizzy.com"}`},
          ].map((item,i)=>(
            <a key={i} href={item.href} style={{ textDecoration:"none", display:"flex", alignItems:"center", gap:14, padding:"13px 0", borderBottom:i===0?`1px solid ${C.pearl}`:"none" }}>
              <div style={{ width:44, height:44, borderRadius:12, background:item.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22 }}>{item.icon}</div>
              <div style={{ flex:1 }}><div style={{ fontWeight:600, fontSize:14, color:C.ink }}>{item.title}</div><div style={{ fontSize:12, color:C.slate }}>{item.sub}</div></div>
            </a>
          ))}
        </Card>
        {settings?.disclaimerText&&(
          <Card style={{ background:C.warning+"10", border:`1px solid ${C.warning}30` }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.warning, marginBottom:10 }}>⚠️ Platform Disclaimer</div>
            <div style={{ fontSize:13, color:C.slate, lineHeight:1.7 }}>{settings.disclaimerText}</div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// PRESCRIPTION (kept from original)
// ═══════════════════════════════════════════════════════════
function PrescriptionScreen({ navigate, onDone, vertical="medical" }) {
  const isLab = vertical === "lab";
  const [step, setStep] = useState("disclaimer");
  const [accepted, setAccepted] = useState(false);
  const [rxFile, setRxFile] = useState(null);
  const [rxPreview, setRxPreview] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [precompressedBlob, setPrecompressedBlob] = useState(null);
  const [editorMode, setEditorMode] = useState("marker");
  const [markers, setMarkers] = useState([]);
  const [cropBox, setCropBox] = useState(null);
  const [dragging, setDragging] = useState(null); // {type:"new"|"move"|"resize", handle, sx,sy, origBox}
  const [exportedBlob, setExportedBlob] = useState(null);
  const [liveRect, setLiveRect] = useState(null);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // Saved prescriptions
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [savedRxList, setSavedRxList] = useState([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  // Save prompt after upload
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [saveLabel, setSaveLabel] = useState("");
  const [savePerson, setSavePerson] = useState("Me");
  const [savingRx, setSavingRx] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const loadSavedRx = async () => {
    setLoadingSaved(true);
    const res = await api("GET", "/prescriptions");
    if (res.success) setSavedRxList(res.data || []);
    setLoadingSaved(false);
    setShowSavedModal(true);
  };

  const selectSavedRx = (rx) => {
    setUploadedUrl(rx.imageUrl);
    setShowSavedModal(false);
    setSavedOk(true); // already saved — skip save prompt
    setStep("success");
  };

  const saveToWallet = async () => {
    if (!saveLabel.trim()) return;
    setSavingRx(true);
    const res = await api("POST", "/prescriptions", {
      label: saveLabel, personName: savePerson, imageUrl: uploadedUrl,
      vertical: isLab ? "lab" : "medical",
    });
    if (res.success) setSavedOk(true);
    setSavingRx(false);
  };

  // Handle size in % units for hit-testing
  const HANDLE_PCT = 3.5;

  // Returns which handle (if any) the pointer is on for the cropBox
  const getHandle = (x, y, box) => {
    if (!box) return null;
    const { x:bx, y:by, w:bw, h:bh } = box;
    const cx = bx+bw/2, cy = by+bh/2;
    const ht = HANDLE_PCT;
    const near = (hx,hy) => Math.abs(x-hx)<ht && Math.abs(y-hy)<ht;
    if (near(bx,by))       return "tl";
    if (near(bx+bw,by))    return "tr";
    if (near(bx,by+bh))    return "bl";
    if (near(bx+bw,by+bh)) return "br";
    if (near(cx,by))       return "tm";
    if (near(cx,by+bh))    return "bm";
    if (near(bx,cy))       return "ml";
    if (near(bx+bw,cy))    return "mr";
    // Inside box = move
    if (x>bx&&x<bx+bw&&y>by&&y<by+bh) return "move";
    return null;
  };

  const drawHandle = (ctx, hx, hy, W, H) => {
    const px = hx*W/100, py = hy*H/100, r = 10;
    ctx.beginPath(); ctx.arc(px, py, r, 0, Math.PI*2);
    ctx.fillStyle = "white"; ctx.fill();
    ctx.strokeStyle = "#4CAF50"; ctx.lineWidth = 2.5; ctx.stroke();
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas||!img||!img.complete) return;
    const ctx=canvas.getContext("2d"); const W=canvas.width, H=canvas.height;
    ctx.clearRect(0,0,W,H); ctx.drawImage(img,0,0,W,H);
    const box = liveRect && editorMode==="crop" ? liveRect : cropBox;
    if (box && editorMode==="crop") {
      const cx=box.x*W/100, cy=box.y*H/100, cw=box.w*W/100, ch=box.h*H/100;
      // Dim outside
      ctx.fillStyle="rgba(0,0,0,0.55)";
      ctx.fillRect(0,0,W,cy); ctx.fillRect(0,cy+ch,W,H-cy-ch);
      ctx.fillRect(0,cy,cx,ch); ctx.fillRect(cx+cw,cy,W-cx-cw,ch);
      // Show inside
      ctx.drawImage(img,cx,cy,cw,ch,cx,cy,cw,ch);
      // Border
      ctx.strokeStyle="#4CAF50"; ctx.lineWidth=2.5; ctx.setLineDash([]);
      ctx.strokeRect(cx,cy,cw,ch);
      // Grid lines (rule of thirds)
      ctx.strokeStyle="rgba(255,255,255,0.3)"; ctx.lineWidth=1;
      ctx.beginPath(); ctx.moveTo(cx+cw/3,cy); ctx.lineTo(cx+cw/3,cy+ch); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx+2*cw/3,cy); ctx.lineTo(cx+2*cw/3,cy+ch); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx,cy+ch/3); ctx.lineTo(cx+cw,cy+ch/3); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx,cy+2*ch/3); ctx.lineTo(cx+cw,cy+2*ch/3); ctx.stroke();
      // Corner + mid handles
      const { x:bx, y:by, w:bw, h:bh } = box;
      const bcx = bx+bw/2, bcy = by+bh/2;
      [[bx,by],[bx+bw,by],[bx,by+bh],[bx+bw,by+bh],[bcx,by],[bcx,by+bh],[bx,bcy],[bx+bw,bcy]].forEach(([hx,hy])=>drawHandle(ctx,hx,hy,W,H));
    }
    ctx.fillStyle="black";
    markers.forEach(m=>ctx.fillRect(m.x*W/100,m.y*H/100,m.w*W/100,m.h*H/100));
    if (liveRect && editorMode==="marker") {
      ctx.fillStyle="rgba(0,0,0,0.75)"; ctx.fillRect(liveRect.x*W/100,liveRect.y*H/100,liveRect.w*W/100,liveRect.h*H/100);
    }
  };
  useEffect(()=>{ redraw(); },[markers,cropBox,liveRect,editorMode]);

  const loadCanvas = (src) => {
    const img=new window.Image();
    img.onload=()=>{ imgRef.current=img; const c=canvasRef.current; if(!c)return; c.width=img.naturalWidth; c.height=img.naturalHeight; redraw(); };
    img.src=src;
  };
  useEffect(()=>{ if(rxPreview) loadCanvas(rxPreview); },[rxPreview]);

  const getPct = (e) => {
    const r=canvasRef.current.getBoundingClientRect();
    return { x:Math.max(0,Math.min(100,((e.clientX-r.left)/r.width)*100)), y:Math.max(0,Math.min(100,((e.clientY-r.top)/r.height)*100)) };
  };

  const onPD = (e) => {
    e.preventDefault();
    const {x,y} = getPct(e);
    if (editorMode==="crop" && cropBox) {
      const handle = getHandle(x, y, cropBox);
      if (handle) {
        setDragging({ type: handle==="move"?"move":"resize", handle, sx:x, sy:y, origBox:{...cropBox} });
        return;
      }
    }
    // Start new draw
    setDragging({ type:"new", sx:x, sy:y });
    setLiveRect(null);
  };

  const onPM = (e) => {
    if (!dragging) return;
    e.preventDefault();
    const {x,y} = getPct(e);
    if (dragging.type==="new") {
      setLiveRect({ x:Math.min(dragging.sx,x), y:Math.min(dragging.sy,y), w:Math.abs(x-dragging.sx), h:Math.abs(y-dragging.sy) });
      return;
    }
    if (dragging.type==="move") {
      const dx=x-dragging.sx, dy=y-dragging.sy;
      const ob=dragging.origBox;
      setCropBox({ x:Math.max(0,Math.min(100-ob.w,ob.x+dx)), y:Math.max(0,Math.min(100-ob.h,ob.y+dy)), w:ob.w, h:ob.h });
      return;
    }
    if (dragging.type==="resize") {
      const ob = dragging.origBox;
      let {x:bx,y:by,w:bw,h:bh} = ob;
      const dx=x-dragging.sx, dy=y-dragging.sy;
      const h=dragging.handle;
      if(h==="tl"){bx+=dx;by+=dy;bw-=dx;bh-=dy;}
      else if(h==="tr"){bw+=dx;by+=dy;bh-=dy;}
      else if(h==="bl"){bx+=dx;bw-=dx;bh+=dy;}
      else if(h==="br"){bw+=dx;bh+=dy;}
      else if(h==="tm"){by+=dy;bh-=dy;}
      else if(h==="bm"){bh+=dy;}
      else if(h==="ml"){bx+=dx;bw-=dx;}
      else if(h==="mr"){bw+=dx;}
      if(bw>3&&bh>3) setCropBox({ x:Math.max(0,bx), y:Math.max(0,by), w:Math.min(100-Math.max(0,bx),bw), h:Math.min(100-Math.max(0,by),bh) });
    }
  };

  const onPU = (e) => {
    if (!dragging) return;
    if (dragging.type==="new" && liveRect && liveRect.w>2 && liveRect.h>2) {
      if (editorMode==="marker") setMarkers(p=>[...p,liveRect]);
      else setCropBox(liveRect);
    }
    setDragging(null);
    setLiveRect(null);
  };

  const exportCanvas = () => new Promise(resolve=>{
    const canvas=canvasRef.current; const img=imgRef.current;
    if(!canvas||!img){resolve(null);return;}
    const W=canvas.width, H=canvas.height;
    const ctx=canvas.getContext("2d");
    ctx.drawImage(img,0,0,W,H);
    if(cropBox){ctx.fillStyle="rgba(0,0,0,0.55)";ctx.fillRect(0,0,W,H);ctx.drawImage(img,cropBox.x*W/100,cropBox.y*H/100,cropBox.w*W/100,cropBox.h*H/100,cropBox.x*W/100,cropBox.y*H/100,cropBox.w*W/100,cropBox.h*H/100);}
    ctx.fillStyle="black"; markers.forEach(m=>ctx.fillRect(m.x*W/100,m.y*H/100,m.w*W/100,m.h*H/100));
    // Scale down to max 1200px before toBlob — avoids slow encoding at full camera resolution (3000x4000+)
    const MAX=1200;
    const srcX=cropBox?Math.round(cropBox.x*W/100):0;
    const srcY=cropBox?Math.round(cropBox.y*H/100):0;
    const srcW=cropBox?Math.round(cropBox.w*W/100):W;
    const srcH=cropBox?Math.round(cropBox.h*H/100):H;
    const ratio=Math.min(MAX/srcW,MAX/srcH,1);
    const out=document.createElement("canvas");
    out.width=Math.round(srcW*ratio); out.height=Math.round(srcH*ratio);
    out.getContext("2d").drawImage(canvas,srcX,srcY,srcW,srcH,0,0,out.width,out.height);
    out.toBlob(resolve,"image/jpeg",0.82);
  });

  const handleFileSelect=(e)=>{ const f=e.target.files[0]; if(!f)return; setRxFile(f); setMarkers([]); setCropBox(null); const r=new FileReader(); r.onload=ev=>{setRxPreview(ev.target.result);setStep("edit");}; r.readAsDataURL(f); };
const pickPrescriptionImage = async (source) => {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
      let perm = await Camera.checkPermissions();
      const needed = source === "camera" ? perm.camera : perm.photos;
      if (needed !== "granted" && needed !== "limited") {
        perm = await Camera.requestPermissions({ permissions: source === "camera" ? ["camera"] : ["photos"] });
      }
      const finalStatus = source === "camera" ? perm.camera : perm.photos;
      if (finalStatus !== "granted" && finalStatus !== "limited") {
        alert("Camera access is needed to capture your prescription. Please enable it in your phone's Settings → Apps → Denizzy → Permissions.");
        return;
      }
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.DataUrl,
        source: source === "camera" ? CameraSource.Camera : CameraSource.Photos,
        quality: 80,
      });
      const blob = await (await fetch(photo.dataUrl)).blob();
      setRxFile(blob);
      setMarkers([]);
      setCropBox(null);
      setRxPreview(photo.dataUrl);
      setStep("edit");
    } catch(e) {
      if (e?.message !== "User cancelled photos app") console.log("Camera error:", e.message);
    }
    return;
  }
  document.getElementById(source === "camera" ? "rx-camera" : "rx-gallery").click();
};

  // uploadAndDone moved inside confirm step

  const accentClr = isLab ? "#0891B2" : C.health;

  if(step==="disclaimer") return (
    <div style={{flex:1,overflowY:"auto",background:C.bg}}>
      <TopBar title={isLab?"📋 Upload Referral / Prescription":"📷 Upload Prescription"} onBack={()=>navigate(isLab?"labsLanding":"medSearch")} color={accentClr} />
      <div style={{padding:16}}>

        {/* How it works */}
        <div style={{background:accentClr+"10",borderRadius:16,padding:18,marginBottom:14,border:`1px solid ${accentClr}20`}}>
          <div style={{fontSize:28,marginBottom:8}}>{isLab?"🔬":"💊"}</div>
          <div style={{fontWeight:700,fontSize:16,color:accentClr,marginBottom:12}}>
            {isLab?"How Lab Test Booking Works":"How Prescription Sharing Works"}
          </div>
          {isLab ? (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {[
                ["1️⃣","Upload referral","Share your doctor's referral so nearby labs can give you accurate test prices."],
                ["2️⃣","Compare quotes","Nearby labs quote their prices. Pick the best."],
                ["3️⃣","Book your slot","While confirming, choose a time slot — Morning, Afternoon, or Evening."],
                ["4️⃣","Lab calls to confirm","The lab will call you to confirm the slot and give preparation instructions (fasting, etc)."],
              ].map(([num,title,desc],i)=>(
                <div key={i} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
                  <span style={{fontSize:20,flexShrink:0}}>{num}</span>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,color:accentClr}}>{title}</div>
                    <div style={{fontSize:12,color:C.slate,marginTop:2,lineHeight:1.5}}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{fontSize:13,color:C.slate,lineHeight:1.9}}>
              📍 Shared with <strong>nearby pharmacists</strong> to get you the <strong>best price</strong>.<br/>
              ✅ <strong>Keep doctor name &amp; date visible</strong> — required for verification.<br/>
              ✏️ You can <strong>black-out or crop</strong> your name/address before sharing.<br/>
              🔒 Prescription verification is the pharmacist's legal responsibility.
            </div>
          )}
        </div>

        {/* Privacy note for lab */}
        {isLab && (
          <div style={{background:C.pearl,borderRadius:12,padding:"10px 14px",marginBottom:14}}>
            <div style={{fontSize:12,color:C.slate,lineHeight:1.6}}>
              🔒 <strong>Privacy:</strong> Your referral is only shared with nearby labs to generate quotes. Lab accreditation and test accuracy is the lab's responsibility. Denizzy is a marketplace platform only.
            </div>
          </div>
        )}

        <div onClick={()=>setAccepted(!accepted)} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"14px 16px",background:C.white,borderRadius:14,border:`1px solid ${accepted?accentClr:C.pearl}`,marginBottom:16,cursor:"pointer"}}>
          <div style={{width:22,height:22,borderRadius:6,border:`2px solid ${accepted?accentClr:C.mist}`,background:accepted?accentClr:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1}}>
            {accepted&&<span style={{color:C.white,fontSize:14}}>✓</span>}
          </div>
          <div style={{fontSize:13,color:C.slate,lineHeight:1.6}}>
            {isLab
              ? "I consent to sharing my referral/prescription with nearby labs to get test quotes."
              : "I understand my prescription will be shared with nearby pharmacies to get me the best price."}
          </div>
        </div>
        <Btn full color={accentClr} disabled={!accepted} onClick={()=>setStep("upload")}>Continue to Upload →</Btn>
      </div>
    </div>
  );

  if(step==="upload") return (
    <div style={{flex:1,overflowY:"auto",background:C.bg}}>
      <TopBar title="📷 Take Prescription Photo" onBack={()=>setStep("disclaimer")} />

      {/* Saved prescriptions bottom sheet */}
      {showSavedModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:900, display:"flex", alignItems:"flex-end" }} onClick={()=>setShowSavedModal(false)}>
          <div style={{ background:C.white, borderRadius:"20px 20px 0 0", padding:20, width:"100%", maxHeight:"75vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:4 }}>📋 My Saved Prescriptions</div>
            <div style={{ fontSize:12, color:C.slate, marginBottom:16 }}>Tap to use — no upload needed</div>
            {loadingSaved ? (
              <div style={{ textAlign:"center", padding:30, color:C.slate }}>Loading...</div>
            ) : savedRxList.length === 0 ? (
              <div style={{ textAlign:"center", padding:30, color:C.slate }}>
                <div style={{ fontSize:36, marginBottom:8 }}>📋</div>
                <div>No saved prescriptions yet.</div>
              </div>
            ) : savedRxList.map(rx=>(
              <div key={rx._id} onClick={()=>selectSavedRx(rx)}
                style={{ display:"flex", gap:12, alignItems:"center", padding:"10px 0", borderBottom:`1px solid ${C.pearl}`, cursor:"pointer" }}>
                <img src={rx.imageUrl} alt={rx.label} style={{ width:56, height:56, objectFit:"cover", borderRadius:10, border:`1px solid ${C.pearl}` }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:14, color:C.text }}>{rx.label}</div>
                  <div style={{ fontSize:12, color:C.slate }}>{rx.personName||"Me"} · {new Date(rx.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short"})}</div>
                </div>
                <div style={{ fontSize:20, color:C.health }}>›</div>
              </div>
            ))}
            <button onClick={()=>setShowSavedModal(false)} style={{ width:"100%", marginTop:16, padding:"12px", background:C.bg, border:`1px solid ${C.pearl}`, borderRadius:12, fontFamily:"inherit", fontSize:14, cursor:"pointer", color:C.slate }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{padding:16}}>
        <input type="file" id="rx-camera" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleFileSelect}/>
        <input type="file" id="rx-gallery" accept="image/*" style={{display:"none"}} onChange={handleFileSelect}/>
        <div onClick={()=>pickPrescriptionImage("camera")} style={{background:C.white,border:`2px dashed ${C.health}`,borderRadius:16,padding:"36px 20px",textAlign:"center",cursor:"pointer",marginBottom:12}}>
          <div style={{fontSize:52,marginBottom:10}}>📷</div>
          <div style={{fontWeight:700,fontSize:16,color:C.health}}>Take Photo</div>
          <div style={{fontSize:13,color:C.slate,marginTop:4}}>Use camera to capture prescription</div>
        </div>
        <div onClick={()=>pickPrescriptionImage("gallery")} style={{background:C.white,border:`2px dashed ${C.mist}`,borderRadius:16,padding:"18px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:14,marginBottom:12}}>
          <div style={{width:48,height:48,borderRadius:14,background:C.pearl,display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>🖼️</div>
          <div><div style={{fontWeight:600,fontSize:14,color:C.ink}}>Choose from Gallery</div><div style={{fontSize:12,color:C.slate}}>Select existing photo</div></div>
        </div>

        {/* Choose from saved prescriptions */}
        <div onClick={loadSavedRx} style={{background:C.ocean+"08",border:`2px dashed ${C.ocean}40`,borderRadius:16,padding:"18px 20px",cursor:"pointer",display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
          <div style={{width:48,height:48,borderRadius:14,background:C.ocean+"15",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26}}>📋</div>
          <div>
            <div style={{fontWeight:700,fontSize:14,color:C.ocean}}>Choose from My Prescriptions</div>
            <div style={{fontSize:12,color:C.slate}}>Reuse a previously saved prescription</div>
          </div>
        </div>

        <div style={{background:C.health+"10",borderRadius:12,padding:"10px 14px",fontSize:12,color:C.slate}}>📍 Shared with nearby pharmacists to get you the best price.</div>
      </div>
    </div>
  );
 
if(step==="edit") return (
    <div style={{minHeight:"100%",display:"flex",flexDirection:"column",background:"#111",overflowY:"auto"}}>
      <div style={{background:C.deep,padding:"10px 16px",display:"flex",alignItems:"center",gap:12,flexShrink:0}}>
        <button onClick={()=>setStep("upload")} style={{background:"rgba(255,255,255,0.15)",border:"none",borderRadius:10,width:36,height:36,color:"white",fontSize:18,cursor:"pointer"}}>‹</button>
        <div style={{flex:1}}><div style={{color:"white",fontWeight:700,fontSize:14}}>Edit Prescription</div><div style={{color:"rgba(255,255,255,0.5)",fontSize:11}}>Mark private areas or crop before sharing</div></div>
      </div>
      <div style={{background:C.deep,padding:"8px 16px 10px",display:"flex",gap:8,flexShrink:0}}>
        <button onClick={()=>setEditorMode("marker")} style={{flex:1,background:editorMode==="marker"?"#000":"rgba(255,255,255,0.12)",border:editorMode==="marker"?"2px solid white":"2px solid transparent",borderRadius:12,padding:"9px 0",color:"white",fontWeight:700,fontSize:13,cursor:"pointer"}}>✏️ Marker</button>
        <button onClick={()=>setEditorMode("crop")} style={{flex:1,background:editorMode==="crop"?"#4CAF50":"rgba(255,255,255,0.12)",border:editorMode==="crop"?"2px solid white":"2px solid transparent",borderRadius:12,padding:"9px 0",color:"white",fontWeight:700,fontSize:13,cursor:"pointer"}}>✂️ Crop</button>
        {markers.length>0&&<button onClick={()=>setMarkers([])} style={{background:"rgba(255,80,80,0.35)",border:"none",borderRadius:12,padding:"9px 12px",color:"white",fontSize:12,cursor:"pointer",fontWeight:600}}>🗑️</button>}
        {cropBox&&<button onClick={()=>setCropBox(null)} style={{background:"rgba(255,80,80,0.35)",border:"none",borderRadius:12,padding:"9px 12px",color:"white",fontSize:12,cursor:"pointer",fontWeight:600}}>↩️</button>}
      </div>
      <div style={{background:"rgba(255,255,255,0.07)",padding:"5px 0",flexShrink:0,textAlign:"center"}}>
        <span style={{fontSize:11,color:"rgba(255,255,255,0.55)"}}>
          {editorMode==="marker"?"✏️ Drag to draw black box over name/address — tap box to remove":"✂️ Drag to set crop area — keeps only what you select"}
        </span>
      </div>
      <div style={{overflowY:"auto",display:"flex",alignItems:"center",justifyContent:"center",background:"#111",padding:8, maxHeight:"calc(100vh - 200px)", minHeight:160}}>
        <canvas ref={canvasRef} style={{maxWidth:"100%",maxHeight:"60vh",touchAction:"none",cursor:"crosshair",borderRadius:8,boxShadow:"0 0 20px rgba(0,0,0,0.5)"}}
          onPointerDown={onPD} onPointerMove={onPM} onPointerUp={onPU} onPointerLeave={()=>{setDragging(null);setLiveRect(null);}}/>
      </div>
      <div style={{background:C.deep,padding:"12px 16px 28px",flexShrink:0}}>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.4)",textAlign:"center",marginBottom:10}}>
          {markers.length>0||cropBox?`${markers.length} area${markers.length!==1?"s":""} marked${cropBox?" · crop set":""}` : "Tap Done to continue without editing"}
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={()=>{setMarkers([]);setCropBox(null);setStep("upload");}} style={{flex:1,background:"rgba(255,255,255,0.1)",border:"none",borderRadius:14,padding:"14px 0",color:"white",fontSize:14,cursor:"pointer"}}>Retake</button>
         <button onClick={async()=>{setExporting(true);const blob=await exportCanvas();setExportedBlob(blob);setStep("confirm");setPrecompressedBlob(null);compressImage(blob,800,0.6).then(c=>setPrecompressedBlob(c)).catch(()=>{});setExporting(false);}}
style={{flex:2,background:exporting?"#888":C.health,border:"none",borderRadius:14,padding:"14px 0",color:"white",fontWeight:700,fontSize:15,cursor:exporting?"not-allowed":"pointer",opacity:exporting?0.8:1}}>{exporting?"⏳ Processing...":"✅ Done"}</button>
        </div>
      </div>
    </div>
  );


const uploadAndDoneInternal = async () => {
    setUploading(true);
    setUploadProgress(0);
    try {
      // Use pre-compressed blob if ready (compressed in background while user read confirm screen)
      let toUpload = precompressedBlob;
      if (!toUpload) {
        const blob = exportedBlob || (rxFile ? await exportCanvas() : null);
        let src = blob || rxFile;
        if (!src) { setUploading(false); return; }
        // Ensure src is a Blob — Capacitor camera returns dataUrl strings in some cases
        if (typeof src === "string" && src.startsWith("data:")) {
          const res = await fetch(src);
          src = await res.blob();
        }
        toUpload = await compressImage(src, 800, 0.6);
      }
      const file = new File([toUpload], "prescription.jpg", { type: "image/jpeg" });
      const fd = new FormData();
      fd.append("photo", file);
      // Use XHR instead of fetch — only XHR gives real upload progress events
      const uploadUrl = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${BASE}/upload/prescription`);
        const token = getToken();
        if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 30000;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data.success && data.url) resolve(data.url);
            else reject(new Error("Upload failed"));
          } catch { reject(new Error("Parse error")); }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.ontimeout = () => reject(new Error("Timeout"));
        xhr.send(fd);
      });
      setUploadProgress(100);
      setUploadedUrl(uploadUrl);
      setStep("success");
    } catch(err){ console.error("Upload:", err.message); }
    setUploading(false);
  };

  if(step==="confirm") return (
    <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:C.bg,padding:24,textAlign:"center"}}>
      <div style={{fontSize:56}}>{isLab?"📋":"📋"}</div>
      <div style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:700,color:accentClr,marginTop:16}}>
        {isLab?"Referral Ready to Upload":"Prescription Ready"}
      </div>
      <div style={{color:C.slate,fontSize:13,marginTop:6,marginBottom:10}}>
        {markers.length>0||cropBox?`${markers.length} area${markers.length!==1?"s":""} marked${cropBox?" · cropped":""}` : "No edits — full document will be shared"}
      </div>
      <div style={{background:accentClr+"10",borderRadius:14,padding:14,marginBottom:28,border:`1px solid ${accentClr}20`,maxWidth:300}}>
        <div style={{fontSize:13,color:C.slate,lineHeight:1.6}}>
          {isLab?"✅ Will be shared with nearby labs to get accurate test prices.":"✅ Will be shared with nearby pharmacies to get you the best price."}
        </div>
      </div>
      <Btn full color={accentClr} loading={uploading} onClick={uploadAndDoneInternal}>
        {uploading?`📤 Uploading ${uploadProgress}%...`:`📤 Upload & ${isLab?"Get Lab Quotes →":"Find Best Prices →"}`}
      </Btn>
      {uploading && (
        <div style={{marginTop:10,width:"100%"}}>
          <div style={{background:C.mist+"40",borderRadius:99,height:7,overflow:"hidden"}}>
            <div style={{background:accentClr,height:"100%",borderRadius:99,width:`${uploadProgress}%`,transition:"width 0.3s ease"}}/>
          </div>
          <div style={{fontSize:11,color:C.mist,marginTop:5,textAlign:"center"}}>
            {uploadProgress<10?"Compressing image...":uploadProgress<100?"Sending securely...":"Almost done..."}
          </div>
        </div>
      )}
      <Btn variant="ghost" style={{marginTop:10}} onClick={()=>setStep("edit")}>← Back to Edit</Btn>
    </div>
  );

  // ── SUCCESS step — shows after successful upload ────────────────────────────
  if(step==="success") return (
    <div style={{flex:1,overflowY:"auto",background:C.bg}}>
      <div style={{background:`linear-gradient(135deg,${isLab?"#065f75,#0891B2":"#1a7a5e,#2eab6e"})`,padding:"32px 24px 28px",textAlign:"center"}}>
        <div style={{fontSize:52,marginBottom:8}}>✅</div>
        <div style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:700,color:"white"}}>
          {isLab?"Referral Uploaded!":"Prescription Uploaded!"}
        </div>
        <div style={{color:"rgba(255,255,255,0.8)",fontSize:13,marginTop:6}}>
          {isLab?"Nearby labs will now quote for your tests.":"Nearby pharmacies will now quote your medicines."}
        </div>
      </div>
      <div style={{padding:16}}>
        {/* Preview of uploaded image */}
        {uploadedUrl && (
          <div style={{marginBottom:16}}>
            <div style={{fontSize:12,fontWeight:700,color:C.slate,textTransform:"uppercase",letterSpacing:"1px",marginBottom:8}}>
              {isLab?"Your Referral / Prescription":"Your Prescription"}
            </div>
            <img src={uploadedUrl} alt={isLab?"Referral":"Prescription"} style={{width:"100%",borderRadius:14,objectFit:"cover",maxHeight:220,border:`2px solid ${accentClr}30`}} />
          </div>
        )}

        {/* Next steps */}
        <div style={{background:accentClr+"10",borderRadius:14,padding:16,marginBottom:16,border:`1px solid ${accentClr}20`}}>
          <div style={{fontWeight:700,fontSize:14,color:accentClr,marginBottom:10}}>📍 What happens next</div>
          {(isLab ? [
            "Nearby labs receive your referral and quote prices",
            "You'll see quotes appear in a few minutes",
            "Compare and confirm the best quote",
            "Choose your slot — lab will call you to confirm",
          ] : [
            "Nearby pharmacies receive your prescription",
            "Quotes will appear in a few minutes",
            "Compare prices and confirm",
          ]).map((step,i)=>(
            <div key={i} style={{display:"flex",gap:10,marginBottom:8,fontSize:13,color:C.slate,alignItems:"flex-start"}}>
              <span style={{color:accentClr,fontWeight:700,flexShrink:0}}>{i+1}.</span>{step}
            </div>
          ))}
        </div>

        <Btn full color={accentClr} onClick={()=>onDone(uploadedUrl)}>
          {isLab?"🔬 Proceed to Send Request →":"💊 Proceed to Request →"}
        </Btn>

        {/* Save for future use prompt */}
        {!savedOk ? (
          <div style={{marginTop:16,background:C.ocean+"08",borderRadius:16,padding:16,border:`1px solid ${C.ocean}20`}}>
            <div style={{fontWeight:700,fontSize:14,color:C.ocean,marginBottom:4}}>💾 Save for future use?</div>
            <div style={{fontSize:12,color:C.slate,marginBottom:12,lineHeight:1.5}}>Reuse this prescription next time — no need to search your gallery.</div>
            {!showSavePrompt ? (
              <Btn full color={C.ocean} onClick={()=>setShowSavePrompt(true)}>💾 Save to My Prescriptions</Btn>
            ) : (
              <>
                <div style={{marginBottom:8}}>
                  <div style={{fontSize:11,color:C.slate,marginBottom:4,fontWeight:600}}>LABEL *</div>
                  <input value={saveLabel} onChange={e=>setSaveLabel(e.target.value)} placeholder={isLab?"e.g. Dad's thyroid test":"e.g. Mom's BP meds"}
                    style={{width:"100%",padding:"10px 12px",border:`1px solid ${C.pearl}`,borderRadius:10,fontSize:14,outline:"none",fontFamily:"inherit",boxSizing:"border-box"}} />
                </div>
                <div style={{marginBottom:12}}>
                  <div style={{fontSize:11,color:C.slate,marginBottom:6,fontWeight:600}}>FOR WHOM</div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {["Me","Mom","Dad","Child"].map(p=>(
                      <div key={p} onClick={()=>setSavePerson(p)}
                        style={{padding:"6px 16px",borderRadius:20,border:`1.5px solid ${savePerson===p?C.health:C.pearl}`,background:savePerson===p?C.health+"15":"transparent",color:savePerson===p?C.health:C.slate,fontSize:13,fontWeight:savePerson===p?700:400,cursor:"pointer"}}>
                        {p}
                      </div>
                    ))}
                  </div>
                </div>
                <Btn full color={C.ocean} loading={savingRx} onClick={saveToWallet} disabled={!saveLabel.trim()}>
                  {savingRx?"Saving...":"✅ Save Prescription"}
                </Btn>
              </>
            )}
          </div>
        ) : (
          <div style={{marginTop:16,background:C.success+"12",borderRadius:14,padding:14,textAlign:"center",border:`1px solid ${C.success}30`}}>
            <div style={{fontSize:24,marginBottom:4}}>✅</div>
            <div style={{fontWeight:700,fontSize:13,color:C.success}}>Saved to My Prescriptions!</div>
            <div style={{fontSize:12,color:C.slate,marginTop:2}}>Find it anytime in the sidebar → My Prescriptions</div>
          </div>
        )}
      </div>
    </div>
  );

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SAVED PRESCRIPTIONS SCREEN — patient prescription wallet
// ═══════════════════════════════════════════════════════════════════════════════
function SavedPrescriptionsScreen({ navigate }) {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editPerson, setEditPerson] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [viewImg, setViewImg] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await api("GET", "/prescriptions");
    if (res.success) setPrescriptions(res.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = (rx) => {
    setEditingId(rx._id);
    setEditLabel(rx.label);
    setEditPerson(rx.personName || "Me");
    setEditNotes(rx.notes || "");
    setMsg("");
  };

  const saveEdit = async () => {
    if (!editLabel.trim()) return setMsg("Label is required");
    setSaving(true);
    const res = await api("PUT", `/prescriptions/${editingId}`, {
      label: editLabel, personName: editPerson, notes: editNotes,
    });
    if (res.success) { setEditingId(null); load(); setMsg("✅ Updated"); }
    else setMsg("❌ Failed to update");
    setSaving(false);
  };

  const deletePrescription = async (id) => {
    const res = await api("DELETE", `/prescriptions/${id}`);
    if (res.success) { setConfirmDelete(null); load(); setMsg("🗑️ Deleted"); }
  };

  const personColors = { "Me":C.health, "Mom":"#E91E8C", "Dad":C.ocean, "Child":"#FF9800" };
  const pColor = (p) => personColors[p] || C.slate;

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="📋 My Prescriptions" onBack={()=>navigate("home")} />

      {/* Full image viewer */}
      {viewImg && (
        <div onClick={()=>setViewImg(null)} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:999, display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}>
          <img src={viewImg} alt="Prescription" style={{ maxWidth:"100%", maxHeight:"90vh", borderRadius:12 }} />
          <div style={{ position:"absolute", top:20, right:20, color:"white", fontSize:28, cursor:"pointer" }}>✕</div>
        </div>
      )}

      {/* Confirm delete */}
      {confirmDelete && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:998, display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ background:C.white, borderRadius:20, padding:24, width:"100%", maxWidth:340, textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🗑️</div>
            <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:8 }}>Delete Prescription?</div>
            <div style={{ fontSize:13, color:C.slate, marginBottom:20 }}>"{confirmDelete.label}" will be permanently removed.</div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={()=>setConfirmDelete(null)} style={{ flex:1, padding:"12px", background:C.bg, border:`1px solid ${C.pearl}`, borderRadius:12, fontFamily:"inherit", fontSize:14, cursor:"pointer", color:C.slate }}>Cancel</button>
              <button onClick={()=>deletePrescription(confirmDelete._id)} style={{ flex:1, padding:"12px", background:C.danger, border:"none", borderRadius:12, fontFamily:"inherit", fontSize:14, fontWeight:700, cursor:"pointer", color:"white" }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding:16 }}>
        {msg&&<div style={{ background:msg.startsWith("✅")?C.success+"12":msg.startsWith("🗑️")?C.warning+"12":C.danger+"12", borderRadius:10, padding:"10px 14px", marginBottom:12, fontSize:13, color:msg.startsWith("✅")?C.success:msg.startsWith("🗑️")?C.warning:C.danger, fontWeight:600 }}>{msg}</div>}

        {loading ? (
          <div style={{ textAlign:"center", padding:60, color:C.slate }}>Loading...</div>
        ) : prescriptions.length === 0 ? (
          <div style={{ textAlign:"center", padding:40 }}>
            <div style={{ fontSize:56, marginBottom:16 }}>📋</div>
            <div style={{ fontWeight:700, fontSize:18, color:C.text, marginBottom:8 }}>No saved prescriptions</div>
            <div style={{ fontSize:13, color:C.slate, marginBottom:24, lineHeight:1.6 }}>
              Save your prescriptions once — reuse them for future medicine requests without searching your phone gallery.
            </div>
            <Btn color={C.health} onClick={()=>navigate("prescription")}>📷 Upload & Save Prescription</Btn>
          </div>
        ) : (
          <>
            <div style={{ fontSize:12, color:C.slate, marginBottom:14, lineHeight:1.5 }}>
              💡 Tap any prescription to view. Use when placing a medicine request — no need to search your gallery.
            </div>
            {prescriptions.map(rx=>(
              <div key={rx._id} style={{ background:C.white, borderRadius:18, marginBottom:12, overflow:"hidden", boxShadow:"0 2px 12px rgba(0,0,0,0.06)", border:`1px solid ${C.pearl}` }}>
                {editingId === rx._id ? (
                  <div style={{ padding:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:12 }}>✏️ Edit Prescription</div>
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:11, color:C.slate, marginBottom:4, fontWeight:600 }}>LABEL</div>
                      <input value={editLabel} onChange={e=>setEditLabel(e.target.value)}
                        style={{ width:"100%", padding:"10px 12px", border:`1px solid ${C.pearl}`, borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                        placeholder="e.g. Dad's BP meds" />
                    </div>
                    <div style={{ marginBottom:10 }}>
                      <div style={{ fontSize:11, color:C.slate, marginBottom:4, fontWeight:600 }}>PERSON</div>
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        {["Me","Mom","Dad","Child","Other"].map(p=>(
                          <div key={p} onClick={()=>setEditPerson(p)}
                            style={{ padding:"6px 14px", borderRadius:20, border:`1.5px solid ${editPerson===p?pColor(p):C.pearl}`, background:editPerson===p?pColor(p)+"15":"transparent", color:editPerson===p?pColor(p):C.slate, fontSize:13, fontWeight:editPerson===p?700:400, cursor:"pointer" }}>
                            {p}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div style={{ marginBottom:14 }}>
                      <div style={{ fontSize:11, color:C.slate, marginBottom:4, fontWeight:600 }}>NOTES (optional)</div>
                      <input value={editNotes} onChange={e=>setEditNotes(e.target.value)}
                        style={{ width:"100%", padding:"10px 12px", border:`1px solid ${C.pearl}`, borderRadius:10, fontSize:14, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
                        placeholder="e.g. Monthly refill" />
                    </div>
                    {msg&&<div style={{ fontSize:12, color:C.danger, marginBottom:8 }}>{msg}</div>}
                    <div style={{ display:"flex", gap:10 }}>
                      <button onClick={()=>setEditingId(null)} style={{ flex:1, padding:"11px", background:C.bg, border:`1px solid ${C.pearl}`, borderRadius:12, fontFamily:"inherit", fontSize:14, cursor:"pointer", color:C.slate }}>Cancel</button>
                      <button onClick={saveEdit} disabled={saving} style={{ flex:2, padding:"11px", background:C.health, border:"none", borderRadius:12, fontFamily:"inherit", fontSize:14, fontWeight:700, cursor:"pointer", color:"white", opacity:saving?0.7:1 }}>
                        {saving?"Saving...":"Save Changes"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display:"flex", gap:0 }}>
                    {/* Thumbnail */}
                    <div onClick={()=>setViewImg(rx.imageUrl)} style={{ width:90, flexShrink:0, cursor:"pointer" }}>
                      <img src={rx.imageUrl} alt={rx.label} style={{ width:"100%", height:100, objectFit:"cover" }} />
                      <div style={{ background:C.health+"15", padding:"4px 0", textAlign:"center" }}>
                        <div style={{ fontSize:10, color:C.health, fontWeight:700 }}>TAP TO VIEW</div>
                      </div>
                    </div>
                    {/* Info */}
                    <div style={{ flex:1, padding:"12px 14px" }}>
                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:4 }}>{rx.label}</div>
                          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <span style={{ background:pColor(rx.personName)+"15", color:pColor(rx.personName), fontSize:11, fontWeight:700, padding:"2px 10px", borderRadius:20 }}>{rx.personName||"Me"}</span>
                            <span style={{ fontSize:11, color:C.mist }}>{rx.vertical==="lab"?"🔬 Lab":"💊 Medical"}</span>
                          </div>
                          {rx.notes&&<div style={{ fontSize:12, color:C.slate }}>{rx.notes}</div>}
                          <div style={{ fontSize:11, color:C.mist, marginTop:4 }}>
                            {new Date(rx.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
                          </div>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:8, marginTop:10 }}>
                        <button onClick={()=>startEdit(rx)} style={{ flex:1, padding:"7px", background:C.ocean+"10", border:`1px solid ${C.ocean}30`, borderRadius:10, fontSize:12, fontWeight:600, color:C.ocean, cursor:"pointer", fontFamily:"inherit" }}>✏️ Edit</button>
                        <button onClick={()=>setConfirmDelete(rx)} style={{ flex:1, padding:"7px", background:C.danger+"10", border:`1px solid ${C.danger}30`, borderRadius:10, fontSize:12, fontWeight:600, color:C.danger, cursor:"pointer", fontFamily:"inherit" }}>🗑️ Delete</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function AlertsScreen({ navigate, currentRequest, orders }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const buildAlerts = async () => {
      const list = [];
      // Check active request for new quotes
      if (currentRequest?._id) {
        try {
          const res = await api("GET", `/requests/${currentRequest._id}`);
          if (res.success && res.data?.quotes?.length > 0) {
            res.data.quotes.forEach(q => {
              list.push({
                id: q._id, type: "quote",
                icon: "💰", color: C.health,
                title: "New quote received!",
                body: `${q.vendor?.name||"A shop"} quoted ₹${q.totalAmount} — tap to view`,
                time: q.createdAt ? new Date(q.createdAt).toLocaleTimeString("en-IN",{hour:"2-digit",minute:"2-digit"}) : "Just now",
                action: () => navigate("quotes"),
              });
            });
          }
        } catch {}
      }
     // Order status updates
      orders.forEach(o => {
        if (o.status === "preparing") list.push({ id:o._id+"prep", type:"order", icon:"⚙️", color:C.saffron, title:"Order being prepared", body:`${o.vendor?.name||"Shop"} is preparing your order`, time:"", action:()=>navigate("orders") });
        if (o.status === "ready") list.push({ id:o._id+"ready", type:"order", icon:"✅", color:C.success, title:"Order ready for pickup!", body:`${o.vendor?.name||"Shop"} says your order is ready`, time:"", action:()=>navigate("orders") });
      });
      // Only show the 5 most recently delivered orders, so this list doesn't grow forever
      orders
        .filter(o => o.status === "delivered")
        .sort((a,b) => new Date(b.updatedAt||b.createdAt) - new Date(a.updatedAt||a.createdAt))
        .slice(0,5)
        .forEach(o => list.push({ id:o._id+"del", type:"order", icon:"🎉", color:C.ocean, title:"Order delivered!", body:`Rate your experience with ${o.vendor?.name||"Shop"}`, time:"", action:()=>navigate("orders") }));
      // Platform alert
      list.push({ id:"platform1", type:"broadcast", icon:"📢", color:C.ocean, title:"Welcome to Denizzy!", body:"Compare prices from nearby shops and get the best deals in Hulimavu.", time:"Today" });
      setAlerts(list);
      setLoading(false);
    };
    buildAlerts();
    const interval = setInterval(buildAlerts, 30000);
    return () => clearInterval(interval);
  }, [currentRequest, orders]);

  const filtered = filter === "all" ? alerts : alerts.filter(a => a.type === filter);

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="🔔 Notifications" onBack={()=>navigate("home")} />
      <div style={{ padding:"10px 16px 6px", background:C.white, borderBottom:`1px solid ${C.pearl}`, display:"flex", gap:8 }}>
        {[["all","All"],["quote","Quotes"],["order","Orders"],["broadcast","Updates"]].map(([val,label])=>(
          <button key={val} onClick={()=>setFilter(val)} style={{ background:filter===val?C.ocean:C.pearl, color:filter===val?C.white:C.slate, border:"none", borderRadius:20, padding:"6px 14px", fontSize:12, cursor:"pointer", fontWeight:600 }}>{label}</button>
        ))}
      </div>
      <div style={{ padding:"12px 16px" }}>
        {loading ? <div style={{ textAlign:"center", padding:"60px 0", color:C.mist }}>Loading...</div>
        : filtered.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🔔</div>
            <div style={{ fontSize:15, fontWeight:600, color:C.ink }}>No notifications yet</div>
            <div style={{ fontSize:13, color:C.slate, marginTop:6 }}>Send a request to start receiving quotes</div>
          </div>
        ) : filtered.map(alert => (
          <div key={alert.id} onClick={alert.action} style={{ display:"flex", gap:14, padding:"14px 16px", background:C.white, borderRadius:16, border:`1px solid ${C.pearl}`, marginBottom:10, cursor:alert.action?"pointer":"default" }}>
            <div style={{ width:44, height:44, borderRadius:14, background:alert.color+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>{alert.icon}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>{alert.title}</div>
              <div style={{ fontSize:13, color:C.slate, marginTop:3, lineHeight:1.5 }}>{alert.body}</div>
              {alert.time&&<div style={{ fontSize:11, color:C.mist, marginTop:4 }}>{alert.time}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ORDER DETAIL SCREEN
// ═══════════════════════════════════════════════════════════════
function OrderDetailScreen({ navigate, order, onReorder, onOrderCancelled, settings }) {  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [ratingDone, setRatingDone] = useState(()=>{
    if (order?.customerRating) return "done";
    try { return JSON.parse(ls.get("dz_rated_orders")||"[]").includes(order?._id) ? "done" : null; } catch { return null; }
  });
  const [showShopAddress, setShowShopAddress] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [fullImage, setFullImage] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showReportIssue, setShowReportIssue] = useState(false);

  // ── Live delivery tracking ─────────────────────────────────────────────────
  const [vendorLoc, setVendorLoc] = useState(null);
  const [locAge, setLocAge]       = useState(null);
  const [eta, setEta] = useState("");
const [routeLine, setRouteLine] = useState(null);
  const trackMapRef    = useRef(null);
  const trackLeafletRef= useRef(null);
  const vendorPinRef   = useRef(null);
  const routeRef = useRef(null);

  // Poll + Socket.IO for vendor location when out_for_delivery
  useEffect(()=>{
    if(order.status!=="out_for_delivery") return;
    const update=(lat,lng)=>{ setVendorLoc({lat,lng}); setLocAge(Date.now()); };
    const onWS=(e)=>{ if(e.detail.orderId===order._id) update(e.detail.lat,e.detail.lng); };
    window.addEventListener("dz-vendor-loc",onWS);
    const poll=async()=>{
      const res=await api("GET",`/requests/order/${order._id}/location`);
      if(res.success&&res.data) update(res.data.lat,res.data.lng);
    };
    poll(); const iv=setInterval(poll,15000);
    return ()=>{ clearInterval(iv); window.removeEventListener("dz-vendor-loc",onWS); };
  },[order.status,order._id]);

  // Init Leaflet map when status is out_for_delivery
  useEffect(()=>{
    if(order.status!=="out_for_delivery") return;
    const patCoords=(()=>{ try{const c=ls.get("dz_patient_coords");return c?JSON.parse(c):null;}catch{return null;}})();
    const center=patCoords?[patCoords[1],patCoords[0]]:[12.9716,77.5946];
    const init=async()=>{
      if(!window.L){
        if(!document.getElementById("leaflet-css")){
          const lnk=document.createElement("link"); lnk.id="leaflet-css"; lnk.rel="stylesheet";
          lnk.href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; document.head.appendChild(lnk);
        }
        await new Promise(res=>{ const s=document.createElement("script"); s.src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; s.onload=res; document.head.appendChild(s); });
      }
      if(!trackMapRef.current||trackLeafletRef.current) return;
      const L=window.L;
      const map=L.map(trackMapRef.current,{zoomControl:false}).setView(center,15);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",{attribution:"© OSM",maxZoom:19}).addTo(map);
      if(patCoords){ const pi=L.divIcon({html:"📍",className:"",iconSize:[28,28],iconAnchor:[14,28]}); L.marker(center,{icon:pi,zIndexOffset:100}).addTo(map); }
      trackLeafletRef.current=map;
    };
    init();
    return ()=>{ if(trackLeafletRef.current){trackLeafletRef.current.remove();trackLeafletRef.current=null;vendorPinRef.current=null;} };
  },[order.status]);

  // Update vendor pin when location arrives
  useEffect(() => {

  if (!vendorLoc || !trackLeafletRef.current) return;

  const L = window.L;
  if (!L) return;

  const patCoords = (() => {
    try {
      const c = ls.get("dz_patient_coords");
      return c ? JSON.parse(c) : null;
    } catch {
      return null;
    }
  })();

  const ll = [vendorLoc.lat, vendorLoc.lng];

  if (!vendorPinRef.current) {

    const vi = L.divIcon({
      html: "🛵",
      className: "",
      iconSize: [28, 28],
      iconAnchor: [14, 14]
    });

    vendorPinRef.current =
      L.marker(ll, { icon: vi }).addTo(trackLeafletRef.current);

  } else {

    vendorPinRef.current.setLatLng(ll);

  }

  if (!patCoords) return;

  fetch(
    `https://router.project-osrm.org/route/v1/driving/` +
    `${vendorLoc.lng},${vendorLoc.lat};` +
    `${patCoords[0]},${patCoords[1]}` +
    `?overview=full&geometries=geojson`
  )
    .then(r => r.json())
    .then(data => {

      if (!data.routes?.length) return;

      const route = data.routes[0];

      setEta(
        Math.ceil(route.duration / 60) + " min"
      );

      const coords =
        route.geometry.coordinates.map(c => [c[1], c[0]]);

      if (routeRef.current) {

        trackLeafletRef.current.removeLayer(routeRef.current);

      }

      routeRef.current = L.polyline(coords, {
        color: "#1976D2",
        weight: 5
      }).addTo(trackLeafletRef.current);

      trackLeafletRef.current.fitBounds(
        routeRef.current.getBounds(),
        { padding: [40, 40] }
      );

    });

}, [vendorLoc]);
  const savedBilling = order?._id ? JSON.parse(localStorage.getItem(`dz_billing_${order._id}`) || "{}") : {};

  if (!order) return (
    <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", background:C.bg }}>
      <div style={{ textAlign:"center" }}>
        <div style={{ fontSize:40 }}>📦</div>
        <div style={{ fontSize:14, color:C.slate, marginTop:10 }}>Order not found</div>
        <Btn small color={C.ocean} style={{ marginTop:16 }} onClick={()=>navigate("orders")}>Back to Orders</Btn>
      </div>
    </div>
  );

  const color = order.vertical==="medical" ? C.health : LABS_COLOR;
  const statusColor = { confirmed:C.success, preparing:C.saffron, ready:C.ocean, out_for_delivery:C.ocean, delivered:C.slate, cancelled:C.danger };
  const getStatusLabel = () => {

  if (order.vertical === "lab") {

    if (order.deliveryType === "home") {
      return {
        confirmed:"✅ Booking Confirmed",
        preparing:"🧪 Preparing for Home Collection",
        packed:"🚗 Staff Out for Collection",
        delivered:"🧪 Sample Collected",
        report_shared:"📄 Report Shared",
        cancelled:"❌ Cancelled"
      };
    }

    return {
      confirmed:"✅ Booking Confirmed",
      preparing:"🧪 Preparing for Lab Visit",
      ready:"📍 Ready for Lab Visit",
      delivered:"🧪 Sample Collected",
      report_shared:"📄 Report Shared",
      cancelled:"❌ Cancelled"
    };
  }

  return {
    confirmed:"✅ Confirmed",
    preparing:"⚙️ Preparing",
    packed:"📦 Packed for Delivery",
    ready:"🏃 Ready for Pickup",
    out_for_delivery:"🚚 Out for Delivery",
    delivered:"🎉 Delivered",
    cancelled:"❌ Cancelled"
  };
};

  const submitRating = async () => {
    if (!rating) return;
    setRatingDone("loading");
    const res = await api("POST", `/orders/${order._id}/rate`, { rating, review });
    if (res.success) {
      // Persist so rating form doesn't reappear after navigating away
      try { const rated=JSON.parse(ls.get("dz_rated_orders")||"[]"); ls.set("dz_rated_orders",JSON.stringify([...rated,order._id])); } catch {}
      setRatingDone("done");
    }
    else { setRatingDone(null); alert("Failed to submit rating. Please try again."); }
  };

  return (
    <>
      {showCancelModal&&<CancelOrderModal order={order} onClose={()=>setShowCancelModal(false)} onCancelled={()=>{ setShowCancelModal(false); onOrderCancelled&&onOrderCancelled(); }} />}
      {showReportIssue&&<ReportIssueModal order={order} onClose={()=>setShowReportIssue(false)} supportNumber={settings?.supportWhatsappPatient||settings?.supportWhatsapp} />}
      {fullImage && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.92)", zIndex:1100, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={()=>setFullImage(null)}>
          <img src={fullImage} alt="Full view" style={{ maxWidth:"95%", maxHeight:"85%", objectFit:"contain", borderRadius:8 }} />
          <button onClick={()=>setFullImage(null)} style={{ position:"absolute", top:20, right:20, background:"rgba(255,255,255,0.15)", border:"none", borderRadius:10, width:40, height:40, color:"white", fontSize:20, cursor:"pointer" }}>✕</button>
        </div>
      )}
      <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <TopBar title="📦 Order Details" onBack={()=>navigate("orders")} />
      <div style={{ padding:16 }}>
        {/* Status banner */}
        <div style={{ background:statusColor[order.status]+"15", borderRadius:16, padding:16, marginBottom:16, border:`1px solid ${statusColor[order.status]}30`, textAlign:"center" }}>
          <div style={{ fontSize:28, marginBottom:6 }}>{order.status==="delivered"?"🎉":order.status==="out_for_delivery"?"🛵":order.status==="ready"?"🏃":"⚙️"}</div>
          <div style={{ fontWeight:700, fontSize:16, color:statusColor[order.status] }}>{getStatusLabel()[order.status] || order.status}</div>
          
{/* OUT FOR DELIVERY */}
{order.status==="out_for_delivery" && (
  <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>
    {order.vertical==="lab"
      ? "🚗 Collection staff is on the way."
      : "🚚 Your order is on the way."}
  </div>
)}


        {/* ── Live tracking map ── */}
        {order.status==="out_for_delivery"&&(
          <Card style={{ marginBottom:12, overflow:"hidden", padding:0 }}>
            <div style={{ padding:"12px 14px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div style={{ fontWeight:700, fontSize:14, color:C.ocean }}>{order.vertical==="lab"
  ? "🚗 Collection Tracking"
  : "🛵 Live Tracking"}</div>
              {locAge&&<div style={{ fontSize:11, color:C.mist }}>Updated {Math.floor((Date.now()-locAge)/1000)}s ago</div>}
            </div>
            {/* Map container — always rendered so Leaflet can mount */}
            <div ref={trackMapRef} style={{ width:"100%", height:200, background:C.pearl }} />
            {!vendorLoc&&(
              <div style={{ padding:"12px 14px", textAlign:"center" }}>
                <div style={{ fontSize:13, color:C.mist }}>⏳ Waiting for vendor to start tracking...</div>
                <div style={{ fontSize:11, color:C.mist, marginTop:4 }}>{order.vertical==="lab"
  ? "Collection staff will share location shortly."
  : "Delivery staff will share location shortly."}</div>
              </div>
            )}
            {vendorLoc&&(()=>{
              const patCoords=(()=>{ try{const c=ls.get("dz_patient_coords");return c?JSON.parse(c):null;}catch{return null;}})();
              if(!patCoords) return null;
              const dist=getDistanceKm(vendorLoc.lat,vendorLoc.lng,patCoords[1],patCoords[0]);
              return <div style={{ padding:"8px 14px 12px", fontSize:13, color:C.ocean, fontWeight:700, textAlign:"center" }}>🛵 <div
  style={{
    padding:"8px 14px 12px",
    textAlign:"center"
  }}
>

  <div
    style={{
      fontWeight:700,
      color:C.ocean,
      fontSize:14
    }}
  >
    {order.vertical==="lab"
  ? `🚗 Collection Staff is ${dist} away`
  : `🛵 Delivery Staff is ${dist} away`}
  </div>

  {eta && (

    <div
      style={{
        marginTop:4,
        color:C.slate,
        fontSize:13
      }}
    >
      ⏱ ETA : {eta}
    </div>

  )}

</div></div>;
            })()}
          </Card>
        )}
         {/* READY */}
{order.status==="ready" && (
  <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>
    {order.vertical==="lab"
      ? "📍 Your lab is ready for your visit."
      : "🏃 Go to shop to pick up your order."}
  </div>
)}

          {/* PREPARING */}
{order.status==="preparing" && (
  <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>
    {order.vertical==="lab"
      ? (order.deliveryType==="home"
          ? "🧪 Preparing for home collection."
          : "🧪 Preparing for your lab visit.")
      : "📦 Your order is being prepared."}
  </div>
)}
        </div>
 
 {/* PACKED */}
{order.status==="packed" && (
  <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>
    {order.vertical==="lab"
      ? "🚗 Collection staff is on the way."
      : "📦 Your order has been packed and will be delivered soon."}
  </div>
)}

 {/* SAMPLE COLLECTED / DELIVERED */}
{order.status==="delivered" && (
  <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>
    {order.vertical==="lab"
      ? "🧪 Sample collected successfully."
      : "🎉 Your order has been delivered successfully."}
  </div>
)}

 {/* REPORT SHARED */}
{order.status==="report_shared" && (
  <div style={{ fontSize:13, color:C.slate, marginTop:4 }}>
    📄 Your lab report has been shared.
  </div>
)}

 {/* CANCELLED — show reason */}
        {order.status==="cancelled" && (
          <div style={{ background:C.danger+"10", borderRadius:12, padding:"12px 14px", marginTop:8, border:`1px solid ${C.danger}20`, textAlign:"left" }}>
            {order.cancelledBy && (
              <div style={{ fontSize:12, fontWeight:700, color:C.danger, marginBottom:4 }}>
                Cancelled by {order.cancelledBy==="patient"?"you":"the vendor"}
              </div>
            )}
            {order.cancellationReason && (
              <div style={{ fontSize:13, color:C.ink }}>Reason: {order.cancellationReason}</div>
            )}
            {order.cancellationNote && (
              <div style={{ fontSize:12, color:C.slate, marginTop:4 }}>"{order.cancellationNote}"</div>
            )}
          </div>
        )}
        {order.status !== "cancelled" && (
          <div style={{ background:C.white, borderRadius:16, padding:"20px 16px", marginBottom:16, border:`1px solid ${C.pearl}` }}>
            {(() => {
              const isHomeDelivery = order.deliveryType === "home";

let steps;

if (order.vertical === "lab") {

  if (isHomeDelivery) {

    steps = [
      { key:"confirmed", label:"Booking Confirmed" },
      { key:"preparing", label:"Preparing" },
      { key:"packed", label:"Staff On the Way" },
      { key:"delivered", label:"Sample Collected" },
      { key:"report_shared", label:"Report Shared" },
    ];

  } else {

    steps = [
      { key:"confirmed", label:"Booking Confirmed" },
      { key:"preparing", label:"Preparing" },
      { key:"ready", label:"Ready for Lab Visit" },
      { key:"delivered", label:"Sample Collected" },
      { key:"report_shared", label:"Report Shared" },
    ];

  }

} else {

  if (isHomeDelivery) {

    steps = [
      { key:"confirmed", label:"Confirmed" },
      { key:"preparing", label:"Preparing" },
      { key:"packed", label:"Packed" },
      { key:"out_for_delivery", label:"On the Way" },
      { key:"delivered", label:"Delivered" },
    ];

  } else {

    steps = [
      { key:"confirmed", label:"Confirmed" },
      { key:"preparing", label:"Preparing" },
      { key:"ready", label:"Ready for Pickup" },
      { key:"delivered", label:"Picked Up" },
    ];

  }

}
              const currentIdx = steps.findIndex(s=>s.key===order.status);
              return (
                <div style={{ display:"flex", alignItems:"flex-start" }}>
                  {steps.flatMap((s,i) => {
                    const done = currentIdx >= 0 && i <= currentIdx;
                    const isCurrent = i === currentIdx;
                    const circle = (
                      <div key={s.key} style={{ display:"flex", flexDirection:"column", alignItems:"center", flex:1 }}>
                        <div style={{ width:32, height:32, borderRadius:"50%", background: done ? color : C.pearl, display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color: done ? C.white : C.mist, boxShadow: isCurrent ? `0 0 0 4px ${color}25` : "none" }}>{done ? "✓" : i+1}</div>
                        <div style={{ fontSize:10, color: done ? C.ink : C.mist, fontWeight: isCurrent?700:500, marginTop:6, textAlign:"center" }}>{s.label}</div>
                      </div>
                    );
                    const connector = i < steps.length-1 ? (
                      <div key={s.key+"-line"} style={{ flex:1, height:2, background: i < currentIdx ? color : C.pearl, marginTop:15 }} />
                    ) : null;
                    return connector ? [circle, connector] : [circle];
                  })}
                </div>
              );
            })()}
          </div>
        )}

         {/* Order ID & Shop */}
        <Card style={{ marginBottom:12 }}>
          {/* Shop Address Modal */}
          {showShopAddress && (
            <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"flex-end" }} onClick={()=>setShowShopAddress(false)}>
              <div style={{ background:C.white, borderRadius:"20px 20px 0 0", padding:"24px 20px 40px", width:"100%", boxSizing:"border-box" }} onClick={e=>e.stopPropagation()}>
                <div style={{ width:40, height:4, background:C.pearl, borderRadius:2, margin:"0 auto 20px" }} />
                <div style={{ display:"flex", gap:14, alignItems:"center", marginBottom:16 }}>
                  <div style={{ width:50, height:50, borderRadius:14, background:color+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:26 }}>{order.vertical==="medical"?"💊":"🔬"}</div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:17, color:C.ink }}>{order.vendor?.name||"Shop"}</div>
                    <div style={{ fontSize:12, color:C.slate }}>{order.vendor?.city}</div>
                  </div>
                </div>
                {(order.vendor?.address || order.vendor?.area || order.vendor?.city) && (
                  <div style={{ background:C.bg, borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                    <div style={{ fontSize:11, color:C.slate, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>📍 Address</div>
                    {order.vendor.address && <div style={{ fontSize:14, color:C.ink, lineHeight:1.5, marginBottom:2 }}>{order.vendor.address}</div>}
                    <div style={{ fontSize:13, color:C.slate }}>
                      {[order.vendor.area, order.vendor.city && order.vendor.city!==order.vendor.area?order.vendor.city:null].filter(Boolean).join(", ")||order.vendor.city||""}
                      {order.vendor.pincode ? ` - ${order.vendor.pincode}` : ""}
                    </div>
                  </div>
                )}
                {/* Google Maps — copies address to clipboard then opens Maps */}
                {(() => {
                  const addrText = [order.vendor?.address, order.vendor?.area, order.vendor?.city, order.vendor?.pincode].filter(Boolean).join(", ");
                  const coords = order.vendor?.coordinates;
                  const hasCoords = Array.isArray(coords) && coords[0]!==0 && coords[1]!==0;
                  if (!addrText && !hasCoords) return null;
                  const mapsUrl = addrText
                    ? `https://maps.google.com/?q=${encodeURIComponent(addrText)}`
                    : `https://maps.google.com/?q=${coords[1]},${coords[0]}`;
                  return (
                    <div onClick={async()=>{
                      try { await navigator.clipboard.writeText(addrText); } catch {}
                      window.open(mapsUrl, "_blank");
                    }}
                      style={{ display:"flex", alignItems:"center", gap:10, background:"#E8F5E9", borderRadius:12, padding:"10px 14px", marginBottom:12, border:"1px solid #C8E6C9", cursor:"pointer" }}>
                      <span style={{ fontSize:18 }}>🗺️</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:"#2E7D32" }}>View on Google Maps</div>
                        <div style={{ fontSize:11, color:C.slate }}>Address copied to clipboard automatically</div>
                      </div>
                    </div>
                  );
                })()}
                {order.vendor?.phone && (
                  <a href={`tel:${order.vendor.phone}`} style={{ textDecoration:"none", display:"block" }}>
                    <div style={{ background:C.ocean+"10", borderRadius:12, padding:"12px 14px", marginBottom:12, display:"flex", alignItems:"center", gap:12 }}>
                      <span style={{ fontSize:20 }}>📞</span>
                      <div>
                        <div style={{ fontWeight:700, fontSize:14, color:C.ocean }}>Call Shop</div>
                        <div style={{ fontSize:12, color:C.slate }}>{order.vendor.phone}</div>
                      </div>
                    </div>
                  </a>
                )}
                {order.vendor?.workingHours?.weekday && (
                  <div style={{ background:C.bg, borderRadius:12, padding:"12px 14px", marginBottom:12 }}>
                    <div style={{ fontSize:11, color:C.slate, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>⏰ Working Hours</div>
                    <div style={{ fontSize:13, color:C.ink }}>Weekday: {order.vendor.workingHours.weekday}</div>
                    {order.vendor.workingHours.weekend && <div style={{ fontSize:13, color:C.ink }}>Weekend: {order.vendor.workingHours.weekend}</div>}
                  </div>
                )}
                <button onClick={()=>setShowShopAddress(false)} style={{ width:"100%", background:C.ink, border:"none", borderRadius:14, padding:"14px", color:C.white, fontWeight:700, fontSize:15, cursor:"pointer" }}>Close</button>
              </div>
            </div>
          )}
          <div style={{ display:"flex", gap:14, alignItems:"center" }}>
            <div style={{ width:48, height:48, borderRadius:14, background:color+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>{order.vertical==="medical"?"💊":"🔬"}</div>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"monospace", fontSize:15, color:color, fontWeight:700 }}>{formatOrderId(order._id, order.vertical, order.orderNumber)}</div>
              <div onClick={()=>setShowShopAddress(true)}
                style={{ fontWeight:700, fontSize:15, color:C.ink, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
                {order.vendor?.name||"Medical Shop"}
                <span style={{ fontSize:11, color:C.ocean, fontWeight:600 }}>📍 See address</span>
              </div>
              {order.vendor?.city&&<div style={{ fontSize:12, color:C.slate }}>{order.vendor.city}</div>}
            </div>
          </div>
          {order.vendor?.phone&&(
            <a href={`tel:${order.vendor.phone}`} style={{ textDecoration:"none", display:"block", marginTop:12 }}>
              <div style={{ background:C.ocean+"10", borderRadius:10, padding:"10px 14px", display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:18 }}>📞</span>
                <span style={{ fontSize:13, color:C.ocean, fontWeight:600 }}>Call Shop: {order.vendor.phone}</span>
              </div>
            </a>
          )}
        </Card>

        {/* Items + prescription from request */}
        <Card style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>{order.vertical==="lab"?"🔬 Tests Ordered":"🛍️ Items Ordered"}</div>
            {order.vertical==="medical" && (
              <div style={{ background:order.exactItemsOnly?C.danger+"15":C.success+"15", borderRadius:20, padding:"3px 10px", fontSize:11, fontWeight:700, color:order.exactItemsOnly?C.danger:C.success, border:`1px solid ${order.exactItemsOnly?C.danger+"40":C.success+"40"}` }}>
                {order.exactItemsOnly?"❌ Exact only":"✅ Substitutes OK"}
              </div>
            )}
          </div>
          {(()=>{
            const allItems = order.requestItems||[];
            const realItems = allItems.filter(it=>{
              const n=(it.name||it.itemName||"").toLowerCase().trim();
              return !["as per prescription","as per referral/prescription","as per referral"].some(x=>n.includes(x));
            });

            return (
              <div>
                {/* All manually typed items — always shown, never hidden */}
                {realItems.length>0 ? (
                  <div style={{ marginBottom: order.prescriptionUrl ? 12 : 0 }}>
                    {realItems.map((item,i)=>(
                      <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"7px 0", borderBottom:i<realItems.length-1?`1px solid ${C.pearl}`:"none", fontSize:13 }}>
                        <span style={{ color:C.ink }}>{item.name||item.itemName}{item.quantity>1?` × ${item.quantity}`:""}</span>
                        {item.unit&&<span style={{ fontSize:11, color:C.slate }}>{item.unit}</span>}
                      </div>
                    ))}
                  </div>
                ) : !order.prescriptionUrl ? (
                  <div style={{ fontSize:13, color:C.slate }}>{order.vertical==="lab"?"Tests as per referral":"Items as per prescription"}</div>
                ) : null}

                {/* Prescription / referral — always shown when exists */}
                {order.prescriptionUrl && (
                  <div style={{ marginTop: realItems.length>0 ? 0 : 0 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:C.health, marginBottom:8 }}>
                      {order.vertical==="lab"?"📋 Referral / Prescription attached":"📷 Prescription attached"}
                    </div>
                    <img src={order.prescriptionUrl} alt="Prescription"
                      onClick={()=>setFullImage(order.prescriptionUrl)}
                      style={{ width:"100%", borderRadius:12, objectFit:"cover", maxHeight:200, border:`1px solid ${C.health}30`, cursor:"zoom-in" }} />
                    <div style={{ fontSize:11, color:C.slate, marginTop:4, textAlign:"center" }}>Tap to expand · Cross-check items above with this prescription</div>
                  </div>
                )}
              </div>
            );
          })()}
        </Card>

        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:10 }}>🧾 Your Bill</div>
          {(order.billingPhotoUrl || savedBilling.billingPhotoUrl) ? (
            <BillingTab
              billingPhotoUrl={order.billingPhotoUrl || savedBilling.billingPhotoUrl}
              items={order.billingItems?.length ? order.billingItems : savedBilling.billingItems}
              vendorNotes={order.vendorNotes || savedBilling.vendorNotes}
              onFullImage={(url)=>setFullImage(url)}
            />
          ) : ["delivered","report_shared"].includes(order.status) ? (
            <div style={{ background:C.warning+"10", borderRadius:10, padding:"10px 14px", border:`1px solid ${C.warning}20` }}>
              <div style={{ fontSize:13, color:C.warning, fontWeight:600 }}>⏳ Bill not yet uploaded by vendor</div>
              <div style={{ fontSize:11, color:C.slate, marginTop:4 }}>Contact the {order.vertical==="lab"?"lab":"pharmacy"} if you need your bill.</div>
            </div>
          ) : (
            <div style={{ background:C.bg, borderRadius:10, padding:"10px 14px" }}>
              <div style={{ fontSize:13, color:C.slate }}>📄 Bill will appear here once the {order.vertical==="lab"?"lab processes":"pharmacy prepares"} your order.</div>
            </div>
          )}
        </Card>

        {/* Amount + Order Info — matches confirmation screen layout */}
        <Card style={{ marginBottom:12 }}>
          {/* Amount with discount */}
          {(() => {
            const discount   = order.discountAmount || 0;
            const total      = order.totalAmount || 0;
            const original   = discount > 0 ? total + discount : total;
            const delivCharge= order.deliveryCharge || 0;
            return (
              <>
                {/* Amount row */}
                <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${C.pearl}` }}>
                  <span style={{ fontSize:13, color:C.slate }}>Amount</span>
                  <div style={{ textAlign:"right" }}>
                    {discount > 0 && (
                      <div style={{ fontSize:12, color:C.mist, textDecoration:"line-through" }}>₹{original}</div>
                    )}
                    <span style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:"monospace" }}>₹{total}</span>
                  </div>
                </div>
                {/* Denizzy Discount row */}
                {discount > 0 && (
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${C.pearl}`, alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:13 }}>🎟️</span>
                      <span style={{ fontSize:13, color:C.slate }}>Denizzy Discount</span>
                    </div>
                    <span style={{ fontSize:13, fontWeight:700, color:C.success, fontFamily:"monospace" }}>-₹{discount}</span>
                  </div>
                )}
                {/* Delivery charge */}
                {order.deliveryType === "home" && (
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${C.pearl}` }}>
                    <span style={{ fontSize:13, color:C.slate }}>Delivery</span>
                    <span style={{ fontSize:13, fontWeight:600, color:C.ink }}>{delivCharge > 0 ? `₹${delivCharge}` : "Free 🚚"}</span>
                  </div>
                )}
                {/* Total */}
                <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${C.pearl}` }}>
                  <span style={{ fontSize:14, fontWeight:700, color:C.ink }}>Total</span>
                  <span style={{ fontSize:16, fontWeight:700, color, fontFamily:"monospace" }}>₹{total}</span>
                </div>
                {/* Order type */}
                <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", alignItems:"center",
                  ...(order.visitDate || order.visitSlot ? { borderBottom:`1px solid ${C.pearl}` } : {}) }}>
                  <span style={{ fontSize:13, color:C.slate }}>Type</span>
                  <span style={{ fontSize:13, fontWeight:600, color:C.ink }}>
                    {order.vertical === "lab"
                      ? (order.deliveryType === "home" ? "🏠 Home Sample Collection" : "🔬 Lab Visit")
                      : (order.deliveryType === "home" ? "🚚 Home Delivery" : "🏃 Pickup")}
                  </span>
                </div>
                {/* Appointment slot — lab only */}
                {(order.visitDate || order.visitSlot) && (
                  <div style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontSize:13 }}>📅</span>
                      <span style={{ fontSize:13, color:C.slate }}>Appointment</span>
                    </div>
                    <span style={{ fontSize:13, fontWeight:600, color:C.ink }}>
                      {[
                        order.visitDate ? new Date(order.visitDate).toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"}) : null,
                        order.visitSlot || null,
                      ].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                )}
              </>
            );
          })()}
        </Card>

        {/* Delivery address — only for home delivery */}
        {order.deliveryType === "home" && order.customerDetails?.address && (
          <Card style={{ marginBottom:12 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6 }}>📍 Delivery Address</div>
            <div style={{ fontSize:13, color:C.ink, lineHeight:1.6 }}>
              {typeof order.customerDetails.address === "object"
                ? `${order.customerDetails.address.area||""} - ${order.customerDetails.address.pincode||""}`
                : order.customerDetails.address}
            </div>
            {order.customerDetails?.phone && (
              <div style={{ fontSize:12, color:C.slate, marginTop:4 }}>📱 {order.customerDetails.phone}</div>
            )}
          </Card>
        )}

        {/* Rating */}
        {(
  (order.vertical==="medical" && order.status==="delivered") ||
  (order.vertical==="lab" && order.status==="report_shared")
) && ratingDone !== "done" && (
          <Card style={{ marginBottom:12, border:`1.5px solid ${C.saffron}30` }}>
            <div style={{ fontWeight:700, fontSize:15, color:C.ink, marginBottom:12 }}>⭐ Rate Your Experience</div>
            <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:12 }}>
              {[1,2,3,4,5].map(star=>(
                <button key={star} onClick={()=>setRating(star)} style={{ background:"none", border:"none", fontSize:32, cursor:"pointer", opacity:star<=rating?1:0.3 }}>⭐</button>
              ))}
            </div>
            {rating>0&&(
              <>
                <textarea value={review} onChange={e=>setReview(e.target.value)} placeholder="Share your experience (optional)..." rows={3}
                  style={{ width:"100%", background:C.bg, border:`1px solid ${C.pearl}`, borderRadius:10, padding:"10px 12px", fontSize:13, outline:"none", boxSizing:"border-box", resize:"none", marginBottom:10 }} />
                <Btn full color={C.saffron} style={{ color:C.ink }} onClick={submitRating} loading={ratingDone==="loading"} disabled={!rating}>Submit Rating →</Btn>
              </>
            )}
          </Card>
        )}
        {ratingDone==="done"&&<div style={{ background:C.success+"10", borderRadius:14, padding:14, marginBottom:12, textAlign:"center", border:`1px solid ${C.success}20` }}><div style={{ color:C.success, fontWeight:700 }}>⭐ Thanks for your rating!</div><div style={{ fontSize:12, color:C.slate, marginTop:4 }}>Your feedback helps other customers.</div></div>}

       {/* Need help — for all active orders */}
        {!["cancelled","delivered","report_shared"].includes(order.status)&&(
          <button onClick={()=>setShowReportIssue(true)}
            style={{ width:"100%", background:"none", border:"1.5px solid #25D366", borderRadius:14, padding:"13px", color:"#25D366", fontWeight:700, fontSize:14, cursor:"pointer", marginBottom:10 }}>
            💬 Need Help with this Order?
          </button>
        )}

        {/* Cancel — only for confirmed/preparing */}
        {["confirmed","preparing"].includes(order.status)&&(
          <button onClick={()=>setShowCancelModal(true)}
            style={{ width:"100%", background:"none", border:`1.5px solid ${C.danger}`, borderRadius:14, padding:"13px", color:C.danger, fontWeight:700, fontSize:14, cursor:"pointer", marginBottom:10 }}>
            ❌ Cancel Order
          </button>
        )}

        {/* Reorder */}
        {order.vertical&&(
          <Btn full color={color} onClick={()=>{ if(onReorder) onReorder(order); navigate(order.vertical==="medical"?"medSearch":"labsLanding"); }} style={{ marginBottom:10 }}>
            🔄 Reorder from {order.vendor?.name||"this shop"}
          </Btn>
        )}
        <Btn full variant="ghost" onClick={()=>navigate("orders")}>← Back to Orders</Btn>
      </div>
    </div>
  </> 
 );
}


// ═══════════════════════════════════════════════════════════════════════════════
// LABS LANDING SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
const LAB_TESTS = [
  { icon:"🩸", name:"Blood Test", sub:"CBC, HbA1c, Blood Sugar, Iron..." },
  { icon:"🧪", name:"Urine Test", sub:"Complete Urine Examination..." },
  { icon:"⚡", name:"ECG", sub:"Electrocardiogram heart check" },
  { icon:"📷", name:"X-Ray", sub:"Chest, Spine, Limbs..." },
  { icon:"🔊", name:"Ultrasound", sub:"Abdomen, Pelvis, Thyroid..." },
  { icon:"🧫", name:"Culture Test", sub:"Bacterial culture & sensitivity" },
  { icon:"🦋", name:"Thyroid Panel", sub:"T3, T4, TSH..." },
  { icon:"💉", name:"Diabetes Panel", sub:"HbA1c, FBS, PPBS, Insulin..." },
  { icon:"❤️", name:"Lipid Profile", sub:"Cholesterol, Triglycerides..." },
  { icon:"🫀", name:"Liver Function", sub:"SGOT, SGPT, Bilirubin..." },
  { icon:"🫘", name:"Kidney Function", sub:"Creatinine, Urea, Uric Acid..." },
  { icon:"✏️", name:"Custom Tests", sub:"Type the specific tests needed" },
];
function LabsLandingScreen({ navigate, onSetItems }) {
  const [selected, setSelected] = useState([]);
  const [labSuggestions, setLabSuggestions] = useState([]);
  const [customTests, setCustomTests] = useState("");
  const [hasReferral, setHasReferral] = useState(()=>!!ls.get("dz_labs_rx_url"));

  useEffect(()=>{
    setHasReferral(!!ls.get("dz_labs_rx_url"));
  },[]);

  const toggle = (name) => setSelected(prev => prev.includes(name) ? prev.filter(n=>n!==name) : [...prev,name]);

  const getQuotes = () => {
    const customList = customTests.split(",").map(t=>t.trim()).filter(Boolean);
    const tests = [...selected, ...customList];
    if (tests.length===0 && !hasReferral) return;
    // Pass test names to onSetItems so vendor receives them
    if (onSetItems) onSetItems(tests.length > 0 ? tests : [], "lab");
    navigate("labsRequest");
  };

  const canProceed = selected.length > 0 || customTests.trim() || hasReferral;

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:0, background:C.bg }}>
      <div style={{ background:`linear-gradient(135deg, #065f75, ${LABS_COLOR})`, padding:"40px 20px 24px", position:"relative", overflow:"hidden", flexShrink:0 }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.1 }} viewBox="0 0 400 40" preserveAspectRatio="none" height="40" width="100%"><path d="M0,20 C80,38 160,3 240,20 C320,38 370,5 400,20 L400,40 L0,40 Z" fill="white"/></svg>
        <div style={{ position:"relative" }}>
          <button onClick={()=>navigate("home")} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, color:"white", fontSize:16, cursor:"pointer", marginBottom:8, padding:"4px 10px" }}>‹ Back</button>
          <div style={{ color:"rgba(255,255,255,0.7)", fontSize:13, marginBottom:4 }}>🔬 Denizzy Labs</div>
          <div style={{ color:"white", fontFamily:"Georgia,serif", fontSize:22, fontWeight:700 }}>Book Lab Tests</div>
          <div style={{ color:"rgba(255,255,255,0.65)", fontSize:13, marginTop:6 }}>Compare prices from nearby labs · Get best rates</div>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"16px 16px 100px" }}>
        {/* Upload referral option */}
        {/* Upload referral - same as prescription */}
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>Upload Doctor's Referral</div>
          <div style={{ display:"flex", gap:10 }}>
            <div onClick={()=>navigate("labsPrescription")}
              style={{ flex:2, background:hasReferral?LABS_COLOR+"15":C.white, borderRadius:16, padding:"16px 14px", border:`2px solid ${hasReferral?LABS_COLOR:C.pearl}`, cursor:"pointer", display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:44, height:44, borderRadius:12, background:hasReferral?LABS_COLOR:C.pearl, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>
                {hasReferral?"✅":"📷"}
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:hasReferral?LABS_COLOR:C.ink }}>{hasReferral?"Referral Uploaded ✓":"Upload Photo"}</div>
                <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>Doctor prescription / referral slip</div>
              </div>
            </div>
          </div>
        </div>

        {/* Manual test name entry */}
        <div style={{ background:C.white, borderRadius:14, padding:"12px 14px", marginBottom:14, border:`1px solid ${C.pearl}` }}>
          <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:8 }}>Type Test Name</div>
          
	    <div style={{ position:"relative" }}>
            <input
            value={customTests}
            onChange={e=>{
  setCustomTests(e.target.value);
  const last = e.target.value.split(",").pop().trim().toLowerCase();
  if(last.length<2){ setLabSuggestions([]); return; }
  setLabSuggestions(LAB_TESTS_AUTOCOMPLETE.filter(t=>t.toLowerCase().includes(last)).slice(0,6));
}}
            placeholder="e.g. CBC, HbA1c, Lipid Profile, Urine Routine..."
            style={{ width:"100%", background:C.bg, border:`1.5px solid ${customTests?LABS_COLOR:C.pearl}`, borderRadius:10, padding:"10px 12px", fontSize:13, outline:"none", boxSizing:"border-box" }}
          />
          {labSuggestions.length>0&&(
    <div style={{ position:"absolute", top:"100%", left:0, right:0, background:C.white, borderRadius:12, boxShadow:"0 6px 24px rgba(0,0,0,0.12)", zIndex:200, border:`1px solid ${C.pearl}`, overflow:"hidden", marginTop:4 }}>
      {labSuggestions.map((test,i)=>(
        <div key={test} onClick={()=>{
          const existing=customTests.trim();
          const last=existing.lastIndexOf(",");
          const before=last>=0?existing.substring(0,last).trim():"";
          setCustomTests(before?`${before}, ${test}, `:`${test}, `);
          setLabSuggestions([]);
        }}
          style={{ padding:"11px 14px", fontSize:13, color:C.ink, borderBottom:i<labSuggestions.length-1?`1px solid ${C.pearl}`:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
          <span>🧪</span><span>{test}</span>
        </div>
      ))}
    </div>
  )}
</div>
          <div style={{ fontSize:11, color:C.mist, marginTop:6 }}>Separate multiple tests with a comma. Or upload referral above.</div>
        </div>

        {/* Test selector */}
        <div style={{ fontSize:12, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:10 }}>Quick Select Tests</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:16 }}>
          {LAB_TESTS.map(test=>{
            const sel = selected.includes(test.name);
            return (
              <div key={test.name} onClick={()=>toggle(test.name)}
                style={{ padding:"14px 12px", background:sel?LABS_COLOR+"12":C.white, borderRadius:16, border:`1.5px solid ${sel?LABS_COLOR:C.pearl}`, cursor:"pointer" }}>
                <div style={{ fontSize:24, marginBottom:6 }}>{test.icon}</div>
                <div style={{ fontWeight:700, fontSize:13, color:sel?LABS_COLOR:C.ink }}>{test.name}</div>
                <div style={{ fontSize:11, color:C.slate, marginTop:2, lineHeight:1.4 }}>{test.sub}</div>
                {sel&&<div style={{ fontSize:10, color:LABS_COLOR, fontWeight:700, marginTop:4 }}>✓ Selected</div>}
              </div>
            );
          })}
        </div>

        {/* Custom test input */}
        {selected.includes("Custom Tests") && (
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:12, fontWeight:700, color:C.slate, marginBottom:8 }}>Enter specific tests (comma-separated):</div>
            <textarea value={customTests} onChange={e=>setCustomTests(e.target.value)} rows={2}
              placeholder="e.g. Vitamin D, B12, FSH, LH..." 
              style={{ width:"100%", background:C.white, border:`1.5px solid ${LABS_COLOR}60`, borderRadius:12, padding:"10px 12px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box" }} />
          </div>
        )}

        {/* Home collection */}
        <div style={{ background:LABS_COLOR+"10", borderRadius:14, padding:14, marginBottom:16, border:`1px solid ${LABS_COLOR}20` }}>
          <div style={{ fontWeight:700, fontSize:13, color:LABS_COLOR, marginBottom:4 }}>🏠 Home collection available</div>
          <div style={{ fontSize:12, color:C.slate }}>Many labs offer home sample collection. You can request it when getting quotes.</div>
        </div>
      </div>

      {/* Sticky bottom */}
      <div style={{ position:"sticky", bottom:0, background:C.white, borderTop:`1px solid ${C.pearl}`, padding:"12px 16px 24px" }}>
        <div style={{ fontSize:12, color:C.slate, marginBottom:10, textAlign:"center" }}>
          {selected.length > 0 || customTests.trim() || hasReferral
            ? `${selected.length} test${selected.length!==1?"s":""} selected${hasReferral?" + referral":""}${customTests.trim()?" + custom":""}`
            : "Select tests above, type a name, or upload a referral"}
        </div>
        <Btn full color={LABS_COLOR} disabled={!canProceed} onClick={getQuotes}>
          🔬 Get Lab Quotes →
        </Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LABS REQUEST SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
function LabsRequestScreen({ navigate, requestItems, onRequestCreated, city, area, patientCoords, settings }) {
  const [delivery, setDelivery] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [urgent, setUrgent] = useState(false);
  // Preferred slot preference (send with request so vendor sees it immediately)

 const [preferredDate, setPreferredDate] = useState("none");
const [preferredTime, setPreferredTime] = useState("none");
  const [labsRxUrl, setLabsRxUrl] = useState(()=>ls.get("dz_labs_rx_url")||null);

  const items = (requestItems && requestItems.length > 0)
    ? requestItems.map(name => ({ name, quantity:1, unit:"test" }))
    : [{ name:"As per referral/prescription", quantity:1, unit:"test" }];

  const submit = async () => {
    if (!consent) return;
    setLoading(true); setError("");
    const currentRxUrl = ls.get("dz_labs_rx_url") || null;
    const res = await api("POST", "/requests", {
      vertical: "lab",
      items,
      city: city||"Bangalore", area: area||"Hulimavu",
      filters:{ homeDeliveryOnly:false, urgent, radiusKm:settings?.requestRadiusKm||5 },
      homeDelivery: false,
      deliveryAddress: null,
      location:{ type:"Point", coordinates: patientCoords || AREA_COORDS[area]||[77.6108,12.8754] },
      notes: notes.trim()||null,
prescriptionUrl: currentRxUrl,

preferredDate,
preferredTime,

    });
    if (res.success) { ls.del("dz_labs_rx_url"); setLabsRxUrl(null); }
   if (res.success && res.data) { onRequestCreated({ ...res.data, vertical:"lab", location:{ type:"Point", coordinates: patientCoords||[0,0] } }); navigate("browseWhileWaiting"); }
else if (res.code === "NO_VENDORS_IN_AREA") { navigate("noVendors"); }
else setError(res.message||"Failed to send request");
    setLoading(false);
  };

  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg, paddingBottom:24 }}>
      <TopBar title="🔬 Send Lab Request" onBack={()=>navigate("labsLanding")} color={LABS_COLOR} />
      <div style={{ padding:16 }}>
        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:10 }}>🔬 Tests Requested ({items.length})</div>
          {items.map((item,i) => <div key={i} style={{ padding:"7px 0", borderBottom:i<items.length-1?`1px solid ${C.pearl}`:"none", fontSize:13, color:C.ink }}>🧪 {item.name}</div>)}
        </Card>

        {/* ── Prescription / Referral card ── */}
        {labsRxUrl ? (
          // Prescription uploaded — show preview
          <div style={{ background:LABS_COLOR+"10", borderRadius:14, padding:"12px 16px", marginBottom:12, border:`1.5px solid ${LABS_COLOR}40` }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:LABS_COLOR, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>📋</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:13, color:LABS_COLOR }}>✅ Referral / Prescription Attached</div>
                <div style={{ fontSize:11, color:C.slate, marginTop:1 }}>Shared with nearby labs to get accurate quotes</div>
              </div>
            </div>
            <img src={labsRxUrl} alt="Referral" style={{ width:"100%", borderRadius:10, maxHeight:140, objectFit:"cover", border:`1px solid ${LABS_COLOR}30` }} />
            <button onClick={()=>{ ls.del("dz_labs_rx_url"); setLabsRxUrl(null); }}
              style={{ background:"none", border:"none", color:C.danger, fontSize:12, cursor:"pointer", marginTop:8, fontWeight:600, padding:0 }}>
              🗑️ Remove referral
            </button>
          </div>
        ) : (
          // No prescription — prompt to upload
          <div onClick={()=>navigate("labsPrescription")}
            style={{ background:C.white, borderRadius:14, border:`1.5px dashed ${LABS_COLOR}`, padding:"14px 16px", marginBottom:12, cursor:"pointer", display:"flex", alignItems:"center", gap:14 }}>
            <div style={{ width:40, height:40, borderRadius:12, background:LABS_COLOR+"15", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, flexShrink:0 }}>📋</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:13, color:LABS_COLOR }}>Upload Doctor's Referral / Prescription</div>
              <div style={{ fontSize:11, color:C.slate, marginTop:2 }}>Helps labs give you accurate quotes (optional but recommended)</div>
            </div>
            <span style={{ color:LABS_COLOR, fontSize:18 }}>›</span>
          </div>
        )}

        <Card style={{ marginBottom:12 }}>

  <div style={{ fontWeight:700, fontSize:14, marginBottom:12 }}>
    📅 Preferred Appointment (Optional)
  </div>

  <div style={{ fontSize:12, color:C.slate, marginBottom:8 }}>
    Preferred Date
  </div>

  <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>

    {(()=>{
  const opts = [["none","No Preference"]];
  for(let i=0;i<3;i++){
    const d = new Date();
    d.setDate(d.getDate()+i);
    const val = d.toISOString().split("T")[0]; // "2026-06-27"
    const label = i===0?"Today":i===1?"Tomorrow":d.toLocaleDateString("en-IN",{weekday:"short",day:"numeric",month:"short"});
    opts.push([val,label]);
  }
  return opts;
})().map(([v,l])=>(

      <button
        key={v}
        onClick={()=>setPreferredDate(v)}
        style={{
          padding:"8px 14px",
          borderRadius:20,
          border:`1px solid ${preferredDate===v?LABS_COLOR:C.pearl}`,
          background:preferredDate===v?LABS_COLOR+"15":C.white,
          cursor:"pointer"
        }}
      >
        {l}
      </button>

    ))}

  </div>

  <div style={{ fontSize:12, color:C.slate, marginBottom:8 }}>
    Preferred Time
  </div>

  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>

    {[
      ["none","No Preference"],
      ["morning","Morning"],
      ["afternoon","Afternoon"],
      ["evening","Evening"]
    ].map(([v,l])=>(

      <button
        key={v}
        onClick={()=>setPreferredTime(v)}
        style={{
          padding:"8px 14px",
          borderRadius:20,
          border:`1px solid ${preferredTime===v?LABS_COLOR:C.pearl}`,
          background:preferredTime===v?LABS_COLOR+"15":C.white,
          cursor:"pointer"
        }}
      >
        {l}
      </button>

    ))}

  </div>

  <div
    style={{
      marginTop:12,
      fontSize:11,
      color:C.slate,
      lineHeight:1.6
    }}
  >
    💡 If you don't know the correct slot for your test (fasting, morning sample, etc.), choose <b>No Preference</b>. The lab can suggest the best available appointment in their quote.
  </div>

</Card>

        <Card style={{ marginBottom:12 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>⚡ Urgent</div>
              <div style={{ fontSize:12, color:C.slate }}>Need results as soon as possible</div>
            </div>
            <Toggle value={urgent} onChange={setUrgent} color={C.danger} />
          </div>
          {urgent && (
            <div style={{ background:C.danger+"10", borderRadius:10, padding:"8px 12px", marginTop:10, border:`1px solid ${C.danger}20` }}>
              <div style={{ fontSize:11, color:C.danger, fontWeight:600 }}>⚠️ Urgent requests may have additional charges. The lab will inform you about the urgent surcharge when they call to confirm.</div>
            </div>
          )}
        </Card>

        <Card style={{ marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:8 }}>📝 Notes (optional)</div>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2}
            placeholder="Any special instructions? e.g. 'Fasting sample needed', 'Senior citizen', 'Diabetic patient'..."
            style={{ width:"100%", background:C.bg, border:`1.5px solid ${notes?LABS_COLOR+"60":C.pearl}`, borderRadius:10, padding:"9px 12px", fontSize:13, outline:"none", resize:"none", boxSizing:"border-box" }} />
        </Card>



        {/* Marketplace disclaimer */}
        <div style={{ background:"#f8f9fa", borderRadius:12, padding:"10px 14px", marginBottom:12, border:"1px solid #dee2e6" }}>
          <div style={{ fontSize:11, color:"#6c757d", lineHeight:1.6 }}>
            🔒 <strong>Denizzy Marketplace:</strong> Your request goes to nearby independent diagnostic labs. Denizzy does not conduct tests or guarantee results. Test accuracy, NABL accreditation, and report quality are the lab's responsibility. Always verify lab credentials before booking.
          </div>
        </div>

        <div onClick={()=>setConsent(!consent)} style={{ display:"flex", gap:12, alignItems:"flex-start", padding:"13px 16px", background:C.white, borderRadius:14, border:`1px solid ${consent?LABS_COLOR:C.pearl}`, marginBottom:12, cursor:"pointer" }}>
          <div style={{ width:22, height:22, borderRadius:6, border:`2px solid ${consent?LABS_COLOR:C.mist}`, background:consent?LABS_COLOR:"transparent", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:1 }}>
            {consent&&<span style={{ color:C.white, fontSize:14 }}>✓</span>}
          </div>
          <div style={{ fontSize:12, color:C.slate, lineHeight:1.6 }}>I consent to sharing my test request with nearby labs to get quotes. Denizzy is a marketplace — lab accreditation is the lab's responsibility.</div>
        </div>

        {error&&<div style={{ background:C.danger+"15", borderRadius:12, padding:"10px 14px", marginBottom:12, color:C.danger, fontSize:13 }}>⚠️ {error}</div>}
        <Btn full color={LABS_COLOR} disabled={!consent} loading={loading} onClick={submit}>🔍 Get Quotes from Nearby Labs →</Btn>
      </div>
      {/* Lab delivery modal removed - using starting location */}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REMINDERS SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
const RX_COLORS = ["white","#FFB3BA","#FF6B6B","#FFA07A","#FFD700","#90EE90","#87CEEB","#DDA0DD","#F5DEB3"];
const RX_COLOR_NAMES = ["White","Pink","Red","Orange","Yellow","Green","Blue","Purple","Cream"];
const RX_SHAPES = ["Round","Oval","Capsule","Square","Diamond"];

const getRems = () => { try { return JSON.parse(ls.get("dz_reminders")||"[]"); } catch { return []; } };
const saveRems = (r) => ls.set("dz_reminders", JSON.stringify(r));

// ── Capacitor Local Notification ID tracking ──────────────────────────────────
const getLocalNotifIds = () => { try { return JSON.parse(ls.get("dz_notif_ids")||"{}"); } catch { return {}; } };
const saveLocalNotifIds = (ids) => ls.set("dz_notif_ids", JSON.stringify(ids));

// Schedule native local notifications — fires even when app is closed/locked
const scheduleLocalReminder = async (reminder) => {
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const perm = await LocalNotifications.requestPermissions();
    if (perm.display !== "granted") return;
    await cancelLocalReminder(reminder.id);
    if (!reminder.active) return;
    const notifications = [];
    const usedIds = [];
    const now = new Date();
    const daysToSchedule = Math.min(7, reminder.days || 30);
    for (let day = 0; day < daysToSchedule; day++) {
      for (let ti = 0; ti < reminder.times.length; ti++) {
        const time = reminder.times[ti];
        const [h, m] = time.split(":").map(Number);
        const scheduled = new Date(now);
        scheduled.setDate(scheduled.getDate() + day);
        scheduled.setHours(h, m, 0, 0);
        if (scheduled <= now) continue;
        const base = (parseInt(reminder.id.slice(-6)) || 1) % 100000;
        const notifId = ((base * 1000 + day * 10 + ti) % 2000000000) + 1;
        usedIds.push(notifId);
        notifications.push({
          id: notifId,
          title: "🔔 Denizzy Reminder",
          body: reminder.forSelf
            ? `💊 Time to take ${reminder.medicineName}! (${reminder.timing} food)`
            : `💊 ${reminder.personName} needs to take ${reminder.medicineName}`,
          schedule: { at: scheduled, allowWhileIdle: true },
          channelId: "denizzy_reminders",
          sound:     "alarm_sound",  // matches channel sound — plays even in background
          extra: { reminderId: reminder.id, time },
        });
      }
    }
    if (notifications.length > 0) {
      await LocalNotifications.schedule({ notifications });
      const ids = getLocalNotifIds();
      ids[reminder.id] = usedIds;
      saveLocalNotifIds(ids);
    }
  } catch (e) { console.log("LocalNotifications:", e.message); }
};

const cancelLocalReminder = async (reminderId) => {
  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");
    const ids = getLocalNotifIds();
    const notifIds = ids[reminderId];
    if (notifIds?.length) {
      await LocalNotifications.cancel({ notifications: notifIds.map(id => ({ id })) });
      delete ids[reminderId];
      saveLocalNotifIds(ids);
    }
  } catch {}
};

// ── Backend reminder sync helpers ────────────────────────────────────────────
const syncRemindersFromBackend = async () => {
  const token = getToken(); // uses existing getToken() → dz_patient_token
  if (!token) return;
  try {
    const res = await api("GET", "/reminders");
    if (res.success && Array.isArray(res.data) && res.data.length > 0) {
      // Map backend reminders to local format (use localId as id, fall back to _id)
      const backendRems = res.data.map(r => ({
        ...r,
        id: r.localId || r._id,
        backendId: r._id,
        firedLog: r.firedLog || {},
        takenLog: r.takenLog || {},
      }));
      saveRems(backendRems);
    }
  } catch {}
};

const saveReminderToBackend = async (reminder) => {
  const token = getToken();
  if (!token) return null;
  try {
    if (reminder.backendId) {
      // Update existing
      const res = await api("PUT", `/reminders/${reminder.backendId}`, reminder);
      return res.success ? res.data : null;
    } else {
      // Create new
      const res = await api("POST", "/reminders", { ...reminder, localId: reminder.id });
      if (res.success) {
        // Update local copy with backendId
        const rems = getRems().map(r => r.id === reminder.id ? { ...r, backendId: res.data._id } : r);
        saveRems(rems);
        return res.data;
      }
      return null;
    }
  } catch { return null; }
};

const deleteReminderFromBackend = async (reminder) => {
  if (!reminder?.backendId) return;
  try { await api("DELETE", `/reminders/${reminder.backendId}`); } catch {}
};

const markTakenOnBackend = async (reminder, time) => {
  if (!reminder?.backendId) return;
  try { await api("PUT", `/reminders/${reminder.backendId}/taken`, { time }); } catch {}
};

// ── In-App Alarm Overlay — full-screen modal when reminder fires while app is open ──
function AlarmOverlayModal({ rem, onTaken, onSnooze, onDismiss }) {
  if (!rem) return null;
  const colorHex = RX_COLORS[RX_COLOR_NAMES.indexOf(rem.color)] || "#fff";
  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, zIndex:9999, background:"rgba(0,18,36,0.94)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24 }}>
      {/* Pulsing pill icon */}
      <div style={{ width:90, height:90, borderRadius:"50%", background:C.health, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:20, animation:"dz-pulse 1.2s ease-in-out infinite" }}>
        <span style={{ fontSize:42 }}>💊</span>
      </div>
      <div style={{ color:"white", fontFamily:"Georgia,serif", fontSize:23, fontWeight:700, textAlign:"center", marginBottom:6 }}>Time to take medicine!</div>
      <div style={{ color:"rgba(255,255,255,0.6)", fontSize:14, marginBottom:22 }}>
        {rem.forSelf ? "This is for you" : `This is for ${rem.personName}`}
      </div>
      {/* Medicine card */}
      <div style={{ background:"rgba(255,255,255,0.1)", borderRadius:16, padding:"16px 24px", marginBottom:28, width:"100%", maxWidth:320 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, justifyContent:"center" }}>
          <div style={{ width:30, height:30, borderRadius:rem.shape==="Capsule"?"15px":"50%", background:colorHex, border:"2px solid rgba(255,255,255,0.3)", flexShrink:0 }}/>
          <div>
            <div style={{ color:"white", fontWeight:700, fontSize:16 }}>{rem.medicineName}</div>
            <div style={{ color:"rgba(255,255,255,0.55)", fontSize:12, marginTop:2 }}>
              {rem.timing==="before"?"Before food":"After food"} · {rem.firedAt}
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <button onClick={onTaken} style={{ width:"100%", maxWidth:320, background:C.health, border:"none", borderRadius:16, padding:"17px 0", color:"white", fontWeight:700, fontSize:17, cursor:"pointer", marginBottom:10 }}>
        ✅ Mark as Taken
      </button>
      <div style={{ display:"flex", gap:10, width:"100%", maxWidth:320 }}>
        <button onClick={()=>onSnooze(10)} style={{ flex:1, background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:14, padding:"13px 0", color:"white", fontWeight:600, fontSize:13, cursor:"pointer" }}>
          ⏰ Snooze 10m
        </button>
        <button onClick={onDismiss} style={{ flex:1, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:14, padding:"13px 0", color:"rgba(255,255,255,0.45)", fontWeight:600, fontSize:13, cursor:"pointer" }}>
          ✖ Dismiss
        </button>
      </div>
    </div>
  );
}

function RemindersScreen({ navigate }) {
  const [reminders, setReminders] = useState(getRems);
  const [now, setNow] = useState(new Date());

  // On mount — sync reminders from backend (keeps backend as source of truth)
  useEffect(() => {
    const doSync = async () => {
      await syncRemindersFromBackend();
      const latest = getRems();
      setReminders(latest);
      for (const rem of latest) {
        if (rem.active) await scheduleLocalReminder(rem);
      }
    };
    doSync();
  }, []);

  // UI clock refresh + pull firedLog updates written by global alarm check
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
      setReminders(getRems());
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const refresh = () => setReminders(getRems());

  const markTaken = (id, time) => {
    const today = new Date().toISOString().split("T")[0];
    const rems = getRems().map(r => {
      if (r.id !== id) return r;
      const taken = r.takenLog || {};
      const todayTaken = taken[today] || [];
      return { ...r, takenLog: { ...taken, [today]: [...todayTaken, time] } };
    });
    saveRems(rems); refresh();
    // Sync to backend
    const rem = getRems().find(r => r.id === id);
    if (rem) markTakenOnBackend(rem, time);
  };

  const deleteRem = (id) => {
    const rem = getRems().find(r => r.id === id);
    saveRems(getRems().filter(r=>r.id!==id)); refresh();
    if (rem) { deleteReminderFromBackend(rem); cancelLocalReminder(id); }
  };

  const toggleActive = (id) => {
    const rem = getRems().find(r => r.id === id);
    const newActive = !rem?.active;
    const updated = { ...rem, active: newActive };
    saveRems(getRems().map(r=>r.id===id?updated:r)); refresh();
    if (rem) { saveReminderToBackend(updated); scheduleLocalReminder(updated); }
  };

  const waLink = (rem) => {
    const phone = (rem.personPhone||"").replace(/\D/g,"");
    const name = rem.personName || "there";
    const msg = rem.forSelf
      ? `🕐 Time to take your tablet! ${rem.medicineName} (${rem.timing} food) 💊 Your body needs care!`
      : `Hi ${name}! 🕐 Did you take your tablet? \n💊 ${rem.medicineName} (${rem.timing} food)\n\nTake care! ❤️`;
    return `https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`;
  };

  const today = now.toISOString().split("T")[0];
  const nowHHMM = `${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`;

  // Next dose countdown
  const getNextDose = (rem) => {
    const sorted = [...rem.times].sort();
    const [nh,nm] = nowHHMM.split(":").map(Number);
    for (const t of sorted) {
      const [h,m] = t.split(":").map(Number);
      const diff = (h*60+m) - (nh*60+nm);
      if (diff > 0) return diff < 60 ? `${diff}m` : `${Math.floor(diff/60)}h ${diff%60>0?diff%60+"m":""}`;
    }
    // Tomorrow's first dose
    if (!sorted.length) return null;
    const [h,m] = sorted[0].split(":").map(Number);
    const diff = 24*60 - (nh*60+nm) + h*60+m;
    return diff < 60 ? `${diff}m` : `${Math.floor(diff/60)}h ${diff%60>0?diff%60+"m":""}`;
  };

  // 7-day adherence %
  const getAdherence = (rem) => {
    const log = rem.takenLog || {};
    let total = 0, taken = 0;
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toISOString().split("T")[0];
      total += rem.times.length;
      taken += (log[key] || []).length;
    }
    return total > 0 ? Math.round((taken / total) * 100) : 0;
  };

  const getDaysLeft = (rem) => {
    if (!rem.startDate) return rem.days;
    const start = new Date(rem.startDate);
    const diff = Math.floor((now - start)/(1000*60*60*24));
    return Math.max(0, rem.days - diff);
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", background:C.bg }}>
      <div style={{ background:C.ocean, padding:"40px 20px 20px", position:"relative", overflow:"hidden", flexShrink:0 }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.1 }} viewBox="0 0 400 40" preserveAspectRatio="none" height="40" width="100%"><path d="M0,20 C80,38 160,3 240,20 C320,38 370,5 400,20 L400,40 L0,40 Z" fill="white"/></svg>
        <div style={{ position:"relative", display:"flex", justifyContent:"space-between", alignItems:"flex-end" }}>
          <div>
            <button onClick={()=>navigate("home")} style={{ background:"rgba(255,255,255,0.15)", border:"none", borderRadius:8, color:"white", fontSize:16, cursor:"pointer", marginBottom:8, padding:"4px 10px" }}>‹ Back</button>
          <div style={{ color:"rgba(255,255,255,0.7)", fontSize:13 }}>💊 Medicine</div>
            <div style={{ color:"white", fontFamily:"Georgia,serif", fontSize:22, fontWeight:700 }}>Reminders</div>
            <div style={{ color:"rgba(255,255,255,0.6)", fontSize:12, marginTop:4 }}>
              {String(now.getHours()).padStart(2,"0")}:{String(now.getMinutes()).padStart(2,"0")} · {reminders.filter(r=>r.active).length} active
            </div>
          </div>
          <button onClick={()=>navigate("addReminder")}
            style={{ background:"rgba(255,255,255,0.2)", border:"2px solid rgba(255,255,255,0.4)", borderRadius:14, padding:"10px 16px", color:"white", fontWeight:700, fontSize:14, cursor:"pointer" }}>
            + Add
          </button>
        </div>
      </div>

      <div style={{ flex:1, overflowY:"auto", padding:"12px 16px" }}>
        {reminders.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:56, marginBottom:16 }}>💊</div>
            <div style={{ fontFamily:"Georgia,serif", fontSize:18, fontWeight:700, color:C.ink }}>No reminders yet</div>
            <div style={{ fontSize:13, color:C.slate, marginTop:8, marginBottom:24, lineHeight:1.6 }}>Add medicine reminders for yourself or family members. Get WhatsApp reminders for loved ones.</div>
            <Btn color={C.ocean} onClick={()=>navigate("addReminder")}>+ Add First Reminder</Btn>
          </div>
        ) : reminders.map(rem => {
          const daysLeft = getDaysLeft(rem);
          const takenToday = (rem.takenLog?.[today] || []);
          const colorHex = RX_COLORS[RX_COLOR_NAMES.indexOf(rem.color)] || "#fff";
          const isDue = rem.active && rem.times.some(t => {
            const [h,m] = t.split(":").map(Number);
            const [nh,nm] = nowHHMM.split(":").map(Number);
            return (h*60+m) <= (nh*60+nm) && (h*60+m) > (nh*60+nm-60);
          });

          return (
            <div key={rem.id} style={{ marginBottom:14 }}>
              <div style={{ background:isDue?C.health+"08":C.white, borderRadius:18, border:`1.5px solid ${isDue?C.health:rem.active?C.pearl:C.pearl}`, overflow:"hidden", opacity:rem.active?1:0.6 }}>
                {isDue&&<div style={{ background:C.health, padding:"6px 16px", display:"flex", alignItems:"center", gap:8 }}><span style={{ fontSize:14 }}>⏰</span><span style={{ color:"white", fontWeight:700, fontSize:12 }}>Time to take medicine!</span></div>}
                <div style={{ padding:"14px 16px" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                    {/* Color dot */}
                    <div style={{ width:36, height:36, borderRadius:rem.shape==="Capsule"?"18px":"50%", background:colorHex, border:`2px solid ${C.pearl}`, flexShrink:0, marginTop:2, boxShadow:"inset 0 1px 3px rgba(0,0,0,0.15)" }} />
                    <div style={{ flex:1 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                        <div style={{ fontWeight:700, fontSize:15, color:C.ink }}>{rem.medicineName}</div>
                        {/* 7-day adherence badge */}
                        {(() => { const adh = getAdherence(rem); return adh > 0 ? (
                          <span style={{ background:adh>=80?C.success+"20":adh>=50?C.saffron+"25":C.danger+"15", color:adh>=80?C.success:adh>=50?"#B45000":C.danger, fontSize:10, fontWeight:700, borderRadius:6, padding:"2px 7px" }}>
                            {adh}% 7d
                          </span>
                        ) : null; })()}
                      </div>
                      <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>
                        {rem.timing==="before"?"Before food":"After food"} · {rem.shape} · {rem.color}
                      </div>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:8 }}>
                        {rem.times.map(t=>{
                          const taken = takenToday.includes(t);
                          const [h,m] = t.split(":").map(Number);
                          const [nh,nm] = nowHHMM.split(":").map(Number);
                          const isPast = (h*60+m) < (nh*60+nm - 5);
                          const isMissed = isPast && !taken;
                          return (
                            <button key={t} onClick={()=>!taken&&markTaken(rem.id,t)}
                              style={{ background:taken?C.success+"20":isMissed?C.danger+"15":C.ocean+"15", border:`1px solid ${taken?C.success:isMissed?C.danger:C.ocean}30`, borderRadius:8, padding:"4px 10px", fontSize:12, fontWeight:600, color:taken?C.success:isMissed?C.danger:C.ocean, cursor:taken?"default":"pointer" }}>
                              {taken?"✅":isMissed?"❌":"🕐"} {t}
                            </button>
                          );
                        })}
                      </div>
                      {/* Next dose countdown */}
                      {rem.active && !isDue && (() => { const nd = getNextDose(rem); return nd ? (
                        <div style={{ fontSize:11, color:C.ocean, marginTop:6, fontWeight:600 }}>⏳ Next dose in {nd}</div>
                      ) : null; })()}
                      <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:6 }}>
                        <div style={{ fontSize:11, color:C.slate }}>
                          {!rem.forSelf&&<span>👤 {rem.personName} · </span>}
                          {daysLeft > 0 ? <span style={{ color:daysLeft<=3?C.danger:C.slate }}>{daysLeft} days left{daysLeft<=3?" ⚠️ Running low!":""}</span> : <span style={{ color:C.danger }}>Course complete</span>}
                          {rem.strips&&<span> · {rem.strips} strip{rem.strips>1?"s":""}</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      <button onClick={()=>toggleActive(rem.id)} style={{ background:rem.active?C.success+"15":C.pearl, border:"none", borderRadius:8, padding:"4px 8px", fontSize:10, fontWeight:700, color:rem.active?C.success:C.mist, cursor:"pointer" }}>
                        {rem.active?"ON":"OFF"}
                      </button>
                      <button onClick={()=>navigate("editReminder_"+rem.id)} style={{ background:C.ocean+"15", border:"none", borderRadius:8, padding:"4px 8px", fontSize:10, fontWeight:700, color:C.ocean, cursor:"pointer" }}>✏️</button>
                      <button onClick={()=>deleteRem(rem.id)} style={{ background:C.danger+"15", border:"none", borderRadius:8, padding:"4px 8px", fontSize:10, fontWeight:700, color:C.danger, cursor:"pointer" }}>🗑️</button>
                    </div>
                  </div>

                  {/* Call + WhatsApp buttons for family */}
                  {!rem.forSelf && rem.personPhone && (
                    <div style={{ display:"flex", gap:8, marginTop:12 }}>
                      <a href={`tel:${rem.personPhone}`} style={{ textDecoration:"none", flex:1 }}>
                        <div style={{ background:C.success, borderRadius:12, padding:"10px 8px", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                          <span style={{ fontSize:16 }}>📞</span>
                          <div style={{ color:"white", fontWeight:700, fontSize:12 }}>Call</div>
                        </div>
                      </a>
                      <a href={waLink(rem)} target="_blank" rel="noopener noreferrer" style={{ textDecoration:"none", flex:2 }}>
                        <div style={{ background:"#25D366", borderRadius:12, padding:"10px 8px", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                          <span style={{ fontSize:16 }}>💬</span>
                          <div style={{ color:"white", fontWeight:700, fontSize:12 }}>WhatsApp {rem.personName}</div>
                        </div>
                      </a>
                    </div>
                  )}

                  {/* Reorder button if running low */}
                  {daysLeft <= 3 && daysLeft > 0 && (
                    <div onClick={()=>navigate("medSearch")}
                      style={{ background:C.danger+"10", borderRadius:12, padding:"8px 14px", marginTop:10, cursor:"pointer", border:`1px solid ${C.danger}20`, display:"flex", alignItems:"center", gap:8 }}>
                      <span style={{ fontSize:14 }}>💊</span>
                      <div style={{ flex:1, fontSize:12, color:C.danger, fontWeight:600 }}>Running low! Order more {rem.medicineName} →</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div style={{ height:20 }} />
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD REMINDER SCREEN
// ═══════════════════════════════════════════════════════════════════════════════
// Disease → common medicines suggestion map

function AddReminderScreen({ navigate, editingReminder }) {
  const isEdit = !!editingReminder;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(isEdit ? { ...editingReminder } : {
    medicineName:"", color:"White", shape:"Round", timing:"after",
    times:["08:00"], startDate:new Date().toISOString().split("T")[0],
    days:30, forSelf:true, personName:"", personPhone:"",
  });
  const setF = (k,v) => setForm(f=>({...f,[k]:v}));
  const addTime = () => setF("times",[...form.times,"08:00"]);
  const updateTime = (i,v) => setF("times",form.times.map((t,idx)=>idx===i?v:t));
  const removeTime = (i) => setF("times",form.times.filter((_,idx)=>idx!==i));
  const QUICK = ["BP","Sugar / Diabetes","Fever","Headache","Cold & Cough","Thyroid","Vitamin D3","Vitamin B12","Calcium","Heart Med","Cholesterol","Acidity","Iron","Pain Relief","Eye Drops"];
  const save = async () => {
    if (!form.medicineName.trim()) return;
    const rems = getRems().filter(r=>r.id!==form.id);
    const updated = { ...form, id:isEdit?form.id:Date.now().toString(), active:isEdit?form.active:true, takenLog:form.takenLog||{} };
    rems.unshift(updated);
    saveRems(rems);
    // Save to backend so cron job can send push notification at the right time
    saveReminderToBackend(updated);
    // Schedule native local notification (works when app is closed or locked)
    scheduleLocalReminder(updated);
    navigate("reminders");
  };
  const colorHex = RX_COLORS[RX_COLOR_NAMES.indexOf(form.color)] || "#fff";
  const steps = ["💊 What","⏰ When","👤 Who"];

  return (
    <div style={{ position:"fixed", top:0, left:0, right:0, bottom:0, display:"flex", flexDirection:"column", background:C.bg }}>
      {/* Header with step indicator */}
      <div style={{ background:C.ocean, padding:"14px 16px 12px", flexShrink:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
          <button onClick={()=>step>1?setStep(step-1):navigate("reminders")}
            style={{ background:"rgba(255,255,255,0.2)", border:"none", borderRadius:10, width:36, height:36, color:"white", fontSize:18, cursor:"pointer" }}>‹</button>
          <span style={{ color:"white", fontWeight:700, fontSize:16, flex:1 }}>{isEdit?"Edit Reminder":"Add Reminder"}</span>
          <span style={{ color:"rgba(255,255,255,0.7)", fontSize:12 }}>Step {step}/3</span>
        </div>
        <div style={{ display:"flex", gap:6 }}>
          {steps.map((s,i)=>(
            <div key={i} onClick={()=>i<step-1&&setStep(i+1)}
              style={{ flex:1, background:step===i+1?"white":step>i+1?"rgba(255,255,255,0.4)":"rgba(255,255,255,0.15)", borderRadius:8, padding:"6px 4px", textAlign:"center", cursor:i<step-1?"pointer":"default" }}>
              <span style={{ fontSize:11, fontWeight:700, color:step===i+1?C.ocean:"rgba(255,255,255,0.85)" }}>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Scrollable body — minHeight:0 fixes flex scroll on mobile */}
      <div style={{ flex:1, minHeight:0, overflowY:"auto", WebkitOverflowScrolling:"touch", padding:"16px 16px 32px" }}>

        {step===1 && (
          <>
            {/* Preview */}
            <div style={{ background:C.white, borderRadius:16, padding:14, marginBottom:14, display:"flex", alignItems:"center", gap:14, border:"1.5px solid "+C.pearl }}>
              <div style={{ width:50, height:50, borderRadius:form.shape==="Capsule"?"25px":form.shape==="Square"?"10px":"50%", background:colorHex, border:"2px solid "+C.pearl, flexShrink:0, boxShadow:"inset 0 2px 4px rgba(0,0,0,0.1)" }} />
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontWeight:700, fontSize:15, color:C.ink, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{form.medicineName||"Medicine name..."}</div>
                <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>{form.shape} · {form.color}</div>
              </div>
            </div>

            {/* Name */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:12, border:"1px solid "+C.pearl }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, letterSpacing:"1px", marginBottom:8 }}>MEDICINE OR CONDITION *</div>
              <input value={form.medicineName} onChange={e=>setF("medicineName",e.target.value)}
                placeholder="BP / Fever / Paracetamol 500mg..."
                style={{ width:"100%", background:C.bg, border:"2px solid "+(form.medicineName?C.ocean:C.pearl), borderRadius:12, padding:"12px 14px", fontSize:15, outline:"none", boxSizing:"border-box" }} />
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginTop:10 }}>
                {QUICK.map((q,i)=>(
                  <button key={i} onClick={()=>setF("medicineName",q)}
                    style={{ background:form.medicineName===q?C.ocean:C.white, color:form.medicineName===q?"white":C.ink, border:"1.5px solid "+(form.medicineName===q?C.ocean:C.pearl), borderRadius:20, padding:"5px 12px", fontSize:12, cursor:"pointer", fontWeight:600 }}>
                    {q}
                  </button>
                ))}
              </div>
            </div>

            {/* Colour */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:12, border:"1px solid "+C.pearl }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, letterSpacing:"1px", marginBottom:10 }}>TABLET COLOUR</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                {RX_COLOR_NAMES.map((name,i)=>(
                  <button key={name} onClick={()=>setF("color",name)}
                    style={{ width:38, height:38, borderRadius:"50%", background:RX_COLORS[i], border:"3px solid "+(form.color===name?C.ocean:"transparent"), cursor:"pointer", outline:"none", boxShadow:form.color===name?"0 0 0 2px white,0 0 0 4px "+C.ocean:"0 1px 3px rgba(0,0,0,0.2)" }}
                    title={name} />
                ))}
              </div>
            </div>

            {/* Shape */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:20, border:"1px solid "+C.pearl }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, letterSpacing:"1px", marginBottom:10 }}>TABLET SHAPE</div>
              <div style={{ display:"flex", gap:8 }}>
                {RX_SHAPES.map(s=>(
                  <button key={s} onClick={()=>setF("shape",s)}
                    style={{ flex:1, background:form.shape===s?C.ocean:C.white, color:form.shape===s?"white":C.ink, border:"1.5px solid "+(form.shape===s?C.ocean:C.pearl), borderRadius:10, padding:"8px 2px", fontSize:10, fontWeight:700, cursor:"pointer", textAlign:"center", lineHeight:1.5 }}>
                    {s==="Round"?"⬤":s==="Oval"?"⬭":s==="Capsule"?"💊":s==="Square"?"⬛":"◆"}<br/>{s}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={()=>{ if(!form.medicineName.trim())return; setStep(2); }}
              style={{ width:"100%", background:form.medicineName.trim()?C.ocean:"#ccc", color:"white", border:"none", borderRadius:14, padding:"14px 0", fontWeight:700, fontSize:15, cursor:form.medicineName.trim()?"pointer":"not-allowed" }}>
              Next: When to Take →
            </button>
          </>
        )}

        {step===2 && (
          <>
            {/* Timing */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:12, border:"1px solid "+C.pearl }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, letterSpacing:"1px", marginBottom:10 }}>TAKE MEDICINE</div>
              <div style={{ display:"flex", gap:8 }}>
                {[["before","🍽️","Before Food"],["after","✅","After Food"],["any","🕐","Anytime"]].map(([v,icon,l])=>(
                  <button key={v} onClick={()=>setF("timing",v)}
                    style={{ flex:1, background:form.timing===v?C.ocean:C.white, color:form.timing===v?"white":C.ink, border:"1.5px solid "+(form.timing===v?C.ocean:C.pearl), borderRadius:12, padding:"12px 4px", fontSize:11, fontWeight:700, cursor:"pointer", textAlign:"center", lineHeight:1.5 }}>
                    <div style={{ fontSize:18 }}>{icon}</div>{l}
                  </button>
                ))}
              </div>
            </div>

            {/* Alarm times */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:12, border:"1px solid "+C.pearl }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, letterSpacing:"1px", marginBottom:12 }}>⏰ ALARM TIMES EACH DAY</div>
              {form.times.map((t,i)=>(
                <div key={i} style={{ display:"flex", gap:8, marginBottom:10, alignItems:"center" }}>
                  <input type="time" value={t} onChange={e=>updateTime(i,e.target.value)}
                    style={{ flex:1, background:C.bg, border:"1.5px solid "+C.pearl, borderRadius:10, padding:"10px 12px", fontSize:16, outline:"none" }} />
                  {form.times.length>1 && (
                    <button onClick={()=>removeTime(i)}
                      style={{ background:C.danger+"15", border:"none", borderRadius:8, padding:"10px 12px", color:C.danger, cursor:"pointer", fontSize:16 }}>✕</button>
                  )}
                </div>
              ))}
              <button onClick={addTime}
                style={{ width:"100%", background:"none", border:"1.5px dashed "+C.ocean, borderRadius:10, padding:"10px 0", color:C.ocean, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                + Add Another Alarm
              </button>
            </div>

            {/* Duration */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:20, border:"1px solid "+C.pearl }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.slate, letterSpacing:"1px", marginBottom:10 }}>COURSE DURATION</div>
              <div style={{ display:"flex", gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:C.slate, marginBottom:6 }}>Start Date</div>
                  <input type="date" value={form.startDate} onChange={e=>setF("startDate",e.target.value)}
                    style={{ width:"100%", background:C.bg, border:"1.5px solid "+C.pearl, borderRadius:10, padding:"10px", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, color:C.slate, marginBottom:6 }}>Days</div>
                  <input type="number" value={form.days} onChange={e=>setF("days",parseInt(e.target.value)||1)} min={1} max={365}
                    style={{ width:"100%", background:C.bg, border:"1.5px solid "+C.pearl, borderRadius:10, padding:"10px", fontSize:13, outline:"none", boxSizing:"border-box" }} />
                </div>
              </div>
            </div>

            <button onClick={()=>setStep(3)}
              style={{ width:"100%", background:C.ocean, color:"white", border:"none", borderRadius:14, padding:"14px 0", fontWeight:700, fontSize:15, cursor:"pointer" }}>
              Next: Who is this for? →
            </button>
          </>
        )}

        {step===3 && (
          <>
            {/* For self or family */}
            <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:14, border:"1px solid "+C.pearl }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:form.forSelf?0:16 }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:15, color:C.ink }}>For a Family Member?</div>
                  <div style={{ fontSize:12, color:C.slate, marginTop:2 }}>WhatsApp 💬 and 📞 Call reminders</div>
                </div>
                <Toggle value={!form.forSelf} onChange={v=>setF("forSelf",!v)} color={C.ocean} />
              </div>
              {!form.forSelf && (
                <>
                  <input value={form.personName||""} onChange={e=>setF("personName",e.target.value)} placeholder="Their name (e.g. Mum, Dad)"
                    style={{ width:"100%", background:C.bg, border:"1.5px solid "+C.pearl, borderRadius:10, padding:"11px 12px", fontSize:14, outline:"none", boxSizing:"border-box", marginBottom:10 }} />
                  <input value={form.personPhone||""} onChange={e=>setF("personPhone",e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="Their WhatsApp / Mobile" type="tel"
                    style={{ width:"100%", background:C.bg, border:"1.5px solid "+(form.personPhone&&form.personPhone.length===10?C.success:C.pearl), borderRadius:10, padding:"11px 12px", fontSize:14, outline:"none", boxSizing:"border-box", marginBottom:form.personPhone&&form.personPhone.length===10?10:0 }} />
                  {form.personPhone&&form.personPhone.length===10&&(
                    <div style={{ background:"#25D36610", borderRadius:10, padding:"10px 12px", border:"1px solid #25D36630" }}>
                      <span style={{ fontSize:12, color:"#25D366", fontWeight:600 }}>💬 WhatsApp & 📞 Call buttons will appear on the reminder card</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Summary card */}
            <div style={{ background:C.ocean+"10", borderRadius:16, padding:16, marginBottom:20, border:"1px solid "+C.ocean+"25" }}>
              <div style={{ fontSize:12, fontWeight:700, color:C.ocean, marginBottom:10 }}>📋 Reminder Summary</div>
              <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:10 }}>
                <div style={{ width:40, height:40, borderRadius:form.shape==="Capsule"?"20px":"50%", background:colorHex, border:"2px solid "+C.pearl, flexShrink:0 }} />
                <div>
                  <div style={{ fontWeight:700, fontSize:14, color:C.ink }}>{form.medicineName}</div>
                  <div style={{ fontSize:12, color:C.slate }}>{form.shape} · {form.color}</div>
                </div>
              </div>
              {[
                ["⏰",form.times.join(", ")+" · "+(form.timing==="before"?"Before food":"After food")],
                ["📅",form.days+" days · starts "+form.startDate],
                ["👤",form.forSelf?"For yourself":"For "+(form.personName||"family member")+(form.personPhone?" ("+form.personPhone+")":"")],
              ].map(([icon,txt],i)=>(
                <div key={i} style={{ fontSize:13, color:C.slate, marginBottom:4 }}>{icon} {txt}</div>
              ))}
            </div>

            <button onClick={save}
              disabled={!form.medicineName.trim()||(!form.forSelf&&(!form.personName||(form.personPhone&&form.personPhone.length!==10)))}
              style={{ width:"100%", background:C.ocean, color:"white", border:"none", borderRadius:14, padding:"14px 0", fontWeight:700, fontSize:15, cursor:"pointer" }}>
              {isEdit?"✅ Update Reminder":"💊 Save Reminder"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SET NAME SCREEN — shown once after first login
// ═══════════════════════════════════════════════════════════════
function NoVendorsScreen({ navigate, area, city }) {
  const [notified, setNotified] = useState(false);
  return (
    <div style={{ flex:1, overflowY:"auto", background:C.bg }}>
      <div style={{ background:C.deep, padding:"48px 24px 40px", textAlign:"center", borderBottomLeftRadius:28, borderBottomRightRadius:28, position:"relative", overflow:"hidden" }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 50" preserveAspectRatio="none" height="50" width="100%"><path d="M0,25 C80,48 160,3 240,25 C320,48 370,8 400,25 L400,50 L0,50 Z" fill={C.foam}/></svg>
        <div style={{ position:"relative" }}>
          <div style={{ fontSize:64, marginBottom:16 }}>🏗️</div>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:22, fontWeight:700 }}>Coming Soon to {area||city||"Your Area"}!</div>
          <div style={{ color:"rgba(255,255,255,0.65)", fontSize:14, marginTop:8, lineHeight:1.6 }}>We don't have any shops in your area yet. We're working on it!</div>
        </div>
      </div>
      <div style={{ padding:20 }}>
        <div style={{ background:C.saffron+"15", borderRadius:16, padding:16, marginBottom:16, border:`1px solid ${C.saffron}30` }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.warning, marginBottom:8 }}>📍 No vendors found near {area||city}</div>
          <div style={{ fontSize:13, color:C.slate, lineHeight:1.7 }}>Denizzy is currently expanding to your area. Our team has been notified about the demand in your locality.</div>
        </div>
        {!notified ? (
          <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:16, border:`1px solid ${C.pearl}` }}>
            <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:8 }}>🔔 Get notified when we launch near you</div>
            <div style={{ fontSize:13, color:C.slate, marginBottom:14 }}>We'll send you a notification as soon as shops are available in {area||city}.</div>
            <Btn full color={C.ocean} onClick={()=>setNotified(true)}>🔔 Notify Me When Available</Btn>
          </div>
        ) : (
          <div style={{ background:C.success+"12", borderRadius:16, padding:16, marginBottom:16, border:`1px solid ${C.success}30`, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:8 }}>🎉</div>
            <div style={{ fontWeight:700, fontSize:15, color:C.success }}>You're on the list!</div>
            <div style={{ fontSize:13, color:C.slate, marginTop:6 }}>We'll notify you when Denizzy launches near {area||city}.</div>
          </div>
        )}
        <div style={{ background:C.white, borderRadius:16, padding:16, marginBottom:16, border:`1px solid ${C.pearl}` }}>
          <div style={{ fontWeight:700, fontSize:14, color:C.ink, marginBottom:14 }}>What would you like to do?</div>
          <Btn full color={C.health} onClick={()=>navigate("cityPicker")} style={{ marginBottom:10 }}>📍 Change Location</Btn>
          <Btn full variant="ghost" onClick={()=>navigate("home")}>← Back to Home</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// LANDING SCREEN — shown on desktop (width > 900px) when patient not logged in
// Mobile users go straight to the app
// ═══════════════════════════════════════════════════════════════════════════════
function LandingScreen({ onGetStarted }) {
  const [animStep, setAnimStep] = useState(0);

  // Animate the live request flow demo
  useEffect(() => {
    const t = setInterval(() => setAnimStep(s => (s + 1) % 4), 1800);
    return () => clearInterval(t);
  }, []);

  const steps = [
    { icon:"💊", label:"Request sent", sub:"3 items · Nearby pharmacies" },
    { icon:"🏪", label:"Quotes arriving", sub:"Tulasi Medical ₹340" },
    { icon:"⚖️", label:"Compare prices", sub:"3 quotes received" },
    { icon:"✅", label:"Order confirmed", sub:"Delivering in 30 mins" },
  ];

  const features = [
    { icon:"📍", title:"Hyperlocal", desc:"Pharmacies and labs within 3-5km of you. No waiting for distant deliveries." },
    { icon:"⚖️", title:"Compare Quotes", desc:"Multiple vendors quote you simultaneously. Pick the best price — always." },
    { icon:"🔬", title:"Lab Tests Too", desc:"Book diagnostic tests from nearby labs. Home collection available." },
    { icon:"🔒", title:"Secure & Private", desc:"Prescriptions shared only with vendors you choose. OTP login, no passwords." },
    { icon:"💊", title:"Medicine Reminders", desc:"Set alarms for your daily medicines. Never miss a dose." },
    { icon:"📋", title:"Prescription Wallet", desc:"Save prescriptions for family. Reuse anytime without searching your gallery." },
  ];

  const N = { navy:"#0A2540", ocean:"#1A5F7A", saffron:"#F5A623", white:"#FFFFFF", bg:"#F8FAFC", mid:"#6B7C93", border:"#E2E8F0" };

  return (
    <div style={{ fontFamily:"system-ui,-apple-system,sans-serif", background:N.bg, minHeight:"100vh", color:N.navy }}>
      <style>{`
        @keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
        @keyframes pulse { 0%,100% { transform:scale(1); } 50% { transform:scale(1.04); } }
        .dz-step { transition: all 0.4s ease; }
        .dz-btn:hover { opacity:0.88; transform:translateY(-1px); }
        .dz-card:hover { transform:translateY(-4px); box-shadow:0 12px 40px rgba(10,37,64,0.12); }
        .dz-card { transition: all 0.25s ease; }
      `}</style>

      {/* NAV */}
      <nav style={{ background:N.navy, padding:"16px 48px", display:"flex", alignItems:"center", position:"sticky", top:0, zIndex:100, boxShadow:"0 2px 20px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize:24, fontWeight:800, color:N.white, fontFamily:"Georgia,serif" }}>
          Den<span style={{ color:N.saffron }}>izzy</span>
        </div>
        <div style={{ marginLeft:"auto", display:"flex", gap:24, alignItems:"center" }}>
          {[["How it works","#how"],["For Vendors","#vendors"],["Contact","#contact"]].map(([l,h])=>(
            <a key={l} href={h} style={{ color:"rgba(255,255,255,0.7)", fontSize:14, textDecoration:"none", fontWeight:500 }}
              onMouseEnter={e=>e.target.style.color="white"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.7)"}>{l}</a>
          ))}
          <button onClick={onGetStarted} className="dz-btn"
            style={{ background:N.saffron, border:"none", borderRadius:10, padding:"10px 22px", color:N.navy, fontWeight:700, fontSize:14, cursor:"pointer", fontFamily:"inherit" }}>
            Open App →
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section style={{ background:`linear-gradient(135deg, ${N.navy} 0%, #0F3460 60%, #1A5F7A 100%)`, padding:"80px 48px", display:"flex", alignItems:"center", gap:64, maxWidth:1200, margin:"0 auto" }}>
        <div style={{ flex:1, animation:"fadeUp 0.7s ease" }}>
          <div style={{ display:"inline-block", background:N.saffron+"20", border:`1px solid ${N.saffron}40`, borderRadius:20, padding:"5px 14px", fontSize:12, fontWeight:700, color:N.saffron, letterSpacing:"1px", marginBottom:20 }}>
            🚀 NOW LIVE IN HULIMAVU, BANGALORE
          </div>
          <h1 style={{ fontSize:48, fontWeight:800, color:N.white, fontFamily:"Georgia,serif", lineHeight:1.2, marginBottom:16 }}>
            Medicine quotes<br/>
            <span style={{ color:N.saffron }}>in minutes.</span>
          </h1>
          <p style={{ fontSize:18, color:"rgba(255,255,255,0.75)", lineHeight:1.7, marginBottom:32, maxWidth:480 }}>
            Compare prices from nearby pharmacies. Book lab tests. Track your order.
            All from one app — no calls, no waiting.
          </p>
          <div style={{ display:"flex", gap:12 }}>
            <button onClick={onGetStarted} className="dz-btn"
              style={{ background:N.saffron, border:"none", borderRadius:12, padding:"14px 28px", color:N.navy, fontWeight:700, fontSize:16, cursor:"pointer", fontFamily:"inherit" }}>
              Get Started Free →
            </button>
            <a href="#how" style={{ textDecoration:"none" }}>
              <button className="dz-btn"
                style={{ background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.25)", borderRadius:12, padding:"14px 28px", color:N.white, fontWeight:600, fontSize:16, cursor:"pointer", fontFamily:"inherit" }}>
                See how it works
              </button>
            </a>
          </div>
          <div style={{ marginTop:32, display:"flex", gap:24 }}>
            {[["Free to use","No fees for patients"],["3–5 km radius","Truly hyperlocal"],["Multiple quotes","Best price always"]].map(([t,s])=>(
              <div key={t} style={{ borderLeft:`2px solid ${N.saffron}`, paddingLeft:12 }}>
                <div style={{ fontWeight:700, color:N.white, fontSize:13 }}>{t}</div>
                <div style={{ color:"rgba(255,255,255,0.55)", fontSize:12, marginTop:2 }}>{s}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Live demo animation */}
        <div style={{ flexShrink:0, width:320, background:"rgba(255,255,255,0.05)", borderRadius:24, padding:24, border:"1px solid rgba(255,255,255,0.1)" }}>
          <div style={{ fontSize:12, fontWeight:700, color:N.saffron, letterSpacing:"1px", marginBottom:16 }}>LIVE REQUEST FLOW</div>
          {steps.map((s,i)=>(
            <div key={i} className="dz-step" style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:14, marginBottom:8,
              background: i===animStep ? "rgba(245,166,35,0.15)" : i<animStep ? "rgba(255,255,255,0.05)" : "transparent",
              border: `1px solid ${i===animStep?"rgba(245,166,35,0.4)":i<animStep?"rgba(255,255,255,0.08)":"transparent"}`,
              opacity: i>animStep?0.4:1 }}>
              <div style={{ width:36, height:36, borderRadius:10, background:i===animStep?N.saffron+"25":"rgba(255,255,255,0.08)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{s.icon}</div>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color:i===animStep?N.saffron:N.white }}>{s.label}</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:2 }}>{s.sub}</div>
              </div>
              {i===animStep&&<div style={{ marginLeft:"auto", width:8, height:8, borderRadius:"50%", background:N.saffron, animation:"pulse 1s infinite" }} />}
            </div>
          ))}
          <div style={{ marginTop:16, background:"rgba(255,255,255,0.04)", borderRadius:12, padding:"10px 14px", fontSize:12, color:"rgba(255,255,255,0.4)", textAlign:"center" }}>
            Avg response time: <strong style={{ color:"rgba(255,255,255,0.7)" }}>4 minutes</strong>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" style={{ padding:"80px 48px", maxWidth:1200, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:56 }}>
          <div style={{ fontSize:12, fontWeight:700, color:N.ocean, letterSpacing:"2px", marginBottom:12 }}>HOW IT WORKS</div>
          <h2 style={{ fontSize:36, fontWeight:800, fontFamily:"Georgia,serif", color:N.navy }}>Medicine in 4 simple steps</h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:24 }}>
          {[
            { n:"1", icon:"📱", title:"Type or Upload", desc:"Search for medicines by name or upload your prescription photo." },
            { n:"2", icon:"📡", title:"Quotes Arrive", desc:"Nearby pharmacies receive your request and send competitive quotes." },
            { n:"3", icon:"⚖️", title:"Compare & Pick", desc:"See all quotes side by side. Pick the best price and vendor." },
            { n:"4", icon:"🛵", title:"Delivered to You", desc:"Confirm the order. Pick up from the shop or get it delivered home." },
          ].map(s=>(
            <div key={s.n} className="dz-card" style={{ background:N.white, borderRadius:20, padding:28, border:`1px solid ${N.border}`, boxShadow:"0 2px 12px rgba(0,0,0,0.04)" }}>
              <div style={{ fontSize:32, marginBottom:12 }}>{s.icon}</div>
              <div style={{ fontSize:12, fontWeight:700, color:N.saffron, marginBottom:6 }}>STEP {s.n}</div>
              <div style={{ fontWeight:700, fontSize:16, color:N.navy, marginBottom:8 }}>{s.title}</div>
              <div style={{ fontSize:14, color:N.mid, lineHeight:1.6 }}>{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section style={{ background:N.navy, padding:"80px 48px" }}>
        <div style={{ maxWidth:1200, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:56 }}>
            <h2 style={{ fontSize:36, fontWeight:800, fontFamily:"Georgia,serif", color:N.white }}>Everything you need</h2>
            <p style={{ color:"rgba(255,255,255,0.6)", fontSize:16, marginTop:12 }}>Built for patients in Bangalore — not a generic delivery app</p>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {features.map(f=>(
              <div key={f.title} className="dz-card" style={{ background:"rgba(255,255,255,0.05)", borderRadius:18, padding:24, border:"1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize:28, marginBottom:12 }}>{f.icon}</div>
                <div style={{ fontWeight:700, fontSize:15, color:N.white, marginBottom:8 }}>{f.title}</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.55)", lineHeight:1.7 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOR VENDORS */}
      <section id="vendors" style={{ padding:"80px 48px", maxWidth:1200, margin:"0 auto", display:"flex", gap:64, alignItems:"center" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:12, fontWeight:700, color:N.ocean, letterSpacing:"2px", marginBottom:12 }}>FOR PHARMACIES & LABS</div>
          <h2 style={{ fontSize:36, fontWeight:800, fontFamily:"Georgia,serif", color:N.navy, marginBottom:16 }}>Grow your customer base</h2>
          <p style={{ fontSize:16, color:N.mid, lineHeight:1.8, marginBottom:24 }}>
            Receive medicine requests from nearby patients in real time. Send quotes in seconds. No monthly fees to start.
          </p>
          {["Receive nearby patient requests instantly","Quote and win orders without phone calls","Paid vendors get requests first (priority routing)","Manage orders from a simple mobile app"].map(b=>(
            <div key={b} style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:12 }}>
              <div style={{ width:20, height:20, borderRadius:"50%", background:N.saffron, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:N.navy, flexShrink:0, marginTop:2 }}>✓</div>
              <div style={{ fontSize:15, color:N.navy }}>{b}</div>
            </div>
          ))}
          <a href="mailto:support@denizzy.com" style={{ textDecoration:"none" }}>
            <button className="dz-btn" style={{ marginTop:20, background:N.navy, border:`2px solid ${N.ocean}`, borderRadius:12, padding:"12px 24px", color:N.white, fontWeight:700, fontSize:15, cursor:"pointer", fontFamily:"inherit" }}>
              Register Your Pharmacy →
            </button>
          </a>
        </div>
        <div style={{ flexShrink:0, width:340, background:`linear-gradient(135deg,${N.ocean},${N.navy})`, borderRadius:24, padding:32, color:N.white }}>
          <div style={{ fontSize:12, fontWeight:700, color:N.saffron, letterSpacing:"1px", marginBottom:20 }}>VENDOR DASHBOARD</div>
          {[["New requests today","12"],["Quotes sent","9"],["Orders confirmed","5"],["Earnings this week","₹4,200"]].map(([l,v])=>(
            <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"12px 0", borderBottom:"1px solid rgba(255,255,255,0.1)", fontSize:14 }}>
              <span style={{ color:"rgba(255,255,255,0.65)" }}>{l}</span>
              <span style={{ fontWeight:700 }}>{v}</span>
            </div>
          ))}
          <div style={{ marginTop:20, background:"rgba(245,166,35,0.15)", borderRadius:12, padding:"12px 16px", fontSize:13, color:N.saffron, textAlign:"center", fontWeight:600 }}>
            🟢 Online — receiving requests
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" style={{ background:"#F0F4F8", padding:"64px 48px" }}>
        <div style={{ maxWidth:700, margin:"0 auto", textAlign:"center" }}>
          <h2 style={{ fontSize:30, fontWeight:800, fontFamily:"Georgia,serif", color:N.navy, marginBottom:12 }}>Get in touch</h2>
          <p style={{ fontSize:15, color:N.mid, marginBottom:32 }}>Questions? Partnership enquiries? We respond within 2 business days.</p>
          <div style={{ display:"flex", justifyContent:"center", gap:20, flexWrap:"wrap" }}>
            {[["📧 Email","support@denizzy.com","mailto:support@denizzy.com"],["📍 Location","Bengaluru, Karnataka","#contact"],["🌐 App","denizzy.com","https://denizzy.com"]].map(([l,v,h])=>(
              <a key={l} href={h} style={{ textDecoration:"none", background:N.white, borderRadius:16, padding:"20px 28px", border:`1px solid ${N.border}`, textAlign:"center", minWidth:180 }}>
                <div style={{ fontSize:12, fontWeight:700, color:N.ocean, letterSpacing:"1px", marginBottom:6 }}>{l}</div>
                <div style={{ fontSize:14, color:N.navy, fontWeight:600 }}>{v}</div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ background:N.navy, padding:"28px 48px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div style={{ fontSize:18, fontWeight:800, color:N.white, fontFamily:"Georgia,serif" }}>Den<span style={{ color:N.saffron }}>izzy</span></div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.4)" }}>© 2026 Denizzy · Bengaluru, Karnataka</div>
        <div style={{ display:"flex", gap:20 }}>
          <a href="/privacy-policy.html" style={{ fontSize:13, color:"rgba(255,255,255,0.5)", textDecoration:"none" }}
            onMouseEnter={e=>e.target.style.color="white"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.5)"}>Privacy Policy</a>
          <a href="mailto:support@denizzy.com" style={{ fontSize:13, color:"rgba(255,255,255,0.5)", textDecoration:"none" }}
            onMouseEnter={e=>e.target.style.color="white"} onMouseLeave={e=>e.target.style.color="rgba(255,255,255,0.5)"}>Contact</a>
        </div>
      </footer>
    </div>
  );
}

// ── ONBOARDING — shown once on first login
// ═══════════════════════════════════════════════════════════
const ONBOARDING_SLIDES = [
  {
    bg:      `linear-gradient(160deg, #0A2540 0%, #1A5F7A 100%)`,
    emoji:   "💊",
    title:   "Compare Medicine Prices",
    body:    "Type your medicines or upload a prescription. Nearby pharmacies compete to give you the best price — all in minutes.",
    hint:    "No more calling shops one by one!",
    color:   "#2E9BB5",
  },
  {
    bg:      `linear-gradient(160deg, #065f75 0%, #0891B2 100%)`,
    emoji:   "🔬",
    title:   "Book Lab Tests",
    body:    "Request diagnostic tests from labs near you. Choose home sample collection or visit the lab — at the best price.",
    hint:    "Blood tests, scans, reports — all nearby.",
    color:   "#0891B2",
  },
  {
    bg:      `linear-gradient(160deg, #0F3460 0%, #1A5F7A 100%)`,
    emoji:   "📡",
    title:   "How It Works",
    body:    "Your request goes to nearby shops automatically. Multiple vendors quote you — simply pick the best one before the window closes.",
    hint:    "More you wait, more quotes you get.",
    color:   "#1A5F7A",
  },
  {
    bg:      `linear-gradient(160deg, #1A3A0F 0%, #1A7A5E 100%)`,
    emoji:   "✅",
    title:   "Pickup or Home Delivery",
    body:    "Confirm the quote you like. Pick up from the shop or get it delivered home — your choice per vendor.",
    hint:    "Delivery option is shown per vendor in each quote.",
    color:   "#1A7A5E",
  },
];

function OnboardingScreen({ onDone }) {
  const [slide, setSlide] = useState(0);
  const total = ONBOARDING_SLIDES.length;
  const s = ONBOARDING_SLIDES[slide];
  const isLast = slide === total - 1;

  const next = () => { if (isLast) { onDone(); return; } setSlide(i => i + 1); };
  const prev = () => setSlide(i => Math.max(0, i - 1));

  // Touch swipe support
  const touchRef = useRef(null);
  const onTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchRef.current === null) return;
    const diff = touchRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) < 40) return;
    if (diff > 0 && !isLast) setSlide(i => i + 1);
    else if (diff < 0 && slide > 0) setSlide(i => i - 1);
    touchRef.current = null;
  };

  const btnStyle = {
    border:"none", cursor:"pointer", fontFamily:"inherit",
    touchAction:"manipulation", WebkitTapHighlightColor:"transparent",
    userSelect:"none",
  };

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{ flex:1, display:"flex", flexDirection:"column", background:s.bg,
               position:"relative", overflow:"hidden",
               transition:"background 0.4s ease" }}>

      {/* Skip */}
      <button onClick={onDone} style={{ ...btnStyle, position:"absolute", top:16, right:16, zIndex:10,
        background:"rgba(255,255,255,0.18)", borderRadius:20, padding:"7px 16px",
        color:"rgba(255,255,255,0.9)", fontSize:13, fontWeight:600 }}>
        Skip
      </button>

      {/* Dots */}
      <div style={{ position:"absolute", top:22, left:0, right:0, display:"flex", justifyContent:"center", gap:7, zIndex:5 }}>
        {ONBOARDING_SLIDES.map((_,i)=>(
          <div key={i} onClick={()=>setSlide(i)}
            style={{ width:i===slide?26:7, height:7, borderRadius:4,
                     background:i===slide?"white":"rgba(255,255,255,0.35)",
                     transition:"all 0.25s", cursor:"pointer" }} />
        ))}
      </div>

      {/* Content — opacity transition instead of keyframe animation */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center",
                    justifyContent:"center", padding:"70px 32px 24px", textAlign:"center",
                    transition:"opacity 0.25s ease", opacity:1 }}>

        <div style={{ width:100, height:100, borderRadius:32, background:"rgba(255,255,255,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:52, marginBottom:28, boxShadow:"0 8px 32px rgba(0,0,0,0.2)" }}>
          {s.emoji}
        </div>

        <div style={{ color:"white", fontFamily:"Georgia,serif", fontSize:24,
                      fontWeight:700, lineHeight:1.3, marginBottom:14 }}>
          {s.title}
        </div>
        <div style={{ color:"rgba(255,255,255,0.85)", fontSize:15, lineHeight:1.75, marginBottom:20 }}>
          {s.body}
        </div>
        <div style={{ background:"rgba(255,255,255,0.12)", borderRadius:20, padding:"6px 16px" }}>
          <span style={{ color:"rgba(255,255,255,0.75)", fontSize:12, fontWeight:600 }}>💡 {s.hint}</span>
        </div>
      </div>

      {/* Bottom buttons */}
      <div style={{ padding:"0 24px 52px" }}>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          {slide > 0 && (
            <button onClick={prev} style={{ ...btnStyle,
              background:"rgba(255,255,255,0.18)", borderRadius:14,
              padding:"16px 20px", fontSize:20, color:"white" }}>
              ‹
            </button>
          )}
          <button onClick={next} style={{ ...btnStyle,
            flex:1, background:"white", borderRadius:16, padding:"16px",
            fontSize:16, fontWeight:700, color:s.color||C.deep,
            boxShadow:"0 4px 20px rgba(0,0,0,0.2)",
            transition:"transform 0.1s", }}>
            {isLast ? "🚀 Get Started →" : "Next →"}
          </button>
        </div>
      </div>

      {/* Wave decoration */}
      <svg style={{ position:"absolute", bottom:0, opacity:0.06, pointerEvents:"none" }}
        viewBox="0 0 400 80" preserveAspectRatio="none" height="80" width="100%">
        <path d="M0,40 C80,75 160,5 240,40 C320,75 370,10 400,40 L400,80 L0,80 Z" fill="white"/>
      </svg>
    </div>
  );
}

function SetNameScreen({ onDone }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const save = async () => {
    setLoading(true);
    if (name.trim()) await api("PUT", "/auth/user/profile", { name: name.trim() });
    onDone(name.trim());
    setLoading(false);
  };

  return (
    <div style={{ flex:1, display:"flex", flexDirection:"column", background:C.bg }}>
      <div style={{ background:C.deep, padding:"80px 24px 40px", position:"relative", overflow:"hidden" }}>
        <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 50" preserveAspectRatio="none" height="50" width="100%"><path d="M0,25 C80,48 160,3 240,25 C320,48 370,8 400,25 L400,50 L0,50 Z" fill={C.foam}/></svg>
        <div style={{ position:"relative", textAlign:"center" }}>
          <div style={{ marginBottom:16 }}><Logo size={40} white /></div>
          <div style={{ color:C.white, fontFamily:"Georgia,serif", fontSize:22, fontWeight:700 }}>Welcome to Denizzy! 👋</div>
          <div style={{ color:"rgba(255,255,255,0.6)", fontSize:14, marginTop:8 }}>What should we call you?</div>
        </div>
      </div>
      <div style={{ flex:1, padding:"32px 24px" }}>
        <div style={{ fontSize:13, fontWeight:700, color:C.slate, textTransform:"uppercase", letterSpacing:"1px", marginBottom:10 }}>Your Name</div>
        <input
          value={name} onChange={e=>setName(e.target.value)}
          placeholder="e.g. Rahul Kumar"
          autoFocus
          style={{ width:"100%", background:C.white, border:`2px solid ${name?C.ocean:C.pearl}`, borderRadius:16, padding:"16px 18px", fontSize:16, outline:"none", boxSizing:"border-box", marginBottom:12 }}
        />
        <div style={{ fontSize:12, color:C.mist, marginBottom:32 }}>This helps pharmacists address you personally.</div>
        <Btn full color={C.ocean} onClick={save} loading={loading} disabled={!name.trim()}>
          Continue →
        </Btn>
        <button onClick={()=>onDone("")} style={{ display:"block", width:"100%", background:"none", border:"none", color:C.mist, fontSize:13, cursor:"pointer", marginTop:12, fontFamily:"inherit" }}>
          Skip for now
        </button>
      </div>
    </div>
  );
}

export default function App() {
  // Check before any hooks — isDesktop is stable (doesn't need state)
  const isDesktop = typeof window !== "undefined" && window.innerWidth > 900;
  const hasToken  = !!(ls.get("dz_patient_token"));

  if (isDesktop && !hasToken) {
    return <LandingScreen onGetStarted={()=>{ window.location.reload(); }} />;
  }

  return <PatientApp />;
}

function PatientApp() {
  const [token, setToken] = useState(()=>ls.get("dz_patient_token")||null);
  const [user, setUser] = useState(null);
 const [city, setCity] = useState(()=>getCity()||null);
const [area, setArea] = useState(()=>getArea()||null);
  const [screen, setScreen] = useState(()=>{
    const t = ls.get("dz_patient_token");
    if (!t) return "splash";
    if (!getCity() || !getArea()) return "cityPicker";
    // Restore last tab on refresh
    const lastTab = (() => { try { return sessionStorage.getItem("dz_last_tab") || "home"; } catch { return "home"; } })();
    return lastTab;
  });
  const [tab, setTab] = useState(()=>{
  const path = window.location.pathname;
  if (path === "/privacy" || path === "/terms") return "profile";
  try { return sessionStorage.getItem("dz_last_tab")||"home"; } catch { return "home"; }
});
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [requestItems, setRequestItems] = useState([]);
  const [prescriptionUrl, setPrescriptionUrl] = useState(()=>ls.get("dz_rx_url")||null);
  const [prescriptionAttached, setPrescriptionAttached] = useState(()=>!!(ls.get("dz_rx_url")||ls.get("dz_rx_attached")));
  const [currentRequest, setCurrentRequest] = useState(()=>getStoredRequest());
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [pendingNotifOrder, setPendingNotifOrder] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [ratingOrder, setRatingOrder] = useState(null);
  const [editingReminder, setEditingReminder] = useState(null);
  const [alarmRing, setAlarmRing] = useState(null);
  // Multiple active requests — one per vertical
  const [activeRequests, setActiveRequests] = useState(()=>({
    medical: getStoredRequest("medical"),
    lab: getStoredRequest("lab"),
  }));
  const [cancelTarget, setCancelTarget] = useState(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsDetected, setGpsDetected] = useState(false);
  const [patientCoords, setPatientCoords] = useState(()=>{ try { const c=ls.get("dz_patient_coords"); return c?JSON.parse(c):null; } catch { return null; } });
  const [allVendors, setAllVendors] = useState([]);

  // ── Global reminder engine — always running while app is open, ──────────────
  // ── independent of which screen is mounted. Fixes "didn't alarm on time" ────
  // ── (interval was previously screen-scoped) and Safari audio lock issues. ───
  useEffect(()=>{
    // Request notification permission as early as possible
    try { if (window.Notification && Notification.permission === "default") Notification.requestPermission(); } catch {}
    // Unlock AudioContext on first user interaction anywhere in the app (Safari requirement)
    const unlock = () => { unlockAudio(); };
    window.addEventListener("click", unlock, { passive:true });
    window.addEventListener("touchstart", unlock, { passive:true });
    // Show in-app overlay when alarm fires while app is open
    const onAlarm = (e) => { setAlarmRing(e.detail); };
    window.addEventListener("dz-alarm", onAlarm);
    // Setup Capacitor Local Notifications channel + tap-to-open handler
    (async () => {
      try {
        const { LocalNotifications } = await import("@capacitor/local-notifications");

        // ── Delete and recreate channel ────────────────────────────────────────
        // Android locks channel settings once created — deleting first ensures
        // our updated settings (importance, sound, vibration) actually apply.
        // Safe to do on every launch; recreates immediately.
        try { await LocalNotifications.deleteChannel({ id: "denizzy_reminders" }); } catch {}
        await LocalNotifications.createChannel({
          id:          "denizzy_reminders",
          name:        "Medicine Reminders",
          description: "Alerts when it's time to take your medicine",
          importance:  5,          // IMPORTANCE_HIGH — shows as heads-up notification
          vibration:   true,
          lights:      true,
          lightColor:  "#1A5F7A",  // Denizzy ocean blue
          visibility:  1,          // PUBLIC — shows full content on lock screen
          sound:       "alarm_sound", // custom sound in android/app/src/main/res/raw/alarm_sound.wav
                                      // falls back to default if file missing
        });

        // ── Foreground: local notification fires while app is open ─────────────
        LocalNotifications.addListener("localNotificationReceived", (notification) => {
          try { playAlarm(3); } catch {}
          const { reminderId, time } = notification.extra || {};
          if (reminderId) {
            const rem = getRems().find(r => r.id === reminderId);
            if (rem) setAlarmRing({ ...rem, firedAt: time });
          }
        });

        // ── Tap: patient taps notification (background or killed state) ────────
        // App opens → listener fires → playAlarm() runs immediately
        LocalNotifications.addListener("localNotificationActionPerformed", (action) => {
          try { playAlarm(3); } catch {}
          const { reminderId, time } = action.notification?.extra || {};
          if (reminderId) {
            const rem = getRems().find(r => r.id === reminderId);
            if (rem) setAlarmRing({ ...rem, firedAt: time });
          }
          // Also call checkAndFireReminders in case multiple alarms missed
          try { checkAndFireReminders(); } catch {}
        });

      } catch (e) { console.log("LocalNotifications setup error:", e.message); }
    })();
    // Check every 15s for due reminders — runs at App level so it survives navigation
    checkAndFireReminders();
    const remInterval = setInterval(checkAndFireReminders, 15000);
    return () => {
      window.removeEventListener("click", unlock);
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("dz-alarm", onAlarm);
      clearInterval(remInterval);
    };
  }, []);

  // Alarm overlay action handlers
  const handleAlarmTaken = () => {
    if (!alarmRing) return;
    const today = new Date().toISOString().split("T")[0];
    const rems = getRems().map(r => {
      if (r.id !== alarmRing.id) return r;
      const taken = r.takenLog || {};
      return { ...r, takenLog: { ...taken, [today]: [...(taken[today]||[]), alarmRing.firedAt] } };
    });
    saveRems(rems);
    markTakenOnBackend(alarmRing, alarmRing.firedAt);
    setAlarmRing(null);
  };

  const handleAlarmSnooze = (mins) => {
    if (!alarmRing) return;
    const t = new Date(Date.now() + mins * 60000);
    const hhmm = `${String(t.getHours()).padStart(2,"0")}:${String(t.getMinutes()).padStart(2,"0")}`;
    window.__dzSnoozeMap = window.__dzSnoozeMap || {};
    window.__dzSnoozeMap[alarmRing.id] = hhmm;
    setAlarmRing(null);
  };

  useEffect(()=>{
    api("GET","/vendors").then(res=>{
      if (res.success && res.data) {
        setAllVendors(res.data.filter(v=>v.status==="approved").map(v=>({
          id:v._id, _id:v._id, name:v.name, type:v.vertical,
          area:v.area||"",           // ← was incorrectly v.city
          city:v.city||"",
          pincode:v.pincode||"",
          rating:v.rating>0?v.rating:null,           // null if no real rating yet
          totalOrders:v.totalOrders||0,
          avgResponseTimeMinutes:v.avgResponseTimeMinutes&&v.avgResponseTimeMinutes!==20?v.avgResponseTimeMinutes:null,
          responseTime:v.avgResponseTimeMinutes&&v.avgResponseTimeMinutes!==20?`~${v.avgResponseTimeMinutes} mins`:null,
          isOpen:v.isOnline||false,
          workingHoursObj:v.workingHours||null,      // full object {weekday, weekend}
          workingHours:v.workingHours?.weekday||null,
          capabilities:v.capabilities||{},
          address:v.address||"", phone:v.phone||"",
          isBlurred:v.isBlurred||false, shopPhoto:v.shopPhoto||null,
          drugLicense:v.drugLicense||null, gstin:v.gstin||null,
          location:v.location?.coordinates||null,
          coordinates:v.location?.coordinates||null,
          plan:v.plan||"free",
        })));
      }
    });
  },[]);
  const [referralWelcome, setReferralWelcome] = useState(false);
  const [welcomeVouchers, setWelcomeVouchers] = useState([]);

  const logout = () => {
    clearToken();
    clearStoredRequest();
    setToken(null); setUser(null);
    setScreen("splash");
  };

  const onLogin = (t, userData, isNewUser) => {
    setToken(t);
    if (userData) setUser(userData);
    // If brand-new user — fetch referral vouchers immediately using the fresh token.
    // Using fetch() directly so we can pass the token before setToken propagates.
    // Don't rely on URL params — unreliable in Capacitor after screen navigation.
    if (isNewUser) {
      fetch(`${BASE}/vouchers/my-referral`, {
        headers: { Authorization: `Bearer ${t}` }
      })
        .then(r => r.json())
        .then(res => {
          if (res.success && res.data?.length > 0) {
            setWelcomeVouchers(res.data);
            setReferralWelcome(true);
          }
        })
        .catch(() => {}); // non-fatal — welcome popup is nice-to-have
    }
    // Ask name if first time login (no name set)
    if (!userData?.name) { setScreen("setName"); return; }
    // Show onboarding once if not seen yet
    const alreadyOnboarded = ls.get("dz_onboarded");
    if (!alreadyOnboarded) { setScreen("onboarding"); return; }
    // Go to city picker if city/area not set, else home
    if (!getCity() || !getArea()) setScreen("cityPicker");
    else setScreen("home");
  };

  const onCitySelect = (c, a, lat, lng) => {
    setCity(c);
    setArea(a || c);
    saveCity(c); saveArea(a || c);
    api("PUT", "/auth/user/profile", { city:c, area:a||c });
    if (lat && lng) {
      const coords = [lng, lat];
      setPatientCoords(coords);
      ls.set("dz_patient_coords", JSON.stringify(coords));
    }
    if (token) setScreen("home");
    else setScreen("login");
  };

  const fetchOrders = useCallback(async () => {
    // Check if any delivered orders need rating
    const checkRating = (newOrders) => {
      const ratedIds = JSON.parse(ls.get("dz_rated_orders")||"[]");
      const delivered = newOrders.find(o=>o.status==="delivered"&&!ratedIds.includes(o._id)&&!o.customerRating);
      if (delivered) setRatingOrder(delivered);
    };
    if (!getToken()) return;
    setOrdersLoading(true);
    const res = await api("GET","/orders/my");
    if (res.success && res.data) setOrders(res.data);
    setOrdersLoading(false);
  },[]);

  // ── App-level quote count poller ─────────────────────────────────────────────
  // Polls quote counts for both active requests every 20 seconds.
  // Updates activeRequests[vertical].quoteCount so home screen cards show live counts
  // without patient needing to open BrowseWhileWaiting.
  const activeRequestsRef = useRef(activeRequests);
  useEffect(() => { activeRequestsRef.current = activeRequests; }, [activeRequests]);

  useEffect(() => {
    if (!token) return;
    const pollQuoteCounts = async () => {
      const current = activeRequestsRef.current;
      for (const vertical of ["medical","lab"]) {
        const req = current?.[vertical];
        if (!req?._id || req.status !== "active") continue;
        try {
          const res = await api("GET", `/requests/${req._id}/quotes`);
          if (res.success) {
            const count = Array.isArray(res.data) ? res.data.length : (res.count || 0);
            if (count !== (req.quoteCount || 0)) {
              setActiveRequests(prev => ({
                ...prev,
                [vertical]: prev[vertical] ? { ...prev[vertical], quoteCount: count } : prev[vertical],
              }));
            }
          }
        } catch {}
      }
    };
    pollQuoteCounts();
    const quoteInterval = setInterval(pollQuoteCounts, 20000);
    return () => clearInterval(quoteInterval);
  }, [token]);


  // pendingNotifOrder is set by the pushNotificationActionPerformed listener.
  // We watch it here (inside App) where navigate and setSelectedOrder are available.
  useEffect(()=>{
    if (!pendingNotifOrder || !token) return;
    setSelectedOrder(pendingNotifOrder);
    // Also update this order in the orders list so status is current
    setOrders(prev => {
      const exists = prev.some(o => o._id === pendingNotifOrder._id);
      return exists
        ? prev.map(o => o._id === pendingNotifOrder._id ? pendingNotifOrder : o)
        : [pendingNotifOrder, ...prev];
    });
    navigate("orderDetail");
    setPendingNotifOrder(null);
  },[pendingNotifOrder, token]);

  useEffect(()=>{
    api("GET","/settings/public").then(res=>{
      if (res.success) setSettings(res.data||res);
    });
    if (token) {
      api("GET","/auth/user/me").then(res=>{
        if (res.success && res.data) setUser(res.data);
        else if (res.message?.toLowerCase().includes("unauthorized")) logout();
      });
      fetchOrders();
    }
  },[token]);

  // Socket.IO — real-time order status + vendor location updates
  useEffect(()=>{
    if(!token||!user?._id) return;
    let socket;
    const connect = async()=>{
      if(!window.io){
        await new Promise((res,rej)=>{
          const s=document.createElement("script");
          s.src="https://denizzy.onrender.com/socket.io/socket.io.js";
          s.onload=res; s.onerror=rej; document.head.appendChild(s);
        });
      }
      socket=window.io("https://denizzy.onrender.com",{transports:["websocket","polling"]});
      socket.on("connect",()=>socket.emit("register_user",String(user._id)));
      socket.on("vendor_location",({lat,lng,orderId})=>{
        window.dispatchEvent(new CustomEvent("dz-vendor-loc",{detail:{lat,lng,orderId}}));
      });
      socket.on("order_status",({orderId,status})=>{
        setOrders(prev=>prev.map(o=>o._id===orderId?{...o,status}:o));
      });
    };
    connect().catch(err=>console.log("[WS Patient]",err.message));
    return ()=>{ if(socket) socket.disconnect(); };
  },[token,user?._id]);

// ── Request notification permission + save FCM token ──────────────────────
useEffect(()=>{
  if (!token) return;
  const initFCM = async () => {
  try {
    if (Capacitor.isNativePlatform()) {
      const { PushNotifications } = await import("@capacitor/push-notifications");
      let perm = await PushNotifications.checkPermissions();
      if (perm.receive !== "granted") perm = await PushNotifications.requestPermissions();
      if (perm.receive !== "granted") return;
      await PushNotifications.register();
      PushNotifications.addListener("registration", (token) => {
        api("PUT", "/auth/user/profile", { fcmToken: token.value });
      });
      PushNotifications.addListener("registrationError", (err) => {
        console.log("Push registration error:", err);
      });
      PushNotifications.addListener("pushNotificationReceived", (notification) => {
        try { playAlarm(3); } catch {}
        if (notification.data?.type === "reminder") checkAndFireReminders();
        // Fast UI update — update order status immediately from FCM payload
        // No refetch needed — order state updated in-place
        if (notification.data?.type === "order_status_update" && notification.data?.orderId) {
          const { orderId, status } = notification.data;
          setOrders(prev => prev.map(o => o._id === orderId ? { ...o, status } : o));
        }
      });
      // Tap handler — fires when patient taps notification (background or cold start)
      PushNotifications.addListener("pushNotificationActionPerformed", async (action) => {
        try {
          const data = action.notification?.data || {};
          // New quote arrived — navigate to compare quotes screen
          if (data.type === "new_quote" && data.requestId) {
            const vertical = data.vertical || "medical";
            const req = activeRequestsRef.current?.[vertical];
            if (req?._id) setCurrentRequest(req);
            navigate("browseWhileWaiting");
            return;
          }
          // Order status update — navigate to order detail
          if (data.orderId && ["order_status_update","order_cancelled","order_confirmed"].includes(data.type)) {
            const res = await api("GET", `/orders/${data.orderId}`);
            if (res.success && res.data) setPendingNotifOrder(res.data);
          }
        } catch (e) { console.log("Notif tap error:", e.message); }
      });
      // ── Deep link handler (appUrlOpen) ─────────────────────────────────────
      // Fires for BOTH cold start and warm start when Android opens the app
      // via the denizzy:// intent filter. This is the most reliable path
      // and fires before pushNotificationActionPerformed in cold start.
      const { App: CapApp } = await import("@capacitor/app");
      CapApp.addListener("appUrlOpen", async (event) => {
        try {
          const url = event.url || "";
          // Handle order deep links: denizzy://order/{orderId}
          if (url.startsWith("denizzy://order/")) {
            const orderId = url.replace("denizzy://order/", "").split("?")[0].trim();
            if (orderId) {
              const res = await api("GET", `/orders/${orderId}`);
              if (res.success && res.data) setPendingNotifOrder(res.data);
            }
          }
          // Handle request/quote deep links: denizzy://request/{requestId}
          if (url.startsWith("denizzy://request/")) {
            const requestId = url.replace("denizzy://request/", "").split("?")[0].trim();
            if (requestId) {
              // Find which vertical this request belongs to
              const current = activeRequestsRef.current;
              if (current?.medical?._id === requestId) {
                setCurrentRequest(current.medical);
              } else if (current?.lab?._id === requestId) {
                setCurrentRequest(current.lab);
              }
              navigate("browseWhileWaiting");
            }
          }
        } catch (e) { console.log("appUrlOpen error:", e.message); }
      });
      return;
    }
    const { requestNotificationPermission, onForegroundMessage } = await import("./firebase");
    const fcm = await requestNotificationPermission();
    if (fcm) {
      api("PUT", "/auth/user/profile", { fcmToken: fcm });
    }
    onForegroundMessage(payload => {
      try { playAlarm(3); } catch {}
      if (payload.data?.type === "reminder") checkAndFireReminders();
    });
  } catch(err) {
    console.log("FCM setup error:", err.message);
  }
};
  initFCM();
},[token]);
   // ── Auto-detect GPS on login / app open ──────────────────────
useEffect(()=>{
  if (!token) return;
  setGpsLoading(true);
  detectGPSLocation()
    .then(({ area: detectedArea, city: detectedCity, lat, lng }) => {
      if (detectedCity && detectedArea) {
        saveCity(detectedCity);
        saveArea(detectedArea);
        setCity(detectedCity);
        setArea(detectedArea);
        setGpsDetected(true);
        api("PUT", "/auth/user/profile", { city:detectedCity, area:detectedArea, lat, lng });
        const coords = [lng, lat];
        setPatientCoords(coords);
        ls.set("dz_patient_coords", JSON.stringify(coords));
      }
    })
    .catch(err => console.log("GPS:", err))
    .finally(() => setGpsLoading(false));
},[token]);
   
  // ── Navigation history stack — powers Android back button ────────────────────
  const navHistoryRef = useRef([]);

  const navigate = (s) => {
    const targetScreen = s.startsWith("editReminder_") ? "editReminder" : s;

    // Screens that clear history (root-level destinations)
    const rootScreens = ["home","shops","orders","alerts","reminders","profile","login","splash","onboarding","cityPicker","setName"];
    if (rootScreens.includes(targetScreen)) {
      navHistoryRef.current = [];
    } else {
      // Push current screen to history before navigating
      navHistoryRef.current = [...navHistoryRef.current, screen];
    }

    setScreen(targetScreen);

    if (s.startsWith("editReminder_")) {
      const remId = s.split("_")[1];
      const rem = getRems().find(r=>r.id===remId);
      if (rem) setEditingReminder(rem);
      return;
    }
    // When navigating to browseWhileWaiting, set currentRequest from activeRequests
    if (s === "browseWhileWaiting" && !currentRequest?._id) {
      const med = activeRequests.medical;
      const lab = activeRequests.lab;
      if (med && med.status !== "cancelled") setCurrentRequest(med);
      else if (lab && lab.status !== "cancelled") setCurrentRequest(lab);
    }
    if (["home","shops","orders","alerts","reminders","profile"].includes(s)) {
      setTab(s);
      try { sessionStorage.setItem("dz_last_tab", s); } catch {}
      if (s==="orders") fetchOrders();
    }
  };

  // ── Android hardware back button ──────────────────────────────────────────────
  // Mirrors WhatsApp: goes to previous screen, minimizes when on home
  const cancelTargetRef = useRef(cancelTarget);
  useEffect(() => { cancelTargetRef.current = cancelTarget; }, [cancelTarget]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const handler = CapacitorApp.addListener("backButton", () => {
      // Close any open modals first
      if (cancelTargetRef.current) { setCancelTarget(null); return; }

      // Navigate back through history
      if (navHistoryRef.current.length > 0) {
        const prev = navHistoryRef.current[navHistoryRef.current.length - 1];
        navHistoryRef.current = navHistoryRef.current.slice(0, -1);
        setScreen(prev);
      } else {
        // No history — minimize app (don't close, like WhatsApp)
        CapacitorApp.minimizeApp();
      }
    });

    return () => { handler.then(h => h.remove()).catch(() => {}); };
  }, []); // Set up only once

  const onSetItems = (items, vertical) => {
    setRequestItems(items);
  };

  const onRequestCreated = (req) => {
    const vertical = req?.vertical || "medical";
    setCurrentRequest(req);
    setActiveRequests(prev => ({ ...prev, [vertical]: req }));
    storeRequest(req);
    setPrescriptionAttached(false);
    setPrescriptionUrl(null);
    ls.del("dz_rx_url");
    ls.del("dz_rx_attached");
  };
  // Clear finished request for a vertical
  const clearRequest = (vertical) => {
    clearStoredRequest(vertical);
    setActiveRequests(prev => ({ ...prev, [vertical]: null }));
    if (currentRequest?.vertical === vertical) setCurrentRequest(null);
    // Clear prescription URLs for this vertical
    if (vertical === "lab") ls.del("dz_labs_rx_url");
    if (vertical === "medical") { ls.del("dz_rx_url"); ls.del("dz_rx_attached"); }
  };
  // Check if vertical has active request
  const hasActiveForVertical = (vertical) => {
    const req = activeRequests[vertical];
    if (!req || !req._id) return false;
    if (req.status === "completed" || req.status === "cancelled") return false;
    // Expired requests still count as active — patient can still see and confirm quotes
    return true;
  };
  const isExpiredForVertical = (vertical) => {
    const req = activeRequests[vertical];
    if (!req?._id) return false;
    return req.expiresAt && new Date(req.expiresAt) < new Date();
  };

  // Should we block patient from sending new request for this vertical?
  // Uses quoteCount stored in the request object (updated by BrowseWhileWaiting)
  const shouldBlockVertical = (vertical) => {
    if (!hasActiveForVertical(vertical)) return false;
    if (!isExpiredForVertical(vertical)) return true; // active not expired = block
    const storedCount = activeRequests[vertical] && activeRequests[vertical].quoteCount;
    if (storedCount && storedCount > 0) return true; // expired with quotes = block
    // Expired with no quotes — clear and allow
    return false;
  };

  // Auto-clear expired requests with no quotes
  useEffect(()=>{
    ["medical","lab"].forEach(v => {
      const req = activeRequests[v];
      if (req && req._id && isExpiredForVertical(v) && !(req.quoteCount > 0)) {
        clearRequest(v);
      }
    });
  // eslint-disable-next-line
  }, [activeRequests.medical, activeRequests.lab]);
  const onOrderPlaced = (order) => {
    // Mark this request as ordered so it can never reappear as "active"
    // even if a stray quote-poll re-writes the storage key (FIX #10)
    markRequestOrdered(currentRequest?._id || order?.requestId);
    // Clear the vertical's active request on order placement
    const vertical = currentRequest?.vertical || order?.vertical;
    if (vertical) clearRequest(vertical);
    else { clearStoredRequest(); setCurrentRequest(null); }
    // Also clear prescription URLs
    ls.del("dz_rx_url"); ls.del("dz_rx_attached");
    ls.del("dz_labs_rx_url");
    setOrders(prev=>[order,...prev]);
    // Auto-save delivery address
    if (order.customerDetails?.area) {
      try {
        const addrs = JSON.parse(ls.get("dz_saved_addresses")||"[]");
        const newAddr = { label:"Home", ...order.customerDetails, savedAt: new Date().toISOString() };
        const updated = [newAddr, ...addrs.filter(a=>a.pincode!==newAddr.pincode)].slice(0,5);
        ls.set("dz_saved_addresses", JSON.stringify(updated));
      } catch {}
    }
  };

  const mainScreens = ["home","shops","orders","alerts","reminders","profile"];
  const noNavScreens = ["setName","splash","login","cityPicker","addReminder","onboarding"];

  const renderScreen = () => {
    switch(screen) {
      case "splash": return (
        <div style={{ flex:1, background:C.deep, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
          <svg style={{ position:"absolute", bottom:0, opacity:0.08 }} viewBox="0 0 400 100" preserveAspectRatio="none" height="120" width="100%"><path d="M0,50 C80,90 160,10 240,50 C320,90 370,20 400,50 L400,100 L0,100 Z" fill={C.foam}/></svg>
          <Logo size={64} white />
          <div style={{ color:"rgba(255,255,255,0.5)", fontSize:13, marginTop:12, letterSpacing:"2px", textTransform:"uppercase" }}>Your neighbourhood, at your fingertips</div>
          <Btn style={{ marginTop:48, background:C.saffron, color:C.ink, padding:"14px 40px" }} onClick={()=>navigate("login")}>Get Started →</Btn>
        </div>
      );
      case "login": return <LoginScreen onLogin={onLogin} />;
      case "setName": return <SetNameScreen onDone={(name)=>{ if(name) setUser(u=>({...u,name})); const alreadyOnboarded = ls.get("dz_onboarded"); navigate(alreadyOnboarded ? (getCity()?"home":"cityPicker") : "onboarding"); }} />;
      case "onboarding": return <OnboardingScreen onDone={()=>{ ls.set("dz_onboarded","1"); navigate(getCity()?"home":"cityPicker"); }} />;
      case "cityPicker": return <LocationPickerScreen onSelect={onCitySelect} currentCity={city} currentArea={area} isChanging={false} />;
      case "home": return <HomeScreen navigate={navigate} onSelectOrder={setSelectedOrder} user={user} orders={orders} settings={settings} city={city} area={area} currentRequest={currentRequest} activeRequests={activeRequests} setCurrentRequest={setCurrentRequest} gpsLoading={gpsLoading} gpsDetected={gpsDetected} onChangeLocation={()=>navigate("cityPicker")} />;
     case "shops": return <ShopsScreen navigate={navigate} city={city} area={area} settings={settings} patientCoords={patientCoords} shops={allVendors} />;
      case "medSearch": return shouldBlockVertical("medical")
        ? <ActiveRequestBlocker vertical="medical" existingRequest={activeRequests.medical} isExpired={isExpiredForVertical("medical")} quoteCount={(activeRequests.medical&&activeRequests.medical.quoteCount)||0} onViewRequest={()=>{ setCurrentRequest(activeRequests.medical); navigate("browseWhileWaiting"); }} onCancelRequest={()=>setCancelTarget(activeRequests.medical)} />
        : <MedSearchScreen navigate={navigate} onSetItems={onSetItems} />;
      case "medRequest": return <MedRequestScreen navigate={navigate} requestItems={requestItems} onRequestCreated={onRequestCreated} city={city} area={area} prescriptionUrl={prescriptionUrl} prescriptionAttached={prescriptionAttached} patientCoords={patientCoords} settings={settings} />;
      case "labsLanding": return shouldBlockVertical("lab")
        ? <ActiveRequestBlocker vertical="lab" existingRequest={activeRequests.lab} isExpired={isExpiredForVertical("lab")} quoteCount={(activeRequests.lab&&activeRequests.lab.quoteCount)||0} onViewRequest={()=>{ setCurrentRequest(activeRequests.lab); navigate("browseWhileWaiting"); }} onCancelRequest={()=>setCancelTarget(activeRequests.lab)} />
        : <LabsLandingScreen navigate={navigate} onSetItems={onSetItems} />;
      case "labsPrescription": return <PrescriptionScreen navigate={navigate} vertical="lab" onDone={(url)=>{ if(url){ ls.set("dz_labs_rx_url",url); } navigate("labsRequest"); }} />;
      case "labsRequest": return <LabsRequestScreen navigate={navigate} requestItems={requestItems} onRequestCreated={onRequestCreated} city={city} area={area} patientCoords={patientCoords} settings={settings} />;
      case "prescription": return <PrescriptionScreen navigate={navigate} vertical="medical" onDone={(url)=>{ setPrescriptionUrl(url||null); setPrescriptionAttached(true); if(url) ls.set("dz_rx_url",url); ls.set("dz_rx_attached","true"); navigate("medRequest"); }} />;
     case "noVendors": return <NoVendorsScreen navigate={navigate} area={area} city={city} />;     
 case "browseWhileWaiting": return <BrowseWhileWaiting navigate={navigate} currentRequest={currentRequest} onCancelRequest={(v)=>{ clearRequest(v||currentRequest?.vertical); }} settings={settings} />;
      case "quotes": return <QuotesScreen navigate={navigate} currentRequest={currentRequest} onSelectQuote={(q)=>{ setSelectedQuote(q); navigate("confirmOrder"); }} patientCoords={patientCoords} allVendors={allVendors} />;
      case "confirmOrder": return <ConfirmOrderScreen navigate={navigate} selectedQuote={selectedQuote} currentRequest={currentRequest} onOrderPlaced={onOrderPlaced}  patientCoords={patientCoords} />;
      case "orders": return <OrdersScreen orders={orders} loading={ordersLoading} onSelectOrder={o=>{ setSelectedOrder(o); navigate("orderDetail"); }} onRateOrder={o=>setRatingOrder(o)} navigate={navigate} />;
      case "orderDetail": return <OrderDetailScreen navigate={navigate} order={selectedOrder} onReorder={()=>{}} settings={settings} onOrderCancelled={()=>{ fetchOrders(); navigate("orders"); }} />;
      case "alerts": return <AlertsScreen navigate={navigate} currentRequest={currentRequest} orders={orders} />;
      case "reminders": return <RemindersScreen navigate={navigate} />;
      case "savedPrescriptions": return <SavedPrescriptionsScreen navigate={navigate} />;
      case "addReminder": return <AddReminderScreen navigate={navigate} />;
      case "editReminder": return <AddReminderScreen navigate={navigate} editingReminder={editingReminder} />;
      case "profile": return <ProfileScreen navigate={navigate} user={user} settings={settings} city={city} area={area} onLogout={logout} />;
      case "referral": return <ReferralScreen navigate={navigate} user={user} />;
      case "support": return <SupportScreen settings={settings} navigate={navigate} />;
      default: return <HomeScreen navigate={navigate} user={user} orders={orders} settings={settings} city={city} area={area} currentRequest={currentRequest} activeRequests={activeRequests} setCurrentRequest={setCurrentRequest} gpsLoading={gpsLoading} gpsDetected={gpsDetected} onChangeLocation={()=>navigate("cityPicker")} />;
    }
  };


  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100vh", maxWidth:390, margin:"0 auto", background:C.bg, fontFamily:"system-ui,sans-serif", border:"1px solid #ddd", borderRadius:24, overflow:"hidden", boxShadow:"0 20px 60px rgba(0,0,0,0.15)", position:"relative" }}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0;}input,textarea,button{font-family:inherit;}::-webkit-scrollbar{width:0;}@keyframes slideDown{from{transform:translateY(-100%);opacity:0;}to{transform:translateY(0);opacity:1;}}@keyframes slideInRight{from{transform:translateX(48px);opacity:0;}to{transform:translateX(0);opacity:1;}}@keyframes slideInLeft{from{transform:translateX(-48px);opacity:0;}to{transform:translateX(0);opacity:1;}}@keyframes dz-pulse{0%,100%{box-shadow:0 0 0 0 rgba(26,122,94,0.55)}50%{box-shadow:0 0 0 22px rgba(26,122,94,0)}}`}</style>
      {alarmRing && <AlarmOverlayModal rem={alarmRing} onTaken={handleAlarmTaken} onSnooze={handleAlarmSnooze} onDismiss={()=>setAlarmRing(null)} />}
      {referralWelcome && welcomeVouchers.length > 0 && (
        <div style={{ position:"fixed", inset:0, zIndex:9000, background:"rgba(0,0,0,0.6)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ background:C.white, borderRadius:22, padding:24, width:"100%", maxWidth:340, textAlign:"center", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:48, marginBottom:8 }}>🎁</div>
            <div style={{ fontSize:20, fontWeight:800, color:C.deep, marginBottom:6 }}>Welcome to Denizzy!</div>
            <div style={{ fontSize:13, color:C.slate, marginBottom:20, lineHeight:1.6 }}>
              Your friend gave you a referral bonus. Here are your voucher{welcomeVouchers.length>1?"s":""}:
            </div>
            {welcomeVouchers.map((v,i)=>(
              <div key={i} style={{ background:`linear-gradient(135deg,${C.saffron}20,${C.ocean}08)`, borderRadius:14, padding:"12px 16px", marginBottom:10, border:`1.5px dashed ${C.saffron}60`, textAlign:"left", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontFamily:"monospace", fontSize:16, fontWeight:800, color:C.deep, letterSpacing:1 }}>{v.code}</div>
                  <div style={{ fontSize:11, color:C.slate, marginTop:3 }}>
                    {v.type==="welcome" ? "Use on your first order" : "Referral reward"}
                    {v.vertical && v.vertical!=="both" ? ` · ${v.vertical==="lab"?"🔬 Lab only":"💊 Medical only"}` : ""}
                  </div>
                </div>
                <div style={{ fontSize:18, fontWeight:800, color:C.saffron, marginLeft:10 }}>
                  {v.discountType==="flat"?"₹":""}{v.discountAmount}{v.discountType==="percent"?"%":""} off
                </div>
              </div>
            ))}
            <div style={{ fontSize:11, color:C.mist, marginBottom:16 }}>
              Apply these codes at checkout. You can always find them in Profile → Your Vouchers.
            </div>
            <button onClick={()=>setReferralWelcome(false)}
              style={{ width:"100%", background:C.saffron, border:"none", borderRadius:14, padding:"14px 0", color:C.deep, fontSize:15, fontWeight:700, cursor:"pointer" }}>
              🎉 Got it! Start Shopping
            </button>
          </div>
        </div>
      )}
      {renderScreen()}
      {/* Hamburger menu button — shown on main screens */}
      {mainScreens.includes(screen)&&token&&(
        <button onClick={()=>setSidebarOpen(true)} style={{ position:"absolute", top:14, left:14, zIndex:90, background:C.deep+"EE", border:"none", borderRadius:12, width:40, height:40, display:"flex", flexDirection:"column", justifyContent:"center", alignItems:"center", gap:5, cursor:"pointer", boxShadow:"0 2px 8px rgba(0,0,0,0.25)" }}>
          <div style={{ width:18, height:2, background:"white", borderRadius:1 }}/>
          <div style={{ width:18, height:2, background:"white", borderRadius:1 }}/>
          <div style={{ width:18, height:2, background:"white", borderRadius:1 }}/>
        </button>
      )}
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)} navigate={navigate} user={user} currentRequest={currentRequest} activeRequests={activeRequests} setCurrentRequest={setCurrentRequest} orders={orders} city={city} area={area} onLogout={logout} />
      {ratingOrder&&<RatingPopup order={ratingOrder} onClose={()=>setRatingOrder(null)} onSubmit={async(id,r,rev)=>{ await api("POST",`/orders/${id}/rate`,{rating:r,review:rev}); const rated=JSON.parse(ls.get("dz_rated_orders")||"[]"); ls.set("dz_rated_orders",JSON.stringify([...rated,id])); }} />}
      {cancelTarget&&<CancelRequestModal request={cancelTarget} onKeep={()=>setCancelTarget(null)} onCancel={()=>{ clearRequest(cancelTarget.vertical); setCancelTarget(null); navigate("home"); }} />}
      {mainScreens.includes(screen)&&token&&<BottomNav active={tab} navigate={navigate} remindCount={0} />}
    </div>
  );
}
